import { useState, useRef, useEffect, Component, Fragment } from "react";
// ──────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — theming knobs for the prototype.
// Change values here to restyle large swaths of the app at once.
// A formal design pass will eventually replace these with a proper system.
// ──────────────────────────────────────────────────────────────────────────

// Brand (purple family)
const BRAND = "#7c3aed";
const BRAND_LIGHT = "#ede9fe";
const BRAND_LIGHTER = "#faf5ff";
const BRAND_SOFT = "#a78bfa";
const BRAND_SOFTER = "#c4b5fd";
const BRAND_TINT = "#d8b4fe";
const BRAND_TINT_2 = "#e9d5ff";

// Text
const TEXT_PRIMARY = "#111";
const TEXT_SECONDARY = "#374151";
const TEXT_MUTED = "#6b7280";
const TEXT_SUBTLE = "#9ca3af";
const TEXT_DISABLED = "#d1d5db";
const TEXT_INVERSE = "#fff";

// Surfaces
const SURFACE = "#fff";
const SURFACE_ALT = "#fafafa";
const SURFACE_SUNKEN = "#f9fafb";
const SURFACE_HOVER = "#f3f4f6";

// Borders
const BORDER = "#e5e7eb";
const BORDER_STRONG = "#d1d5db";
const BORDER_SUBTLE = "#f3f4f6";

// Status — Success (green)
const SUCCESS = "#15803d";
const SUCCESS_BG = "#dcfce7";
const SUCCESS_LIGHT = "#86efac";
const SUCCESS_SOFT = "#22c55e";
const SUCCESS_SURFACE = "#f0fdf4";

// Status — Danger (red)
const DANGER = "#dc2626";
const DANGER_SOFT = "#ef4444";
const DANGER_BG = "#fee2e2";
const DANGER_BORDER = "#fca5a5";

// Status — Warning (amber)
const WARNING = "#92400e";
const WARNING_BG = "#fef3c7";
const WARNING_SURFACE = "#fffbeb";
const WARNING_BORDER = "#fcd34d";
const WARNING_ACCENT = "#c2410c";
const WARNING_ACCENT_SURFACE = "#ffedd5";
const WARNING_SOFT = "#fdba74";
const WARNING_DEEP = "#854d0e";
const WARNING_PALE = "#fef9c3";

// Status — Info (blue)
const INFO = "#1d4ed8";
const INFO_BG = "#dbeafe";
const INFO_SURFACE = "#eff6ff";
const INFO_BORDER = "#bfdbfe";
const INFO_DEEP = "#1e40af";
const INFO_ACCENT = "#3b82f6";
const INFO_ACCENT_2 = "#4338ca";
const INFO_ACCENT_SOFT = "#6366f1";
const INFO_ACCENT_TINT = "#e0e7ff";

// Other accents used in badges / highlights
const NEUTRAL_DARK = "#1f2937";
const SLATE = "#475569";
const SLATE_BG = "#f1f5f9";
const TEAL = "#14b8a6";
const TEAL_DEEP = "#0f766e";
const TEAL_BG = "#ccfbf1";
const PINK = "#ec4899";
const PINK_DEEP = "#be185d";
const PINK_BG = "#fce7f3";
const AMBER_ACCENT = "#eab308";
const ORANGE_ACCENT = "#f97316";
const RED_TINT = "#fecaca";

// Shadows / overlays
const SHADOW_SM = "rgba(0,0,0,0.08)";
const SHADOW_MD = "rgba(0,0,0,0.1)";
const SHADOW_LG = "rgba(0,0,0,0.18)";
const OVERLAY_SOFT = "rgba(0,0,0,0.2)";
const OVERLAY = "rgba(0,0,0,0.25)";
const OVERLAY_STRONG = "rgba(0,0,0,0.35)";
const OVERLAY_STRONGER = "rgba(0,0,0,0.4)";
const OVERLAY_MAX = "rgba(0,0,0,0.45)";

// Back-compat aliases — keep these so existing references to P/PL keep working
const P = BRAND;
const PL = BRAND_LIGHT;


const LANGS=["Amharic","Arabic","Ewe","Fula","French","Hausa","Igbo","Kinyarwanda","Mandarin","Oromo","Portuguese","Somali","Spanish","Swahili","Tigrinya","Twi","Wolof","Yoruba","Zulu"];
const TOPIC_OPTIONS=["Any","Business","Culture","Customer Service","Daily Life","Education","Family","Finance","Food","Healthcare","History","Humor","Mental Health","Music","Nature","Pharmacy","Politics","Religion","Science","Sports","Storytelling","Technology","Travel"];
// Reviewer task types — the universe of work a reviewer can be approved for.
// Kept split into TRANSCRIPT_TASKS / AUDIO_TASKS only because batch-creation modals
// pick one bucket at a time; REVIEW_TASKS is the merged list used everywhere else.
const TRANSCRIPT_TASKS=[
  "Full Transcript Review",
  "Full Transcript Review QA",
  "Transcript Review (no diarization)",
  "Transcript Creation",
  "Transcript Creation QA",
  "Diarization Review",
  "Diarization Creation",
  "topic",
  "topic + sentiment",
  "sentiment",
  "NER/PII Recognition",
];
const AUDIO_TASKS=[
  "Audio Review",
  "Post-Model Audio Review",
];
const REVIEW_TASKS=[...TRANSCRIPT_TASKS,...AUDIO_TASKS];
const shortName=n=>{if(!n)return n;const p=n.trim().split(" ");return p.length<2?n:`${p[0]} ${p[p.length-1][0]}.`;};

const Badge=({label,color})=>{const s={purple:{bg:PL,c:P},green:{bg:SUCCESS_BG,c:SUCCESS},orange:{bg:WARNING_ACCENT_SURFACE,c:WARNING_ACCENT},blue:{bg:INFO_BG,c:INFO},gray:{bg:SURFACE_HOVER,c:TEXT_MUTED},red:{bg:DANGER_BG,c:DANGER},yellow:{bg:WARNING_PALE,c:WARNING_DEEP},amber:{bg:WARNING_BG,c:WARNING},teal:{bg:TEAL_BG,c:TEAL_DEEP}}[color]||{bg:SURFACE_HOVER,c:TEXT_MUTED};return <span style={{background:s.bg,color:s.c,fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:999,whiteSpace:"nowrap"}}>{label}</span>;};

const Switcher=({role,setRole})=>(
  <div style={{background:"#1e1b4b",padding:"8px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
    <span style={{color:"#a5b4fc",fontSize:11,fontWeight:600,letterSpacing:"0.1em"}}>VIEWING AS:</span>
    {["Admin","Ops Manager","Reviewer","Reviewer (QA)","Content Creator","Reviewer & Creator"].map(r=>(
      <button key={r} onClick={()=>setRole(r)} style={{padding:"4px 14px",borderRadius:999,border:"none",background:role===r?P:"transparent",color:role===r?SURFACE:"#a5b4fc",fontSize:12,fontWeight:role===r?600:400,cursor:"pointer"}}>{r}</button>
    ))}
    <span style={{marginLeft:"auto",color:INFO_ACCENT_2,fontSize:11}}>Prototype switcher — not part of the product</span>
  </div>
);

const Sidebar=({nav,user,onProfile})=>(
  <div style={{width:176,background:"#f5f3ff",borderRight:`1px solid ${BRAND_LIGHT}`,display:"flex",flexDirection:"column",padding:"14px 0",flexShrink:0}}>
    <div style={{padding:"0 14px 18px",fontWeight:700,fontSize:20,color:P}}>jeenius</div>
    {nav.map(({label,active,onClick,badge})=>(
      <div key={label} onClick={onClick} style={{padding:"7px 14px",margin:"1px 6px",borderRadius:8,background:active?BRAND_LIGHT:"transparent",color:active?P:TEXT_MUTED,cursor:"pointer",fontSize:13,fontWeight:active?500:400,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span>{label}</span>
        {badge>0&&<span style={{background:DANGER,color:SURFACE,fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:999,minWidth:18,textAlign:"center"}}>{badge}</span>}
      </div>
    ))}
    <div style={{marginTop:"auto",padding:"12px 14px",borderTop:`1px solid ${BRAND_LIGHT}`}}>
      <div onClick={onProfile} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"4px 2px"}}>
        <div style={{width:28,height:28,borderRadius:"50%",background:P,color:SURFACE,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600}}>{user.i}</div>
        <div><div style={{fontSize:12,fontWeight:500,color:TEXT_PRIMARY}}>{user.name}</div></div>
      </div>
      <div style={{marginTop:6,fontSize:12,color:TEXT_SUBTLE,cursor:"pointer"}}>→ Log out</div>
    </div>
  </div>
);

const TopicList=({topics})=>{const[show,setShow]=useState(false);const vis=topics.slice(0,3),hid=topics.slice(3);return(<div style={{display:"flex",flexWrap:"wrap",gap:4,alignItems:"center"}}>{vis.map(t=><span key={t} style={{fontSize:11,color:TEXT_MUTED,background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,padding:"2px 7px",borderRadius:4}}>{t}</span>)}{hid.length>0&&<div style={{position:"relative"}}><span onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)} style={{fontSize:11,color:P,background:PL,padding:"2px 7px",borderRadius:4,cursor:"default",fontWeight:600}}>+{hid.length} more</span>{show&&<div style={{position:"absolute",bottom:"calc(100% + 6px)",left:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,padding:"8px 10px",boxShadow:"0 4px 12px rgba(0,0,0,0.1)",zIndex:50,minWidth:130}}>{hid.map(t=><div key={t} style={{fontSize:12,color:TEXT_SECONDARY,whiteSpace:"nowrap",padding:"2px 0"}}>{t}</div>)}</div>}</div>}</div>);};

const savedPartners=["Maria González","Kwame Boateng","Zara Mensah","Diego Fernández"];
const savedPartnerTimezones={
  "Maria González":"Central Time (CT)",
  "Kwame Boateng":"West Africa (WAT)",
  "Zara Mensah":"West Africa (WAT)",
  "Diego Fernández":"Eastern Time (ET)",
};
const allUsers=[...savedPartners,"Fatima Al-Rashid","Chen Wei","Priya Nair","Lucas Oliveira"];
const initialBuddyRequests=[
  {id:"br1",name:"Fatima Al-Rashid",initials:"FA",email:"fatima.al-rashid@example.com",mutualRecordings:0},
  {id:"br2",name:"Lucas Oliveira",initials:"LO",email:"lucas.oliveira@example.com",mutualRecordings:1},
];

const initialCreatorNotifs=[
  {id:"n1",read:false,time:"2 min ago",category:"Recording",title:"Maria G. accepted your invitation",body:"She confirmed for Spanish Conversational Audio – Batch 3 · Daily Life.",action:"recordings"},
  {id:"n2",read:false,time:"18 min ago",category:"Recording",title:"Priya N. invited you to record",body:"She'd like you to join French Medical Dialogues · Healthcare.",action:"recordings"},
  {id:"n3",read:false,time:"1 hr ago",category:"Recording",title:"Kwame B. selected your availability window",body:"He picked Apr 16, 11:00–11:45 AM for Spanish Conversational Audio.",action:"recordings"},
  {id:"n4",read:true,time:"Yesterday",category:"QA",title:"Recording passed QA",body:"Your Spanish · Daily Life recording (TSK-001) has been approved.",action:"history"},
  {id:"n5",read:true,time:"Yesterday",category:"QA",title:"Recording did not pass QA",body:"Your French · Healthcare recording (TSK-005) did not pass QA. Feedback available.",action:"history"},
  {id:"n6",read:true,time:"2 days ago",category:"Payment",title:"Payment sent: $24.60",body:"Payment for 3 approved recordings has been processed to your account.",action:"history"},
  {id:"n7",read:false,time:"3 days ago",category:"Buddies",title:"Fatima A. sent you a Buddy Request",body:"She'd like to be added to your Saved Buddies.",action:"partners"},
  {id:"n8",read:true,time:"4 days ago",category:"Buddies",title:"Maria G. accepted your Buddy Request",body:"She's now in your Saved Buddies list and can be invited to record.",action:"partners"},
  {id:"n9",read:true,time:"5 days ago",category:"Buddies",title:"Diego F. joined Jeenius!",body:"Your referred contact Diego Fernández just created an account.",action:"partners"},
  {id:"n10",read:false,time:"6 days ago",category:"Batch",title:"New batch: Yoruba Community Stories",body:"A new recording batch matching your approved languages has been posted.",action:"main"},
];

const initialOpsNotifs=[
  {id:"on1",read:false,time:"30 min ago",category:"Recording Batch",title:"Batch completed: French Medical Dialogues",body:"All 20 allocated recording hours have been used. The batch is now closed.",action:"main"},
  {id:"on2",read:false,time:"2 hr ago",category:"Deadline",title:"Deadline passed: Swahili Daily Conversations",body:"The deadline for Swahili Daily Conversations – Pilot passed with 50% completion.",action:"main"},
  {id:"on3",read:false,time:"4 hr ago",category:"User Report",title:"User report filed",body:"Diego F. reported an issue with Priya N. Click the arrow to review.",action:"report-detail",report:{reporter:"Diego Fernández",reporterInitials:"DF",reported:"Priya Nair",reportedInitials:"PN",reasons:["Inappropriate behavior"],details:"During our recording session on Apr 14, the other user made several comments that felt inappropriate and off-topic. I felt uncomfortable continuing the session.",time:"4 hours ago",project:"Spanish Conversational Audio – Batch 3",topic:"Daily Life"}},
];
const JeeniusUserDropdown=({value,onChange})=>{const[open,setOpen]=useState(false),[q,setQ]=useState("");const ref=useRef(null);useEffect(()=>{const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);const filtered=savedPartners.filter(u=>u.toLowerCase().includes(q.toLowerCase()));const sel=u=>{onChange(u);setQ(u);setOpen(false);};return(<div ref={ref} style={{position:"relative"}}><input value={q} onChange={e=>{setQ(e.target.value);onChange("");setOpen(true);}} onFocus={()=>setOpen(true)} placeholder="Search your Saved Buddies…" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BRAND_TINT}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none",background:BRAND_LIGHTER}}/>{open&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:60,overflow:"hidden"}}><div style={{padding:"8px 12px 4px",fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>SAVED BUDDIES</div>{filtered.length>0?filtered.map(u=>(<div key={u} onClick={()=>sel(u)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}><div style={{width:28,height:28,borderRadius:"50%",background:PL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:P,flexShrink:0}}>{u.split(" ").map(n=>n[0]).join("")}</div><div><div style={{fontSize:13,fontWeight:500,color:TEXT_PRIMARY}}>{shortName(u)}</div><div style={{fontSize:11,color:TEXT_SUBTLE}}>{u.toLowerCase().replace(" ",".")+"@example.com"}</div></div></div>)):<div style={{padding:"12px",fontSize:13,color:TEXT_SUBTLE,textAlign:"center"}}>{q?"No Buddies match your search":"No Saved Buddies yet"}</div>}</div>}</div>);};

// Simulated pool of discoverable creators. In production this would come from the API,
// filtered to creators approved for the relevant batch language(s).
// Visible info is intentionally limited to first name, last initial, and timezone.
const discoverableCreators=[
  {id:"dc1",displayName:"Fatima A.",timezone:"Central Time (CT)",langs:["Spanish","French"],favoriteTopics:["Culture","Family","Food"]},
  {id:"dc2",displayName:"Lucas O.",timezone:"Eastern Time (ET)",langs:["Spanish","Portuguese"],favoriteTopics:["Sports","Music","Travel"]},
  {id:"dc3",displayName:"Priya N.",timezone:"Pacific Time (PT)",langs:["French","English"],favoriteTopics:["Healthcare","Science","Technology"]},
  {id:"dc4",displayName:"Chen W.",timezone:"Pacific Time (PT)",langs:["Mandarin","Spanish"],favoriteTopics:["Business","Finance","Technology"]},
  {id:"dc5",displayName:"Amara O.",timezone:"West Africa (WAT)",langs:["Spanish","French"],favoriteTopics:["Education","Healthcare","Daily Life"]},
  {id:"dc6",displayName:"Diego F.",timezone:"Central Time (CT)",langs:["Spanish"],favoriteTopics:["Food","Humor","Sports"]},
  {id:"dc7",displayName:"Yuki T.",timezone:"Japan (JST)",langs:["Spanish","Mandarin"],favoriteTopics:["Culture","Storytelling","Travel"]},
  {id:"dc8",displayName:"Aisha M.",timezone:"East Africa (EAT)",langs:["Swahili","French"],favoriteTopics:["Education","Family","Nature"]},
];

const MultiInviteDropdown=({max,selected,onChange,projLangs})=>{
  const[open,setOpen]=useState(false),[q,setQ]=useState("");
  const ref=useRef(null);
  useEffect(()=>{const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);
  // Saved buddies — full names, searchable
  const availableBuddies=savedPartners.filter(u=>!selected.includes(u)&&u.toLowerCase().includes(q.toLowerCase()));
  // Discoverable creators — filtered by batch language if provided, then by search query
  const eligibleDiscoverable=discoverableCreators.filter(dc=>{
    if(projLangs&&projLangs.length>0&&!projLangs.some(l=>dc.langs.includes(l)))return false;
    if(q&&!dc.displayName.toLowerCase().includes(q.toLowerCase())&&!dc.timezone.toLowerCase().includes(q.toLowerCase()))return false;
    // Don't show if already selected by display name
    if(selected.includes(dc.displayName))return false;
    return true;
  });
  const toggle=u=>{if(selected.includes(u)){onChange(selected.filter(x=>x!==u));}else if(selected.length<max){onChange([...selected,u]);}};
  const hasBuddies=availableBuddies.length>0;
  const hasDiscoverable=eligibleDiscoverable.length>0;
  return(<div ref={ref} style={{position:"relative"}}>
    {selected.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>{selected.map(u=><span key={u} style={{display:"flex",alignItems:"center",gap:4,background:PL,color:P,fontSize:12,fontWeight:600,padding:"3px 8px 3px 10px",borderRadius:999}}>{shortName(u)}<span onClick={()=>toggle(u)} style={{cursor:"pointer",fontSize:14,lineHeight:1,color:BRAND_SOFT,marginLeft:1}}>×</span></span>)}</div>}
    <input value={q} onChange={e=>{setQ(e.target.value);setOpen(true);}} onFocus={()=>setOpen(true)} placeholder={selected.length>=max?"Max selected":"Search Buddies or discoverable creators…"} disabled={selected.length>=max} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BRAND_TINT}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none",background:selected.length>=max?SURFACE_SUNKEN:BRAND_LIGHTER,opacity:selected.length>=max?0.6:1}}/>
    {open&&selected.length<max&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:60,overflow:"hidden",maxHeight:280,overflowY:"auto"}}>
      {hasBuddies&&<>
        <div style={{padding:"8px 12px 4px",fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>SAVED BUDDIES</div>
        {availableBuddies.map(u=>(<div key={u} onClick={()=>{toggle(u);setQ("");}} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>
          <div style={{width:28,height:28,borderRadius:"50%",background:PL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:P,flexShrink:0}}>{u.split(" ").map(n=>n[0]).join("")}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:500,color:TEXT_PRIMARY}}>{shortName(u)}</div>
            {savedPartnerTimezones[u]&&<div style={{fontSize:11,color:TEXT_SUBTLE}}>{savedPartnerTimezones[u]}</div>}
          </div>
        </div>))}
      </>}
      {hasDiscoverable&&<>
        <div style={{padding:"8px 12px 4px",fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",borderTop:hasBuddies?`1px solid ${SURFACE_HOVER}`:"none",marginTop:hasBuddies?4:0}}>DISCOVERABLE CREATORS</div>
        {eligibleDiscoverable.map(dc=>(<div key={dc.id} onClick={()=>{toggle(dc.displayName);setQ("");}} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>
          <div style={{width:28,height:28,borderRadius:"50%",background:SUCCESS_SURFACE,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:SUCCESS,flexShrink:0}}>{dc.displayName.split(" ").map(n=>n[0]).join("")}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:500,color:TEXT_PRIMARY}}>{dc.displayName}</div>
            <div style={{fontSize:11,color:TEXT_SUBTLE}}>{dc.timezone}</div>
          </div>
        </div>))}
      </>}
      {!hasBuddies&&!hasDiscoverable&&<div style={{padding:"12px",fontSize:13,color:TEXT_SUBTLE,textAlign:"center"}}>No matches</div>}
    </div>}
  </div>);
};

const InviteNewModal=({onClose})=>{const[name,setName]=useState(""),[email,setEmail]=useState(""),[sent,setSent]=useState(false);return(<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}><div style={{background:SURFACE,borderRadius:12,padding:26,width:400}}>{!sent?<><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}><span style={{fontWeight:600,fontSize:16}}>Invite someone to Jeenius</span><button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button></div><p style={{fontSize:13,color:TEXT_MUTED,marginBottom:16}}>They'll receive an email to create an account and join this task once they sign up.</p><div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}><div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>NAME</div><input value={name} onChange={e=>setName(e.target.value)} placeholder="Their full name" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/></div><div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>EMAIL</div><input value={email} onChange={e=>setEmail(e.target.value)} placeholder="their@email.com" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/></div></div><div style={{display:"flex",justifyContent:"flex-end",gap:8}}><button onClick={onClose} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={()=>setSent(true)} disabled={!email} style={{padding:"8px 16px",borderRadius:8,border:"none",background:email?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:500,cursor:email?"pointer":"default"}}>Send Invite →</button></div></>:<div style={{textAlign:"center",padding:"10px 0 6px"}}><div style={{width:48,height:48,borderRadius:"50%",background:SUCCESS_BG,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,margin:"0 auto 14px"}}>✓</div><div style={{fontWeight:600,fontSize:16,marginBottom:6}}>Invite sent!</div><p style={{fontSize:13,color:TEXT_MUTED,marginBottom:20}}>Invitation sent to <strong>{email}</strong>{name?` (${name})`:""} to join Jeenius.</p><button onClick={onClose} style={{padding:"8px 20px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Done</button></div>}</div></div>);};

// My availability windows — owner creates/removes their own windows.
// Partners viewing a creator's profile see these windows (read-only) and select one to confirm.
const WindowsModal=({onClose,projTitle,partnerName,windows:extWindows,setWindows:extSetWindows})=>{
  // partnerName=undefined → owner view; partnerName=string → partner selecting a window
  const isPartner=!!partnerName;
  // Support either controlled (external) or uncontrolled (internal) windows state
  const[intWindows,setIntWindows]=useState([
    {id:"w1",date:"2025-04-14",start:"10:00",end:"10:45"},
    {id:"w2",date:"2025-04-14",start:"14:00",end:"14:45"},
    {id:"w3",date:"2025-04-15",start:"09:00",end:"09:45"},
    {id:"w4",date:"2025-04-16",start:"11:00",end:"11:45"},
  ]);
  const windows=extWindows||intWindows;
  const setWindows=extSetWindows||setIntWindows;
  const[showAdd,setShowAdd]=useState(false),[newDate,setNewDate]=useState(""),[newStart,setNewStart]=useState(""),[newEnd,setNewEnd]=useState("");
  const[selected,setSelected]=useState([]); // partner multi-select
  const addWindow=()=>{if(newDate&&newStart&&newEnd){setWindows(w=>[...w,{id:`w${Date.now()}`,date:newDate,start:newStart,end:newEnd}]);setNewDate("");setNewStart("");setNewEnd("");setShowAdd(false);}};
  const toggleSelect=id=>setSelected(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);
  const fmtDate=d=>{const dt=new Date(d+"T00:00:00");return dt.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});};
  const fmtTime=t=>{const[h,m]=t.split(":");const hr=parseInt(h);return `${hr>12?hr-12:hr||12}:${m} ${hr>=12?"PM":"AM"}`;};
  const grouped=windows.reduce((acc,w)=>{(acc[w.date]=acc[w.date]||[]).push(w);return acc;},{});
  return(<div style={{position:"fixed",inset:0,background:OVERLAY_MAX,display:"flex",alignItems:"center",justifyContent:"center",zIndex:150}}>
    <div style={{background:SURFACE,borderRadius:12,width:520,maxHeight:"85vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"20px 24px",borderBottom:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontWeight:600,fontSize:16}}>{isPartner?"Choose a Window":"My Availability Windows"}</div>
          <div style={{fontSize:12,color:TEXT_MUTED,marginTop:2}}>{projTitle}</div>
        </div>
        <button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:28,height:28,cursor:"pointer",fontSize:13,color:TEXT_MUTED}}>✕</button>
      </div>
      <div style={{padding:"16px 24px",overflowY:"auto",flex:1}}>
        {isPartner
          ?<p style={{fontSize:13,color:TEXT_MUTED,marginBottom:14}}>Select one or more windows from {partnerName}'s availability that work for you. They'll be notified of your selection.</p>
          :<div style={{marginBottom:14}}>
            <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:12}}>Add the times you're available to record. Potential partners will see these windows and pick one that works for them.</p>
            <button onClick={()=>setShowAdd(v=>!v)} style={{fontSize:13,color:P,background:"none",border:`1px solid ${P}`,borderRadius:7,padding:"6px 14px",cursor:"pointer",fontWeight:500}}>+ Add availability window</button>
            {showAdd&&<TimeRangePicker newDate={newDate} setNewDate={setNewDate} newStart={newStart} setNewStart={setNewStart} newEnd={newEnd} setNewEnd={setNewEnd} onAdd={addWindow} onCancel={()=>setShowAdd(false)}/>}
          </div>
        }
        {Object.keys(grouped).sort().map(date=>(
          <div key={date} style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:600,color:TEXT_SECONDARY,marginBottom:8,paddingBottom:4,borderBottom:`1px solid ${SURFACE_HOVER}`}}>{fmtDate(date)}</div>
            {grouped[date].map(w=>{
              const isSel=selected.includes(w.id);
              return(<div key={w.id} onClick={isPartner?()=>toggleSelect(w.id):undefined}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",
                  border:`1.5px solid ${isSel?BRAND:BORDER}`,borderRadius:8,marginBottom:6,
                  background:isSel?PL:SURFACE,cursor:isPartner?"pointer":"default",transition:"all 0.1s"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  {isPartner&&<div style={{width:18,height:18,borderRadius:4,border:`2px solid ${isSel?P:TEXT_DISABLED}`,background:isSel?P:SURFACE,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:11,color:SURFACE,fontWeight:700}}>{isSel?"✓":""}</div>}
                  <div style={{fontSize:13,fontWeight:500,color:TEXT_PRIMARY}}>{fmtTime(w.start)} – {fmtTime(w.end)}</div>
                </div>
                {!isPartner&&<button onClick={()=>setWindows(ws=>ws.filter(x=>x.id!==w.id))} style={{fontSize:11,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:0}}>Remove</button>}
              </div>);
            })}
          </div>
        ))}
        {windows.length===0&&<div style={{textAlign:"center",color:TEXT_SUBTLE,fontSize:13,padding:"24px 0"}}>No windows added yet. Add your availability above.</div>}
      </div>
      <div style={{padding:"14px 24px",borderTop:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        {isPartner&&selected.length>0
          ?<span style={{fontSize:12,color:P,fontWeight:500}}>{selected.length} window{selected.length>1?"s":""} selected</span>
          :<span/>
        }
        <div style={{display:"flex",gap:8}}>
          {isPartner&&<button onClick={onClose} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>}
          <button onClick={onClose} disabled={isPartner&&selected.length===0} style={{padding:"8px 20px",borderRadius:8,border:"none",background:isPartner&&selected.length===0?BRAND_SOFTER:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:isPartner&&selected.length===0?"default":"pointer"}}>
            {isPartner?`Confirm Selection${selected.length>1?"s":""}`:"Done"}
          </button>
        </div>
      </div>
    </div>
  </div>);
};

const reviewerProfile={name:"Amara Osei",initials:"AO",email:"amara.osei@example.com",roleLabel:"Reviewer",
  capabilities:[
    {task:"Full Transcript Review",lang:"Spanish",kind:"review"},
    {task:"Diarization Review",lang:"Spanish",kind:"review"},
  ]};
const combinedProfile={name:"Amara Osei",initials:"AO",email:"amara.osei@example.com",roleLabel:"Reviewer · Creator",approvedLangs:["Spanish","French"],
  favoriteTopics:["Healthcare","Daily Life","Education"],
  capabilities:[
    {task:"Unscripted Audio Creation",lang:"Spanish",kind:"create"},
    {task:"Unscripted Audio Creation",lang:"French",kind:"create"},
    {task:"Full Transcript Review",lang:"Spanish",kind:"review"},
    {task:"Diarization Review",lang:"Spanish",kind:"review"},
    {task:"Diarization Review",lang:"French",kind:"review"},
  ]};
const creatorProfile={name:"Sofia Reyes",initials:"SR",email:"sofia.reyes@example.com",roleLabel:"Content Creator",approvedLangs:["Spanish","French"],
  favoriteTopics:["Daily Life","Food","Travel"],
  capabilities:[
    {task:"Unscripted Audio Creation",lang:"Spanish",kind:"create"},
    {task:"Unscripted Audio Creation",lang:"French",kind:"create"},
  ]};
const qaReviewerProfile={name:"Marcus Chen",initials:"MC",email:"marcus.chen@example.com",roleLabel:"Reviewer (QA)",
  capabilities:[
    {task:"Full Transcript Review QA",lang:"Spanish",kind:"review"},
    {task:"Full Transcript Review QA",lang:"French",kind:"review"},
    {task:"Transcript Creation QA",lang:"Spanish",kind:"review"},
    {task:"Transcript Creation QA",lang:"French",kind:"review"},
  ]};

function CapabilitiesSection({capabilities}){
  const byLang=capabilities.reduce((acc,c)=>{(acc[c.lang]=acc[c.lang]||[]).push(c);return acc;},{});
  return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:22}}>
    <div style={{fontSize:12,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:16}}>APPROVED CAPABILITIES</div>
    {Object.entries(byLang).map(([lang,caps],li)=>(
      <div key={lang} style={{marginBottom:li<Object.entries(byLang).length-1?18:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}><Badge label={lang} color="purple"/></div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <tbody>{caps.map((c,i)=>(
            <tr key={i} style={{borderBottom:i<caps.length-1?`1px solid ${SURFACE_SUNKEN}`:"none"}}>
              <td style={{padding:"6px 8px 6px 0",color:TEXT_SECONDARY}}>{c.task}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    ))}
  </div>);
}

const TIMEZONES=[
  {label:"Eastern Time (ET)",value:"America/New_York"},
  {label:"Central Time (CT)",value:"America/Chicago"},
  {label:"Mountain Time (MT)",value:"America/Denver"},
  {label:"Pacific Time (PT)",value:"America/Los_Angeles"},
  {label:"Alaska Time (AKT)",value:"America/Anchorage"},
  {label:"Hawaii Time (HT)",value:"Pacific/Honolulu"},
  {label:"GMT / London (BST)",value:"Europe/London"},
  {label:"Central European (CET)",value:"Europe/Paris"},
  {label:"West Africa (WAT)",value:"Africa/Lagos"},
  {label:"East Africa (EAT)",value:"Africa/Nairobi"},
  {label:"South Africa (SAST)",value:"Africa/Johannesburg"},
  {label:"Gulf Standard (GST)",value:"Asia/Dubai"},
  {label:"India (IST)",value:"Asia/Kolkata"},
  {label:"China Standard (CST)",value:"Asia/Shanghai"},
  {label:"Japan (JST)",value:"Asia/Tokyo"},
  {label:"Australia Eastern (AEST)",value:"Australia/Sydney"},
];

function ProfileScreen({profile,discoverable,setDiscoverable,timezone,setTimezone,isCreator}){
  const[avail,setAvail]=useState(true);
  const[favoriteTopics,setFavoriteTopics]=useState(profile.favoriteTopics||[]);
  const[topicDropOpen,setTopicDropOpen]=useState(false);
  const topicDropRef=useRef(null);
  useEffect(()=>{const h=e=>{if(topicDropRef.current&&!topicDropRef.current.contains(e.target))setTopicDropOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);
  const toggleFavTopic=t=>setFavoriteTopics(prev=>prev.includes(t)?prev.filter(x=>x!==t):[...prev,t]);
  const[showDisclosureModal,setShowDisclosureModal]=useState(false);
  const[pendingOn,setPendingOn]=useState(false);

  const handleDiscoverableToggle=()=>{
    if(!discoverable){
      // turning on for first time → show disclosure
      setPendingOn(true);
      setShowDisclosureModal(true);
    } else {
      setDiscoverable(false);
    }
  };
  const confirmDiscoverable=()=>{
    setDiscoverable(true);
    setPendingOn(false);
    setShowDisclosureModal(false);
  };

  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    {/* Discoverability disclosure modal */}
    {showDisclosureModal&&<div style={{position:"fixed",inset:0,background:OVERLAY_MAX,display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}>
      <div style={{background:SURFACE,borderRadius:14,padding:28,width:460,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
        <div style={{fontWeight:700,fontSize:16,marginBottom:6}}>Before you become discoverable</div>
        <p style={{fontSize:13,color:TEXT_MUTED,lineHeight:1.6,marginBottom:12}}>Here's what other creators will — and won't — be able to see:</p>
        <div style={{background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:10,padding:"14px 16px",marginBottom:14,display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <div style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.55}}><strong>Visible to other creators:</strong> your first name, last initial, and selected time zone only. You will only be visible to creators who are approved for the same language(s) as you.</div>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <div style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.55}}><strong>Your email address is not shared</strong> until you and another creator confirm a recording session together — at that point it is shared so you can coordinate.</div>
          </div>
        </div>
        <div style={{background:BRAND_LIGHT,border:`1px solid ${BRAND_SOFTER}`,borderRadius:10,padding:"12px 14px",marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:P,letterSpacing:"0.07em",marginBottom:6}}>WHERE YOU'LL APPEAR</div>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <div style={{display:"flex",gap:8,fontSize:12,color:TEXT_SECONDARY,alignItems:"flex-start"}}><span style={{flexShrink:0}}>→</span><span><strong>When inviting a recording partner</strong> — when another creator uses the search bar in the "Invite a specific Jeenius user" step of the Create / Find a Meeting flow, your name and timezone will appear as an option for batches you're eligible for.</span></div>
            <div style={{display:"flex",gap:8,fontSize:12,color:TEXT_SECONDARY,alignItems:"flex-start"}}><span style={{flexShrink:0}}>→</span><span><strong>My Buddies browse</strong> — other creators can browse and filter discoverable users by timezone and send you a Buddy Request directly, without needing to be in an active batch.</span></div>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>{setShowDisclosureModal(false);setPendingOn(false);}} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={confirmDiscoverable} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Turn on discoverability →</button>
        </div>
      </div>
    </div>}

    <h1 style={{fontSize:21,fontWeight:600,marginBottom:20}}>My Profile</h1>
    <div style={{maxWidth:520,display:"flex",flexDirection:"column",gap:14}}>
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:22}}>
        <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20}}>
          <div style={{width:52,height:52,borderRadius:"50%",background:P,color:SURFACE,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,fontWeight:600}}>{profile.initials}</div>
          <div><div style={{fontWeight:600,fontSize:16}}>{profile.name}</div><div style={{fontSize:13,color:TEXT_MUTED}}>{profile.roleLabel}</div></div>
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:13,color:avail?SUCCESS:TEXT_MUTED,fontWeight:500}}>{avail?"Available":"Unavailable"}</span>
            <div onClick={()=>setAvail(v=>!v)} style={{width:40,height:22,borderRadius:999,background:avail?P:TEXT_DISABLED,cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}><div style={{position:"absolute",top:3,left:avail?19:3,width:16,height:16,borderRadius:"50%",background:SURFACE,transition:"left 0.2s"}}/></div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:"10px 0",fontSize:13,borderTop:`1px solid ${SURFACE_HOVER}`,paddingTop:16}}>
          <span style={{color:TEXT_SUBTLE}}>Email</span><span>{profile.email}</span>
          <span style={{color:TEXT_SUBTLE}}>Member since</span><span>Jan 2024</span>
          <span style={{color:TEXT_SUBTLE,display:"flex",alignItems:"center",paddingTop:2}}>Time zone</span>
          <span>
            <select value={timezone} onChange={e=>setTimezone(e.target.value)} style={{padding:"5px 8px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",color:TEXT_PRIMARY,background:SURFACE,cursor:"pointer"}}>
              {TIMEZONES.map(tz=><option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
          </span>
        </div>
      </div>

      {isCreator&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:22}}>
        <div style={{fontSize:12,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:14}}>DISCOVERABILITY</div>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
          <div style={{flex:1}}>
            <div style={{fontWeight:500,fontSize:13,marginBottom:4}}>Discoverable to other creators</div>
            <p style={{fontSize:12,color:TEXT_MUTED,lineHeight:1.6,margin:0}}>
              {discoverable
                ? "You're visible to other creators in the partner search bar when inviting someone to record and in the Browse Discoverable Creators section of the My Buddies page. Only your first name, last initial, and time zone are shown. Your email is not shared until you confirm a recording session with someone, at which point it's shared so you can coordinate."
                : "You're currently hidden from other creators. Turn this on to appear in the partner search bar when inviting someone to record and in the Browse Discoverable Creators section of the My Buddies page."}
            </p>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5,flexShrink:0}}>
            <div onClick={handleDiscoverableToggle} style={{width:40,height:22,borderRadius:999,background:discoverable?P:TEXT_DISABLED,cursor:"pointer",position:"relative",transition:"background 0.2s"}}><div style={{position:"absolute",top:3,left:discoverable?19:3,width:16,height:16,borderRadius:"50%",background:SURFACE,transition:"left 0.2s"}}/></div>
            <span style={{fontSize:11,color:discoverable?SUCCESS:TEXT_SUBTLE,fontWeight:600}}>{discoverable?"ON":"OFF"}</span>
          </div>
        </div>
      </div>}

      {isCreator&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:22}}>
        <div style={{fontSize:12,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>FAVORITE TOPICS</div>
        <p style={{fontSize:12,color:TEXT_MUTED,lineHeight:1.6,margin:"0 0 12px"}}>
          Topics you enjoy recording. Other creators will see these on your profile when considering you as a buddy, and you can filter by these when browsing potential buddies.
        </p>
        <div ref={topicDropRef} style={{position:"relative",marginBottom:favoriteTopics.length>0?10:0}}>
          <button onClick={()=>setTopicDropOpen(v=>!v)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",border:`1px solid ${favoriteTopics.length>0?P:BORDER}`,borderRadius:8,background:SURFACE,color:TEXT_SECONDARY,fontSize:13,cursor:"pointer",outline:"none"}}>
            <span>{favoriteTopics.length===0?"Select your favorite topics…":`${favoriteTopics.length} topic${favoriteTopics.length!==1?"s":""} selected`}</span>
            <span style={{fontSize:10,color:TEXT_SUBTLE,marginLeft:6}}>▾</span>
          </button>
          {topicDropOpen&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:9,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:60,padding:"6px 0",maxHeight:260,overflowY:"auto"}}>
            {TOPIC_OPTIONS.filter(t=>t!=="Any").map(t=>(
              <label key={t} style={{display:"flex",alignItems:"center",gap:9,padding:"7px 13px",cursor:"pointer",background:favoriteTopics.includes(t)?PL:SURFACE}} onMouseEnter={e=>!favoriteTopics.includes(t)&&(e.currentTarget.style.background=SURFACE_SUNKEN)} onMouseLeave={e=>!favoriteTopics.includes(t)&&(e.currentTarget.style.background=SURFACE)}>
                <input type="checkbox" checked={favoriteTopics.includes(t)} onChange={()=>toggleFavTopic(t)} style={{accentColor:P,flexShrink:0}}/>
                <span style={{fontSize:13,color:favoriteTopics.includes(t)?P:TEXT_PRIMARY,fontWeight:favoriteTopics.includes(t)?500:400}}>{t}</span>
              </label>
            ))}
          </div>}
        </div>
        {favoriteTopics.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:5}}>
          {favoriteTopics.map(t=><span key={t} style={{display:"flex",alignItems:"center",gap:4,background:PL,color:P,fontSize:11,fontWeight:600,padding:"3px 8px 3px 10px",borderRadius:999}}>{t}<span onClick={()=>toggleFavTopic(t)} style={{cursor:"pointer",fontSize:13,lineHeight:1,color:BRAND_SOFT,marginLeft:2}}>×</span></span>)}
        </div>}
      </div>}

      <CapabilitiesSection capabilities={profile.capabilities}/>
    </div>
  </div>);
}

// Select-based time picker — guaranteed 5-minute increments regardless of browser
const MINS_5=["00","05","10","15","20","25","30","35","40","45","50","55"];
const HOURS_12=["12","1","2","3","4","5","6","7","8","9","10","11"];
const TimeSelect=({value,onChange,label,hasError})=>{
  const parseVal=v=>{if(!v)return{h:"",m:"",ap:"AM"};const[hh,mm]=v.split(":");const hr=parseInt(hh);return{h:(hr%12||12).toString(),m:mm,ap:hr>=12?"PM":"AM"};};
  const{h,m,ap}=parseVal(value);
  const emit=(nh,nm,nap)=>{if(!nh||!nm)return;let h24=parseInt(nh);if(nap==="AM"){if(h24===12)h24=0;}else{if(h24!==12)h24+=12;}onChange(`${String(h24).padStart(2,"0")}:${nm}`);};
  const sel={padding:"6px 7px",border:`1px solid ${hasError?DANGER_BORDER:BORDER}`,borderRadius:6,fontSize:12,outline:"none",background:SURFACE,cursor:"pointer"};
  return(<div><div style={{fontSize:10,color:TEXT_SUBTLE,fontWeight:600,marginBottom:3}}>{label}</div>
    <div style={{display:"flex",alignItems:"center",gap:3}}>
      <select value={h} onChange={e=>emit(e.target.value,m||"00",ap)} style={sel}>
        <option value="">--</option>{HOURS_12.map(x=><option key={x}>{x}</option>)}
      </select>
      <span style={{color:TEXT_SUBTLE,fontSize:12}}>:</span>
      <select value={m} onChange={e=>emit(h||"12",e.target.value,ap)} style={sel}>
        <option value="">--</option>{MINS_5.map(x=><option key={x}>{x}</option>)}
      </select>
      <select value={ap} onChange={e=>emit(h||"12",m||"00",e.target.value)} style={sel}>
        <option>AM</option><option>PM</option>
      </select>
    </div>
  </div>);
};

// Reusable time range picker with end-before-start validation
const TimeRangePicker=({newDate,setNewDate,newStart,setNewStart,newEnd,setNewEnd,onAdd,onCancel})=>{
  const timeError=newStart&&newEnd&&newEnd<=newStart;
  return(<div style={{background:SURFACE,borderRadius:7,padding:12,marginTop:8,display:"flex",flexDirection:"column",gap:10,border:`1px solid ${BORDER}`}}>
    <div><div style={{fontSize:10,color:TEXT_SUBTLE,fontWeight:600,marginBottom:3}}>DATE</div>
      <input type="date" value={newDate} onChange={e=>setNewDate(e.target.value)} style={{padding:"6px 9px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,outline:"none",width:"100%",boxSizing:"border-box"}}/>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
      <TimeSelect value={newStart} onChange={setNewStart} label="START TIME"/>
      <TimeSelect value={newEnd} onChange={setNewEnd} label="END TIME" hasError={timeError}/>
    </div>
    {timeError&&<div style={{fontSize:11,color:DANGER}}>End time must be after start time.</div>}
    <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
      <button onClick={onCancel} style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:11,cursor:"pointer"}}>Cancel</button>
      <button onClick={onAdd} disabled={!newDate||!newStart||!newEnd||timeError} style={{padding:"5px 12px",borderRadius:6,border:"none",background:(!newDate||!newStart||!newEnd||timeError)?BRAND_SOFTER:P,color:SURFACE,fontSize:11,fontWeight:500,cursor:(!newDate||!newStart||!newEnd||timeError)?"default":"pointer"}}>Add</button>
    </div>
  </div>);
};
const initialOtherUsersWindows=[
  {userId:"u6",name:"Maria González",initials:"MG",windows:[
    {id:"og1",date:"2025-04-14",start:"10:00",end:"10:45"},
    {id:"og2",date:"2025-04-15",start:"14:00",end:"14:45"},
  ]},
  {userId:"u7",name:"Diego Fernández",initials:"DF",windows:[
    {id:"og3",date:"2025-04-14",start:"16:00",end:"16:45"},
  ]},
  {userId:"u8",name:"Priya Nair",initials:"PN",windows:[
    {id:"og4",date:"2025-04-16",start:"09:00",end:"09:45"},
    {id:"og5",date:"2025-04-16",start:"11:00",end:"11:45"},
    {id:"og6",date:"2025-04-17",start:"10:00",end:"10:45"},
  ]},
];

function CreateFindMeetingScreen({proj,profile,onBack,onClaim}){
  const[partnerMethod,setPartnerMethod]=useState("invite"); // default to first option
  const[windowsTab,setWindowsTab]=useState("mine");
  const[topic,setTopic]=useState(proj.topics[0]);
  const[myWindows,setMyWindows]=useState([]); // start empty — user must add their own
  const[otherWindows,setOtherWindows]=useState(initialOtherUsersWindows);
  const[showAdd,setShowAdd]=useState(false),[newDate,setNewDate]=useState(""),[newStart,setNewStart]=useState(""),[newEnd,setNewEnd]=useState("");
  const[selectedOther,setSelectedOther]=useState(null);
  const[invitees,setInvitees]=useState([]);
  const[inviteeName,setInviteeName]=useState(""),[inviteeEmail,setInviteeEmail]=useState(""),[inviteeSent,setInviteeSent]=useState(false);
  const maxInvites=(proj.numParticipants||2)-1;
  const[windowsAckDone,setWindowsAckDone]=useState(false);
  const[showWindowsAck,setShowWindowsAck]=useState(false);
  const[showSlotEmailWarn,setShowSlotEmailWarn]=useState(false);
  const[slotEmailConfirmed,setSlotEmailConfirmed]=useState(false);

  const addWindow=()=>{if(newDate&&newStart&&newEnd){setMyWindows(w=>[...w,{id:`mw${Date.now()}`,date:newDate,start:newStart,end:newEnd}]);setNewDate("");setNewStart("");setNewEnd("");setShowAdd(false);}};
  const removeWindow=id=>setMyWindows(ws=>ws.filter(w=>w.id!==id));
  const fmtDate=d=>{const dt=new Date(d+"T00:00:00");return dt.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});};
  const fmtTime=t=>{const[h,m]=t.split(":");const hr=parseInt(h);return `${hr>12?hr-12:hr||12}:${m} ${hr>=12?"PM":"AM"}`;};

  const totalOthers=otherWindows.reduce((n,u)=>n+u.windows.length,0);

  // Validation: posting windows requires at least one window
  const canConfirm=
    (partnerMethod==="invite"&&invitees.length>=maxInvites)||
    (partnerMethod==="inviteNew"&&inviteeSent)||
    (partnerMethod==="windows"&&windowsTab==="mine"&&myWindows.length>0)||
    (partnerMethod==="windows"&&windowsTab==="others"&&selectedOther);

  const doConfirm=()=>{
    if(partnerMethod==="windows"&&windowsTab==="others"&&selectedOther){
      setOtherWindows(prev=>prev.map(u=>u.userId===selectedOther.userId
        ?{...u,windows:u.windows.filter(w=>w.id!==selectedOther.windowId)}
        :u
      ).filter(u=>u.windows.length>0));
    }
    onClaim({proj,topic,
      partner:partnerMethod==="invite"?invitees[0]||null:partnerMethod==="windows"&&selectedOther?selectedOther.name:null,
      partners:partnerMethod==="invite"&&invitees.length>1?invitees:null,
      window:selectedOther?.window||null,
    });
  };

  const handleConfirm=()=>{
    if(partnerMethod==="windows"&&windowsTab==="others"&&selectedOther&&!slotEmailConfirmed){
      setShowSlotEmailWarn(true);
      return;
    }
    doConfirm();
  };

  const confirmLabel=
    partnerMethod==="invite"&&invitees.length>0?`Invite ${invitees.map(shortName).join(" + ")} →`
    :partnerMethod==="inviteNew"?"Send Invite & Continue →"
    :partnerMethod==="windows"&&windowsTab==="others"&&selectedOther?`Take this slot with ${shortName(selectedOther.name)} →`
    :partnerMethod==="windows"&&windowsTab==="mine"&&myWindows.length>0?"Post Availability & Continue →"
    :"Save & Continue →";

  const RadioCard=({id,checked,onChange,title,sub,children})=>(
    <div style={{border:`1.5px solid ${checked?P:BORDER}`,borderRadius:9,marginBottom:8,background:checked?PL:SURFACE}}>
      <label style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 13px",cursor:"pointer"}}>
        <input type="radio" checked={checked} onChange={onChange} style={{accentColor:P,marginTop:2}}/>
        <div style={{flex:1}}><div style={{fontWeight:500,fontSize:13}}>{title}</div>{sub&&<div style={{fontSize:12,color:TEXT_SUBTLE,marginTop:1}}>{sub}</div>}</div>
      </label>
      {checked&&children&&<div style={{padding:"0 13px 13px",borderTop:`1px solid ${BRAND_TINT_2}`,paddingTop:10,background:BRAND_LIGHTER}}>{children}</div>}
    </div>
  );

  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back</button>
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:4}}>Create / Find a Meeting</h1>
    <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:22}}>{proj.title}</p>
    <div style={{maxWidth:540}}>
      {/* Task details */}
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:18,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>TASK DETAILS</div>
        <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:"8px 0",fontSize:13}}>
          <span style={{color:TEXT_SUBTLE}}>Format</span><span><Badge label={proj.scripted?"Scripted":"Unscripted"} color={proj.scripted?"blue":"teal"}/></span>
          <span style={{color:TEXT_SUBTLE}}>Languages</span><span>{proj.langs.filter(l=>profile.approvedLangs.includes(l)).join(" ↔ ")}</span>
          <span style={{color:TEXT_SUBTLE}}>Duration</span><span>{proj.durationRange||"20–45 min"}</span>
          <span style={{color:TEXT_SUBTLE}}>Deadline</span><span>{proj.deadline}</span>
          <span style={{color:TEXT_SUBTLE}}>Topic</span><span><select value={topic} onChange={e=>setTopic(e.target.value)} style={{padding:"3px 7px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:13}}>{proj.topics.map(t=><option key={t}>{t}</option>)}</select></span>
        </div>
      </div>

      {/* Partner options — order: invite specific, post/browse windows, invite new */}
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:18,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:12}}>FIND A RECORDING PARTNER</div>

        {/* 1. Invite specific Jeenius user */}
        <RadioCard id="invite" checked={partnerMethod==="invite"} onChange={()=>setPartnerMethod("invite")}
          title="Invite a specific Jeenius user" sub={maxInvites>1?`Select up to ${maxInvites} people`:"Search by name or email"}>
          <MultiInviteDropdown
            max={maxInvites}
            selected={invitees}
            onChange={setInvitees}
            projLangs={proj.langs}
          />
          {invitees.length>0&&invitees.length<maxInvites&&<div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:6}}>Select {maxInvites-invitees.length} more to continue</div>}
        </RadioCard>

        {/* 2. Post or browse availability windows */}
        <RadioCard id="windows" checked={partnerMethod==="windows"} onChange={()=>setPartnerMethod("windows")}
          title="Post or browse availability windows" sub="Post your own times, or take an open slot someone else posted">
          <div style={{display:"flex",gap:0,borderBottom:`1px solid ${BRAND_TINT_2}`,marginBottom:12}}>
            {[["mine","My Availability"],["others",`Others' Windows${totalOthers>0?" ("+totalOthers+")":""}`]].map(([k,l])=>(
              <button key={k} onClick={()=>setWindowsTab(k)} style={{padding:"7px 14px",border:"none",background:"none",color:windowsTab===k?P:TEXT_MUTED,fontSize:12,fontWeight:windowsTab===k?600:400,cursor:"pointer",borderBottom:windowsTab===k?`2px solid ${P}`:"2px solid transparent",marginBottom:-1}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:10}}>All times in <strong style={{color:TEXT_SECONDARY}}>Central Time (CT)</strong></div>

          {windowsTab==="mine"&&<>
            {showWindowsAck&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}>
              <div style={{background:SURFACE,borderRadius:12,padding:28,width:440,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
                <div style={{fontWeight:600,fontSize:16,marginBottom:14}}>Before you post availability</div>
                <p style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.7,marginBottom:10}}>Posting a window makes your <strong>first name visible</strong> to other users eligible for this batch who are also looking for a partner.</p>
                <p style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.7,marginBottom:24}}>If another user accepts your time slot, you will be able to <strong>see each other's email addresses</strong> so that you can coordinate.</p>
                <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                  <button onClick={()=>setShowWindowsAck(false)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
                  <button onClick={()=>{setWindowsAckDone(true);setShowWindowsAck(false);setShowAdd(true);}} style={{padding:"8px 16px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>I understand — continue</button>
                </div>
              </div>
            </div>}
            {myWindows.length>0&&<div style={{marginBottom:10}}>
              {Object.entries(myWindows.reduce((acc,w)=>{(acc[w.date]=acc[w.date]||[]).push(w);return acc;},{})).sort().map(([date,ws])=>(
                <div key={date} style={{marginBottom:8}}>
                  <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,marginBottom:4,textTransform:"uppercase"}}>{fmtDate(date)}</div>
                  {ws.map(w=>(
                    <div key={w.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,marginBottom:4,background:SURFACE}}>
                      <span style={{fontSize:12,fontWeight:500}}>{fmtTime(w.start)} – {fmtTime(w.end)}</span>
                      <button onClick={()=>removeWindow(w.id)} style={{fontSize:11,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer"}}>Remove</button>
                    </div>
                  ))}
                </div>
              ))}
            </div>}
            <button onClick={()=>{if(!windowsAckDone){setShowWindowsAck(true);}else{setShowAdd(v=>!v);}}} style={{fontSize:12,color:P,background:"none",border:`1px solid ${P}`,borderRadius:6,padding:"6px 12px",cursor:"pointer",fontWeight:500}}>+ Add availability window</button>
            {showAdd&&<TimeRangePicker newDate={newDate} setNewDate={setNewDate} newStart={newStart} setNewStart={setNewStart} newEnd={newEnd} setNewEnd={setNewEnd} onAdd={addWindow} onCancel={()=>setShowAdd(false)}/>}
            {myWindows.length===0&&!showAdd&&<p style={{fontSize:11,color:TEXT_SUBTLE,margin:"6px 0 0"}}>No windows posted yet. Add one to let others see when you're available.</p>}
            {partnerMethod==="windows"&&windowsTab==="mine"&&myWindows.length===0&&<div style={{fontSize:11,color:DANGER,marginTop:6}}>You must add at least one window before continuing.</div>}
          </>}

          {windowsTab==="others"&&<>
            {showSlotEmailWarn&&selectedOther&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}>
              <div style={{background:SURFACE,borderRadius:12,padding:28,width:420,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
                <div style={{fontWeight:600,fontSize:16,marginBottom:14}}>Before you take this slot</div>
                <p style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.7,marginBottom:24}}>If you take this slot, you and <strong>{shortName(selectedOther.name)}</strong> will be able to see each other's email addresses so that you can coordinate.</p>
                <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                  <button onClick={()=>setShowSlotEmailWarn(false)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
                  <button onClick={()=>{setSlotEmailConfirmed(true);setShowSlotEmailWarn(false);doConfirm();}} style={{padding:"8px 16px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>I understand — continue</button>
                </div>
              </div>
            </div>}
            {otherWindows.length===0&&<p style={{fontSize:12,color:TEXT_SUBTLE}}>No one else has posted availability for this project yet.</p>}
            {otherWindows.map(u=>{
              const isBuddy=savedPartners.includes(u.name);
              return(<div key={u.userId} style={{marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <div style={{width:26,height:26,borderRadius:"50%",background:isBuddy?PL:SUCCESS_SURFACE,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,color:isBuddy?P:SUCCESS,flexShrink:0}}>{u.initials}</div>
                  <span style={{fontSize:12,fontWeight:500}}>{shortName(u.name)}</span>
                  {isBuddy
                    ?<span style={{fontSize:10,fontWeight:600,background:PL,color:P,padding:"1px 7px",borderRadius:999}}>Buddy</span>
                    :<span style={{fontSize:10,fontWeight:600,background:SUCCESS_SURFACE,color:SUCCESS,padding:"1px 7px",borderRadius:999}}>Discoverable</span>
                  }
                </div>
                {Object.entries(u.windows.reduce((acc,w)=>{(acc[w.date]=acc[w.date]||[]).push(w);return acc;},{})).sort().map(([date,ws])=>(
                  <div key={date} style={{marginBottom:6,paddingLeft:34}}>
                    <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,marginBottom:4,textTransform:"uppercase"}}>{fmtDate(date)}</div>
                    {ws.map(w=>{
                      const isSel=selectedOther?.userId===u.userId&&selectedOther?.windowId===w.id;
                      return(<div key={w.id} onClick={()=>setSelectedOther(isSel?null:{userId:u.userId,windowId:w.id,name:u.name,window:w})}
                        style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",border:`1.5px solid ${isSel?P:BORDER}`,borderRadius:7,marginBottom:4,background:isSel?PL:SURFACE,cursor:"pointer"}}>
                        <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${isSel?P:TEXT_DISABLED}`,background:isSel?P:SURFACE,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:SURFACE,fontWeight:700}}>{isSel?"✓":""}</div>
                        <span style={{fontSize:12,fontWeight:500}}>{fmtTime(w.start)} – {fmtTime(w.end)}</span>
                      </div>);
                    })}
                  </div>
                ))}
              </div>);
            })}
          </>}
        </RadioCard>

        {/* 3. Invite someone new to Jeenius */}
        <RadioCard id="inviteNew" checked={partnerMethod==="inviteNew"} onChange={()=>setPartnerMethod("inviteNew")}
          title="Invite someone new to Jeenius" sub="They'll receive a link to set up an account">
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div>
              <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.07em",marginBottom:4}}>THEIR NAME</div>
              <input value={inviteeName} onChange={e=>setInviteeName(e.target.value)} placeholder="Full name" style={{width:"100%",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,boxSizing:"border-box",outline:"none"}}/>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.07em",marginBottom:4}}>THEIR EMAIL <span style={{color:DANGER}}>*</span></div>
              <input type="email" value={inviteeEmail} onChange={e=>setInviteeEmail(e.target.value)} placeholder="their@email.com" style={{width:"100%",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,boxSizing:"border-box",outline:"none"}}/>
            </div>
            {inviteeSent&&<div style={{fontSize:12,color:SUCCESS,fontWeight:500}}>✓ Invite sent to {inviteeEmail}</div>}
            {!inviteeSent&&<button onClick={()=>inviteeEmail&&setInviteeSent(true)} disabled={!inviteeEmail} style={{padding:"6px 14px",borderRadius:7,border:"none",background:inviteeEmail?P:BRAND_SOFTER,color:SURFACE,fontSize:12,fontWeight:500,cursor:inviteeEmail?"pointer":"default",alignSelf:"flex-start"}}>Send Invite →</button>}
          </div>
        </RadioCard>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button onClick={onBack} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:14,cursor:"pointer"}}>Cancel</button>
        <button onClick={handleConfirm} disabled={!canConfirm} style={{flex:2,padding:"10px",borderRadius:8,border:"none",background:canConfirm?P:BRAND_SOFTER,color:SURFACE,fontSize:14,fontWeight:500,cursor:canConfirm?"pointer":"default"}}>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>);
}

function RecorderScreen({task,myName,myInitials,onBack,onFinish,onFinishAndEnd,onFinishAndRecordAgain,batchTopics,isUnscripted}){
  const[phase,setPhase]=useState("pre"),[t,setT]=useState(0),[ref,setRef]=useState(null);
  const[muted,setMuted]=useState(false),[videoOn,setVideoOn]=useState(true),[discardModal,setDiscardModal]=useState(false),[videoBanner,setVideoBanner]=useState(false);
  const[ackDone,setAckDone]=useState(!isUnscripted);
  const[backWarnModal,setBackWarnModal]=useState(false);
  const[playback,setPlayback]=useState(false);
  const[recordAgainModal,setRecordAgainModal]=useState(false);
  const[recordAgainTopic,setRecordAgainTopic]=useState(task.topic);
  const[showSubmitConfirm,setShowSubmitConfirm]=useState(false);
  const[pendingSubmitFn,setPendingSubmitFn]=useState(null);
  const hasTopics=(batchTopics||[]).length>1;
  const REPORT_REASONS=["Verbal abuse","Inappropriate behavior","Suspected fraud"];
  const startRec=()=>{setPhase("recording");const r=setInterval(()=>setT(s=>s+1),1000);setRef(r);};
  const stopRec=()=>{setPhase("stopped");clearInterval(ref);};
  const finish=()=>onFinish();
  const handleVideoToggle=()=>{if(!videoOn){setVideoBanner(true);setTimeout(()=>setVideoBanner(false),4000);}setVideoOn(v=>!v);};
  const handleBack=()=>{if(phase==="stopped"){setBackWarnModal(true);}else{onBack();}};
  const fmt=s=>`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto",display:"flex",flexDirection:"column"}}>
    {/* Submit confirmation modal */}
    {showSubmitConfirm&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:350}}>
      <div style={{background:SURFACE,borderRadius:12,padding:28,width:380,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
        <div style={{fontWeight:600,fontSize:16,marginBottom:8}}>Submit this recording?</div>
        <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:24,lineHeight:1.6}}>Once submitted, this recording will be sent for review. You won't be able to make changes.</p>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={()=>{setShowSubmitConfirm(false);setPendingSubmitFn(null);}} style={{padding:"8px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={()=>{setShowSubmitConfirm(false);if(pendingSubmitFn)pendingSubmitFn();setPendingSubmitFn(null);}} style={{padding:"8px 18px",borderRadius:8,border:"none",background:SUCCESS,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Yes, submit</button>
        </div>
      </div>
    </div>}
    {recordAgainModal&&<div style={{position:"fixed",inset:0,background:"rgba(17,24,39,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}>
      <div style={{background:SURFACE,borderRadius:16,padding:28,width:420,boxShadow:"0 16px 48px rgba(0,0,0,0.3)"}}>
        <div style={{fontWeight:700,fontSize:17,marginBottom:6}}>Start next recording?</div>
        <p style={{fontSize:13,color:TEXT_MUTED,lineHeight:1.6,marginBottom:hasTopics?20:24}}>
          {task.solo
            ?"Your current recording will be submitted. You can start the next one right away."
            :"Your current recording will be submitted. Decide on your next topic with your partners and then start when you all are ready."}
        </p>
        {hasTopics&&<div style={{marginBottom:22}}>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>TOPIC FOR NEXT RECORDING</div>
          <select value={recordAgainTopic} onChange={e=>setRecordAgainTopic(e.target.value)} style={{width:"100%",padding:"9px 11px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",color:TEXT_PRIMARY}}>
            {(batchTopics||[]).map(top=><option key={top} value={top}>{top}</option>)}
          </select>
        </div>}
        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>{setRecordAgainModal(false);onFinishAndRecordAgain(recordAgainTopic);}} style={{flex:2,padding:"11px",borderRadius:9,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Submit &amp; Start Next →</button>
          <button onClick={()=>setRecordAgainModal(false)} style={{flex:1,padding:"11px",borderRadius:9,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
        </div>
      </div>
    </div>}
    {!ackDone&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400}}>
      <div style={{background:SURFACE,borderRadius:14,padding:32,width:420,textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
        <div style={{width:56,height:56,borderRadius:"50%",background:"#f5f3ff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,margin:"0 auto 16px"}}></div>
        <div style={{fontWeight:700,fontSize:18,marginBottom:10}}>Unscripted Recording</div>
        <p style={{fontSize:14,color:TEXT_SECONDARY,lineHeight:1.6,marginBottom:8}}>This session must be <strong>completely unscripted.</strong></p>
        <p style={{fontSize:13,color:TEXT_MUTED,lineHeight:1.6,marginBottom:24}}>Do not read from a script, notes, teleprompter, or any written material. Natural, spontaneous conversation only.</p>
        <button onClick={()=>setAckDone(true)} style={{width:"100%",padding:"11px",borderRadius:9,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:600,cursor:"pointer"}}>I understand — continue to recorder</button>
      </div>
    </div>}
    {backWarnModal&&<div style={{position:"fixed",inset:0,background:OVERLAY_MAX,display:"flex",alignItems:"center",justifyContent:"center",zIndex:350}}>
      <div style={{background:SURFACE,borderRadius:12,padding:28,width:400,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}><span style={{fontSize:22}}>!️</span><div style={{fontWeight:600,fontSize:16}}>Recording not submitted</div></div>
        <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:20,lineHeight:1.6}}>You have a stopped recording that hasn't been submitted yet. If you leave now, it will be lost.</p>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setBackWarnModal(false)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Stay here</button>
          <button onClick={onBack} style={{padding:"8px 16px",borderRadius:8,border:"none",background:DANGER,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Leave without submitting</button>
        </div>
      </div>
    </div>}
    {discardModal&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}><div style={{background:SURFACE,borderRadius:12,padding:26,width:380}}><div style={{fontWeight:600,fontSize:16,marginBottom:8}}>Discard this recording?</div><p style={{fontSize:13,color:TEXT_MUTED,marginBottom:20}}>This recording will be permanently deleted. You can start a fresh take.</p><div style={{display:"flex",justifyContent:"flex-end",gap:8}}><button onClick={()=>setDiscardModal(false)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={()=>{setPhase("pre");setT(0);setMuted(false);setVideoOn(true);setDiscardModal(false);setPlayback(false);}} style={{padding:"8px 16px",borderRadius:8,border:"none",background:DANGER,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Discard &amp; Start Over</button></div></div></div>}
    <button onClick={handleBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back to task</button>
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:4}}>Audio Recorder</h1>
    <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:14}}>{task.project} · {task.topic}{task.scripted&&<Badge label="Scripted" color="blue"/>}</p>
    <div style={{display:"flex",gap:20,alignItems:"flex-start"}}>
    <div style={{width:task.partners&&task.partners.length>=2?256:400,flexShrink:0}}>
      <div style={{background:`linear-gradient(135deg,${BRAND} 0%,${INFO_ACCENT_2} 100%)`,borderRadius:12,padding:"14px 20px",marginBottom:14,display:"flex",alignItems:"center",gap:16}}>
        <span style={{fontSize:34,lineHeight:1}}>⏱</span>
        <div>
          <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.65)",letterSpacing:"0.12em",marginBottom:2}}>EXPECTED RECORDING DURATION</div>
          <div style={{fontSize:26,fontWeight:800,color:SURFACE,letterSpacing:1,lineHeight:1}}>{task.duration||"—"}</div>
        </div>
      </div>
      {/* ── Video tiles — stacked vertically, square, full column width ── */}
      {task.partners&&task.partners.length>=2
        /* 3-person: 3 stacked square tiles */
        ?<div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
          {task.partners.map((name,i)=>(
            <div key={i} style={{aspectRatio:"1/1",width:"100%",background:i===0?`#111827`:"#0f1e2e",borderRadius:i===0?`12px 12px 4px 4px`:"4px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden"}}>
              <div style={{width:72,height:72,borderRadius:"50%",background:i===0?"#166834":"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:700,color:i===0?SUCCESS_LIGHT:"#93c5fd",marginBottom:12}}>{(name||"").split(" ").map(x=>x[0]).join("")}</div>
              <span style={{fontSize:15,color:i===0?SUCCESS_LIGHT:"#93c5fd",fontWeight:600}}>{shortName(name)}</span>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:4}}>Participant {i+2}</span>
            </div>
          ))}
          {/* Self tile */}
          <div style={{aspectRatio:"1/1",width:"100%",background:"#1a1f2e",borderRadius:"4px 4px 12px 12px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden"}}>
            {videoOn
              ?<svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:10}}><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/></svg>
              :<svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={TEXT_SECONDARY} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:10}}><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/><line x1="1" y1="1" x2="23" y2="23" stroke={DANGER_SOFT}/></svg>
            }
            <span style={{fontSize:13,color:"rgba(255,255,255,0.4)"}}>{myName?.split(" ")[0]} (You)</span>
            {/* Controls overlaid bottom-left of self tile */}
            <div style={{position:"absolute",bottom:12,left:14,display:"flex",gap:8}}>
              <button onClick={handleVideoToggle} style={{width:36,height:36,borderRadius:"50%",border:"none",background:videoOn?"rgba(255,255,255,0.15)":"rgba(220,38,38,0.8)",color:SURFACE,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {videoOn?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/></svg>:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/><line x1="1" y1="1" x2="23" y2="23"/></svg>}
              </button>
              <button onClick={()=>setMuted(v=>!v)} style={{width:36,height:36,borderRadius:"50%",border:"none",background:muted?"rgba(220,38,38,0.8)":"rgba(255,255,255,0.15)",color:SURFACE,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {!muted?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>}
              </button>
              {muted&&<span style={{alignSelf:"center",fontSize:10,color:"rgba(255,100,100,0.9)",fontWeight:600}}>MUTED</span>}
            </div>
          </div>
        </div>
        /* 2-person and solo: single square tile */
        :<div style={{position:"relative",background:"#0d1117",borderRadius:12,overflow:"hidden",marginBottom:12,aspectRatio:"1/1",width:"100%"}}>
          {task.solo
            ?<div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
              {videoOn
                ?<><svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:12}}><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/></svg><span style={{fontSize:14,color:TEXT_MUTED}}>Your video</span></>
                :<><svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={TEXT_SECONDARY} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:12}}><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/><line x1="1" y1="1" x2="23" y2="23" stroke={DANGER_SOFT}/></svg><span style={{fontSize:14,color:TEXT_MUTED}}>Camera off</span></>
              }
            </div>
            :<div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
              <div style={{width:80,height:80,borderRadius:"50%",background:"#166834",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,fontWeight:700,color:SUCCESS_LIGHT,marginBottom:14}}>{(task.partner||"").split(" ").map(x=>x[0]).join("")}</div>
              <span style={{fontSize:15,color:SUCCESS_LIGHT,fontWeight:600}}>{shortName(task.partner)||"Awaiting partner"}</span>
              <span style={{fontSize:12,color:"rgba(255,255,255,0.3)",marginTop:6}}>In call</span>
            </div>
          }
          {/* PiP for 2-person */}
          {!task.solo&&<div style={{position:"absolute",bottom:60,right:14,width:"22%",aspectRatio:"1/1",background:"#1e2433",borderRadius:8,border:"1.5px solid rgba(255,255,255,0.15)",overflow:"hidden",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
            {videoOn?<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/></svg>:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/><line x1="1" y1="1" x2="23" y2="23" stroke={DANGER_SOFT}/></svg>}
            <span style={{fontSize:8,color:"rgba(255,255,255,0.3)",marginTop:3}}>{myName?.split(" ")[0]}</span>
          </div>}
          {/* Controls */}
          <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"10px 14px",background:"linear-gradient(to top,rgba(0,0,0,0.65) 0%,transparent 100%)",display:"flex",alignItems:"center",gap:8}}>
            <button onClick={handleVideoToggle} style={{width:36,height:36,borderRadius:"50%",border:"none",background:videoOn?"rgba(255,255,255,0.18)":"rgba(220,38,38,0.85)",color:SURFACE,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {videoOn?<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/></svg>:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14"/><rect x="2" y="7" width="13" height="10" rx="2"/><line x1="1" y1="1" x2="23" y2="23"/></svg>}
            </button>
            <button onClick={()=>setMuted(v=>!v)} style={{width:36,height:36,borderRadius:"50%",border:"none",background:muted?"rgba(220,38,38,0.85)":"rgba(255,255,255,0.18)",color:SURFACE,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {!muted?<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>}
            </button>
            {muted&&<span style={{fontSize:10,color:"rgba(255,100,100,0.9)",fontWeight:600,letterSpacing:"0.06em"}}>MUTED</span>}
            <span style={{marginLeft:"auto",fontSize:11,color:"rgba(255,255,255,0.4)"}}>{task.solo?myName:shortName(task.partner)||"—"}</span>
          </div>
        </div>
      }
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:22}}>
        <div style={{textAlign:"center",marginBottom:14}}>
          <div style={{fontSize:38,fontWeight:300,fontVariantNumeric:"tabular-nums",color:phase==="recording"?DANGER:phase==="stopped"?SUCCESS:P,letterSpacing:2}}>{fmt(t)}</div>
          <div style={{fontSize:11,color:TEXT_SUBTLE}}>Elapsed time</div>
        </div>
        {phase==="pre"&&<>
          {task.specialNotes&&<div style={{background:`#fff3cd`,border:"2px solid #f59e0b",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:18,flexShrink:0,marginTop:1}}>!️</span>
            <div><div style={{fontSize:11,fontWeight:700,color:WARNING,letterSpacing:"0.08em",marginBottom:4}}>SPECIAL INSTRUCTIONS</div><div style={{fontSize:13,color:"#78350f",lineHeight:1.55,fontWeight:500}}>{task.specialNotes}</div></div>
          </div>}
          <div style={{display:"flex",justifyContent:"center"}}><button onClick={startRec} style={{padding:"9px 28px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:500,cursor:"pointer"}}>Play Start Recording</button></div>
        </>}
        {phase==="recording"&&<div style={{display:"flex",justifyContent:"center"}}><button onClick={stopRec} style={{padding:"9px 24px",borderRadius:8,border:"none",background:TEXT_SECONDARY,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>⏹ Stop Recording</button></div>}
        {phase==="stopped"&&<div>
          <div style={{background:SURFACE_SUNKEN,borderRadius:8,padding:"12px 14px",marginBottom:16,border:`1px solid ${BORDER}`}}>
            <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>LISTEN BACK</div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button onClick={()=>setPlayback(v=>!v)} style={{width:36,height:36,borderRadius:"50%",border:"none",background:P,color:SURFACE,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{playback?"":"Play"}</button>
              <div style={{flex:1}}>
                <div style={{height:4,background:BORDER,borderRadius:999,overflow:"hidden",marginBottom:4}}><div style={{width:playback?"35%":"0%",height:"100%",background:P,borderRadius:999,transition:"width 0.5s"}}/></div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:TEXT_SUBTLE}}><span>{playback?"0:18":"0:00"}</span><span>{fmt(t)}</span></div>
              </div>
            </div>
          </div>
          <div style={{fontSize:13,color:TEXT_MUTED,marginBottom:14,textAlign:"center"}}>Review your recording, then choose an option below.</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{setPendingSubmitFn(()=>onFinishAndEnd||finish);setShowSubmitConfirm(true);}} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:SUCCESS,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Submit &amp; End Session</button>
              <button onClick={()=>{setPendingSubmitFn(()=>()=>setRecordAgainModal(true));setShowSubmitConfirm(true);}} style={{flex:1,padding:"10px",borderRadius:8,border:`1.5px solid ${P}`,background:PL,color:P,fontSize:13,fontWeight:500,cursor:"pointer"}}>Submit &amp; Record Again</button>
            </div>
            <button onClick={()=>setDiscardModal(true)} style={{width:"100%",padding:"9px",borderRadius:8,border:`1px solid ${DANGER_BORDER}`,background:SURFACE,color:DANGER,fontSize:13,cursor:"pointer"}}>Discard</button>
          </div>
        </div>}
      </div>
    </div>
    {/* Script panel — shown to the right of the recorder when task has a script */}
    {task.script&&<div style={{flex:1,minWidth:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"18px 20px",overflowY:"scroll",height:"calc(100vh - 160px)",position:"sticky",top:0}}>
      <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:12}}>YOUR SCRIPT</div>
      <pre style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.75,whiteSpace:"pre-wrap",margin:0,fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"}}>{task.script}</pre>
    </div>}
    </div>
  </div>);
}

function PostRecordingScreen({task,onDone}){
  const allPartners=task.partners||(task.partner?[task.partner]:[]);
  const[partnerStates,setPartnerStates]=useState(()=>Object.fromEntries(allPartners.map(p=>[p,{saved:false,blocked:false,reported:false}])));
  const[reportingPartner,setReportingPartner]=useState(null);
  const[reportReasons,setReportReasons]=useState([]);
  const[reportText,setReportText]=useState("");
  const[reportOtherText,setReportOtherText]=useState("");
  const[reportSent,setReportSent]=useState(false);
  const REPORT_REASONS=["Verbal abuse","Inappropriate behavior","Suspected fraud","Other"];
  const prOtherSelected=reportReasons.includes("Other");
  const prReportValid=(reportReasons.filter(r=>r!=="Other").length>0||(prOtherSelected&&reportOtherText.trim().length>0));
  const toggleState=(name,key)=>setPartnerStates(prev=>({...prev,[name]:{...prev[name],[key]:!prev[name]?.[key]}}));
  const closeReport=()=>{setReportingPartner(null);setReportReasons([]);setReportText("");setReportOtherText("");setReportSent(false);};
  const submitReport=()=>{setPartnerStates(prev=>({...prev,[reportingPartner]:{...prev[reportingPartner],reported:true}}));setReportSent(true);};

  const PartnerCard=({name})=>{
    const ps=partnerStates[name]||{};
    return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:14,padding:20,marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,paddingBottom:14,borderBottom:`1px solid ${SURFACE_HOVER}`}}>
        <div style={{width:40,height:40,borderRadius:"50%",background:PL,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,color:P,flexShrink:0}}>{name.split(" ").map(x=>x[0]).join("")}</div>
        <div style={{fontWeight:600,fontSize:15}}>{shortName(name)}</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        <button onClick={()=>toggleState(name,"saved")} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,border:`1.5px solid ${ps.saved?P:BORDER}`,background:ps.saved?PL:SURFACE,cursor:"pointer",textAlign:"left"}}>
          <span style={{fontSize:18,flexShrink:0}}>{ps.saved?"Star":"Star"}</span>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:ps.saved?P:TEXT_PRIMARY}}>{ps.saved?"Saved as Buddy":"Save as recording Buddy"}</div>
            <div style={{fontSize:11,color:TEXT_SUBTLE}}>They'll be an option on your Saved Buddies list</div>
          </div>
        </button>
        <button onClick={()=>toggleState(name,"blocked")} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,border:`1.5px solid ${ps.blocked?DANGER_BORDER:BORDER}`,background:ps.blocked?`#fff5f5`:SURFACE,cursor:"pointer",textAlign:"left"}}>
          <span style={{fontSize:18,flexShrink:0}}>{ps.blocked?"":""}</span>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:ps.blocked?DANGER:TEXT_PRIMARY}}>{ps.blocked?"Blocked":"Block from future recordings"}</div>
            <div style={{fontSize:11,color:TEXT_SUBTLE}}>You'll no longer be able to invite each other to record</div>
          </div>
        </button>
        <button onClick={()=>{if(!ps.reported){setReportingPartner(name);}}} disabled={ps.reported} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,border:`1.5px solid ${ps.reported?SURFACE_HOVER:BORDER}`,background:ps.reported?SURFACE_SUNKEN:SURFACE,cursor:ps.reported?"default":"pointer",textAlign:"left",opacity:ps.reported?0.6:1}}>
          <span style={{fontSize:18,flexShrink:0}}>Flag</span>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:ps.reported?TEXT_SUBTLE:TEXT_SECONDARY}}>{ps.reported?"Report submitted":"Report this recording Buddy"}</div>
            <div style={{fontSize:11,color:TEXT_SUBTLE}}>{ps.reported?"Our team will follow up if needed":"Flag inappropriate behavior or other issues"}</div>
          </div>
        </button>
      </div>
    </div>);
  };

  return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",background:SURFACE_ALT}}>
    {reportingPartner&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}>
      <div style={{background:SURFACE,borderRadius:12,padding:28,width:440,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
        {!reportSent?<>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}><div style={{fontWeight:600,fontSize:16}}>Report {shortName(reportingPartner)}</div><button onClick={closeReport} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button></div>
          <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:14,lineHeight:1.5}}>Reports are reviewed by our team. Please describe what happened.</p>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>REASON (select all that apply)</div>
            {REPORT_REASONS.map(r=>(
              <div key={r}>
                <label style={{display:"flex",alignItems:"center",gap:9,padding:"7px 10px",borderRadius:7,cursor:"pointer",marginBottom:4,background:reportReasons.includes(r)?PL:SURFACE_SUNKEN,border:`1px solid ${reportReasons.includes(r)?P:BORDER}`}}><input type="checkbox" checked={reportReasons.includes(r)} onChange={()=>setReportReasons(s=>s.includes(r)?s.filter(x=>x!==r):[...s,r])} style={{accentColor:P}}/><span style={{fontSize:13,color:reportReasons.includes(r)?P:TEXT_SECONDARY,fontWeight:reportReasons.includes(r)?500:400}}>{r}</span></label>
                {r==="Other"&&reportReasons.includes("Other")&&<textarea value={reportOtherText} onChange={e=>setReportOtherText(e.target.value)} rows={3} placeholder="Please explain…" style={{width:"100%",padding:"8px 10px",border:`1px solid ${reportOtherText.trim()?BORDER:DANGER_BORDER}`,borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none",lineHeight:1.5,marginBottom:4}}/>}
              </div>
            ))}
          </div>
          <div style={{marginBottom:18}}>
            <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>ADDITIONAL DETAILS</div>
            <textarea value={reportText} onChange={e=>setReportText(e.target.value)} rows={2} placeholder="Any other context…" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none",lineHeight:1.5}}/>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button onClick={closeReport} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
            <button onClick={submitReport} disabled={!prReportValid} style={{padding:"8px 16px",borderRadius:8,border:"none",background:prReportValid?DANGER:DANGER_BORDER,color:SURFACE,fontSize:13,fontWeight:500,cursor:prReportValid?"pointer":"default"}}>Submit Report</button>
          </div>
        </>:<div style={{textAlign:"center",padding:"10px 0 6px"}}>
          <div style={{width:48,height:48,borderRadius:"50%",background:SUCCESS_BG,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,margin:"0 auto 14px"}}>✓</div>
          <div style={{fontWeight:600,fontSize:16,marginBottom:6}}>Report submitted</div>
          <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:20}}>Our team will review your report and follow up if needed.</p>
          <button onClick={closeReport} style={{padding:"8px 20px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Done</button>
        </div>}
      </div>
    </div>}
    <div style={{width:"100%",maxWidth:520}}>
      <div style={{textAlign:"center",marginBottom:32}}>
        <div style={{width:64,height:64,borderRadius:"50%",background:SUCCESS_BG,display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,margin:"0 auto 16px"}}>✓</div>
        <div style={{fontSize:22,fontWeight:700,color:TEXT_PRIMARY,marginBottom:6}}>Recording submitted!</div>
        <div style={{fontSize:14,color:TEXT_MUTED}}>{task.project} · {task.topic}</div>
      </div>
      {allPartners.length>0&&<>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:12}}>{allPartners.length>1?"YOUR PARTNERS":"YOUR PARTNER"}</div>
        {allPartners.map(name=><PartnerCard key={name} name={name}/>)}
      </>}
      <button onClick={onDone} style={{width:"100%",padding:"12px",borderRadius:9,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:600,cursor:"pointer",marginTop:4}}>Done →</button>
    </div>
  </div>);
}
function SoloTopicPickerScreen({proj,profile,onBack,onPick}){
  const[topic,setTopic]=useState(proj.topics[0]);
  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back</button>
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:4}}>{proj.title}</h1>
    <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:22}}>Solo recording · Pick a topic to continue.</p>
    <div style={{maxWidth:540}}>
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:18,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>TASK DETAILS</div>
        <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:"8px 0",fontSize:13}}>
          <span style={{color:TEXT_SUBTLE}}>Format</span><span><Badge label={proj.scripted?"Scripted":"Unscripted"} color={proj.scripted?"blue":"teal"}/></span>
          <span style={{color:TEXT_SUBTLE}}>Language</span><span>{proj.langs?.filter(l=>profile.approvedLangs.includes(l)).join(", ")||proj.langs?.join(", ")}</span>
          <span style={{color:TEXT_SUBTLE}}>Duration</span><span>{proj.durationRange||"30–45 min"}</span>
          <span style={{color:TEXT_SUBTLE}}>Deadline</span><span>{proj.deadline}</span>
          <span style={{color:TEXT_SUBTLE}}>Topic</span><span><select value={topic} onChange={e=>setTopic(e.target.value)} style={{padding:"3px 7px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:13}}>{proj.topics.map(t=><option key={t}>{t}</option>)}</select></span>
        </div>
      </div>
      <button onClick={()=>onPick(topic)} style={{width:"100%",padding:"11px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:500,cursor:"pointer"}}>Continue →</button>
    </div>
  </div>);
}

function ContactScreen({task,onRecorder,onBack}){
  const isSolo=!!task.solo;
  const allPartners=task.partners||(task.partner?[task.partner]:[]);
  const isMulti=allPartners.length>0;
  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back</button>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>{task.id}</h1>
      <Badge label={isSolo?"Solo":"Ready to Record"} color="green"/>
    </div>
    <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:22}}>{task.project} · {task.topic}</p>
    <div style={{maxWidth:460}}>
      {isMulti&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:20,marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:12}}>
          {allPartners.length>1?"YOUR PARTNERS":"YOUR PARTNER"}
        </div>
        {allPartners.map((name,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:i<allPartners.length-1?14:0,paddingBottom:i<allPartners.length-1?14:0,borderBottom:i<allPartners.length-1?`1px solid ${SURFACE_HOVER}`:"none"}}>
            <div style={{width:40,height:40,borderRadius:"50%",background:name?PL:SURFACE_HOVER,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:14,color:name?P:TEXT_SUBTLE,flexShrink:0}}>
              {name?(name.split(" ").map(x=>x[0]).join("")):""}
            </div>
            <div style={{flex:1}}>
              <div style={{fontWeight:500,fontSize:13,color:name?TEXT_PRIMARY:TEXT_SUBTLE}}>{name?shortName(name):"Awaiting partner"}</div>
              {name&&<div style={{fontSize:12,color:P}}>{name.toLowerCase().replace(" ",".")+"@example.com"}</div>}
            </div>
          </div>
        ))}
        <div style={{borderTop:`1px solid ${SURFACE_HOVER}`,paddingTop:12,marginTop:14,display:"grid",gridTemplateColumns:"110px 1fr",gap:"8px 0",fontSize:13}}>
          <span style={{color:TEXT_SUBTLE}}>Duration</span><span>{task.duration}</span>
          <span style={{color:TEXT_SUBTLE}}>Deadline</span><span>{task.deadline}</span>
        </div>
      </div>}
      {isSolo&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:20,marginBottom:12}}>
        <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:"8px 0",fontSize:13}}>
          <span style={{color:TEXT_SUBTLE}}>Batch</span><span>{task.project}</span>
          <span style={{color:TEXT_SUBTLE}}>Topic</span><span>{task.topic}</span>
          <span style={{color:TEXT_SUBTLE}}>Duration</span><span>{task.duration}</span>
          <span style={{color:TEXT_SUBTLE}}>Deadline</span><span>{task.deadline}</span>
        </div>
      </div>}
      {!isSolo&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:20,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>MEETING LINK</div>
        <div style={{display:"flex",gap:8}}>
          <div style={{flex:1,padding:"7px 10px",background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:8,fontSize:12,color:TEXT_SECONDARY,fontFamily:"monospace"}}>https://meet.jeenius.io/room/{task.id}</div>
          <button style={{padding:"7px 10px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Copy</button>
        </div>
      </div>}
      <button onClick={onRecorder} style={{width:"100%",padding:"11px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:500,cursor:"pointer"}}>→ Open Audio Recorder</button>
    </div>
  </div>);
}

const turns=[{speaker:"Speaker A",time:"0:02–0:56",text:"Bueno, em, con estos climas me ha habilitado, por lo menos a mí, trapear más seguido…"},{speaker:"Speaker B",time:"0:56–1:16",text:"Claro, sí, sí. A mí me pasó también, que ayer me puse a limpiar toda la casa…"},{speaker:"Speaker A",time:"1:16–1:16",text:"aham."},{speaker:"Speaker B",time:"1:16–1:32",text:"Así que terminé de limpiar y me puse a barrer. Re fanática. ¿viste?"},{speaker:"Speaker A",time:"1:32–1:33",text:"Mirá."}];
function ReviewPanel({task,onBack}){return(<div style={{flex:1,display:"flex",overflow:"hidden"}}><div style={{width:200,borderRight:`1px solid ${BORDER}`,padding:"18px 14px",flexShrink:0,overflowY:"auto"}}><button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back</button><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>QUALITY SUMMARY</div><div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:5}}><span style={{color:TEXT_MUTED}}>Segments</span><span style={{fontWeight:500}}>20</span></div><div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:18}}><span style={{color:TEXT_MUTED}}>Language</span><span style={{fontWeight:500}}>{task.lang}</span></div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>SCORE PARAMETERS</div>{["Omission","Addition","Distortion","Register","Intent","Speaker Attribution"].map(p=><div key={p} style={{fontSize:13,color:TEXT_SECONDARY,padding:"3px 0"}}>{p}</div>)}<div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",margin:"14px 0 8px"}}>SCORE KEY</div>{[[SUCCESS_SOFT,"0 – None"],["#f59e0b","1 – Minor"],[ORANGE_ACCENT,"2 – Major"],[DANGER_SOFT,"3 – Critical"]].map(([c,l])=>(<div key={l} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,marginBottom:4}}><div style={{width:8,height:8,borderRadius:"50%",background:c}}/>{l}</div>))}</div><div style={{flex:1,padding:"22px 26px",overflowY:"auto"}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:18}}><span style={{fontWeight:600,fontSize:15}}>{task.id}</span><span style={{fontSize:13,color:TEXT_MUTED}}>{task.type}</span><Badge label={task.lang} color="purple"/><Badge label={task.batch} color="gray"/></div><h2 style={{fontSize:15,fontWeight:600,marginBottom:14}}>Scored Transcript</h2>{turns.map((t,i)=>(<div key={i} style={{border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 14px",marginBottom:10}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}><span style={{color:P,fontWeight:600,fontSize:13}}>{t.speaker}</span><span style={{fontSize:12,color:TEXT_SUBTLE}}>{t.time}</span></div><p style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.6,margin:"0 0 8px"}}>{t.text}</p><div style={{display:"flex",gap:6}}>{["Omission","Addition","Distortion"].map(p=>(<select key={p} style={{fontSize:11,padding:"2px 5px",border:`1px solid ${BORDER}`,borderRadius:4,color:TEXT_MUTED}}>{[0,1,2,3].map(n=><option key={n}>{p}: {n}</option>)}</select>))}</div></div>))}<div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:6}}><button style={{padding:"8px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:14,cursor:"pointer"}}>Save Draft</button><button style={{padding:"8px 18px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:500,cursor:"pointer"}}>Submit Review →</button></div></div></div>);}

// QA review mock data — attached to history entries that have been QA'd.
// reviewerSegments = what the original reviewer submitted (the QA's starting point).
// qaSegments       = the QA's final version after any edits they made.
// Diff between the two is what the QA changed on top of the reviewer's work.
const SAMPLE_QA_FEEDBACK_T001={
  qaReviewer:"Marcus Chen",
  qaCompleted:"Jan 28, 2024",
  feedback:"Great work catching the speaker swap in segment 4 — easy to miss. Minor note on segment 3: tightened up the punctuation a touch. Overall solid review, no major concerns.",
  reviewerSpeakers:["speaker-1","speaker-2","speaker-3"],
  reviewerSegments:[
    {id:1,speakerId:"speaker-1",startTime:0,endTime:5.5,text:"Hello there, how are you doing today?"},
    {id:2,speakerId:"speaker-2",startTime:5.5,endTime:11.3,text:"I'm doing alright, just got back from the store [laugh]"},
    {id:3,speakerId:"speaker-1",startTime:11.3,endTime:18.0,text:"That's good. Did you find what you were looking for?"},
    {id:4,speakerId:"speaker-2",startTime:18.0,endTime:24.0,text:"Yeah, they had everything on sale this week."},
    {id:5,speakerId:"speaker-2",startTime:24.0,endTime:32.0,text:"That's great. I should probably head over there too before they run out."},
    {id:6,speakerId:"speaker-1",startTime:32.0,endTime:38.0,text:"Yeah you should, they had a really good deal on coffee."},
    {id:7,speakerId:"speaker-3",startTime:38.0,endTime:43.0,text:"Did somebody mention coffee?"},
  ],
  qaSpeakers:["speaker-1","speaker-2","speaker-3"],
  qaSegments:[
    {id:1,speakerId:"speaker-1",startTime:0,endTime:5.5,text:"Hello there, how are you doing today?"},
    {id:2,speakerId:"speaker-2",startTime:5.5,endTime:11.3,text:"I'm doing alright, just got back from the store [laugh]"},
    {id:3,speakerId:"speaker-1",startTime:11.3,endTime:18.0,text:"That's good — did you find what you were looking for?"}, // QA tightened punctuation
    {id:4,speakerId:"speaker-2",startTime:18.0,endTime:24.0,text:"Yeah, they had everything on sale this week."},
    {id:5,speakerId:"speaker-2",startTime:24.0,endTime:32.0,text:"That's great. I should probably head over there too before they run out."},
    {id:6,speakerId:"speaker-1",startTime:32.0,endTime:38.0,text:"Yeah, you should — they had a really good deal on coffee."}, // QA added a comma + dash
    {id:7,speakerId:"speaker-3",startTime:38.0,endTime:43.0,text:"Did somebody mention coffee?"},
  ],
};

const allHistoryTasks=[
  {id:"5e411664-45ee-47f6-a962-b2d3dbab9a54",kind:"create",type:"Content Creation",project:"Spanish Pilot",completedDate:"Jan 15, 2024",paid:true,qa:"Approved",rejectionReasons:[],notes:""},
  {id:"ed27ca4c-5487-467a-abc1-a0e71b7980c2",kind:"review",type:"Full Transcript Review",project:"French Medical Dialogues",completedDate:"Jan 22, 2024",paid:true,qa:"Approved",rejectionReasons:[],notes:"",qaReview:SAMPLE_QA_FEEDBACK_T001},
  {id:"1d5a120a-e46e-4313-87ed-0ad90ecc557d",kind:"create",type:"Content Creation",project:"Spanish Conversational Audio",completedDate:"Feb 3, 2024",paid:true,qa:"Approved",rejectionReasons:[],notes:""},
  {id:"5c232c70-5d25-4ab6-b375-b8915f69d8ec",kind:"review",type:"Full Transcript Review",project:"Spanish Conversational Audio",completedDate:"Feb 14, 2024",paid:false,qa:"Pending",rejectionReasons:[],notes:""},
  {id:"8bd54125-40bc-4d44-85d2-6d8a5ebdcf49",kind:"create",type:"Content Creation",project:"French Medical Dialogues",completedDate:"Feb 28, 2024",paid:false,qa:"Failed",rejectionReasons:["Low audio quality","Improper duration"],notes:"Background noise throughout. Recording was 12 min, well below the 20 min minimum."},
];
const qaStyle=s=>({Approved:{bg:SUCCESS_BG,c:SUCCESS},Pending:{bg:WARNING_BG,c:WARNING},Failed:{bg:DANGER_BG,c:DANGER}}[s]||{bg:SURFACE_HOVER,c:TEXT_MUTED});

const REJECTION_REASONS=["Low audio quality","Improper duration","Topic mismatch","Taboo topics","Language variant mismatch","Scripted","Unidentified user"];

function RejectionReasonCell({reasons,onChange}){
  const[open,setOpen]=useState(false);
  const ref=useRef(null);
  useEffect(()=>{const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);
  const toggle=r=>onChange(reasons.includes(r)?reasons.filter(x=>x!==r):[...reasons,r]);
  return(<div ref={ref} style={{position:"relative",minWidth:120}}>
    <div onClick={()=>setOpen(v=>!v)} style={{cursor:"pointer",display:"flex",flexWrap:"wrap",gap:3,alignItems:"center",minHeight:22}}>
      {reasons.length===0?<span style={{fontSize:12,color:TEXT_DISABLED}}>—</span>:reasons.map(r=><span key={r} style={{fontSize:10,background:DANGER_BG,color:DANGER,padding:"1px 5px",borderRadius:3,fontWeight:600,whiteSpace:"nowrap"}}>{r}</span>)}
      <span style={{fontSize:10,color:TEXT_SUBTLE,marginLeft:2}}>Edit</span>
    </div>
    {open&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:9,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:60,minWidth:220,padding:"6px 0"}}>
      {REJECTION_REASONS.map(r=>(
        <label key={r} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=""}>
          <input type="checkbox" checked={reasons.includes(r)} onChange={()=>toggle(r)} style={{accentColor:DANGER,flexShrink:0}}/>
          <span style={{fontSize:12,color:TEXT_SECONDARY}}>{r}</span>
        </label>
      ))}
    </div>}
  </div>);
}

function NotesCell({value,onChange}){
  const[editing,setEditing]=useState(false);
  const[draft,setDraft]=useState(value);
  if(editing)return(<div style={{display:"flex",flexDirection:"column",gap:4}}>
    <textarea value={draft} onChange={e=>setDraft(e.target.value)} rows={2} autoFocus style={{fontSize:12,padding:"4px 6px",border:`1px solid ${BRAND_TINT}`,borderRadius:6,outline:"none",resize:"vertical",width:160,lineHeight:1.4}}/>
    <div style={{display:"flex",gap:4}}><button onClick={()=>{onChange(draft);setEditing(false);}} style={{fontSize:11,padding:"2px 8px",borderRadius:5,border:"none",background:P,color:SURFACE,cursor:"pointer"}}>Save</button><button onClick={()=>{setDraft(value);setEditing(false);}} style={{fontSize:11,padding:"2px 8px",borderRadius:5,border:`1px solid ${BORDER}`,background:SURFACE,cursor:"pointer"}}>Cancel</button></div>
  </div>);
  return(<div onClick={()=>setEditing(true)} style={{cursor:"pointer",fontSize:12,color:value?TEXT_SECONDARY:TEXT_DISABLED,maxWidth:160,lineHeight:1.4,display:"flex",alignItems:"flex-start",gap:4}}>
    <span style={{flex:1}}>{value||"—"}</span><span style={{fontSize:10,color:TEXT_SUBTLE,flexShrink:0,marginTop:1}}>Edit</span>
  </div>);
}

function HistoryScreen({kinds}){
  const showFilter=kinds&&kinds.length>1,[kf,setKf]=useState("All");
  const[histTasks]=useState(allHistoryTasks);
  const[dateFrom,setDateFrom]=useState("");
  const[dateTo,setDateTo]=useState("");
  const[qaFilter,setQaFilter]=useState([]);
  const[payFilter,setPayFilter]=useState([]);
  const[viewQA,setViewQA]=useState(null); // task object selected for QA feedback view

  // If a QA feedback view is open, render that instead of the table.
  if(viewQA) return <ReviewerQAFeedbackScreen task={viewQA} qaReview={viewQA.qaReview} onBack={()=>setViewQA(null)}/>;

  const parseDate=str=>{if(!str)return null;const d=new Date(str);return isNaN(d)?null:d;};

  const baseTasks=kinds?histTasks.filter(t=>kinds.includes(t.kind)):histTasks;
  const kindFiltered=showFilter&&kf!=="All"?baseTasks.filter(t=>(kf==="Review"&&t.kind==="review")||(kf==="Record"&&t.kind==="create")):baseTasks;

  const rows=kindFiltered.filter(t=>{
    if(dateFrom){const d=parseDate(t.completedDate);if(!d||d<new Date(dateFrom))return false;}
    if(dateTo){const d=parseDate(t.completedDate);if(!d||d>new Date(dateTo+"T23:59:59"))return false;}
    if(qaFilter.length>0&&!qaFilter.includes(t.qa))return false;
    if(payFilter.length>0){
      if(payFilter.includes("Paid")&&!payFilter.includes("Pending")&&!t.paid)return false;
      if(payFilter.includes("Pending")&&!payFilter.includes("Paid")&&t.paid)return false;
    }
    return true;
  });

  const hasFilters=dateFrom||dateTo||qaFilter.length>0||payFilter.length>0;
  const clearFilters=()=>{setDateFrom("");setDateTo("");setQaFilter([]);setPayFilter([]);};

  // Inline date-range pill pair — styled to match MultiSelectDropdown pills
  const dateActive=dateFrom||dateTo;
  const datePillStyle={padding:"5px 10px",border:`1px solid ${dateActive?P:BORDER}`,borderRadius:6,background:dateActive?PL:SURFACE,color:dateActive?P:TEXT_SECONDARY,fontSize:13,outline:"none",cursor:"pointer"};

  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:16}}>History</h1>

    {showFilter&&<div style={{display:"flex",gap:6,marginBottom:14}}>
      {[["All",baseTasks.length],["Review",baseTasks.filter(t=>t.kind==="review").length],["Record",baseTasks.filter(t=>t.kind==="create").length]].map(([k,n])=>(
        <button key={k} onClick={()=>setKf(k)} style={{padding:"4px 11px",borderRadius:6,border:`1px solid ${kf===k?P:BORDER}`,background:kf===k?PL:SURFACE,color:kf===k?P:TEXT_SECONDARY,fontSize:13,cursor:"pointer",fontWeight:kf===k?500:400}}>{k} <span style={{fontWeight:600}}>{n}</span></button>
      ))}
    </div>}

    <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
      {/* Date range — two inputs styled as a connected pill pair */}
      <div style={{display:"flex",alignItems:"center",gap:0,border:`1px solid ${dateActive?P:BORDER}`,borderRadius:6,background:dateActive?PL:SURFACE,overflow:"hidden"}}>
        <span style={{padding:"5px 8px 5px 10px",fontSize:13,color:dateActive?P:TEXT_SECONDARY,fontWeight:dateActive?500:400,whiteSpace:"nowrap"}}>
          {dateFrom||dateTo?"Date:":"Date"}
        </span>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{border:"none",background:"transparent",fontSize:12,color:dateFrom?P:TEXT_SUBTLE,outline:"none",padding:"5px 4px",cursor:"pointer",width:120}}/>
        <span style={{fontSize:12,color:TEXT_SUBTLE,padding:"0 2px"}}>–</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{border:"none",background:"transparent",fontSize:12,color:dateTo?P:TEXT_SUBTLE,outline:"none",padding:"5px 4px",cursor:"pointer",width:120}}/>
        {dateActive&&<span onClick={()=>{setDateFrom("");setDateTo("");}} style={{padding:"5px 8px",fontSize:13,color:P,cursor:"pointer",lineHeight:1}}>×</span>}
      </div>

      <MultiSelectDropdown label="QA Status" options={["Approved","Pending","Failed"]} selected={qaFilter} onChange={setQaFilter}/>
      <MultiSelectDropdown label="Payment" options={["Paid","Pending"]} selected={payFilter} onChange={setPayFilter}/>

      {hasFilters&&<button onClick={clearFilters} style={{fontSize:12,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:"4px 0"}}>Clear filters</button>}
      <span style={{fontSize:12,color:TEXT_SUBTLE,marginLeft:"auto"}}>{rows.length} of {kindFiltered.length} {kindFiltered.length===1?"entry":"entries"}</span>
    </div>

    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${BORDER}`}}>
          {[...(showFilter?["ID","Kind"]:["ID"]),"Type","Batch","Completed","QA Status","Payment","Rejection Reason","Notes"].map(h=>(
            <th key={h} style={{padding:"9px 13px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE,whiteSpace:"nowrap"}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>{rows.length>0?rows.map(t=>{
          const qa=qaStyle(t.qa);
          return(<tr key={t.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`,verticalAlign:"top"}}>
            <td style={{padding:"10px 13px",color:P,fontWeight:500,fontSize:13}}>{t.id}</td>
            {showFilter&&<td style={{padding:"10px 13px"}}><Badge label={t.kind==="review"?"Review":"Record"} color={t.kind==="review"?"blue":"purple"}/></td>}
            <td style={{padding:"10px 13px",fontSize:13}}>{t.type}</td>
            <td style={{padding:"10px 13px",fontSize:13}}>{t.project}</td>
            <td style={{padding:"10px 13px",fontSize:13}}>{t.completedDate}</td>
            <td style={{padding:"10px 13px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{background:qa.bg,color:qa.c,fontSize:11,fontWeight:600,padding:"2px 9px",borderRadius:999}}>{t.qa}</span>
                {t.qaReview&&<button onClick={()=>setViewQA(t)} style={{fontSize:11,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500,padding:0}}>View QA →</button>}
              </div>
            </td>
            <td style={{padding:"10px 13px"}}><Badge label={t.paid?"Paid":"Pending"} color={t.paid?"green":"orange"}/></td>
            <td style={{padding:"10px 13px"}}>
              {(t.rejectionReasons||[]).length===0
                ? <span style={{fontSize:12,color:TEXT_DISABLED}}>—</span>
                : <div style={{display:"flex",flexWrap:"wrap",gap:3,alignItems:"center"}}>{t.rejectionReasons.map(r=><span key={r} style={{fontSize:10,background:DANGER_BG,color:DANGER,padding:"1px 5px",borderRadius:3,fontWeight:600,whiteSpace:"nowrap"}}>{r}</span>)}</div>}
            </td>
            <td style={{padding:"10px 13px"}}>
              <span style={{fontSize:12,color:t.notes?TEXT_SECONDARY:TEXT_DISABLED,maxWidth:160,lineHeight:1.4,display:"block"}}>{t.notes||"—"}</span>
            </td>
          </tr>);
        }):<tr><td colSpan={showFilter?10:9} style={{padding:"28px",textAlign:"center",fontSize:13,color:TEXT_SUBTLE}}>No entries match the current filters.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>);
}

// Reusable multi-select dropdown filter
const MultiSelectDropdown=({label,options,selected,onChange})=>{
  const[open,setOpen]=useState(false);
  const ref=useRef(null);
  useEffect(()=>{
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);
  },[]);
  const toggle=opt=>onChange(selected.includes(opt)?selected.filter(x=>x!==opt):[...selected,opt]);
  const clearAll=()=>onChange([]);
  const displayLabel=selected.length===0?label:selected.length===1?selected[0]:`${label}: ${selected.length} selected`;
  const active=selected.length>0;
  return(<div ref={ref} style={{position:"relative"}}>
    <button onClick={()=>setOpen(v=>!v)} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 10px",borderRadius:6,border:`1px solid ${active?P:BORDER}`,background:active?PL:SURFACE,color:active?P:TEXT_SECONDARY,fontSize:13,cursor:"pointer",fontWeight:active?500:400,whiteSpace:"nowrap"}}>
      {displayLabel}
      {active&&<span onClick={e=>{e.stopPropagation();clearAll();}} style={{marginLeft:2,fontSize:13,color:P,lineHeight:1}}>×</span>}
      {!active&&<span style={{fontSize:10,color:TEXT_SUBTLE,marginLeft:2}}>▾</span>}
    </button>
    {open&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:9,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:50,minWidth:200,padding:"6px 0"}}>
      {options.map(opt=>(
        <label key={opt} style={{display:"flex",alignItems:"center",gap:9,padding:"7px 13px",cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=""}>
          <input type="checkbox" checked={selected.includes(opt)} onChange={()=>toggle(opt)} style={{accentColor:P,flexShrink:0}}/>
          <span style={{fontSize:13,color:TEXT_PRIMARY}}>{opt}</span>
        </label>
      ))}
      {selected.length>0&&<div style={{borderTop:`1px solid ${SURFACE_HOVER}`,padding:"6px 13px"}}>
        <button onClick={clearAll} style={{fontSize:12,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:0}}>Clear all</button>
      </div>}
    </div>}
  </div>);
};

// Simple single-select filter dropdown (styled like a filter pill)
const FilterSelect=({label,options,value,onChange})=>{
  const active=value&&value!==options[0];
  return(<select value={value} onChange={e=>onChange(e.target.value)} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${active?P:BORDER}`,background:active?PL:SURFACE,color:active?P:TEXT_SECONDARY,fontSize:13,cursor:"pointer",fontWeight:active?500:400,outline:"none",appearance:"auto"}}>
    {options.map(o=><option key={o}>{o}</option>)}
  </select>);
};

const MultiTypeahead=({label,required,options,selected,onAdd,onRemove,placeholder,allowCustom})=>{const[q,setQ]=useState(""),[open,setOpen]=useState(false);const ref=useRef(null);useEffect(()=>{const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);const filtered=options.filter(o=>o.toLowerCase().includes(q.toLowerCase())&&!selected.includes(o));const handleKey=e=>{if(e.key==="Enter"&&q.trim()){if(allowCustom&&!options.includes(q.trim())&&!selected.includes(q.trim())){onAdd(q.trim());setQ("");}else if(filtered.length>0){onAdd(filtered[0]);setQ("");setOpen(false);}}};return(<div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>{label}{required&&<span style={{color:DANGER}}> *</span>}</div>{selected.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:7}}>{selected.map(s=><span key={s} style={{display:"flex",alignItems:"center",gap:4,background:PL,color:P,fontSize:12,fontWeight:600,padding:"3px 8px 3px 10px",borderRadius:999}}>{s}<span onClick={()=>onRemove(s)} style={{cursor:"pointer",fontSize:14,lineHeight:1,color:BRAND_SOFT,marginLeft:1}}>×</span></span>)}</div>}<div ref={ref} style={{position:"relative"}}><input value={q} onChange={e=>{setQ(e.target.value);setOpen(true);}} onFocus={()=>setOpen(true)} onKeyDown={handleKey} placeholder={selected.length>0?"Add more…":placeholder} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/>{open&&(filtered.length>0||(allowCustom&&q.trim()&&!selected.includes(q.trim())))&&(<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:60,maxHeight:200,overflowY:"auto"}}>{filtered.map(o=>(<div key={o} onClick={()=>{onAdd(o);setQ("");setOpen(false);}} style={{padding:"8px 12px",fontSize:13,color:TEXT_PRIMARY,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>{o}</div>))}{allowCustom&&q.trim()&&!options.includes(q.trim())&&!selected.includes(q.trim())&&(<div onClick={()=>{onAdd(q.trim());setQ("");setOpen(false);}} style={{padding:"8px 12px",fontSize:13,color:P,cursor:"pointer",borderTop:filtered.length>0?`1px solid ${SURFACE_HOVER}`:"none"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>+ Add "{q.trim()}"</div>)}</div>)}</div></div>);};

const NewProjectModal=({onClose,onCreate,opProjects})=>{
  const[selLang,setSelLang]=useState(""),[selTopics,setSelTopics]=useState([]),[scripted,setScripted]=useState(null),[notes,setNotes]=useState(""),[name,setName]=useState(""),[numParticipants,setNumParticipants]=useState(2),[deadline,setDeadline]=useState(""),[hours,setHours]=useState("20"),[maxPerUser,setMaxPerUser]=useState("5"),[minMin,setMinMin]=useState("20"),[maxMin,setMaxMin]=useState("45"),[rate,setRate]=useState("0.20"),[groupCap,setGroupCap]=useState("2"),[projAssoc,setProjAssoc]=useState("");
  const isSolo=numParticipants===1;
  const handleCreate=()=>{
    if(!name||!scripted||!selLang||selTopics.length===0||!minMin||!maxMin||!hours||!maxPerUser||!deadline||!rate)return;
    const proj={id:`p${Date.now()}`,title:name,langs:[selLang],scripted:scripted==="Scripted",solo:isSolo,numParticipants,topics:selTopics,hours:parseInt(hours),hoursUsed:0,maxPerUser:parseInt(maxPerUser),deadline,claimable:true,status:"Active",durationRange:`${minMin}–${maxMin} min`,ratePerMin:parseFloat(rate),groupHoursCap:parseFloat(groupCap)||null,projectId:projAssoc||null,specialNotes:notes||undefined};
    onCreate?.(proj);
  };
  const canCreate=name&&scripted&&selLang&&selTopics.length>0&&minMin&&maxMin&&hours&&maxPerUser&&deadline&&rate&&(isSolo||groupCap);
  const Req=()=><span style={{color:DANGER}}> *</span>;
  return(<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}><div style={{background:SURFACE,borderRadius:12,width:520,maxHeight:"90vh",overflowY:"auto"}}>
    <div style={{padding:"22px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}><span style={{fontWeight:600,fontSize:16}}>New Audio Creation Batch</span><button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button></div>
    <div style={{padding:"0 24px 24px",display:"flex",flexDirection:"column",gap:16}}>
      <div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>BATCH NAME <Req/></div><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Spanish Conversational — Batch 4" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/></div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>PROJECT</div>
        <select value={projAssoc} onChange={e=>setProjAssoc(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",color:projAssoc?TEXT_PRIMARY:TEXT_SUBTLE}}>
          <option value="">— No project —</option>
          {(opProjects||initialOpsProjectsList).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>FORMAT <Req/></div><div style={{display:"flex",gap:8}}>{["Unscripted","Scripted"].map(f=>(<label key={f} style={{display:"flex",alignItems:"center",gap:8,flex:1,padding:"10px 14px",border:`1.5px solid ${scripted===f?P:BORDER}`,borderRadius:8,cursor:"pointer",background:scripted===f?PL:SURFACE}}><input type="radio" name="fmt" checked={scripted===f} onChange={()=>setScripted(f)} style={{accentColor:P}}/><div><div style={{fontWeight:500,fontSize:13}}>{f}</div><div style={{fontSize:11,color:TEXT_SUBTLE}}>{f==="Unscripted"?"Natural conversation":"Prepared script"}</div></div></label>))}</div>
      </div>
      {scripted==="Scripted"&&<div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>SCRIPT FOLDER</div>
        <div style={{border:`1.5px dashed ${BRAND_TINT}`,borderRadius:8,padding:"18px 14px",textAlign:"center",background:BRAND_LIGHTER,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
          <span style={{fontSize:24}}></span>
          <div style={{fontSize:13,fontWeight:500,color:P}}>Upload script folder</div>
          <div style={{fontSize:11,color:TEXT_SUBTLE}}>Drop a folder of .txt or .pdf scripts, one per recording. Creators will see their assigned script in the recorder.</div>
          <button style={{marginTop:4,padding:"5px 14px",borderRadius:6,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:12,fontWeight:500,cursor:"pointer"}}>Browse…</button>
        </div>
      </div>}
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>NUMBER OF PARTICIPANTS <Req/></div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <input type="number" min="1" max="3" value={numParticipants} onChange={e=>setNumParticipants(Math.min(3,Math.max(1,parseInt(e.target.value)||1)))} style={{width:72,padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:14,fontWeight:600,outline:"none",textAlign:"center"}}/>
        </div>
        <div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:4}}>Max 3 participants.</div>
      </div>
      {!isSolo&&<div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>MAX HOURS SAME GROUP CAN RECORD TOGETHER</div>
        <div style={{display:"flex",alignItems:"center",border:`1px solid ${BORDER}`,borderRadius:8,overflow:"hidden",maxWidth:140}}>
          <input type="number" min="0.5" step="0.5" value={groupCap} onChange={e=>setGroupCap(e.target.value)} placeholder="10" style={{flex:1,padding:"8px 10px",border:"none",fontSize:13,outline:"none"}}/>
          <span style={{padding:"8px 10px",fontSize:12,color:TEXT_SUBTLE,background:SURFACE_SUNKEN,borderLeft:`1px solid ${BORDER}`,whiteSpace:"nowrap"}}>hrs</span>
        </div>
      </div>}
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>LANGUAGE <Req/></div>
        <select value={selLang} onChange={e=>setSelLang(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",color:selLang?TEXT_PRIMARY:TEXT_SUBTLE}}>
          <option value="">Select language…</option>
          {LANGS.map(l=><option key={l}>{l}</option>)}
        </select>
      </div>
      <MultiTypeahead label="TOPICS" required options={TOPIC_OPTIONS} selected={selTopics} onAdd={t=>setSelTopics(s=>s.includes(t)?s:[...s,t])} onRemove={t=>setSelTopics(s=>s.filter(x=>x!==t))} placeholder="Type to search or add a topic…" allowCustom/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>RECORDING LENGTH (min)<Req/></div><div style={{display:"flex",gap:6,alignItems:"center"}}><input type="number" min="1" value={minMin} onChange={e=>setMinMin(e.target.value)} placeholder="20" style={{width:"60px",padding:"8px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",textAlign:"center"}}/><span style={{fontSize:12,color:TEXT_SUBTLE}}>to</span><input type="number" min="1" value={maxMin} onChange={e=>setMaxMin(e.target.value)} placeholder="45" style={{width:"60px",padding:"8px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",textAlign:"center"}}/></div></div>
        <div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>TOTAL HOURS NEEDED<Req/></div><input type="number" min="1" value={hours} onChange={e=>setHours(e.target.value)} placeholder="40" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/></div>
        <div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>MAX HOURS PER USER<Req/></div><input type="number" min="1" value={maxPerUser} onChange={e=>setMaxPerUser(e.target.value)} placeholder="5" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/></div>
        <div><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>RATE PER MINUTE<Req/></div><div style={{display:"flex",alignItems:"center",border:`1px solid ${BORDER}`,borderRadius:8,overflow:"hidden"}}><span style={{padding:"8px 8px 8px 10px",fontSize:13,color:TEXT_SUBTLE,background:SURFACE_SUNKEN,borderRight:`1px solid ${BORDER}`}}>$</span><input type="number" min="0" step="0.01" value={rate} onChange={e=>setRate(e.target.value)} placeholder="0.20" style={{flex:1,padding:"8px",border:"none",fontSize:13,outline:"none"}}/><span style={{padding:"8px 10px 8px 4px",fontSize:12,color:TEXT_SUBTLE}}>/min</span></div></div>
        <div style={{gridColumn:"1/-1"}}><div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>DEADLINE<Req/></div><input type="datetime-local" value={deadline} onChange={e=>setDeadline(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/></div>
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>SPECIAL INSTRUCTIONS FOR CREATORS</div>
        <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none",lineHeight:1.5}}/>

      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
        <span style={{fontSize:11,color:TEXT_SUBTLE}}><span style={{color:DANGER}}>*</span> Required fields</span>
        <div style={{display:"flex",gap:8}}><button onClick={onClose} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button><button onClick={handleCreate} disabled={!canCreate} style={{padding:"9px 18px",borderRadius:8,border:"none",background:canCreate?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:500,cursor:canCreate?"pointer":"default"}}>Create Batch →</button></div>
      </div>
    </div>
  </div></div>);
};

const NewBatchModal=({onClose,isAudio,projects})=>{
  const[batchName,setBatchName]=useState("");
  const[projAssoc,setProjAssoc]=useState("");
  const[taskType,setTaskType]=useState(isAudio?AUDIO_TASKS[0]:TRANSCRIPT_TASKS[0]);
  const[lang,setLang]=useState("");
  const[billingMode,setBillingMode]=useState("minute"); // "minute" | "task"
  const[rate,setRate]=useState("");
  const TYPES=isAudio?AUDIO_TASKS:TRANSCRIPT_TASKS;
  const canCreate=batchName.trim().length>0&&lang.length>0;
  return(<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}><div style={{background:SURFACE,borderRadius:12,width:560,maxHeight:"90vh",overflowY:"auto"}}>
    <div style={{padding:"22px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
      <span style={{fontWeight:600,fontSize:16}}>New {isAudio?"Audio Review":"Transcript"} Batch</span>
      <button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button>
    </div>
    <div style={{padding:"0 24px 24px",display:"flex",flexDirection:"column",gap:14}}>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>BATCH NAME <span style={{color:DANGER}}>*</span></div>
        <input value={batchName} onChange={e=>setBatchName(e.target.value)} placeholder={isAudio?"e.g. Arabic Scripted – Audio Review Batch 1":"e.g. Spanish Medical – Transcript Validation Batch 1"} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>LANGUAGE <span style={{color:DANGER}}>*</span></div>
          <select value={lang} onChange={e=>setLang(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${!lang?DANGER_BORDER:BORDER}`,borderRadius:8,fontSize:13,outline:"none",color:lang?TEXT_PRIMARY:TEXT_SUBTLE}}>
            <option value="">Select language…</option>
            {LANGS.map(l=><option key={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>TASK TYPE</div>
          <select value={taskType} onChange={e=>setTaskType(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none"}}>
            {TYPES.map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>PROJECT</div>
        <select value={projAssoc} onChange={e=>setProjAssoc(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",color:projAssoc?TEXT_PRIMARY:TEXT_SUBTLE}}>
          <option value="">— No project —</option>
          {(projects||initialOpsProjectsList).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>BILLING MODE <span style={{color:DANGER}}>*</span></div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          {[["minute","Per minute"],["task","Per task"]].map(([v,lbl])=>(
            <label key={v} style={{display:"flex",alignItems:"center",gap:8,flex:1,padding:"9px 12px",border:`1.5px solid ${billingMode===v?P:BORDER}`,borderRadius:8,cursor:"pointer",background:billingMode===v?PL:SURFACE}}>
              <input type="radio" checked={billingMode===v} onChange={()=>setBillingMode(v)} style={{accentColor:P}}/>
              <div style={{fontWeight:500,fontSize:13}}>{lbl}</div>
            </label>
          ))}
        </div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>
          RATE {billingMode==="minute"?"PER MINUTE":"PER TASK"} <span style={{color:DANGER}}>*</span>
        </div>
        <div style={{display:"flex",alignItems:"center",border:`1px solid ${BORDER}`,borderRadius:7,overflow:"hidden"}}>
          <span style={{padding:"7px 8px",fontSize:12,color:TEXT_SUBTLE,background:SURFACE_SUNKEN,borderRight:`1px solid ${BORDER}`}}>$</span>
          <input type="number" min="0" step="0.01" value={rate} onChange={e=>setRate(e.target.value)} placeholder="0.00" style={{flex:1,padding:"7px 8px",border:"none",fontSize:13,outline:"none"}}/>
          <span style={{padding:"7px 10px 7px 4px",fontSize:12,color:TEXT_SUBTLE}}>{billingMode==="minute"?"/min":"/task"}</span>
        </div>
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>{isAudio?"AUDIO FILE IDS":"ENGAGEMENT IDS"}</div>
        <textarea rows={3} placeholder={isAudio?"AUD-1001\nAUD-1002":"ENG-2241\nENG-2198"} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none"}}/>
        {!isAudio&&<div style={{border:`1.5px dashed ${TEXT_DISABLED}`,borderRadius:8,padding:12,textAlign:"center",color:TEXT_SUBTLE,fontSize:13,marginTop:6,cursor:"pointer"}}>Drop CSV or click to browse</div>}
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>DEADLINE</div>
        <input type="datetime-local" style={{padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",width:"100%",boxSizing:"border-box"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:4}}>
        <span style={{fontSize:11,color:TEXT_SUBTLE}}><span style={{color:DANGER}}>*</span> Required</span>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onClose} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={onClose} disabled={!canCreate} style={{padding:"9px 18px",borderRadius:8,border:"none",background:canCreate?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:500,cursor:canCreate?"pointer":"default"}}>Generate Tasks →</button>
        </div>
      </div>
    </div>
  </div></div>);
};

const ProjectCard=({p,approvedLangs,onClaim})=>{
  const myLangs=p.langs.filter(l=>approvedLangs.includes(l));
  const isLimit=p.limitReached;
  const isClosed=p.closedHoursAgo!=null||p.status==="Closed";
  const isFull=!isLimit&&!isClosed&&!p.claimable;
  const isUnavail=isLimit||isClosed||!p.claimable;
  return(<div style={{background:SURFACE,border:`1px solid ${isUnavail?SURFACE_HOVER:BORDER}`,borderRadius:12,padding:16,opacity:isUnavail?0.75:1,display:"flex",flexDirection:"column"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
      <div style={{fontWeight:500,fontSize:13,color:isUnavail?TEXT_SUBTLE:TEXT_PRIMARY,lineHeight:1.4,paddingRight:8}}>{p.title}</div>
      {isLimit&&<Badge label="Your limit reached" color="amber"/>}
      {!isLimit&&isClosed&&<Badge label="Closed" color="gray"/>}
      {!isLimit&&!isClosed&&<Badge label={p.claimable?"Open":"Full"} color={p.claimable?"green":"red"}/>}
    </div>
    <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:4}}>
      {myLangs.length>0?myLangs.map(l=><Badge key={l} label={l} color={isUnavail?"gray":"purple"}/>):<Badge label={p.langs?.[0]||"—"} color="gray"/>}
      {p.scripted!=null&&<Badge label={p.scripted?"Scripted":"Unscripted"} color={p.scripted?"blue":"teal"}/>}
    </div>
    <div style={{fontSize:12,color:TEXT_MUTED,marginBottom:4}}>{getParticipantCount(p) === 1 ? "1 participant per recording" : `${getParticipantCount(p)} participants per recording`}</div>
    {p.durationRange&&<div style={{fontSize:12,color:TEXT_MUTED,marginBottom:8}}>⏱ {p.durationRange} per recording</div>}
    <div style={{borderTop:`1px solid ${SURFACE_HOVER}`,paddingTop:9,marginBottom:10}}>
      <div style={{fontSize:10,fontWeight:700,color:BRAND,letterSpacing:"0.08em",marginBottom:5}}>TOPICS</div>
      <TopicList topics={p.topics}/>
    </div>
    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:5}}>
      <div style={{flex:1,height:4,background:BORDER,borderRadius:999,overflow:"hidden"}}><div style={{width:`${Math.round(p.hoursUsed/p.hours*100)}%`,height:"100%",background:isUnavail?TEXT_DISABLED:P,borderRadius:999}}/></div>
      <span style={{fontSize:11,color:TEXT_SUBTLE}}>{p.hoursUsed}h/{p.hours}h</span>
    </div>
    <div style={{fontSize:12,color:TEXT_SUBTLE,marginBottom:isUnavail?8:12}}>Max {p.maxPerUser}h · Deadline: {p.deadline}</div>
    {isLimit&&<div style={{fontSize:12,color:WARNING,background:WARNING_BG,borderRadius:6,padding:"6px 10px",marginBottom:10}}>You've reached your hour limit for this batch.</div>}
    {isClosed&&<div style={{fontSize:12,color:TEXT_MUTED,background:SURFACE_SUNKEN,borderRadius:6,padding:"6px 10px",marginBottom:10}}>This batch is no longer accepting contributions.</div>}
    {isFull&&<div style={{fontSize:12,color:TEXT_MUTED,background:SURFACE_SUNKEN,borderRadius:6,padding:"6px 10px",marginBottom:10}}>This batch is full and no longer accepting contributions.</div>}
    {!isUnavail&&<button onClick={()=>onClaim(p)} style={{width:"100%",padding:"7px",borderRadius:8,border:`1px solid ${P}`,background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer",marginTop:"auto"}}>{getParticipantCount(p)===1?"Start Recording →":"Create / Find a Meeting →"}</button>}
  </div>);
};

const sampleUsers=[
  {id:"u1",name:"blaize",email:"bdixon@jeenie.com",roles:["admin"],status:"Active",initials:"BD",capabilities:[]},
  {id:"u2",name:"dhruv",email:"dchhapekar@jeenie.com",roles:["reviewer","admin"],status:"Active",initials:"DC",capabilities:[{task:"Full Transcript Review",lang:"French",kind:"review"}]},
  {id:"u3",name:"James Carter",email:"jcrater@jeenie.com",roles:["admin"],status:"Active",initials:"JC",capabilities:[]},
  {id:"u4",name:"Amara Osei",email:"amara.osei@example.com",roles:["reviewer","creator"],status:"Active",initials:"AO",capabilities:[
    {task:"Full Transcript Review",lang:"Spanish",kind:"review"},
    {task:"Diarization Review",lang:"Spanish",kind:"review"},
    {task:"Diarization Review",lang:"French",kind:"review"},
    {task:"Unscripted Audio Creation",lang:"Spanish",kind:"create"},
    {task:"Unscripted Audio Creation",lang:"French",kind:"create"},
  ]},
  {id:"u5",name:"imran",email:"makhdoom.imran@10pearls.com",roles:["reviewer"],status:"Active",initials:"IM",capabilities:[{task:"Full Transcript Review",lang:"Hausa",kind:"review"}]},
  {id:"u6",name:"Sofia Reyes",email:"sofia.reyes@example.com",roles:["creator"],status:"Active",initials:"SR",capabilities:[{task:"Unscripted Audio Creation",lang:"Spanish",kind:"create"},{task:"Unscripted Audio Creation",lang:"French",kind:"create"}]},
  {id:"u7",name:"Kwame Boateng",email:"k.boateng@example.com",roles:["reviewer"],status:"Active",initials:"KB",capabilities:[
    {task:"Full Transcript Review",lang:"Spanish",kind:"review"},
    {task:"Full Transcript Review",lang:"French",kind:"review"},
    {task:"Audio Review",lang:"Spanish",kind:"review"},
    {task:"Audio Review",lang:"French",kind:"review"},
  ]},
  {id:"u8",name:"Zara Mensah",email:"z.mensah@example.com",roles:["reviewer"],status:"Inactive",initials:"ZM",capabilities:[{task:"Full Transcript Review",lang:"French",kind:"review"}]},
];

function AddCapabilityModal({onClose,onAdd}){
  const[selTasks,setSelTasks]=useState([]),[lang,setLang]=useState("");
  const toggleTask=t=>setSelTasks(s=>s.includes(t)?s.filter(x=>x!==t):[...s,t]);
  return(<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}><div style={{background:SURFACE,borderRadius:12,padding:26,width:460,maxHeight:"85vh",overflowY:"auto"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}><span style={{fontWeight:600,fontSize:16}}>Add Capability</span><button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button></div>
    <div style={{display:"flex",flexDirection:"column",gap:14,marginBottom:20}}>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>LANGUAGE <span style={{color:DANGER}}>*</span></div>
        <select value={lang} onChange={e=>setLang(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13}}>
          <option value="">— Select language —</option>
          {LANGS.map(l=><option key={l}>{l}</option>)}
        </select>
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>TASKS <span style={{color:DANGER}}>*</span></div>
        <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:180,overflowY:"auto",border:`1px solid ${BORDER}`,borderRadius:8,padding:8}}>
          {[...REVIEW_TASKS,"Unscripted Audio Creation","Scripted Audio Creation"].map(t=>(
            <label key={t} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 6px",borderRadius:6,cursor:"pointer",background:selTasks.includes(t)?PL:"transparent"}}>
              <input type="checkbox" checked={selTasks.includes(t)} onChange={()=>toggleTask(t)} style={{accentColor:P}}/>
              <span style={{fontSize:13,color:selTasks.includes(t)?P:TEXT_SECONDARY}}>{t}</span>
            </label>
          ))}
        </div>
        {selTasks.length>0&&<div style={{fontSize:11,color:TEXT_MUTED,marginTop:5}}>{selTasks.length} task{selTasks.length>1?"s":""} selected</div>}
      </div>
    </div>
    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
      <button onClick={onClose} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
      <button disabled={!lang||selTasks.length===0} onClick={()=>{selTasks.forEach(task=>onAdd({task,lang,kind:task.includes("Creation")?"create":"review"}));onClose();}} style={{padding:"8px 16px",borderRadius:8,border:"none",background:lang&&selTasks.length?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:500,cursor:lang&&selTasks.length?"pointer":"default"}}>
        Add {selTasks.length>1?`${selTasks.length} Capabilities`:"Capability"}
      </button>
    </div>
  </div></div>);
}

// ── RATE MANAGEMENT (Admin only) ──────────────────────────────────────────────
// Tiers used by both the rates table (variant bonus columns) and the language tier tab.
// T1 is "no variant bonus" — the baseline — so it isn't shown as a column on the rates table.
const LANGUAGE_TIERS=[
  {key:"t1",label:"Tier 1",sublabel:"Cheapest",shortLabel:"T1"},
  {key:"t2",label:"Tier 2",sublabel:null,shortLabel:"T2"},
  {key:"t3",label:"Tier 3",sublabel:null,shortLabel:"T3"},
  {key:"t4",label:"Tier 4",sublabel:"Most expensive",shortLabel:"T4"},
];
const VARIANT_BONUS_TIERS=LANGUAGE_TIERS.slice(1); // T2, T3, T4 — the ones that actually add a bonus

// Per-task rates. Each task has its own unit, base rate, and language-variant bonuses.
// All amounts in USD, 2-decimal precision. Bonuses added on top of base.
// T1 is omitted (always $0.00, baseline).
const initialTaskRates=[
  {task:"Audio Review",             unit:"per task",        base:2.50,t2:0.25,t3:0.50,t4:0.90},
  {task:"Post-Model Audio Review",  unit:"per task",        base:1.80,t2:0.20,t3:0.40,t4:0.70},
  {task:"Diarization Review",       unit:"per audio minute",base:1.20,t2:0.10,t3:0.20,t4:0.40},
  {task:"Diarization Creation",     unit:"per audio minute",base:1.80,t2:0.15,t3:0.30,t4:0.55},
  {task:"Full Transcript Review",   unit:"per word",        base:0.09,t2:0.02,t3:0.03,t4:0.05},
  {task:"Full Transcript Review QA",unit:"per word",        base:0.05,t2:0.01,t3:0.02,t4:0.03},
  {task:"Transcript Review (no diarization)",unit:"per word",base:0.07,t2:0.01,t3:0.02,t4:0.03},
  {task:"Transcript Creation",      unit:"per audio minute",base:1.90,t2:0.15,t3:0.30,t4:0.60},
  {task:"Transcript Creation QA",   unit:"per audio minute",base:1.10,t2:0.10,t3:0.20,t4:0.35},
  {task:"topic + sentiment",        unit:"per word",        base:0.08,t2:0.02,t3:0.03,t4:0.05},
  {task:"topic",                    unit:"per word",        base:0.05,t2:0.01,t3:0.01,t4:0.02},
  {task:"sentiment",                unit:"per word",        base:0.04,t2:0.01,t3:0.01,t4:0.02},
  {task:"NER/PII Recognition",      unit:"per word",        base:0.07,t2:0.01,t3:0.02,t4:0.04},
];

// Language variants — regional dialects, not just languages. Each variant is independently tiered.
const LANGUAGE_VARIANTS=[
  "English (U.S.)","English (U.K.)","English (Australia)","English (India)",
  "Spanish (Mexico)","Spanish (Spain)","Spanish (Argentina)","Spanish (U.S.)",
  "Mandarin (Mainland)","Mandarin (Taiwan)",
  "Hindi (India)",
  "French (France)","French (Canada)",
  "Arabic (MSA)","Arabic (Egyptian)","Arabic (Levantine)","Arabic (Gulf)","Arabic (Maghrebi)",
  "Portuguese (Brazil)","Portuguese (Europe)",
  "Bengali (India)","Bengali (Bangladesh)",
  "Russian (Russia)",
  "Swahili (Kenya)","Swahili (Tanzania)",
  "Vietnamese (Northern)","Vietnamese (Southern)",
  "Turkish (Turkey)",
  "Hausa (Nigeria)","Hausa (Niger)",
  "Yoruba (Nigeria)",
  "Igbo (Nigeria)",
];

// Provisional variant → tier assignment. T1 = most common, T4 = rarest.
const initialLangTier={
  "English (U.S.)":"t1","English (U.K.)":"t1","Spanish (Mexico)":"t1","Spanish (Spain)":"t1",
  "Mandarin (Mainland)":"t1","Hindi (India)":"t1","French (France)":"t1",
  "Portuguese (Brazil)":"t1","Arabic (MSA)":"t1",
  "English (Australia)":"t2","English (India)":"t2","Spanish (Argentina)":"t2","Spanish (U.S.)":"t2",
  "Mandarin (Taiwan)":"t2","French (Canada)":"t2","Arabic (Egyptian)":"t2","Arabic (Levantine)":"t2",
  "Russian (Russia)":"t2","Bengali (India)":"t2","Bengali (Bangladesh)":"t2",
  "Portuguese (Europe)":"t2","Turkish (Turkey)":"t2",
  "Arabic (Gulf)":"t3","Arabic (Maghrebi)":"t3","Vietnamese (Northern)":"t3","Vietnamese (Southern)":"t3",
  "Swahili (Kenya)":"t3","Swahili (Tanzania)":"t3",
  "Hausa (Nigeria)":"t4","Hausa (Niger)":"t4","Yoruba (Nigeria)":"t4","Igbo (Nigeria)":"t4",
};

// Inline-editable currency cell. Click to edit (when editable); Enter/blur saves; Esc cancels. Always 2 decimals.
function RateCell({modelValue,onChange,prefix,editable}){
  const[editing,setEditing]=useState(false);
  const[draft,setDraft]=useState("");
  const start=()=>{if(!editable)return;setDraft(modelValue.toFixed(2));setEditing(true);};
  const commit=()=>{
    const n=parseFloat(draft);
    if(!Number.isNaN(n)&&n>=0) onChange(Math.round(n*100)/100);
    setEditing(false);
  };
  if(editing) return <input
    autoFocus
    type="number"
    min="0"
    step="0.01"
    value={draft}
    onChange={e=>setDraft(e.target.value)}
    onBlur={commit}
    onKeyDown={e=>{if(e.key==="Enter") e.currentTarget.blur(); else if(e.key==="Escape") setEditing(false);}}
    style={{width:78,padding:"3px 6px",border:`1.5px solid ${P}`,borderRadius:5,fontSize:13,textAlign:"right",outline:"none",fontFamily:"inherit",fontVariantNumeric:"tabular-nums",boxSizing:"border-box"}}
  />;
  const isZero=modelValue===0;
  const sign=prefix&&!isZero?prefix:"";
  return <span
    onClick={start}
    title={editable?"Click to edit":undefined}
    style={{cursor:editable?"pointer":"default",display:"inline-block",padding:"3px 6px",borderRadius:5,color:isZero?TEXT_SUBTLE:TEXT_PRIMARY,fontVariantNumeric:"tabular-nums",fontWeight:prefix?400:600,background:editable?BRAND_LIGHTER:"transparent"}}
  >{sign}${modelValue.toFixed(2)}</span>;
}

// Custom hover-triggered tooltip. Cursor-following so it never renders off-screen above
// the trigger (which the previous bounding-rect positioning did for elements near the top
// of the viewport). Pointer-events:none so it doesn't interfere with hover detection.
function Tooltip({content,children}){
  const[pos,setPos]=useState(null);
  const track=(e)=>setPos({x:e.clientX,y:e.clientY});
  const leave=()=>setPos(null);
  return <span onMouseEnter={track} onMouseMove={track} onMouseLeave={leave} style={{display:"inline-block"}}>
    {children}
    {pos!==null?<span style={{position:"fixed",left:pos.x+14,top:pos.y+14,background:"#1f2937",color:"#fff",padding:"8px 12px",borderRadius:6,fontSize:12,lineHeight:1.45,maxWidth:320,width:"max-content",whiteSpace:"normal",zIndex:99999,pointerEvents:"none",boxShadow:"0 6px 20px rgba(0,0,0,0.25)",fontWeight:400,letterSpacing:0,textTransform:"none",textAlign:"left"}}>{content}</span>:null}
  </span>;
}

// Reusable column-grouped grid with drag-and-drop reassignment. Used for both the
// task→category and language→tier mapping tabs. Drag is disabled when editable=false.
function GroupedDraggableGrid({columns,items,onMove,editable}){
  const[dragKey,setDragKey]=useState(null);
  const[hoverCol,setHoverCol]=useState(null);
  const handleDrop=(colKey)=>{
    if(dragKey!=null) onMove(dragKey,colKey);
    setDragKey(null);
    setHoverCol(null);
  };
  return (<div style={{display:"grid",gridTemplateColumns:`repeat(${columns.length},1fr)`,gap:12}}>
    {columns.map(col=>{
      const colItems=items.filter(it=>it.group===col.key);
      const isHover=editable&&hoverCol===col.key;
      return <div
        key={col.key}
        onDragOver={editable?(e=>{e.preventDefault();setHoverCol(col.key);}):undefined}
        onDragLeave={editable?(()=>setHoverCol(prev=>prev===col.key?null:prev)):undefined}
        onDrop={editable?(()=>handleDrop(col.key)):undefined}
        style={{background:isHover?PL:SURFACE_SUNKEN,border:`1.5px ${isHover?"dashed":"solid"} ${isHover?P:BORDER}`,borderRadius:10,padding:12,minHeight:240,transition:"background 0.12s, border-color 0.12s"}}
      >
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",padding:"0 4px",marginBottom:col.sublabel?2:10}}>
          <div style={{fontSize:13,fontWeight:600,color:TEXT_PRIMARY}}>{col.label}</div>
          <div style={{fontSize:11,color:TEXT_SUBTLE}}>{colItems.length}</div>
        </div>
        {col.sublabel?<div style={{fontSize:11,color:TEXT_SUBTLE,padding:"0 4px",marginBottom:10}}>{col.sublabel}</div>:null}
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {colItems.map(it=>(
            <div
              key={it.key}
              draggable={editable}
              onDragStart={editable?(()=>setDragKey(it.key)):undefined}
              onDragEnd={editable?(()=>{setDragKey(null);setHoverCol(null);}):undefined}
              style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:7,padding:"7px 10px",fontSize:13,color:TEXT_PRIMARY,cursor:editable?"grab":"default",opacity:dragKey===it.key?0.4:1,userSelect:"none",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}
            >
              <span style={{flex:1,minWidth:0}}>{it.label}</span>
              {it.metas&&it.metas.length>0?<div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end",flexShrink:0}}>
                {it.metas.map(m=>(
                  <span key={m} style={{fontSize:11,color:TEXT_MUTED,padding:"2px 7px",background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:4,whiteSpace:"nowrap",fontWeight:500}}>{m}</span>
                ))}
              </div>:null}
            </div>
          ))}
          {colItems.length===0?<div style={{fontSize:12,color:TEXT_SUBTLE,fontStyle:"italic",textAlign:"center",padding:"16px 0"}}>{editable?"Drop here":"—"}</div>:null}
        </div>
      </div>;
    })}
  </div>);
}

const RATE_TABS=[
  {key:"rates",label:"Base rates & bonuses"},
  {key:"languages",label:"Language variant tiers"},
];

// Per-tab top action bar: shows Edit button in view mode, Cancel + Save in edit mode.
function RateActionBar({editing,description,onEdit,onCancel,onSave}){
  return <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,padding:"10px 12px",background:editing?BRAND_LIGHTER:"transparent",border:editing?`1px solid ${BRAND_LIGHT}`:"1px solid transparent",borderRadius:8}}>
    <div style={{fontSize:12,color:editing?P:TEXT_MUTED,display:"flex",alignItems:"center",gap:6}}>
      {editing?<><span style={{fontSize:14}}>✎</span><strong>Editing —</strong><span>changes are not saved yet.</span></>:<span>{description}</span>}
    </div>
    <div style={{display:"flex",gap:8}}>
      {editing?<>
        <button onClick={onCancel} style={{padding:"7px 14px",borderRadius:7,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_PRIMARY,fontSize:13,cursor:"pointer"}}>Cancel</button>
        <button onClick={onSave} style={{padding:"7px 14px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Save changes</button>
      </>:<button onClick={onEdit} style={{padding:"7px 14px",borderRadius:7,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:13,fontWeight:500,cursor:"pointer"}}>✎ Edit table</button>}
    </div>
  </div>;
}

function CostCalculator({rates,langTier}){
  const sortedTasks=rates.map(r=>r.task).sort();
  const sortedLangs=[...LANGUAGE_VARIANTS].sort();

  // Per-unit label config. Adding a new unit type? Add an entry here. The fallback at the
  // bottom keeps the calculator working for any unit string we haven't explicitly styled.
  const UNIT_CONFIG={
    "per word":        {singular:"word",   plural:"words",   countLabel:"# OF WORDS",         defaultCount:"500"},
    "per minute":      {singular:"minute", plural:"minutes", countLabel:"# OF MINUTES",       defaultCount:"60"},
    "per audio minute":{singular:"audio minute",plural:"audio minutes",countLabel:"# OF AUDIO MINUTES",defaultCount:"60"},
    "per task":        {singular:"task",   plural:"tasks",   countLabel:"# OF TASKS",         defaultCount:"10"},
  };
  const unitInfo=(u)=>UNIT_CONFIG[u]||{singular:u.replace(/^per\s+/,"")||"unit",plural:(u.replace(/^per\s+/,"")||"unit")+"s",countLabel:`# OF ${(u.replace(/^per\s+/,"")||"unit").toUpperCase()}S`,defaultCount:"1"};

  const[taskName,setTaskName]=useState(sortedTasks[0]);
  const[variant,setVariant]=useState("English (U.S.)");
  // Counts are keyed by unit so the user's input survives a task switch that changes units.
  const[countByUnit,setCountByUnit]=useState(()=>{
    const init={};
    rates.forEach(r=>{if(!(r.unit in init)) init[r.unit]=unitInfo(r.unit).defaultCount;});
    return init;
  });

  const row=rates.find(r=>r.task===taskName);
  if(!row) return null;

  const effectiveUnit=row.unit;
  const info=unitInfo(effectiveUnit);
  const count=countByUnit[effectiveUnit]??info.defaultCount;
  const setCount=(v)=>setCountByUnit(prev=>({...prev,[effectiveUnit]:v}));

  const tier=langTier[variant]||"t1";
  const tierMeta=LANGUAGE_TIERS.find(t=>t.key===tier);
  const tierLabel=tierMeta?tierMeta.label:tier;

  const baseR=row.base;
  const varR=tier==="t1"?0:(row[tier]||0);
  const perUnit=baseR+varR;

  const n=parseFloat(count);
  const validN=!Number.isNaN(n)&&n>=0;
  const total=validN?perUnit*n:0;

  const fieldLabel={fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.06em",marginBottom:5,display:"block"};
  const fieldInput={width:"100%",padding:"7px 9px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,boxSizing:"border-box",outline:"none",background:SURFACE,color:TEXT_PRIMARY};

  return <div style={{marginTop:28}}>
    <h2 style={{fontSize:15,fontWeight:600,margin:"0 0 4px"}}>Cost calculator</h2>
    <p style={{fontSize:12,color:TEXT_MUTED,margin:"0 0 14px"}}>Pick a task type, language variant, and quantity to preview the total cost.</p>

    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:14}}>
        <div>
          <label style={fieldLabel}>TASK TYPE</label>
          <select value={taskName} onChange={e=>setTaskName(e.target.value)} style={fieldInput}>
            {sortedTasks.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={fieldLabel}>LANGUAGE VARIANT BONUS</label>
          <select value={variant} onChange={e=>setVariant(e.target.value)} style={fieldInput}>
            {sortedLangs.map(v=><option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={fieldLabel}>{info.countLabel}</label>
          <input type="number" min="0" value={count} onChange={e=>setCount(e.target.value)} style={{...fieldInput,textAlign:"right",fontVariantNumeric:"tabular-nums"}}/>
        </div>
      </div>

      <div style={{marginTop:18,padding:"14px 16px",background:BRAND_LIGHTER,borderRadius:8}}>
        <div style={{fontSize:11,color:TEXT_SUBTLE,letterSpacing:"0.06em",fontWeight:600,marginBottom:8}}>
          {tierLabel.toUpperCase()} · {effectiveUnit.toUpperCase()}
        </div>
        <div style={{fontSize:13,color:TEXT_MUTED,marginBottom:10,fontVariantNumeric:"tabular-nums",lineHeight:1.5}}>
          ${baseR.toFixed(2)} (base) + ${varR.toFixed(2)} ({tierLabel}) = <strong style={{color:TEXT_PRIMARY}}>${perUnit.toFixed(2)}</strong> per {info.singular}
        </div>
        <div style={{display:"flex",alignItems:"baseline",gap:10}}>
          <span style={{fontSize:24,fontWeight:700,color:P,fontVariantNumeric:"tabular-nums"}}>${total.toFixed(2)}</span>
          <span style={{fontSize:13,color:TEXT_MUTED}}>total{validN?` for ${n.toLocaleString()} ${info.plural}`:""}</span>
        </div>
      </div>
    </div>
  </div>;
}

function RateManagementScreen(){
  const[activeTab,setActiveTab]=useState("rates");

  // Committed (saved) state.
  const[rates,setRates]=useState(initialTaskRates);
  const[langTier,setLangTier]=useState(initialLangTier);

  // Edit-mode state. editingTab is null when no edits are in progress.
  // While editing one tab, the other tabs are locked.
  const[editingTab,setEditingTab]=useState(null);
  const[draftRates,setDraftRates]=useState(null);
  const[draftLangTier,setDraftLangTier]=useState(null);

  // Save confirmation modal: null or {body, onConfirm}.
  const[confirmSave,setConfirmSave]=useState(null);

  // Display state — uses draft when that tab is being edited; committed values otherwise.
  const displayRates=editingTab==="rates"&&draftRates?draftRates:rates;
  const displayLangTier=editingTab==="languages"&&draftLangTier?draftLangTier:langTier;

  const enterEdit=(tab)=>{
    setEditingTab(tab);
    if(tab==="rates") setDraftRates(rates.map(r=>({...r})));
    if(tab==="languages") setDraftLangTier({...langTier});
  };
  const cancelEdit=()=>{
    setEditingTab(null);
    setDraftRates(null);
    setDraftLangTier(null);
  };
  const requestSave=()=>{
    const messages={
      rates:"Updating these rates affects how every reviewer is paid going forward. This change applies to all new tasks created after saving. Confirm to apply.",
      languages:"Changing how language variants map to tiers changes the variant bonus paid for that language going forward. Confirm to apply.",
    };
    setConfirmSave({
      body:messages[editingTab],
      onConfirm:()=>{
        if(editingTab==="rates"&&draftRates) setRates(draftRates);
        if(editingTab==="languages"&&draftLangTier) setLangTier(draftLangTier);
        setEditingTab(null);
        setDraftRates(null);
        setDraftLangTier(null);
        setConfirmSave(null);
      },
    });
  };

  // Update fns: write to draft state when editing, no-op otherwise.
  const updateCell=(rowIdx,field,value)=>{
    if(editingTab!=="rates") return;
    setDraftRates(prev=>prev.map((r,i)=>i===rowIdx?{...r,[field]:value}:r));
  };
  const updateLangTier=(lang,tier)=>{
    if(editingTab!=="languages") return;
    setDraftLangTier(prev=>({...prev,[lang]:tier}));
  };

  // Style helpers shared by the rates matrix.
  const thBase={padding:"8px 10px",textAlign:"center",fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.06em",background:SURFACE_SUNKEN};
  const thLeft={...thBase,textAlign:"left"};
  const groupBorder=`2px solid ${BORDER_STRONG}`;
  const cellBorder=`1px solid ${BORDER}`;
  const tdBase={padding:"6px 10px",fontSize:13,textAlign:"right"};

  // Tab data
  const sortedLangs=[...LANGUAGE_VARIANTS].sort();
  const tierColumns=LANGUAGE_TIERS.map(t=>({key:t.key,label:t.label,sublabel:t.sublabel}));
  const langItems=sortedLangs.map(l=>({key:l,label:l,group:displayLangTier[l]||"t1"}));

  const ratesEditing=editingTab==="rates";
  const langsEditing=editingTab==="languages";

  return (<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    {/* Save confirmation modal */}
    {confirmSave?<div style={{position:"fixed",inset:0,background:OVERLAY_MAX,display:"flex",alignItems:"center",justifyContent:"center",zIndex:600}}>
      <div style={{background:SURFACE,borderRadius:14,padding:24,width:460,boxShadow:"0 16px 48px rgba(0,0,0,0.25)"}}>
        <div style={{fontWeight:600,fontSize:16,marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:18}}>⚠</span>
          <span>Save changes?</span>
        </div>
        <p style={{fontSize:13,color:TEXT_MUTED,lineHeight:1.55,margin:"0 0 18px"}}>{confirmSave.body}</p>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setConfirmSave(null)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={confirmSave.onConfirm} style={{padding:"8px 16px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Yes, save changes</button>
        </div>
      </div>
    </div>:null}

    <div style={{marginBottom:14}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>Rate Management</h1>
    </div>

    {/* Tabs — other tabs are locked while editing */}
    <div style={{display:"flex",gap:0,borderBottom:cellBorder,marginBottom:22}}>
      {RATE_TABS.map(t=>{
        const active=activeTab===t.key;
        const blocked=editingTab&&t.key!==editingTab;
        return <button
          key={t.key}
          onClick={()=>{if(!blocked) setActiveTab(t.key);}}
          disabled={blocked}
          style={{padding:"10px 18px",background:"none",border:"none",borderBottom:active?`2px solid ${P}`:"2px solid transparent",color:blocked?TEXT_SUBTLE:(active?P:TEXT_MUTED),fontSize:13,fontWeight:active?600:500,cursor:blocked?"not-allowed":"pointer",marginBottom:-1,opacity:blocked?0.5:1}}
        >{t.label}{editingTab===t.key?<span style={{marginLeft:6,color:P}}>•</span>:null}</button>;
      })}
    </div>

    {activeTab==="rates"?<div>
      <RateActionBar
        editing={ratesEditing}
        description=""
        onEdit={()=>enterEdit("rates")}
        onCancel={cancelEdit}
        onSave={requestSave}
      />
      <div style={{background:SURFACE,border:cellBorder,borderRadius:10,overflow:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:1080}}>
          <thead>
            <tr>
              <th rowSpan={2} style={{...thLeft,borderBottom:cellBorder}}>TASK TYPE</th>
              <th rowSpan={2} style={{...thLeft,borderLeft:cellBorder,borderBottom:cellBorder}}>BILLING UNIT</th>
              <th rowSpan={2} style={{...thBase,borderLeft:cellBorder,borderBottom:cellBorder}}>BASE RATE</th>
              <th colSpan={3} style={{...thBase,borderLeft:groupBorder,borderBottom:cellBorder}}>LANGUAGE VARIANT BONUS</th>
              <th colSpan={2} style={{...thBase,borderLeft:groupBorder,borderBottom:cellBorder,background:BRAND_LIGHTER}}>RANGE</th>
            </tr>
            <tr>
              {VARIANT_BONUS_TIERS.map((t,i)=>(
                <th key={t.key} style={{...thBase,borderLeft:i===0?groupBorder:undefined,borderBottom:cellBorder}}>{t.shortLabel}</th>
              ))}
              <th style={{...thBase,borderLeft:groupBorder,borderBottom:cellBorder,background:BRAND_LIGHTER,cursor:"help"}}>
                <Tooltip content="Cheapest possible total — just the base rate (Tier 1 variant has no bonus).">
                  <span style={{borderBottom:`1px dotted ${TEXT_SUBTLE}`}}>MIN</span>
                </Tooltip>
              </th>
              <th style={{...thBase,borderBottom:cellBorder,background:BRAND_LIGHTER,cursor:"help"}}>
                <Tooltip content="Priciest possible total — base rate + Tier 4 variant bonus.">
                  <span style={{borderBottom:`1px dotted ${TEXT_SUBTLE}`}}>MAX</span>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRates.map((row,i)=>{
              const isLast=i===displayRates.length-1;
              const td={...tdBase,borderBottom:isLast?"none":cellBorder};
              const minTotal=row.base;
              const maxTotal=row.base+row.t4;
              const unitNoun=row.unit.replace(/^per\s+/,"");
              const minTip=`Base rate only: $${row.base.toFixed(2)} / ${unitNoun}.`;
              const maxTip=`$${row.base.toFixed(2)} (base) + $${row.t4.toFixed(2)} (Tier 4) = $${maxTotal.toFixed(2)} / ${unitNoun}.`;
              const rangeCellStyle={...td,background:BRAND_LIGHTER};
              return <tr key={row.task}>
                <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:TEXT_PRIMARY,verticalAlign:"middle",background:SURFACE_SUNKEN,borderBottom:isLast?"none":cellBorder,textAlign:"left"}}>{row.task}</td>
                <td style={{...td,textAlign:"left",color:TEXT_MUTED,borderLeft:cellBorder}}>{row.unit}</td>
                <td style={{...td,borderLeft:cellBorder}}>
                  <RateCell modelValue={row.base} onChange={v=>updateCell(i,"base",v)} editable={ratesEditing}/>
                </td>
                {VARIANT_BONUS_TIERS.map((t,j)=>(
                  <td key={t.key} style={{...td,borderLeft:j===0?groupBorder:undefined}}>
                    <RateCell modelValue={row[t.key]} onChange={v=>updateCell(i,t.key,v)} prefix="+" editable={ratesEditing}/>
                  </td>
                ))}
                <td style={{...rangeCellStyle,borderLeft:groupBorder,cursor:"help"}}>
                  <Tooltip content={minTip}>
                    <span style={{padding:"3px 6px",color:TEXT_MUTED,fontVariantNumeric:"tabular-nums",fontWeight:600}}>${minTotal.toFixed(2)}</span>
                  </Tooltip>
                </td>
                <td style={{...rangeCellStyle,cursor:"help"}}>
                  <Tooltip content={maxTip}>
                    <span style={{padding:"3px 6px",color:P,fontVariantNumeric:"tabular-nums",fontWeight:700}}>${maxTotal.toFixed(2)}</span>
                  </Tooltip>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      <CostCalculator rates={displayRates} langTier={displayLangTier}/>
    </div>:null}

    {activeTab==="languages"?<div>
      <RateActionBar
        editing={langsEditing}
        description=""
        onEdit={()=>enterEdit("languages")}
        onCancel={cancelEdit}
        onSave={requestSave}
      />
      <GroupedDraggableGrid columns={tierColumns} items={langItems} onMove={(lang,tier)=>updateLangTier(lang,tier)} editable={langsEditing}/>
    </div>:null}
  </div>);
}

function UserManagementScreen(){
  const[sel,setSel]=useState(null),[search,setSearch]=useState(""),[addUserModal,setAddUserModal]=useState(false),[addCapModal,setAddCapModal]=useState(false);
  const[userList,setUserList]=useState(sampleUsers);
  const filtered=userList.filter(u=>u.name.toLowerCase().includes(search.toLowerCase())||u.email.toLowerCase().includes(search.toLowerCase()));
  const addCap=cap=>{setUserList(list=>list.map(u=>u.id===sel.id?{...u,capabilities:[...u.capabilities,cap]}:u));setSel(s=>({...s,capabilities:[...s.capabilities,cap]}));};
  const removeCap=i=>{setUserList(list=>list.map(u=>u.id===sel.id?{...u,capabilities:u.capabilities.filter((_,j)=>j!==i)}:u));setSel(s=>({...s,capabilities:s.capabilities.filter((_,j)=>j!==i)}));};

  if(sel){
    const byLang=sel.capabilities.reduce((acc,c)=>{(acc[c.lang]=acc[c.lang]||[]).push(c);return acc;},{});
    return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
      {addCapModal&&<AddCapabilityModal onClose={()=>setAddCapModal(false)} onAdd={addCap}/>}
      <button onClick={()=>setSel(null)} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:20,padding:0}}>← Back to Users</button>
      <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24}}>
        <div style={{width:52,height:52,borderRadius:"50%",background:P,color:SURFACE,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,fontWeight:600}}>{sel.initials}</div>
        <div><div style={{fontWeight:600,fontSize:18}}>{sel.name}</div><div style={{fontSize:13,color:TEXT_MUTED}}>{sel.email}</div></div>
        <div style={{marginLeft:"auto",display:"flex",gap:8}}>
          <button style={{padding:"7px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Edit</button>
          <button style={{padding:"7px 16px",borderRadius:8,border:`1px solid ${DANGER_BORDER}`,background:SURFACE,color:DANGER,fontSize:13,cursor:"pointer"}}>Delete</button>
        </div>
      </div>
      <div style={{maxWidth:600,display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:20}}>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:14}}>ACCOUNT DETAILS</div>
          <div style={{display:"grid",gridTemplateColumns:"130px 1fr",gap:"10px 0",fontSize:13}}>
            <span style={{color:TEXT_SUBTLE}}>Email</span><span>{sel.email}</span>
            <span style={{color:TEXT_SUBTLE}}>Roles</span><span style={{display:"flex",gap:5,flexWrap:"wrap"}}>{sel.roles.map(r=><Badge key={r} label={r} color="purple"/>)}</span>
            <span style={{color:TEXT_SUBTLE}}>Status</span><span><Badge label={sel.status} color={sel.status==="Active"?"green":"gray"}/></span>
          </div>
        </div>
        <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>APPROVED CAPABILITIES</div>
            <button onClick={()=>setAddCapModal(true)} style={{fontSize:12,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500}}>+ Add capability</button>
          </div>
          {sel.capabilities.length===0&&<div style={{fontSize:13,color:TEXT_SUBTLE,textAlign:"center",padding:"16px 0"}}>No capabilities yet. <span style={{color:P,cursor:"pointer"}} onClick={()=>setAddCapModal(true)}>Add one →</span></div>}
          {Object.entries(byLang).map(([lang,caps],li)=>(
            <div key={lang} style={{marginBottom:li<Object.entries(byLang).length-1?18:0}}>
              <div style={{marginBottom:8}}><Badge label={lang} color="purple"/></div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <tbody>{caps.map((c,i)=>{const globalIdx=sel.capabilities.indexOf(c);return(
                  <tr key={i} style={{borderBottom:i<caps.length-1?`1px solid ${SURFACE_SUNKEN}`:"none"}}>
                    <td style={{padding:"7px 8px 7px 0",color:TEXT_SECONDARY}}>{c.task}</td>
                    <td style={{padding:"7px 0 7px 8px",textAlign:"right"}}><button onClick={()=>removeCap(globalIdx)} style={{fontSize:11,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:0}}>Remove</button></td>
                  </tr>
                );})}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>);
  }

  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    {addUserModal&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}>
      <div style={{background:SURFACE,borderRadius:12,padding:26,width:420}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <span style={{fontWeight:600,fontSize:16}}>Add User</span>
          <button onClick={()=>setAddUserModal(false)} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:18}}>
          {[["NAME","Full name"],["EMAIL","their@email.com"]].map(([l,ph])=>(
            <div key={l}>
              <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>{l}</div>
              <input placeholder={ph} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none"}}/>
            </div>
          ))}
          <div>
            <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5}}>ROLES</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {["admin","reviewer","creator"].map(r=>(
                <label key={r} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 10px",border:`1px solid ${BORDER}`,borderRadius:6,cursor:"pointer",fontSize:13}}>
                  <input type="checkbox" style={{accentColor:P}}/>{r}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setAddUserModal(false)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={()=>setAddUserModal(false)} style={{padding:"8px 16px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Add User →</button>
        </div>
      </div>
    </div>}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
      <div><h1 style={{fontSize:21,fontWeight:600,margin:0,marginBottom:2}}>Users</h1><p style={{fontSize:13,color:TEXT_MUTED,margin:0}}>{userList.length} members</p></div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>setAddUserModal(true)} style={{padding:"8px 16px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>+ Add User</button>
        <button style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:13,fontWeight:500,cursor:"pointer"}}>⬆ Bulk Invite via CSV</button>
      </div>
    </div>
    <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or email…" style={{width:"100%",maxWidth:340,padding:"8px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none",marginBottom:16}}/>
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${BORDER}`}}>
          {["User","Email","Roles","Status","Capabilities",""].map(h=><th key={h} style={{padding:"9px 13px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>{h}</th>)}
        </tr></thead>
        <tbody>{filtered.map(u=>(
          <tr key={u.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`}}>
            <td style={{padding:"10px 13px"}}>
              <div style={{display:"flex",alignItems:"center",gap:9}}>
                <div style={{width:30,height:30,borderRadius:"50%",background:P,color:SURFACE,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,flexShrink:0}}>{u.initials}</div>
                <span style={{fontSize:13,fontWeight:500}}>{u.name}</span>
              </div>
            </td>
            <td style={{padding:"10px 13px",fontSize:13,color:TEXT_MUTED}}>{u.email}</td>
            <td style={{padding:"10px 13px"}}><div style={{display:"flex",gap:4,flexWrap:"wrap"}}>{u.roles.map(r=><Badge key={r} label={r} color="purple"/>)}</div></td>
            <td style={{padding:"10px 13px"}}><Badge label={u.status} color={u.status==="Active"?"green":"gray"}/></td>
            <td style={{padding:"10px 13px",fontSize:13,color:TEXT_MUTED}}>{u.capabilities.length} capability{u.capabilities.length!==1?"ies":"y"}</td>
            <td style={{padding:"10px 13px"}}><button onClick={()=>setSel(u)} style={{fontSize:12,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500}}>View →</button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  </div>);
}

// ── OPS MANAGER SCREENS ───────────────────────────────────────────────────────
const getParticipantCount=p=>p.numParticipants||(p.solo?1:2);

const initialOpsProjects=[
  {id:"p1",title:"Spanish Conversational Audio – Batch 3",langs:["Spanish"],scripted:false,numParticipants:2,topics:["Daily Life","Food","Family","Sports","Humor","Travel","Culture"],hours:40,hoursUsed:28,maxPerUser:5,deadline:"May 10, 2025",claimable:true,status:"Active",durationRange:"20–45 min"},
  {id:"p2",title:"French Medical Dialogues",langs:["French"],scripted:false,numParticipants:2,topics:["Healthcare","Pharmacy","Mental Health"],hours:20,hoursUsed:20,maxPerUser:3,deadline:"Apr 30, 2025",claimable:false,status:"Full",durationRange:"30–45 min"},
  {id:"p3",title:"Arabic Business Scripted",langs:["Arabic"],scripted:true,solo:true,numParticipants:1,topics:["Business","Finance","Technology"],hours:30,hoursUsed:10,maxPerUser:4,deadline:"Jun 1, 2025",claimable:true,status:"Active",durationRange:"30–60 min"},
  {id:"p5",title:"Hausa Community Stories",langs:["Hausa"],scripted:false,numParticipants:2,topics:["Storytelling","Culture","Family"],hours:18,hoursUsed:9,maxPerUser:3,deadline:"May 28, 2025",claimable:true,limitReached:true,status:"Active",durationRange:"20–40 min"},
  {id:"p6",title:"Spanish Family Conversations – 3-Person",langs:["Spanish"],scripted:false,numParticipants:3,topics:["Family","Daily Life","Culture","Food","Humor"],hours:36,hoursUsed:12,maxPerUser:4,deadline:"Jun 20, 2025",claimable:true,status:"Active",durationRange:"25–45 min"},
  {id:"p4",title:"Swahili Daily Conversations – Pilot",langs:["Swahili"],scripted:false,numParticipants:2,topics:["Daily Life","Family","Culture"],hours:12,hoursUsed:6,maxPerUser:3,deadline:"Mar 15, 2025",claimable:false,status:"Closed",closedHoursAgo:480,closedReason:"Batch ended early — sufficient data collected. Not full.",durationRange:"20–40 min"},
];

const reviewBatches=[
  {id:"b1",name:"Spanish Batch 3 – Transcript Validation",title:"Spanish Batch 3 – Transcript Validation",type:"Full Transcript Review",project:"Spanish Conversational Audio",lang:"Spanish",tasks:24,done:10,deadline:"May 12, 2025",rate:0.13},
  {id:"b2",name:"French Medical – Utterance Remediation",title:"French Medical – Utterance Remediation",type:"Full Transcript Review",project:"French Medical Dialogues",lang:"French",tasks:15,done:15,deadline:"May 2, 2025",rate:0.12},
  {id:"b3",name:"Arabic Scripted – Audio Review",title:"Arabic Scripted – Audio Review",type:"Audio Review",project:"Arabic Business Scripted",lang:"Arabic",tasks:30,done:5,deadline:"Jun 5, 2025",rate:0.10},
  {id:"b4",name:"Spanish Medical – Transcription from Scratch",title:"Spanish Medical – Transcription from Scratch",type:"Transcript Creation",project:"Spanish Medical Training",lang:"Spanish",tasks:18,done:4,deadline:"May 25, 2025",rate:0.16},
  {id:"b5",name:"French Medical – Diarization Edits",title:"French Medical – Diarization Edits",type:"Diarization Review",project:"French Medical Dialogues",lang:"French",tasks:20,done:8,deadline:"May 28, 2025",rate:0.12},
  {id:"b6",name:"Arabic Business – Diarization Creation",title:"Arabic Business – Diarization Creation",type:"Diarization Creation",project:"Arabic Business Scripted",lang:"Arabic",tasks:16,done:2,deadline:"Jun 10, 2025",rate:0.14},
  {id:"b7",name:"Spanish Medical – NER Annotation",title:"Spanish Medical – NER Annotation",type:"NER/PII Recognition",project:"Spanish Medical Training",lang:"Spanish",tasks:25,done:12,deadline:"Jun 15, 2025",rate:0.13},
  {id:"b8",name:"French Healthcare – Topic Detection",title:"French Healthcare – Topic Detection",type:"topic",project:"French Healthcare Dialogues",lang:"French",tasks:22,done:5,deadline:"Jun 12, 2025",rate:0.10},
  {id:"b9",name:"Swahili Community – Taboo Detection",title:"Swahili Community – Taboo Detection",type:"sentiment",project:"Swahili Daily Conversations",lang:"Swahili",tasks:14,done:3,deadline:"Jun 18, 2025",rate:0.13},
  {id:"b10",name:"Spanish Medical – Transcript Correction",title:"Spanish Medical – Transcript Correction",type:"Full Transcript Review",project:"Spanish Medical Training",lang:"Spanish",tasks:30,done:10,deadline:"Jun 22, 2025",rate:0.15},
  {id:"b11",name:"French Medical – PII Redaction",title:"French Medical – PII Redaction",type:"NER/PII Recognition",project:"French Medical Dialogues",lang:"French",tasks:20,done:7,deadline:"Jun 25, 2025",rate:0.15},
  {id:"b12",name:"Arabic Business – Quality Review",title:"Arabic Business – Quality Review",type:"Full Transcript Review",project:"Arabic Business Scripted",lang:"Arabic",tasks:12,done:3,deadline:"Jul 1, 2025",rate:0.18},
  {id:"b13",name:"Spanish Audio – Fraud Detection",title:"Spanish Audio – Fraud Detection",type:"Audio Review",project:"Spanish Conversational Audio",lang:"Spanish",tasks:28,done:8,deadline:"Jul 5, 2025",rate:0.16},
  {id:"b14",name:"Arabic Audio – Language Variant Detection",title:"Arabic Audio – Language Variant Detection",type:"Audio Review",project:"Arabic Business Scripted",lang:"Arabic",tasks:18,done:4,deadline:"Jul 10, 2025",rate:0.14},
  {id:"b15",name:"Yoruba Audio – Annotation",title:"Yoruba Audio – Annotation",type:"Audio Review",project:"Yoruba Community Stories",lang:"Yoruba",tasks:24,done:6,deadline:"Jul 8, 2025",rate:0.12},
];

// All individual tasks (for Task Management screen)
const allTasksData=[
  {id:"ed27ca4c-5487-467a-abc1-a0e71b7980c2",type:"Full Transcript Review",lang:"Twi",batchId:"b1",batchName:"Batch A",reviewer:"Kwame Boateng",reviewerInitials:"KB",deadline:"Feb 21",status:"Completed",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"a3a6366f-ed6d-4b60-8774-136369891012",type:"Full Transcript Review",lang:"French",batchId:"b1",batchName:"Batch A",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Feb 22",status:"Assigned",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"0ea7088c-b608-495b-9f65-8a545621c0d1",type:"Full Transcript Review",lang:"Hausa",batchId:"b1",batchName:"Batch A",reviewer:"Zara Mensah",reviewerInitials:"ZM",deadline:"Feb 20",status:"Assigned",kind:"transcript",projectName:"Spanish Medical Training",pastDeadline:true},
  {id:"5c232c70-5d25-4ab6-b375-b8915f69d8ec",type:"Full Transcript Review",lang:"Ewe",batchId:"b1",batchName:"Batch A",reviewer:null,reviewerInitials:null,deadline:"Feb 23",status:"Unassigned",kind:"transcript",projectName:"Spanish Medical Training",assignmentMode:"direct"},
  {id:"1a704b9c-173e-481d-9129-910129f16432",type:"Full Transcript Review",lang:"French",batchId:"b1",batchName:"Batch B",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Feb 21",status:"Assigned",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"6b7368e4-ca68-42e3-b3e5-c26565e725d1",type:"Full Transcript Review",lang:"Twi",batchId:"b1",batchName:"Batch B",reviewer:null,reviewerInitials:null,deadline:"Feb 24",status:"Unassigned",kind:"transcript",projectName:"Spanish Medical Training",assignmentMode:"pool",daysUnclaimed:12},
  {id:"e0a88448-1bf5-45bd-9bd6-aef9181bbc99",type:"NER/PII Recognition",lang:"Twi",batchId:"b2",batchName:"Batch C",reviewer:"Kwame Boateng",reviewerInitials:"KB",deadline:"Feb 25",status:"Completed",kind:"transcript",pii:true,projectName:"Spanish Medical Training"},
  {id:"5dd1df63-69a2-462a-9b15-754348aa496a",type:"Full Transcript Review",lang:"French",batchId:"b2",batchName:"Batch D",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Mar 05",status:"Assigned",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"03d23ef2-8b4c-4fe5-b0a2-6f8ea493b67e",type:"Full Transcript Review",lang:"Twi",batchId:"b2",batchName:"Batch D",reviewer:"Kwame Boateng",reviewerInitials:"KB",deadline:"Mar 07",status:"Assigned",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"253f19ec-d794-4b7d-b9de-d08b89471042",type:"Diarization Review",lang:"Twi",batchId:"b2",batchName:"Batch E",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Mar 10",status:"Completed",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"38891ab4-9117-44bd-9e50-478575dbea1f",type:"Diarization Review",lang:"Hausa",batchId:"b2",batchName:"Batch E",reviewer:"Zara Mensah",reviewerInitials:"ZM",deadline:"Mar 12",status:"Assigned",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"b700abba-874a-4f84-b7fe-1fb8c6d60a8f",type:"Audio Review",lang:"Arabic",batchId:"b3",batchName:"Batch A",reviewer:"Kwame Boateng",reviewerInitials:"KB",deadline:"Jun 5",status:"Assigned",kind:"audio",projectName:"Arabic Business Scripted Content"},
  {id:"810e86b0-b209-4756-a5af-ee9152b0411b",type:"Audio Review",lang:"Arabic",batchId:"b3",batchName:"Batch A",reviewer:null,reviewerInitials:null,deadline:"Jun 5",status:"Unassigned",kind:"audio",assignmentMode:"direct",projectName:"Arabic Business Scripted Content"},
  {id:"72002ebb-5256-4de8-a74d-0f969b8ca9d1",type:"Audio Review",lang:"Arabic",batchId:"b3",batchName:"Batch B",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Jun 6",status:"Completed",kind:"audio",projectName:"Arabic Business Scripted Content"},
  // ── New task types ──
  {id:"2a26acda-382e-4e64-8a6d-74292c06699e",type:"Transcript Creation",lang:"Spanish",batchId:"b4",batchName:"Scratch Batch A",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"May 23",status:"Assigned",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"c93266f5-5299-4d2e-a28a-7d65b2b668bb",type:"Transcript Creation",lang:"Spanish",batchId:"b4",batchName:"Scratch Batch A",reviewer:null,reviewerInitials:null,deadline:"May 25",status:"Unassigned",kind:"transcript",assignmentMode:"pool",daysUnclaimed:14,projectName:"French Healthcare Dialogues"},
  {id:"9b0dee74-7898-4912-8295-681756e5bf9a",type:"Diarization Review",lang:"French",batchId:"b5",batchName:"Dia Edit Batch 1",reviewer:"Kwame Boateng",reviewerInitials:"KB",deadline:"May 26",status:"Completed",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"2d046d15-34ba-4d58-8a08-4ffab02bd93f",type:"Diarization Review",lang:"French",batchId:"b5",batchName:"Dia Edit Batch 1",reviewer:"Priya Nair",reviewerInitials:"PN",deadline:"May 28",status:"Assigned",kind:"transcript",pastDeadline:true,projectName:"French Healthcare Dialogues"},
  {id:"433ec298-ce56-426f-ad70-b124b4e07d8f",type:"Diarization Creation",lang:"Arabic",batchId:"b6",batchName:"Dia Create Batch 1",reviewer:"Fatima Al-Rashid",reviewerInitials:"FA",deadline:"Jun 8",status:"Assigned",kind:"transcript",projectName:"Arabic Business Scripted Content"},
  {id:"c7a032d4-43cb-4b8b-9f63-5625f9bd8e58",type:"Diarization Creation",lang:"Arabic",batchId:"b6",batchName:"Dia Create Batch 1",reviewer:null,reviewerInitials:null,deadline:"Jun 10",status:"Unassigned",kind:"transcript",assignmentMode:"direct",projectName:"Arabic Business Scripted Content"},
  {id:"bac9bc4a-5f44-49f6-96ba-05a8fc2c79c8",type:"NER/PII Recognition",lang:"Spanish",batchId:"b7",batchName:"NER Batch A",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Jun 14",status:"Completed",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"88c437f4-1613-45a5-96e6-d25f88e7c1ac",type:"NER/PII Recognition",lang:"Spanish",batchId:"b7",batchName:"NER Batch A",reviewer:"Lucas Oliveira",reviewerInitials:"LO",deadline:"Jun 15",status:"Assigned",kind:"transcript",projectName:"Spanish Medical Training"},
  {id:"dbf71e3b-6e27-453c-af52-846d5894da94",type:"topic",lang:"French",batchId:"b8",batchName:"Topic Batch 1",reviewer:"Zara Mensah",reviewerInitials:"ZM",deadline:"Jun 10",status:"Assigned",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"3243e398-4360-42f8-b5d2-dcecfe614038",type:"topic",lang:"French",batchId:"b8",batchName:"Topic Batch 1",reviewer:null,reviewerInitials:null,deadline:"Jun 12",status:"Unassigned",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"92d8bb6b-27d3-4646-8c79-23426f27bfdc",type:"sentiment",lang:"Swahili",batchId:"b9",batchName:"Taboo Batch A",reviewer:"Chen Wei",reviewerInitials:"CW",deadline:"Jun 16",status:"Assigned",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"3f4a8dc3-67dd-4fd0-b49d-3d24e409766a",type:"sentiment",lang:"Swahili",batchId:"b9",batchName:"Taboo Batch A",reviewer:"Priya Nair",reviewerInitials:"PN",deadline:"Jun 18",status:"Assigned",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"2d93f81c-b3e6-4e22-8ae4-bbb5ecce5345",type:"Full Transcript Review",lang:"Spanish",batchId:"b10",batchName:"Correction Batch A",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Jun 20",status:"Completed",kind:"transcript",projectName:"Arabic Business Scripted Content"},
  {id:"f865bb66-5e29-4500-98a7-394064b6b3de",type:"Full Transcript Review",lang:"Spanish",batchId:"b10",batchName:"Correction Batch B",reviewer:null,reviewerInitials:null,deadline:"Jun 22",status:"Unassigned",kind:"transcript",projectName:"Arabic Business Scripted Content"},
  {id:"aa5c1403-3542-47d5-bbca-d622e678607f",type:"NER/PII Recognition",lang:"French",batchId:"b11",batchName:"Redaction Batch A",reviewer:"Kwame Boateng",reviewerInitials:"KB",deadline:"Jun 23",status:"Assigned",kind:"transcript",pii:true,projectName:"Spanish Medical Training"},
  {id:"01f76f1c-e16a-4f3d-b544-268d0c1bff52",type:"NER/PII Recognition",lang:"French",batchId:"b11",batchName:"Redaction Batch A",reviewer:"Fatima Al-Rashid",reviewerInitials:"FA",deadline:"Jun 25",status:"Assigned",kind:"transcript",pii:true,projectName:"Spanish Medical Training"},
  {id:"8dc9ca26-b34d-4a30-b210-012dff901b14",type:"Full Transcript Review",lang:"Arabic",batchId:"b12",batchName:"QR Batch 1",reviewer:"Amara Osei",reviewerInitials:"AO",deadline:"Jun 30",status:"Assigned",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"d74d898a-f73a-4cf3-99df-0d0a5d5f3e31",type:"Full Transcript Review",lang:"Arabic",batchId:"b12",batchName:"QR Batch 1",reviewer:null,reviewerInitials:null,deadline:"Jul 1",status:"Unassigned",kind:"transcript",projectName:"French Healthcare Dialogues"},
  {id:"6dfdebff-de2a-4cda-bce5-1d6deab6abfe",type:"Audio Review",lang:"Spanish",batchId:"b13",batchName:"Fraud Batch A",reviewer:"Priya Nair",reviewerInitials:"PN",deadline:"Jul 3",status:"Assigned",kind:"audio",projectName:"Spanish Medical Training"},
  {id:"d114c5cf-24af-4590-8af6-7ccfcd7a1215",type:"Audio Review",lang:"Spanish",batchId:"b13",batchName:"Fraud Batch A",reviewer:"Lucas Oliveira",reviewerInitials:"LO",deadline:"Jul 5",status:"Completed",kind:"audio",projectName:"Spanish Medical Training"},
  {id:"dd698729-4487-44be-afa4-de6f298e932a",type:"Audio Review",lang:"Arabic",batchId:"b14",batchName:"Variant Batch 1",reviewer:"Fatima Al-Rashid",reviewerInitials:"FA",deadline:"Jul 8",status:"Assigned",kind:"audio",projectName:"Arabic Business Scripted Content"},
  {id:"fc50a5ec-0a28-4bfa-90a8-a0d4076bbaf0",type:"Audio Review",lang:"Arabic",batchId:"b14",batchName:"Variant Batch 1",reviewer:null,reviewerInitials:null,deadline:"Jul 10",status:"Unassigned",kind:"audio",projectName:"Arabic Business Scripted Content"},
  {id:"a858fcb6-e238-400b-b1eb-c8ed8e1d59cd",type:"Audio Review",lang:"Yoruba",batchId:"b15",batchName:"Annotation Batch A",reviewer:"Chen Wei",reviewerInitials:"CW",deadline:"Jul 6",status:"Assigned",kind:"audio",projectName:"French Healthcare Dialogues"},
  {id:"e82b3a25-c169-40ab-814e-408ab664ac1e",type:"Audio Review",lang:"Yoruba",batchId:"b15",batchName:"Annotation Batch A",reviewer:"Zara Mensah",reviewerInitials:"ZM",deadline:"Jul 8",status:"Completed",kind:"audio",projectName:"French Healthcare Dialogues"},
];

// Reviewer batch task lists (for batch detail drill-down)
const reviewerBatchTasks={
  b1:[
    {id:"ed27ca4c-5487-467a-abc1-a0e71b7980c2",type:"Full Transcript Review",lang:"Twi",batchName:"Batch A",reviewer:"Kwame Boateng",deadline:"Feb 21",status:"Completed"},
    {id:"a3a6366f-ed6d-4b60-8774-136369891012",type:"Full Transcript Review",lang:"French",batchName:"Batch A",reviewer:"Amara Osei",deadline:"Feb 22",status:"Assigned"},
    {id:"0ea7088c-b608-495b-9f65-8a545621c0d1",type:"Full Transcript Review",lang:"Hausa",batchName:"Batch A",reviewer:"Zara Mensah",deadline:"Feb 20",status:"Assigned"},
    {id:"5c232c70-5d25-4ab6-b375-b8915f69d8ec",type:"Full Transcript Review",lang:"Ewe",batchName:"Batch A",reviewer:null,deadline:"Feb 23",status:"Unassigned"},
  ],
  b2:[
    {id:"e0a88448-1bf5-45bd-9bd6-aef9181bbc99",type:"NER/PII Recognition",lang:"Twi",batchName:"Batch C",reviewer:"Kwame Boateng",deadline:"Feb 25",status:"Completed",pii:true},
    {id:"5dd1df63-69a2-462a-9b15-754348aa496a",type:"Full Transcript Review",lang:"French",batchName:"Batch D",reviewer:"Amara Osei",deadline:"Mar 05",status:"Assigned"},
    {id:"03d23ef2-8b4c-4fe5-b0a2-6f8ea493b67e",type:"Full Transcript Review",lang:"Twi",batchName:"Batch D",reviewer:"Kwame Boateng",deadline:"Mar 07",status:"Assigned"},
    {id:"253f19ec-d794-4b7d-b9de-d08b89471042",type:"Diarization Review",lang:"Twi",batchName:"Batch E",reviewer:"Amara Osei",deadline:"Mar 10",status:"Completed"},
    {id:"38891ab4-9117-44bd-9e50-478575dbea1f",type:"Diarization Review",lang:"Hausa",batchName:"Batch E",reviewer:"Zara Mensah",deadline:"Mar 12",status:"Assigned"},
  ],
  b3:[
    {id:"b700abba-874a-4f84-b7fe-1fb8c6d60a8f",type:"Audio Review",lang:"Arabic",batchName:"Batch A",reviewer:"Kwame Boateng",deadline:"Jun 5",status:"Assigned"},
    {id:"810e86b0-b209-4756-a5af-ee9152b0411b",type:"Audio Review",lang:"Arabic",batchName:"Batch A",reviewer:null,deadline:"Jun 5",status:"Unassigned"},
    {id:"72002ebb-5256-4de8-a74d-0f969b8ca9d1",type:"Audio Review",lang:"Arabic",batchName:"Batch B",reviewer:"Amara Osei",deadline:"Jun 6",status:"Completed"},
  ],
  b4:[
    {id:"2a26acda-382e-4e64-8a6d-74292c06699e",type:"Transcript Creation",lang:"Spanish",batchName:"Scratch Batch A",reviewer:"Amara Osei",deadline:"May 23",status:"Assigned"},
    {id:"c93266f5-5299-4d2e-a28a-7d65b2b668bb",type:"Transcript Creation",lang:"Spanish",batchName:"Scratch Batch A",reviewer:null,deadline:"May 25",status:"Unassigned"},
  ],
  b5:[
    {id:"9b0dee74-7898-4912-8295-681756e5bf9a",type:"Diarization Review",lang:"French",batchName:"Dia Edit Batch 1",reviewer:"Kwame Boateng",deadline:"May 26",status:"Completed"},
    {id:"2d046d15-34ba-4d58-8a08-4ffab02bd93f",type:"Diarization Review",lang:"French",batchName:"Dia Edit Batch 1",reviewer:"Priya Nair",deadline:"May 28",status:"Assigned"},
  ],
  b6:[
    {id:"433ec298-ce56-426f-ad70-b124b4e07d8f",type:"Diarization Creation",lang:"Arabic",batchName:"Dia Create Batch 1",reviewer:"Fatima Al-Rashid",deadline:"Jun 8",status:"Assigned"},
    {id:"c7a032d4-43cb-4b8b-9f63-5625f9bd8e58",type:"Diarization Creation",lang:"Arabic",batchName:"Dia Create Batch 1",reviewer:null,deadline:"Jun 10",status:"Unassigned"},
  ],
  b7:[
    {id:"bac9bc4a-5f44-49f6-96ba-05a8fc2c79c8",type:"NER/PII Recognition",lang:"Spanish",batchName:"NER Batch A",reviewer:"Amara Osei",deadline:"Jun 14",status:"Completed"},
    {id:"88c437f4-1613-45a5-96e6-d25f88e7c1ac",type:"NER/PII Recognition",lang:"Spanish",batchName:"NER Batch A",reviewer:"Lucas Oliveira",deadline:"Jun 15",status:"Assigned"},
  ],
  b8:[
    {id:"dbf71e3b-6e27-453c-af52-846d5894da94",type:"topic",lang:"French",batchName:"Topic Batch 1",reviewer:"Zara Mensah",deadline:"Jun 10",status:"Assigned"},
    {id:"3243e398-4360-42f8-b5d2-dcecfe614038",type:"topic",lang:"French",batchName:"Topic Batch 1",reviewer:null,deadline:"Jun 12",status:"Unassigned"},
  ],
  b9:[
    {id:"92d8bb6b-27d3-4646-8c79-23426f27bfdc",type:"sentiment",lang:"Swahili",batchName:"Taboo Batch A",reviewer:"Chen Wei",deadline:"Jun 16",status:"Assigned"},
    {id:"3f4a8dc3-67dd-4fd0-b49d-3d24e409766a",type:"sentiment",lang:"Swahili",batchName:"Taboo Batch A",reviewer:"Priya Nair",deadline:"Jun 18",status:"Assigned"},
  ],
  b10:[
    {id:"2d93f81c-b3e6-4e22-8ae4-bbb5ecce5345",type:"Full Transcript Review",lang:"Spanish",batchName:"Correction Batch A",reviewer:"Amara Osei",deadline:"Jun 20",status:"Completed"},
    {id:"f865bb66-5e29-4500-98a7-394064b6b3de",type:"Full Transcript Review",lang:"Spanish",batchName:"Correction Batch B",reviewer:null,deadline:"Jun 22",status:"Unassigned"},
  ],
  b11:[
    {id:"aa5c1403-3542-47d5-bbca-d622e678607f",type:"NER/PII Recognition",lang:"French",batchName:"Redaction Batch A",reviewer:"Kwame Boateng",deadline:"Jun 23",status:"Assigned",pii:true},
    {id:"01f76f1c-e16a-4f3d-b544-268d0c1bff52",type:"NER/PII Recognition",lang:"French",batchName:"Redaction Batch A",reviewer:"Fatima Al-Rashid",deadline:"Jun 25",status:"Assigned",pii:true},
  ],
  b12:[
    {id:"8dc9ca26-b34d-4a30-b210-012dff901b14",type:"Full Transcript Review",lang:"Arabic",batchName:"QR Batch 1",reviewer:"Amara Osei",deadline:"Jun 30",status:"Assigned"},
    {id:"d74d898a-f73a-4cf3-99df-0d0a5d5f3e31",type:"Full Transcript Review",lang:"Arabic",batchName:"QR Batch 1",reviewer:null,deadline:"Jul 1",status:"Unassigned"},
  ],
  b13:[
    {id:"6dfdebff-de2a-4cda-bce5-1d6deab6abfe",type:"Audio Review",lang:"Spanish",batchName:"Fraud Batch A",reviewer:"Priya Nair",deadline:"Jul 3",status:"Assigned"},
    {id:"d114c5cf-24af-4590-8af6-7ccfcd7a1215",type:"Audio Review",lang:"Spanish",batchName:"Fraud Batch A",reviewer:"Lucas Oliveira",deadline:"Jul 5",status:"Completed"},
  ],
  b14:[
    {id:"dd698729-4487-44be-afa4-de6f298e932a",type:"Audio Review",lang:"Arabic",batchName:"Variant Batch 1",reviewer:"Fatima Al-Rashid",deadline:"Jul 8",status:"Assigned"},
    {id:"fc50a5ec-0a28-4bfa-90a8-a0d4076bbaf0",type:"Audio Review",lang:"Arabic",batchName:"Variant Batch 1",reviewer:null,deadline:"Jul 10",status:"Unassigned"},
  ],
  b15:[
    {id:"a858fcb6-e238-400b-b1eb-c8ed8e1d59cd",type:"Audio Review",lang:"Yoruba",batchName:"Annotation Batch A",reviewer:"Chen Wei",deadline:"Jul 6",status:"Assigned"},
    {id:"e82b3a25-c169-40ab-814e-408ab664ac1e",type:"Audio Review",lang:"Yoruba",batchName:"Annotation Batch A",reviewer:"Zara Mensah",deadline:"Jul 8",status:"Completed"},
  ],
};

// Ops-level Projects (organizational wrappers linking batches)
const initialOpsProjectsList=[
  {id:"proj1",name:"Spanish Medical Training",description:"End-to-end dataset for Spanish medical conversation AI. Includes conversational recordings, transcript validation, and utterance-level remediation.",batchIds:["p1","b1"],createdAt:"Jan 2025",
    languages:[{lang:"Spanish",hoursGoal:50,hoursRecorded:32,maxPerContributor:1.5,pipeline:{total:80,audioCreation:10,audioReview:15,transcription:20,transcriptionReview:15,complete:20}}],
    pipeline:{total:80,audioCreation:10,audioReview:15,transcription:20,transcriptionReview:15,complete:20}},
  {id:"proj2",name:"French Healthcare Dialogues",description:"Structured dialogue recordings covering pharmacy, mental health, and general healthcare in French. Includes full QA pipeline.",batchIds:["p2","b2"],createdAt:"Feb 2025",
    languages:[
      {lang:"French",hoursGoal:40,hoursRecorded:18,maxPerContributor:2,pipeline:{total:40,audioCreation:3,audioReview:5,transcription:8,transcriptionReview:6,complete:18}},
      {lang:"Arabic",hoursGoal:20,hoursRecorded:14,maxPerContributor:2,pipeline:{total:20,audioCreation:2,audioReview:3,transcription:4,transcriptionReview:4,complete:7}},
    ],
    pipeline:{total:60,audioCreation:5,audioReview:8,transcription:12,transcriptionReview:10,complete:25}},
  {id:"proj3",name:"Arabic Business Scripted Content",description:"Solo scripted recordings for Arabic business and finance use cases, paired with audio review QA.",batchIds:["p3","b3"],createdAt:"Mar 2025",
    languages:[
      {lang:"Arabic",hoursGoal:30,hoursRecorded:22,maxPerContributor:1,pipeline:{total:25,audioCreation:5,audioReview:7,transcription:5,transcriptionReview:3,complete:5}},
      {lang:"French",hoursGoal:15,hoursRecorded:3,maxPerContributor:1,pipeline:{total:10,audioCreation:2,audioReview:2,transcription:2,transcriptionReview:2,complete:2}},
      {lang:"Spanish",hoursGoal:10,hoursRecorded:8.5,maxPerContributor:0.5,pipeline:{total:5,audioCreation:1,audioReview:1,transcription:1,transcriptionReview:1,complete:1}},
    ],
    pipeline:{total:40,audioCreation:8,audioReview:10,transcription:8,transcriptionReview:6,complete:8}},
  {id:"proj4",name:"Mandarin Customer Service Pilot",description:"Pilot dataset for Mandarin customer service training. Saved as draft until ops finalizes the review pipeline.",batchIds:[],createdAt:"Apr 2025",status:"draft",flowMode:"skip",flowId:null,
    languages:[
      {lang:"Mandarin",hoursGoal:25,hoursRecorded:0,maxPerContributor:2},
    ]},
];

// ── Sample engagements + activity log per project ───────────────────────────
// Engagement = one recording + all of its downstream tasks.
// Activity = chronological project event log.
// ── Sample attached-flow snapshot per project ──
// Rendered on the project detail Overview tab as a DAG. Represents the flow
// configuration that was attached when the project was created.
const sampleProjectFlows={
  proj1:{
    name:"Standard Pipeline",
    includedStages:["audioCreation","audioReview","transcription","transcriptionReview","complete"],
    exits:{
      audioCreation:[
        {id:"p1a",langs:[],selection:{type:"sample",pct:20},destination:"audioReview",worker:{type:"human",assignment:"pool",who:null}},
        {id:"p1b",langs:[],selection:{type:"remainder"},destination:"transcription",worker:{type:"machine",assignment:null,who:"ASR-v3.2"}},
      ],
      audioReview:[
        {id:"p1c",langs:[],selection:{type:"all"},destination:"transcription",worker:{type:"machine",assignment:null,who:"ASR-v3.2"}},
      ],
      transcription:[
        {id:"p1d",langs:[],selection:{type:"all"},destination:"transcriptionReview",worker:{type:"human",assignment:"pool",who:null}},
      ],
      transcriptionReview:[
        {id:"p1e",langs:[],selection:{type:"all"},destination:"complete",worker:{type:"human",assignment:"pool",who:null}},
      ],
    },
  },
  proj2:{
    name:"Healthcare Pipeline",
    includedStages:["audioCreation","audioReview","transcription","transcriptionReview","complete"],
    exits:{
      audioCreation:[
        {id:"p2a",langs:[],selection:{type:"all"},destination:"audioReview",worker:{type:"human",assignment:"pool",who:null}},
      ],
      audioReview:[
        {id:"p2b",langs:[],selection:{type:"all"},destination:"transcription",worker:{type:"human",assignment:"specific",who:"Priya Nair"}},
      ],
      transcription:[
        {id:"p2c",langs:[],selection:{type:"all"},destination:"transcriptionReview",worker:{type:"human",assignment:"pool",who:null}},
      ],
      transcriptionReview:[
        {id:"p2d",langs:[],selection:{type:"all"},destination:"complete",worker:{type:"human",assignment:"pool",who:null}},
      ],
    },
  },
  proj3:{
    name:"Audio Only Pipeline",
    includedStages:["audioCreation","audioReview","complete"],
    exits:{
      audioCreation:[
        {id:"p3a",langs:[],selection:{type:"all"},destination:"audioReview",worker:{type:"human",assignment:"pool",who:null}},
      ],
      audioReview:[
        {id:"p3b",langs:[],selection:{type:"all"},destination:"complete",worker:{type:"human",assignment:"pool",who:null}},
      ],
    },
  },
};

const sampleEngagements={
  proj1:[
    {id:"E-1001",lang:"Spanish",currentStage:"complete",createdAt:"Feb 2",durationMins:42,participants:["Sofia Reyes","Amara Osei"],topic:"Daily Life",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Sofia Reyes",completedAt:"Feb 2",qa:"passed",paid:true},
        {stage:"audioReview",status:"completed",assignee:"Kwame Boateng",completedAt:"Feb 4",qa:"passed",paid:true},
        {stage:"transcription",status:"completed",assignee:"Machine: ASR-v3.2",completedAt:"Feb 4",qa:"passed",paid:true},
        {stage:"transcriptionReview",status:"completed",assignee:"Amara Osei",completedAt:"Feb 6",qa:"passed",paid:true},
      ]},
    {id:"E-1002",lang:"Spanish",currentStage:"transcriptionReview",createdAt:"Feb 8",durationMins:38,participants:["Lucas Oliveira","Maria González"],topic:"Food",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Lucas Oliveira",completedAt:"Feb 8",qa:"passed",paid:true},
        {stage:"audioReview",status:"completed",assignee:"Zara Mensah",completedAt:"Feb 10",qa:"passed",paid:true},
        {stage:"transcription",status:"completed",assignee:"Machine: ASR-v3.2",completedAt:"Feb 10",qa:"passed",paid:true},
        {stage:"transcriptionReview",status:"in_progress",assignee:"Priya Nair",startedAt:"Feb 11"},
      ]},
    {id:"E-1003",lang:"Spanish",currentStage:"transcription",createdAt:"Feb 12",durationMins:45,participants:["Maria González","Diego Fernández"],topic:"Healthcare",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Maria González",completedAt:"Feb 12",qa:"passed",paid:true},
        {stage:"audioReview",status:"completed",assignee:"Kwame Boateng",completedAt:"Feb 13",qa:"passed",paid:true},
        {stage:"transcription",status:"pending",assignee:null},
        {stage:"transcriptionReview",status:"pending",assignee:null},
      ]},
    {id:"E-1004",lang:"Spanish",currentStage:"audioReview",createdAt:"Feb 14",durationMins:31,participants:["Diego Fernández","Sofia Reyes"],topic:"Daily Life",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Diego Fernández",completedAt:"Feb 14",qa:"passed",paid:true},
        {stage:"audioReview",status:"in_progress",assignee:"Amara Osei",startedAt:"Feb 15"},
        {stage:"transcription",status:"pending",assignee:null},
        {stage:"transcriptionReview",status:"pending",assignee:null},
      ]},
    {id:"E-1005",lang:"Spanish",currentStage:"audioCreation",createdAt:"Feb 15",durationMins:null,participants:["Sofia Reyes","Lucas Oliveira"],topic:"Daily Life",
      tasks:[
        {stage:"audioCreation",status:"in_progress",assignee:"Sofia Reyes"},
      ]},
    {id:"E-1006",lang:"Spanish",currentStage:"audioCreation",createdAt:"Feb 15",durationMins:null,participants:["None","None"],topic:"Food",
      tasks:[
        {stage:"audioCreation",status:"pending",assignee:null},
      ]},
  ],
  proj2:[
    {id:"E-2001",lang:"French",currentStage:"complete",createdAt:"Feb 20",durationMins:40,participants:["Amara Osei","Kwame Boateng"],topic:"Pharmacy",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Amara Osei",completedAt:"Feb 20",qa:"passed",paid:true},
        {stage:"audioReview",status:"completed",assignee:"Zara Mensah",completedAt:"Feb 22",qa:"passed",paid:true},
        {stage:"transcription",status:"completed",assignee:"Priya Nair",completedAt:"Feb 24",qa:"passed",paid:true},
        {stage:"transcriptionReview",status:"completed",assignee:"Amara Osei",completedAt:"Feb 26",qa:"passed",paid:true},
      ]},
    {id:"E-2002",lang:"French",currentStage:"transcription",createdAt:"Feb 24",durationMins:36,participants:["Kwame Boateng","Priya Nair"],topic:"Mental Health",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Kwame Boateng",completedAt:"Feb 24",qa:"passed",paid:true},
        {stage:"audioReview",status:"completed",assignee:"Amara Osei",completedAt:"Feb 26",qa:"passed",paid:true},
        {stage:"transcription",status:"in_progress",assignee:"Lucas Oliveira",startedAt:"Feb 27"},
        {stage:"transcriptionReview",status:"pending",assignee:null},
      ]},
    {id:"E-2003",lang:"Arabic",currentStage:"audioReview",createdAt:"Feb 28",durationMins:42,participants:["Fatima Al-Rashid","Chen Wei"],topic:"Healthcare",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Fatima Al-Rashid",completedAt:"Feb 28",qa:"passed",paid:true},
        {stage:"audioReview",status:"pending",assignee:null},
        {stage:"transcription",status:"pending",assignee:null},
        {stage:"transcriptionReview",status:"pending",assignee:null},
      ]},
    {id:"E-2004",lang:"Arabic",currentStage:"complete",createdAt:"Mar 1",durationMins:44,participants:["Chen Wei","Fatima Al-Rashid"],topic:"Pharmacy",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Chen Wei",completedAt:"Mar 1",qa:"passed",paid:true},
        {stage:"audioReview",status:"completed",assignee:"Kwame Boateng",completedAt:"Mar 2",qa:"passed",paid:true},
        {stage:"transcription",status:"completed",assignee:"Fatima Al-Rashid",completedAt:"Mar 4",qa:"passed",paid:true},
        {stage:"transcriptionReview",status:"completed",assignee:"Zara Mensah",completedAt:"Mar 5",qa:"passed",paid:true},
      ]},
  ],
  proj3:[
    {id:"E-3001",lang:"Arabic",currentStage:"audioReview",createdAt:"Mar 3",durationMins:48,participants:["Fatima Al-Rashid","None"],topic:"Business",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Fatima Al-Rashid",completedAt:"Mar 3",qa:"passed",paid:true},
        {stage:"audioReview",status:"in_progress",assignee:"Kwame Boateng",startedAt:"Mar 4"},
      ]},
    {id:"E-3002",lang:"Arabic",currentStage:"complete",createdAt:"Mar 5",durationMins:52,participants:["Chen Wei","None"],topic:"Finance",
      tasks:[
        {stage:"audioCreation",status:"completed",assignee:"Chen Wei",completedAt:"Mar 5",qa:"passed",paid:true},
        {stage:"audioReview",status:"completed",assignee:"Amara Osei",completedAt:"Mar 7",qa:"passed",paid:true},
      ]},
    {id:"E-3003",lang:"French",currentStage:"audioCreation",createdAt:"Mar 8",durationMins:null,participants:["None","None"],topic:"Business",
      tasks:[
        {stage:"audioCreation",status:"pending",assignee:null},
      ]},
  ],
};

// Activity log — each project has a chronological list of events
const sampleActivity={
  proj1:[
    {at:"Feb 15, 14:32",type:"task_started",actor:"Sofia Reyes",desc:"started recording E-1005 (Spanish · Daily Life)"},
    {at:"Feb 15, 10:18",type:"task_assigned",actor:"Ops Manager",desc:"assigned Audio Review task on E-1004 to Amara Osei"},
    {at:"Feb 14, 16:45",type:"task_completed",actor:"Diego Fernández",desc:"completed recording E-1004 (31 min, Spanish · Daily Life)"},
    {at:"Feb 13, 11:20",type:"task_completed",actor:"Kwame Boateng",desc:"completed Audio Review on E-1003"},
    {at:"Feb 12, 09:15",type:"task_completed",actor:"Maria González",desc:"completed recording E-1003 (45 min, Spanish · Healthcare)"},
    {at:"Feb 11, 15:02",type:"task_started",actor:"Priya Nair",desc:"started Transcription Review on E-1002"},
    {at:"Feb 10, 13:47",type:"task_auto",actor:"Machine: ASR-v3.2",desc:"auto-completed Transcription on E-1002"},
    {at:"Feb 10, 09:30",type:"task_completed",actor:"Zara Mensah",desc:"completed Audio Review on E-1002"},
    {at:"Feb 8, 14:10",type:"task_completed",actor:"Lucas Oliveira",desc:"completed recording E-1002 (38 min, Spanish · Food)"},
    {at:"Feb 6, 17:22",type:"engagement_complete",actor:"Amara Osei",desc:"engagement E-1001 completed (all stages finished)"},
    {at:"Feb 4, 10:15",type:"task_auto",actor:"Machine: ASR-v3.2",desc:"auto-completed Transcription on E-1001"},
    {at:"Feb 2, 11:00",type:"task_completed",actor:"Sofia Reyes",desc:"completed recording E-1001 (42 min, Spanish · Daily Life)"},
    {at:"Jan 30, 09:00",type:"project_launched",actor:"Ops Manager",desc:"project launched with Standard Pipeline flow attached"},
    {at:"Jan 28, 14:40",type:"project_created",actor:"Ops Manager",desc:"project created"},
  ],
  proj2:[
    {at:"Mar 5, 16:00",type:"engagement_complete",actor:"Zara Mensah",desc:"engagement E-2004 completed"},
    {at:"Mar 4, 10:20",type:"task_completed",actor:"Fatima Al-Rashid",desc:"completed Transcription on E-2004"},
    {at:"Feb 28, 15:30",type:"task_completed",actor:"Fatima Al-Rashid",desc:"completed recording E-2003 (42 min, Arabic · Healthcare)"},
    {at:"Feb 27, 09:00",type:"task_started",actor:"Lucas Oliveira",desc:"started Transcription on E-2002"},
    {at:"Feb 26, 14:45",type:"engagement_complete",actor:"Amara Osei",desc:"engagement E-2001 completed"},
    {at:"Feb 20, 10:00",type:"task_completed",actor:"Amara Osei",desc:"completed recording E-2001 (40 min, French · Pharmacy)"},
    {at:"Feb 15, 09:00",type:"project_launched",actor:"Ops Manager",desc:"project launched"},
    {at:"Feb 14, 11:30",type:"project_created",actor:"Ops Manager",desc:"project created"},
  ],
  proj3:[
    {at:"Mar 8, 13:00",type:"engagement_created",actor:"System",desc:"engagement E-3003 created (French · Business)"},
    {at:"Mar 7, 12:20",type:"engagement_complete",actor:"Amara Osei",desc:"engagement E-3002 completed"},
    {at:"Mar 5, 14:30",type:"task_completed",actor:"Chen Wei",desc:"completed recording E-3002 (52 min, Arabic · Finance)"},
    {at:"Mar 4, 08:45",type:"task_started",actor:"Kwame Boateng",desc:"started Audio Review on E-3001"},
    {at:"Mar 3, 16:20",type:"task_completed",actor:"Fatima Al-Rashid",desc:"completed recording E-3001 (48 min, Arabic · Business)"},
    {at:"Mar 1, 09:00",type:"project_launched",actor:"Ops Manager",desc:"project launched"},
    {at:"Feb 28, 14:15",type:"project_created",actor:"Ops Manager",desc:"project created"},
  ],
};

// Sample project detail data
const projectDetailData={
  p1:{
    pipelineStats:{recorded:50,passedQA:30,paid:10},
    meetings:[
      {id:"M-001",participants:["Sofia Reyes","Amara Osei"],topic:"Daily Life",lang:"Spanish",lengthMins:38,status:"Recorded",qaStatus:"Passed",paid:true,hours:0.63},
      {id:"M-002",participants:["Lucas Oliveira","Maria González"],topic:"Food",lang:"Spanish",lengthMins:null,status:"Scheduled",qaStatus:null,paid:false,hours:0},
      {id:"M-003",participants:["Maria González","None"],topic:"Sports",lang:"Spanish",lengthMins:null,status:"Awaiting Buddy",qaStatus:null,paid:false,hours:0},
    ],
    contributors:[
      {name:"Sofia Reyes",initials:"SR",hours:0.63,recordings:1},
      {name:"Lucas Oliveira",initials:"LO",hours:0,recordings:0,scheduled:1},
      {name:"María González",initials:"MG",hours:0,recordings:0,windowsPosted:3},
      {name:"Amara Osei",initials:"AO",hours:0.63,recordings:1},
    ]
  },
  p2:{
    meetings:[
      {id:"M-010",participants:["Priya Nair","Chen Wei"],topic:"Healthcare",lang:"French",lengthMins:42,status:"Recorded",qaStatus:"Passed",paid:true,hours:0.70},
      {id:"M-011",participants:["Diego Fernández","Fatima Al-Rashid"],topic:"Pharmacy",lang:"French",lengthMins:39,status:"Recorded",qaStatus:"Passed",paid:true,hours:0.65},
      {id:"M-012",participants:["Lucas Oliveira","Priya Nair"],topic:"Mental Health",lang:"French",lengthMins:44,status:"Recorded",qaStatus:"In QA",paid:false,hours:0.73},
      {id:"M-013",participants:["Amara Osei","Maria González"],topic:"Healthcare",lang:"French",lengthMins:36,status:"Recorded",qaStatus:"In QA",paid:false,hours:0.60},
      {id:"M-014",participants:["Sofia Reyes","Chen Wei"],topic:"Pharmacy",lang:"French",lengthMins:41,status:"Recorded",qaStatus:"Passed",paid:false,hours:0.68},
    ],
    contributors:[
      {name:"Priya Nair",initials:"PN",hours:3.0,recordings:3},
      {name:"Chen Wei",initials:"CW",hours:1.5,recordings:2},
      {name:"Diego Fernández",initials:"DF",hours:0.75,recordings:1},
      {name:"Fatima Al-Rashid",initials:"FA",hours:0.75,recordings:1},
      {name:"Lucas Oliveira",initials:"LO",hours:0.75,recordings:1},
      {name:"Amara Osei",initials:"AO",hours:0.75,recordings:1},
      {name:"Maria González",initials:"MG",hours:0.75,recordings:1},
      {name:"Sofia Reyes",initials:"SR",hours:0.75,recordings:1},
    ]
  },
  p4:{
    meetings:[
      {id:"M-020",participants:["Chen Wei","Priya Nair"],topic:"Daily Life",lang:"Swahili",lengthMins:43,status:"Recorded",qaStatus:"Passed",paid:true,hours:0.72},
      {id:"M-021",participants:["Lucas Oliveira","Fatima Al-Rashid"],topic:"Family",lang:"Swahili",lengthMins:37,status:"Recorded",qaStatus:"In QA",paid:false,hours:0.62},
      {id:"M-022",participants:["Sofia Reyes","Diego Fernández"],topic:"Culture",lang:"Swahili",lengthMins:40,status:"Recorded",qaStatus:"Passed",paid:false,hours:0.67},
      {id:"M-023",participants:["Maria González","Chen Wei"],topic:"Daily Life",lang:"Swahili",lengthMins:35,status:"Recorded",qaStatus:"Passed",paid:true,hours:0.58},
    ],
    contributors:[
      {name:"Chen Wei",initials:"CW",hours:1.5,recordings:2},
      {name:"Priya Nair",initials:"PN",hours:0.75,recordings:1},
      {name:"Lucas Oliveira",initials:"LO",hours:0.75,recordings:1},
      {name:"Fatima Al-Rashid",initials:"FA",hours:0.75,recordings:1},
      {name:"Sofia Reyes",initials:"SR",hours:0.75,recordings:1},
      {name:"Diego Fernández",initials:"DF",hours:0.75,recordings:1},
      {name:"Maria González",initials:"MG",hours:0.75,recordings:1},
    ]
  },
  p6:{
    pipelineStats:{recorded:12,passedQA:8,paid:4},
    meetings:[
      {id:"M-040",participants:["Sofia Reyes","Kwame Boateng","Zara Mensah"],topic:"Family",lang:"Spanish",lengthMins:34,status:"Recorded",qaStatus:"Passed",paid:true,hours:0.57},
      {id:"M-041",participants:["Priya Nair","Chen Wei","Lucas Oliveira"],topic:"Daily Life",lang:"Spanish",lengthMins:41,status:"Recorded",qaStatus:"In QA",paid:false,hours:0.68},
      {id:"M-042",participants:["Diego Fernández","Fatima Al-Rashid","Maria González"],topic:"Culture",lang:"Spanish",lengthMins:38,status:"Recorded",qaStatus:"Passed",paid:true,hours:0.63},
      {id:"M-043",participants:["Sofia Reyes","Amara Osei","Priya Nair"],topic:"Food",lang:"Spanish",lengthMins:null,status:"Scheduled",qaStatus:null,paid:false,hours:0},
      {id:"M-044",participants:["Lucas Oliveira","Diego Fernández",null],topic:"Humor",lang:"Spanish",lengthMins:null,status:"Awaiting Buddy",qaStatus:null,paid:false,hours:0},
    ],
    contributors:[
      {name:"Sofia Reyes",initials:"SR",hours:1.2,recordings:2},
      {name:"Kwame Boateng",initials:"KB",hours:0.57,recordings:1},
      {name:"Zara Mensah",initials:"ZM",hours:0.57,recordings:1},
      {name:"Priya Nair",initials:"PN",hours:1.25,recordings:2},
      {name:"Chen Wei",initials:"CW",hours:0.68,recordings:1},
      {name:"Lucas Oliveira",initials:"LO",hours:0.68,recordings:1},
      {name:"Diego Fernández",initials:"DF",hours:0.63,recordings:1},
      {name:"Fatima Al-Rashid",initials:"FA",hours:0.63,recordings:1},
      {name:"Maria González",initials:"MG",hours:0.63,recordings:1},
      {name:"Amara Osei",initials:"AO",hours:0,recordings:0,scheduled:1},
    ]
  }
};

function OpsProjectDetail({project,onBack}){
  const detail=projectDetailData[project.id]||{meetings:[],contributors:[]};
  const statusColor=s=>s==="Recorded"?"green":s==="Scheduled"?"blue":s==="Awaiting Buddy"?"orange":"gray";
  const[tab,setTab]=useState("recordings");

  // Computed stats — use pipelineStats override if present (for demo purposes)
  const recorded=detail.pipelineStats?detail.pipelineStats.recorded:detail.meetings.filter(m=>m.status==="Recorded").length;
  const passedQA=detail.pipelineStats?detail.pipelineStats.passedQA:detail.meetings.filter(m=>m.qaStatus==="Passed").length;
  const paid=detail.pipelineStats?detail.pipelineStats.paid:detail.meetings.filter(m=>m.paid).length;
  const awaitingPartner=detail.meetings.filter(m=>m.status==="Awaiting Buddy").length;
  const scheduled=detail.meetings.filter(m=>m.status==="Scheduled").length;

  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back to Batches</button>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>{project.title}</h1>
      <Badge label={project.status} color={project.status==="Active"?"green":project.status==="Full"?"red":"gray"}/>
    </div>
    <div style={{display:"flex",gap:4,marginBottom:project.closedReason?10:18,flexWrap:"wrap"}}>
      <Badge label={project.langs?.[0]||"—"} color="purple"/>
      <Badge label={project.scripted?"Scripted":"Unscripted"} color={project.scripted?"blue":"teal"}/>
      <Badge label={getParticipantCount(project)===1?"Solo":`${getParticipantCount(project)} participants`} color="gray"/>
    </div>
    {project.closedReason&&<div style={{background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:8,padding:"9px 14px",fontSize:12,color:TEXT_MUTED,marginBottom:16}}>{project.closedReason}</div>}

    {/* Top-level stats: hours + pending */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
      {[
        ["Total Hours",`${project.hoursUsed||0}h / ${project.hours}h`,P],
        ["Awaiting Scheduling",awaitingPartner,"#d97706"],
        ["Scheduled, Not Yet Recorded",scheduled,"#2563eb"],
      ].map(([label,val,color])=>(
        <div key={label} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 16px"}}>
          <div style={{fontSize:20,fontWeight:700,color,marginBottom:3}}>{val}</div>
          <div style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:500,lineHeight:1.4}}>{label}</div>
        </div>
      ))}
    </div>

    {/* Recording pipeline funnel — Recorded → Passed QA → Paid */}
    {recorded>0&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"20px 24px",marginBottom:20}}>
      <div style={{fontSize:12,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:18}}>RECORDING PIPELINE</div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {[
          {label:"Recorded",n:recorded,of:recorded,color:P,bg:PL,border:BRAND_SOFTER},
          {label:"Passed QA",n:passedQA,of:recorded,color:SUCCESS,bg:SUCCESS_BG,border:SUCCESS_LIGHT},
          {label:"Paid",n:paid,of:recorded,color:BRAND,bg:BRAND_LIGHT,border:BRAND_SOFTER},
        ].map((s,i)=>{
          const pct=s.of>0?s.n/s.of:0;
          const barW=`${Math.max(pct*100,s.n>0?8:2)}%`;
          return(
            <div key={s.label}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
                <span style={{fontSize:12,fontWeight:600,color:TEXT_SECONDARY}}>{s.label}</span>
                <span style={{fontSize:12,color:TEXT_SUBTLE}}>{s.n} of {s.of} · {Math.round(pct*100)}%</span>
              </div>
              <div style={{width:barW,height:38,background:s.bg,border:`1.5px solid ${s.border}`,borderRadius:8,display:"flex",alignItems:"center",paddingLeft:14,transition:"width 0.4s",minWidth:s.n>0?48:4,boxSizing:"border-box"}}>
                {s.n>0&&<span style={{fontSize:15,fontWeight:800,color:s.color}}>{s.n}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>}

    {/* Tabs */}
    <div style={{display:"flex",gap:0,borderBottom:`1px solid ${BORDER}`,marginBottom:16}}>
      {[["recordings","Recordings"],["contributors","Contributors"]].map(([k,l])=>(
        <button key={k} onClick={()=>setTab(k)} style={{padding:"9px 18px",border:"none",background:"none",color:tab===k?P:TEXT_MUTED,fontSize:13,fontWeight:tab===k?600:400,cursor:"pointer",borderBottom:tab===k?`2px solid ${P}`:"2px solid transparent",marginBottom:-1}}>
          {l} <span style={{marginLeft:4,fontSize:11,background:tab===k?PL:SURFACE_HOVER,color:tab===k?P:TEXT_MUTED,padding:"1px 6px",borderRadius:999,fontWeight:600}}>
            {k==="recordings"?detail.meetings.length:detail.contributors.length}
          </span>
        </button>
      ))}
    </div>

    {/* Recordings tab */}
    {tab==="recordings"&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:SURFACE_ALT}}>
          {project.solo
            ?["ID","Language","Topic","Length","Status","QA","Payment"].map(h=><th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:TEXT_SUBTLE}}>{h}</th>)
            :["ID","Participants","Language","Topic","Length","Status","QA","Payment"].map(h=><th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:TEXT_SUBTLE}}>{h}</th>)
          }
        </tr></thead>
        <tbody>{detail.meetings.length?detail.meetings.map(m=>{
          const qaColor=m.qaStatus==="Passed"?"green":m.qaStatus==="In QA"?"amber":"gray";
          return(
          <tr key={m.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`}}>
            <td style={{padding:"10px 14px",fontSize:12,color:P,fontWeight:600}}>{m.id}</td>
            {!project.solo&&<td style={{padding:"10px 14px",fontSize:12,maxWidth:200}}>
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                {(m.participants||[m.creator,m.partner].filter(Boolean)).map((name,i)=>(
                  <span key={i} style={{color:name?TEXT_SECONDARY:TEXT_SUBTLE}}>{name?shortName(name):"—"}</span>
                ))}
              </div>
            </td>}
            <td style={{padding:"10px 14px"}}><Badge label={m.lang||project.langs[0]} color="purple"/></td>
            <td style={{padding:"10px 14px",fontSize:12}}>{m.topic}</td>
            <td style={{padding:"10px 14px",fontSize:12,fontWeight:m.lengthMins?500:400,color:m.lengthMins?TEXT_PRIMARY:TEXT_SUBTLE}}>{m.lengthMins?`${m.lengthMins} min`:"—"}</td>
            <td style={{padding:"10px 14px"}}><Badge label={m.status} color={statusColor(m.status)}/></td>
            <td style={{padding:"10px 14px"}}>{m.qaStatus?<Badge label={m.qaStatus} color={qaColor}/>:<span style={{fontSize:12,color:TEXT_SUBTLE}}>—</span>}</td>
            <td style={{padding:"10px 14px"}}>{m.status==="Recorded"?<Badge label={m.paid?"Paid":"Unpaid"} color={m.paid?"green":"gray"}/>:<span style={{fontSize:12,color:TEXT_SUBTLE}}>—</span>}</td>
          </tr>
        )}):<tr><td colSpan={project.solo?7:8} style={{padding:"20px 14px",fontSize:13,color:TEXT_SUBTLE,textAlign:"center"}}>No recordings yet</td></tr>}
        </tbody>
      </table>
    </div>}

    {/* Contributors tab */}
    {tab==="contributors"&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:SURFACE_ALT}}>{["Contributor","Recordings","Hours vs Limit","Status"].map(h=><th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:TEXT_SUBTLE}}>{h}</th>)}</tr></thead>
        <tbody>{detail.contributors.length?detail.contributors.map(c=>{
          const limitHrs=project.maxPerUser||0;
          const atLimit=limitHrs>0&&c.hours>=limitHrs;
          const nearLimit=limitHrs>0&&!atLimit&&c.hours>=limitHrs*0.8;
          const limitPct=limitHrs>0?Math.min(Math.round(c.hours/limitHrs*100),100):0;
          return(
          <tr key={c.name} style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:atLimit?WARNING_SURFACE:SURFACE}}>
            <td style={{padding:"10px 14px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:atLimit?"#fde68a":PL,color:atLimit?WARNING:P,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{c.initials}</div>
                <span style={{fontSize:13,fontWeight:500}}>{c.name}</span>
              </div>
            </td>
            <td style={{padding:"10px 14px",fontSize:13}}>{c.recordings}</td>
            <td style={{padding:"10px 14px",minWidth:140}}>
              {limitHrs>0?<div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:atLimit?WARNING:nearLimit?"#d97706":TEXT_MUTED,marginBottom:3,fontWeight:atLimit||nearLimit?600:400}}>
                  <span>{c.hours.toFixed(1)}h / {limitHrs}h</span>
                  {atLimit&&<span style={{background:"#fde68a",color:WARNING,padding:"0px 5px",borderRadius:3,fontSize:10,fontWeight:700}}>AT LIMIT</span>}
                </div>
                <div style={{height:4,background:BORDER,borderRadius:999,overflow:"hidden"}}>
                  <div style={{width:`${limitPct}%`,height:"100%",background:atLimit?"#f59e0b":nearLimit?"#fbbf24":P,borderRadius:999,transition:"width 0.3s"}}/>
                </div>
              </div>:<span style={{fontSize:12,color:TEXT_SUBTLE}}>—</span>}
            </td>
            <td style={{padding:"10px 14px",fontSize:12,color:TEXT_MUTED}}>{c.windowsPosted?`${c.windowsPosted} windows posted`:c.scheduled?`${c.scheduled} scheduled`:c.recordings>0?"Contributed":"—"}</td>
          </tr>
        )}):<tr><td colSpan={4} style={{padding:"20px 14px",fontSize:13,color:TEXT_SUBTLE,textAlign:"center"}}>No contributors yet</td></tr>}
        </tbody>
      </table>
    </div>}
  </div>);
}

function OpsRecordingProjectsScreen({projects,onAddProject}){
  const[localProjects,setLocalProjects]=useState(projects);
  const[showNewProj,setShowNewProj]=useState(false);
  const[statusFilter,setStatusFilter]=useState([]);
  const[langFilter,setLangFilter]=useState([]);
  const[formatFilter,setFormatFilter]=useState([]);
  const[detailProj,setDetailProj]=useState(null);
  const[closingProj,setClosingProj]=useState(null);
  const handleCreate=(proj)=>{const updated=[...localProjects,proj];setLocalProjects(updated);onAddProject(proj);setShowNewProj(false);};
  const handleCloseBatch=()=>{
    if(!closingProj)return;
    setLocalProjects(prev=>prev.map(p=>p.id===closingProj.id?{...p,status:"Closed",claimable:false,closedReason:"Closed early by ops manager."}:p));
    setClosingProj(null);
  };

  if(detailProj) return <OpsProjectDetail project={localProjects.find(p=>p.id===detailProj.id)||detailProj} onBack={()=>setDetailProj(null)}/>;

  // Derive unique languages across all batches
  const allLangs=[...new Set(localProjects.flatMap(p=>p.langs))].sort();
  const allFormats=[...new Set(localProjects.map(p=>p.scripted?"Scripted":"Unscripted"))].sort();
  const allStatuses=[...new Set(localProjects.map(p=>p.status))].sort();

  const hasFilters=statusFilter.length>0||langFilter.length>0||formatFilter.length>0;
  const clearFilters=()=>{setStatusFilter([]);setLangFilter([]);setFormatFilter([]);};

  const filtered=localProjects.filter(p=>{
    if(statusFilter.length>0&&!statusFilter.includes(p.status))return false;
    if(langFilter.length>0&&!langFilter.some(l=>p.langs.includes(l)))return false;
    if(formatFilter.length>0&&!formatFilter.includes(p.scripted?"Scripted":"Unscripted"))return false;
    return true;
  });

  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    {showNewProj&&<NewProjectModal onClose={()=>setShowNewProj(false)} onCreate={handleCreate} opProjects={initialOpsProjectsList}/>}
    {closingProj&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}>
      <div style={{background:SURFACE,borderRadius:12,padding:28,width:420,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontWeight:600,fontSize:16}}>Close batch early</div>
          <button onClick={()=>setClosingProj(null)} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button>
        </div>
        <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:24,lineHeight:1.6}}>You're closing <strong>{closingProj.title}</strong> before it's full. It will no longer accept new recordings. This cannot be undone.</p>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={()=>setClosingProj(null)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={handleCloseBatch} style={{padding:"8px 16px",borderRadius:8,border:"none",background:DANGER,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Close Batch</button>
        </div>
      </div>
    </div>}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>Recording Batches</h1>
      <button onClick={()=>setShowNewProj(true)} style={{padding:"8px 14px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>+ New Batch</button>
    </div>
    <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
      <MultiSelectDropdown label="Status" options={allStatuses} selected={statusFilter} onChange={setStatusFilter}/>
      <MultiSelectDropdown label="Language" options={allLangs} selected={langFilter} onChange={setLangFilter}/>
      <MultiSelectDropdown label="Format" options={allFormats} selected={formatFilter} onChange={setFormatFilter}/>
      {hasFilters&&<button onClick={clearFilters} style={{fontSize:12,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:"4px 0"}}>Clear filters</button>}
      <span style={{fontSize:12,color:TEXT_SUBTLE,marginLeft:"auto"}}>{filtered.length} of {localProjects.length} batch{localProjects.length!==1?"es":""}</span>
    </div>
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${BORDER}`,background:SURFACE_ALT}}>
          {["Batch Name","Language","Format","Participants","Project","Topics","Progress","Deadline","Status",""].map(h=>(
            <th key={h} style={{padding:"9px 13px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE,whiteSpace:"nowrap"}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>{filtered.map(p=>{
          const pct=Math.round((p.hoursUsed||0)/p.hours*100);
          const inactive=p.status!=="Active";
          const projName=(initialOpsProjectsList.find(op=>op.id===p.projectId)||{}).name||"—";
          return(
            <tr key={p.id} onClick={()=>setDetailProj(p)} style={{borderBottom:`1px solid ${SURFACE_HOVER}`,cursor:"pointer",opacity:inactive?0.75:1}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_ALT} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>
              <td style={{padding:"11px 13px",maxWidth:200}}>
                <div style={{fontWeight:500,fontSize:13,marginBottom:2}}>{p.title}</div>
              </td>
              <td style={{padding:"11px 13px"}}>
                <Badge label={p.langs?.[0]||"—"} color={inactive?"gray":"purple"}/>
              </td>
              <td style={{padding:"11px 13px"}}>
                <Badge label={p.scripted?"Scripted":"Unscripted"} color={p.scripted?"blue":"teal"}/>
              </td>
              <td style={{padding:"11px 13px",fontSize:12,color:TEXT_SECONDARY,whiteSpace:"nowrap"}}>{getParticipantCount(p)}</td>
              <td style={{padding:"11px 13px",fontSize:12,color:TEXT_MUTED,whiteSpace:"nowrap"}}>{projName}</td>
              <td style={{padding:"11px 13px",maxWidth:180}}>
                <TopicList topics={p.topics}/>
              </td>
              <td style={{padding:"11px 13px",minWidth:130}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <div style={{width:70,height:5,background:BORDER,borderRadius:999,overflow:"hidden",flexShrink:0}}>
                    <div style={{width:`${pct}%`,height:"100%",background:inactive?TEXT_DISABLED:pct===100?SUCCESS:P,borderRadius:999}}/>
                  </div>
                  <span style={{fontSize:11,color:TEXT_MUTED,whiteSpace:"nowrap"}}>{p.hoursUsed||0}h / {p.hours}h</span>
                </div>
              </td>
              <td style={{padding:"11px 13px",fontSize:12,color:TEXT_MUTED,whiteSpace:"nowrap"}}>{p.deadline}</td>
              <td style={{padding:"11px 13px"}}>
                <Badge label={p.status} color={p.status==="Active"?"green":p.status==="Full"?"red":"gray"}/>
              </td>
              <td style={{padding:"11px 13px",whiteSpace:"nowrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:12,color:P,fontWeight:500}}>View →</span>
                  {p.status==="Active"&&<button onClick={e=>{e.stopPropagation();setClosingProj(p);}} style={{fontSize:11,color:DANGER,background:"none",border:`1px solid ${DANGER_BORDER}`,borderRadius:6,padding:"3px 9px",cursor:"pointer",fontWeight:500}}>Close</button>}
                </div>
              </td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  </div>);
}

// ── REVIEWER BATCH DETAIL ─────────────────────────────────────────────────────
function ReviewerBatchDetail({batch,onBack}){
  const tasks=reviewerBatchTasks[batch.id]||[];
  const[tab,setTab]=useState("tasks");
  const statusColor=s=>s==="Completed"?"green":s==="Assigned"?"blue":"orange";
  const counts={Completed:tasks.filter(t=>t.status==="Completed").length,Assigned:tasks.filter(t=>t.status==="Assigned").length,Unassigned:tasks.filter(t=>t.status==="Unassigned").length};

  // Derive reviewer stats from task list
  const reviewerMap={};
  tasks.forEach(t=>{
    if(!t.reviewer)return;
    if(!reviewerMap[t.reviewer])reviewerMap[t.reviewer]={name:t.reviewer,assigned:0,completed:0};
    reviewerMap[t.reviewer].assigned++;
    if(t.status==="Completed")reviewerMap[t.reviewer].completed++;
  });
  const reviewerRows=Object.values(reviewerMap).sort((a,b)=>b.assigned-a.assigned);
  const unassignedCount=counts.Unassigned;

  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back to Reviewer Batches</button>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>{batch.name}</h1>
      <Badge label={batch.type} color="purple"/>
    </div>
    <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
      <Badge label={batch.lang} color="blue"/>
      <span style={{fontSize:12,color:TEXT_SUBTLE}}>Deadline: {batch.deadline}</span>
    </div>
    {batch.rate!==undefined&&<div style={{background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:8,padding:"10px 14px",marginBottom:16,display:"flex",gap:16,flexWrap:"wrap"}}>
      <span style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE}}>RATE:</span>
      <span style={{fontSize:12,color:TEXT_SECONDARY}}><strong>${batch.rate.toFixed(2)}/min</strong></span>
    </div>}

    {/* Stats bar */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
      {[["Total Tasks",batch.tasks],["Completed",counts.Completed],["Assigned",counts.Assigned],["Unassigned",counts.Unassigned]].map(([label,val])=>(
        <div key={label} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
          <div style={{fontSize:20,fontWeight:700,color:P,marginBottom:2}}>{val}</div>
          <div style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:500}}>{label}</div>
        </div>
      ))}
    </div>

    {/* Progress bar */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
      <div style={{flex:1,height:6,background:BORDER,borderRadius:999,overflow:"hidden"}}>
        <div style={{width:`${Math.round(batch.done/batch.tasks*100)}%`,height:"100%",background:batch.done===batch.tasks?SUCCESS:P,borderRadius:999}}/>
      </div>
      <span style={{fontSize:12,fontWeight:600,color:TEXT_SECONDARY,whiteSpace:"nowrap"}}>{batch.done}/{batch.tasks} completed</span>
    </div>

    {/* Tabs */}
    <div style={{display:"flex",gap:0,borderBottom:`1px solid ${BORDER}`,marginBottom:16}}>
      {[["tasks","Tasks",tasks.length],["reviewers","Reviewers",reviewerRows.length]].map(([k,l,n])=>(
        <button key={k} onClick={()=>setTab(k)} style={{padding:"9px 18px",border:"none",background:"none",color:tab===k?P:TEXT_MUTED,fontSize:13,fontWeight:tab===k?600:400,cursor:"pointer",borderBottom:tab===k?`2px solid ${P}`:"2px solid transparent",marginBottom:-1}}>
          {l} <span style={{marginLeft:4,fontSize:11,background:tab===k?PL:SURFACE_HOVER,color:tab===k?P:TEXT_SUBTLE,padding:"1px 6px",borderRadius:999,fontWeight:600}}>{n}</span>
        </button>
      ))}
    </div>

    {/* Tasks tab */}
    {tab==="tasks"&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:SURFACE_ALT}}>{["ID","Type","Language","Reviewer","Deadline","Status"].map(h=><th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:TEXT_SUBTLE}}>{h}</th>)}</tr></thead>
        <tbody>{tasks.length?tasks.map(t=>(
          <tr key={t.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`}}>
            <td style={{padding:"10px 14px",fontSize:12,color:P,fontWeight:600}}>{t.id}{t.pii&&<span style={{marginLeft:6,background:DANGER_BG,color:DANGER,fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:4}}>PII</span>}</td>
            <td style={{padding:"10px 14px",fontSize:12}}>{t.type}</td>
            <td style={{padding:"10px 14px"}}><Badge label={t.lang} color="purple"/></td>
            <td style={{padding:"10px 14px",fontSize:12,color:t.reviewer?TEXT_SECONDARY:TEXT_SUBTLE}}>{t.reviewer||"—"}</td>
            <td style={{padding:"10px 14px",fontSize:12}}>{t.deadline}</td>
            <td style={{padding:"10px 14px"}}><Badge label={t.status} color={statusColor(t.status)}/></td>
          </tr>
        )):<tr><td colSpan={6} style={{padding:"20px 14px",fontSize:13,color:TEXT_SUBTLE,textAlign:"center"}}>No tasks yet</td></tr>}
        </tbody>
      </table>
    </div>}

    {/* Reviewers tab */}
    {tab==="reviewers"&&<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:SURFACE_ALT}}>{["Reviewer","Tasks Assigned","Completed","In Progress","Completion Rate"].map(h=><th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:TEXT_SUBTLE}}>{h}</th>)}</tr></thead>
        <tbody>
          {reviewerRows.map(r=>{
            const inProgress=r.assigned-r.completed;
            const pct=r.assigned>0?Math.round(r.completed/r.assigned*100):0;
            return(<tr key={r.name} style={{borderBottom:`1px solid ${SURFACE_HOVER}`}}>
              <td style={{padding:"10px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:PL,color:P,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>
                    {r.name.split(" ").map(x=>x[0]).join("")}
                  </div>
                  <span style={{fontSize:13,fontWeight:500}}>{r.name}</span>
                </div>
              </td>
              <td style={{padding:"10px 14px",fontSize:13}}>{r.assigned}</td>
              <td style={{padding:"10px 14px"}}><Badge label={String(r.completed)} color="green"/></td>
              <td style={{padding:"10px 14px"}}><Badge label={String(inProgress)} color={inProgress>0?"blue":"gray"}/></td>
              <td style={{padding:"10px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:9}}>
                  <div style={{width:80,height:5,background:BORDER,borderRadius:999,overflow:"hidden"}}>
                    <div style={{width:`${pct}%`,height:"100%",background:pct===100?SUCCESS:P,borderRadius:999}}/>
                  </div>
                  <span style={{fontSize:12,color:TEXT_SECONDARY,fontWeight:500}}>{pct}%</span>
                </div>
              </td>
            </tr>);
          })}
          {unassignedCount>0&&<tr style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:WARNING_SURFACE}}>
            <td style={{padding:"10px 14px"}}>
              <span style={{fontSize:12,color:WARNING,fontWeight:500}}>Unassigned</span>
            </td>
            <td style={{padding:"10px 14px",fontSize:13,color:WARNING}}>{unassignedCount}</td>
            <td colSpan={3} style={{padding:"10px 14px",fontSize:12,color:WARNING}}>{unassignedCount} task{unassignedCount!==1?"s":""} not yet assigned to a reviewer</td>
          </tr>}
          {reviewerRows.length===0&&unassignedCount===0&&<tr><td colSpan={5} style={{padding:"20px 14px",fontSize:13,color:TEXT_SUBTLE,textAlign:"center"}}>No reviewers assigned yet</td></tr>}
        </tbody>
      </table>
    </div>}
  </div>);
}

// ── REVIEWER BATCHES SCREEN ───────────────────────────────────────────────────
function OpsReviewerBatchesScreen(){
  const[showNewBatch,setShowNewBatch]=useState(false);
  const[detailBatch,setDetailBatch]=useState(null);
  const[typeFilter,setTypeFilter]=useState([]);
  const[langFilter,setLangFilter]=useState([]);

  const allBatches=reviewBatches;

  // Derive filter options from all batches
  const typeOptions=[...new Set(allBatches.map(b=>b.type))].sort();
  const langOptions=[...new Set(allBatches.map(b=>b.lang))].sort();

  const filteredBatches=allBatches.filter(b=>{
    if(typeFilter.length>0&&!typeFilter.includes(b.type))return false;
    if(langFilter.length>0&&!langFilter.includes(b.lang))return false;
    return true;
  });

  const hasFilters=typeFilter.length>0||langFilter.length>0;
  const clearFilters=()=>{setTypeFilter([]);setLangFilter([]);};

  if(detailBatch) return <ReviewerBatchDetail batch={detailBatch} onBack={()=>setDetailBatch(null)}/>;

  const BatchTable=({batches})=>(
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${BORDER}`,background:SURFACE_ALT}}>{["Batch Name","Type","Project","Language","Progress","Deadline",""].map(h=><th key={h} style={{padding:"9px 13px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>{h}</th>)}</tr></thead>
        <tbody>{batches.length?batches.map(b=>(
          <tr key={b.id} onClick={()=>setDetailBatch(b)} style={{borderBottom:`1px solid ${SURFACE_HOVER}`,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_ALT} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>
            <td style={{padding:"10px 13px",fontSize:13,fontWeight:500,color:P}}>{b.name}</td>
            <td style={{padding:"10px 13px",fontSize:12,color:TEXT_SECONDARY}}>{b.type}</td>
            <td style={{padding:"10px 13px",fontSize:12,color:TEXT_MUTED}}>{b.project||"—"}</td>
            <td style={{padding:"10px 13px"}}><Badge label={b.lang} color="purple"/></td>
            <td style={{padding:"10px 13px"}}>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <div style={{width:80,height:4,background:BORDER,borderRadius:999,overflow:"hidden"}}><div style={{width:`${Math.round(b.done/b.tasks*100)}%`,height:"100%",background:b.done===b.tasks?SUCCESS:P,borderRadius:999}}/></div>
                <span style={{fontSize:11,color:TEXT_SUBTLE}}>{b.done}/{b.tasks}</span>
              </div>
            </td>
            <td style={{padding:"10px 13px",fontSize:13}}>{b.deadline}</td>
            <td style={{padding:"10px 13px",fontSize:12,color:P,fontWeight:500}}>View →</td>
          </tr>
        )):<tr><td colSpan={7} style={{padding:"20px",textAlign:"center",fontSize:13,color:TEXT_SUBTLE}}>No batches match the current filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    {showNewBatch&&<NewBatchModal onClose={()=>setShowNewBatch(false)} isAudio={false}/>}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>Reviewer Batches</h1>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>setShowNewBatch(true)} style={{padding:"8px 14px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>+ Create New Reviewer Batch</button>
      </div>
    </div>
    <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
      {typeOptions.length>1&&<MultiSelectDropdown label="Task Type" options={typeOptions} selected={typeFilter} onChange={setTypeFilter}/>}
      <MultiSelectDropdown label="Language" options={langOptions} selected={langFilter} onChange={setLangFilter}/>
      {hasFilters&&<button onClick={clearFilters} style={{fontSize:12,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:"4px 0"}}>Clear filters</button>}
      <span style={{fontSize:12,color:TEXT_SUBTLE,marginLeft:"auto"}}>{filteredBatches.length} of {allBatches.length} batch{allBatches.length!==1?"es":""}</span>
    </div>
    <BatchTable batches={filteredBatches}/>
  </div>);
}

// ── TASK MANAGEMENT SCREEN ────────────────────────────────────────────────────
function OpsTaskManagementScreen(){
  const[mode,setMode]=useState("all"); // "all" | "attention"
  const[statusFilter,setStatusFilter]=useState("All");
  const[attentionFilter,setAttentionFilter]=useState("All"); // "All" | "Awaiting Assignment" | "Unclaimed" | "Past Deadline"
  const[search,setSearch]=useState("");
  const[reviewerFilter,setReviewerFilter]=useState("All Reviewers");
  const[taskData,setTaskData]=useState(allTasksData);
  const[showNewBatch,setShowNewBatch]=useState(false);
  const[showNewAudioBatch,setShowNewAudioBatch]=useState(false);
  const[assigningTask,setAssigningTask]=useState(null);
  const[assignTarget,setAssignTarget]=useState("");

  // Attention bucket helpers — a task can be in multiple buckets simultaneously
  const isAwaitingAssignment=t=>t.status==="Unassigned"&&t.assignmentMode==="direct";
  const isUnclaimed=t=>t.status==="Unassigned"&&t.assignmentMode==="pool"&&(t.daysUnclaimed||0)>=7;
  const isPastDeadline=t=>!!t.pastDeadline&&t.status!=="Completed";
  const needsAttention=t=>isAwaitingAssignment(t)||isUnclaimed(t)||isPastDeadline(t);

  const reviewers=[...new Set(taskData.filter(t=>t.reviewer).map(t=>t.reviewer))];
  const statusCounts={Unassigned:taskData.filter(t=>t.status==="Unassigned").length,Assigned:taskData.filter(t=>t.status==="Assigned").length,Completed:taskData.filter(t=>t.status==="Completed").length};
  const attentionCounts={
    "Awaiting Assignment":taskData.filter(isAwaitingAssignment).length,
    "Unclaimed":taskData.filter(isUnclaimed).length,
    "Past Deadline":taskData.filter(isPastDeadline).length,
  };
  const attentionTotal=taskData.filter(needsAttention).length;

  const filtered=taskData.filter(t=>{
    // Mode-level gate
    if(mode==="attention"&&!needsAttention(t))return false;
    // Status pill (only in All mode)
    if(mode==="all"&&statusFilter!=="All"&&t.status!==statusFilter)return false;
    // Attention bucket pill (only in attention mode)
    if(mode==="attention"&&attentionFilter!=="All"){
      if(attentionFilter==="Awaiting Assignment"&&!isAwaitingAssignment(t))return false;
      if(attentionFilter==="Unclaimed"&&!isUnclaimed(t))return false;
      if(attentionFilter==="Past Deadline"&&!isPastDeadline(t))return false;
    }
    if(reviewerFilter!=="All Reviewers"&&t.reviewer!==reviewerFilter)return false;
    if(search&&!t.id.toLowerCase().includes(search.toLowerCase())&&!t.type.toLowerCase().includes(search.toLowerCase())&&!t.lang.toLowerCase().includes(search.toLowerCase())&&!(t.reviewer||"").toLowerCase().includes(search.toLowerCase()))return false;
    return true;
  });

  const doAssign=()=>{if(!assignTarget||!assigningTask)return;setTaskData(prev=>prev.map(t=>t.id===assigningTask?{...t,reviewer:assignTarget,reviewerInitials:assignTarget.split(" ").map(x=>x[0]).join(""),status:"Assigned"}:t));setAssigningTask(null);setAssignTarget("");};
  const doUnassign=id=>setTaskData(prev=>prev.map(t=>t.id===id?{...t,reviewer:null,reviewerInitials:null,status:"Unassigned"}:t));

  const SAMPLE_REVIEWERS=["Kwame Boateng","Amara Osei","Zara Mensah","Priya Nair","Lucas Oliveira"];

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    {showNewBatch&&<NewBatchModal onClose={()=>setShowNewBatch(false)} isAudio={false}/>}
    {showNewAudioBatch&&<NewBatchModal onClose={()=>setShowNewAudioBatch(false)} isAudio={true}/>}
    {assigningTask&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}>
      <div style={{background:SURFACE,borderRadius:12,padding:24,width:360}}>
        <div style={{fontWeight:600,fontSize:15,marginBottom:12}}>Assign {assigningTask}</div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,marginBottom:6}}>SELECT REVIEWER</div>
        <select value={assignTarget} onChange={e=>setAssignTarget(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",marginBottom:16}}>
          <option value="">— Choose reviewer —</option>
          {SAMPLE_REVIEWERS.map(r=><option key={r}>{r}</option>)}
        </select>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={()=>setAssigningTask(null)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={doAssign} disabled={!assignTarget} style={{padding:"8px 16px",borderRadius:8,border:"none",background:assignTarget?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:500,cursor:assignTarget?"pointer":"default"}}>Assign →</button>
        </div>
      </div>
    </div>}
    <div style={{padding:"22px 28px 0",flexShrink:0}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8,flexWrap:"wrap"}}>
        <h1 style={{fontSize:21,fontWeight:600,margin:0}}>Task Management</h1>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setShowNewBatch(true)} style={{padding:"8px 14px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>+ Create New Reviewer Batch</button>
        </div>
      </div>

      {/* Mode segment: All Tasks vs Needs Attention */}
      <div style={{display:"inline-flex",background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:9,padding:3,marginBottom:12,gap:2}}>
        <button onClick={()=>{setMode("all");setAttentionFilter("All");}} style={{padding:"6px 14px",borderRadius:7,border:"none",background:mode==="all"?SURFACE:"transparent",color:mode==="all"?TEXT_PRIMARY:TEXT_MUTED,fontSize:12,fontWeight:mode==="all"?600:500,cursor:"pointer",boxShadow:mode==="all"?"0 1px 2px rgba(0,0,0,0.06)":"none"}}>
          All Tasks <span style={{color:mode==="all"?TEXT_SECONDARY:TEXT_SUBTLE,fontWeight:500}}>{taskData.length}</span>
        </button>
        <button onClick={()=>{setMode("attention");setStatusFilter("All");}} style={{padding:"6px 14px",borderRadius:7,border:"none",background:mode==="attention"?SURFACE:"transparent",color:mode==="attention"?TEXT_PRIMARY:TEXT_MUTED,fontSize:12,fontWeight:mode==="attention"?600:500,cursor:"pointer",boxShadow:mode==="attention"?"0 1px 2px rgba(0,0,0,0.06)":"none",display:"flex",alignItems:"center",gap:6}}>
          Needs Attention
          {attentionTotal>0&&<span style={{background:DANGER,color:SURFACE,fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:999,minWidth:18,textAlign:"center"}}>{attentionTotal}</span>}
        </button>
      </div>

      {/* Filters: status-or-attention pills + reviewer dropdown on the left, search on the right */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:10,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
          {mode==="all"?(
            [["All","All"],["Unassigned","Unassigned"],["Assigned","Assigned"],["Completed","Completed"]].map(([k,l])=>{
              const n=k==="All"?taskData.length:statusCounts[k]||0;
              return(<button key={k} onClick={()=>setStatusFilter(k)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${statusFilter===k?P:BORDER}`,background:statusFilter===k?PL:SURFACE,color:statusFilter===k?P:TEXT_SECONDARY,fontSize:12,cursor:"pointer",fontWeight:statusFilter===k?600:400}}>
                {l} <span style={{fontWeight:700}}>{n}</span>
              </button>);
            })
          ):(
            ["All","Awaiting Assignment","Unclaimed","Past Deadline"].map(k=>{
              const n=k==="All"?attentionTotal:attentionCounts[k]||0;
              const active=attentionFilter===k;
              return(<button key={k} onClick={()=>setAttentionFilter(k)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${active?P:BORDER}`,background:active?PL:SURFACE,color:active?P:TEXT_SECONDARY,fontSize:12,cursor:"pointer",fontWeight:active?600:400}}>
                {k} <span style={{fontWeight:700}}>{n}</span>
              </button>);
            })
          )}
          <select value={reviewerFilter} onChange={e=>setReviewerFilter(e.target.value)} style={{padding:"4px 10px",border:`1px solid ${reviewerFilter!=="All Reviewers"?P:BORDER}`,background:reviewerFilter!=="All Reviewers"?PL:SURFACE,color:reviewerFilter!=="All Reviewers"?P:TEXT_SECONDARY,borderRadius:6,fontSize:12,outline:"none",fontWeight:reviewerFilter!=="All Reviewers"?600:400,cursor:"pointer"}}>
            <option>All Reviewers</option>
            {reviewers.map(r=><option key={r}>{r}</option>)}
          </select>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by ID, type, language…" style={{padding:"6px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",width:220}}/>
      </div>
    </div>
    {/* Table */}
    <div style={{flex:1,overflowY:"auto",padding:"0 28px 22px"}}>
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:`1px solid ${BORDER}`,background:SURFACE_ALT}}>
            <th style={{padding:"9px 13px",width:32}}><input type="checkbox" style={{accentColor:P}}/></th>
            {["ID","Type","Lang","Project","Reviewer","Deadline","Status","Attention","Actions"].map(h=><th key={h} style={{padding:"9px 13px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>{h}</th>)}
          </tr></thead>
          <tbody>{filtered.length?filtered.map(t=>{
            const showAwait=isAwaitingAssignment(t);
            const showUnclaimed=isUnclaimed(t);
            const showPast=isPastDeadline(t);
            return(
            <tr key={t.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`}}>
              <td style={{padding:"10px 13px"}}><input type="checkbox" style={{accentColor:P}}/></td>
              <td style={{padding:"10px 13px",color:P,fontWeight:600,fontSize:13}}>{t.id}{t.pii&&<span style={{marginLeft:6,background:DANGER_BG,color:DANGER,fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:4}}>PII</span>}</td>
              <td style={{padding:"10px 13px",fontSize:12,maxWidth:220}}>{t.type}</td>
              <td style={{padding:"10px 13px"}}><Badge label={t.lang} color="purple"/></td>
              <td style={{padding:"10px 13px",fontSize:12,color:TEXT_MUTED}}>{t.projectName||"—"}</td>
              <td style={{padding:"10px 13px",fontSize:12,color:t.reviewer?TEXT_SECONDARY:TEXT_SUBTLE}}>{t.reviewer||"—"}</td>
              <td style={{padding:"10px 13px",fontSize:12}}>{t.deadline}</td>
              <td style={{padding:"10px 13px"}}>
                <span style={{display:"inline-block",padding:"2px 8px",borderRadius:999,fontSize:11,fontWeight:600,background:t.status==="Completed"?SUCCESS_BG:t.status==="Assigned"?INFO_BG:WARNING_BG,color:t.status==="Completed"?SUCCESS:t.status==="Assigned"?INFO:WARNING}}>
                  {t.status}
                </span>
              </td>
              <td style={{padding:"10px 13px"}}>
                <div style={{display:"flex",flexDirection:"column",gap:3,alignItems:"flex-start"}}>
                  {showAwait&&<span title="Directly assigned but no assignee set" style={{display:"inline-block",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700,background:DANGER_BG,color:DANGER,whiteSpace:"nowrap"}}>Awaiting Assignment</span>}
                  {showUnclaimed&&<span title="Pool task that has been sitting without a claim" style={{display:"inline-block",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700,background:WARNING_ACCENT_SURFACE,color:WARNING_ACCENT,whiteSpace:"nowrap"}}>{t.daysUnclaimed}d unclaimed</span>}
                  {showPast&&<span title="Past deadline" style={{display:"inline-block",padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700,background:DANGER_BG,color:DANGER,whiteSpace:"nowrap"}}>Past Deadline</span>}
                  {!showAwait&&!showUnclaimed&&!showPast&&<span style={{fontSize:11,color:TEXT_DISABLED}}>—</span>}
                </div>
              </td>
              <td style={{padding:"10px 13px"}}>
                {t.status==="Completed"&&<button style={{fontSize:12,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500,whiteSpace:"nowrap"}}>View Results →</button>}
                {t.status==="Assigned"&&<button onClick={()=>doUnassign(t.id)} style={{fontSize:12,color:TEXT_MUTED,background:"none",border:`1px solid ${BORDER}`,borderRadius:6,padding:"3px 10px",cursor:"pointer"}}>Unassign</button>}
                {t.status==="Unassigned"&&<button onClick={()=>setAssigningTask(t.id)} style={{fontSize:12,color:SURFACE,background:P,border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontWeight:500}}>Assign →</button>}
              </td>
            </tr>);
          }):<tr><td colSpan={10} style={{padding:"24px",textAlign:"center",fontSize:13,color:TEXT_SUBTLE}}>No tasks match the current filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  </div>);
}

// ── OPS PROJECTS SCREEN ───────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────────
// NEW PROJECT WIZARD — 5-step flow for creating a project with all downstream
// config collected up front. Replaces the old single-modal NewOpsProjectModal.
// For now, this produces a project object with the new shape; auto-generation
// of batches/engagements will be wired up in a later pass.
// ──────────────────────────────────────────────────────────────────────────────

const WIZ_LABEL = {fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:5};
const WIZ_INPUT = {width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,boxSizing:"border-box",outline:"none",background:SURFACE,fontFamily:"inherit"};
const WIZ_STAGES = [
  {key:"audioReview", label:"Audio Review"},
  {key:"transcription", label:"Transcription"},
  {key:"transcriptionReview", label:"Transcription Review"},
];
const WIZ_DEVICE_OPTIONS = [
  {value:"any", label:"Any device"},
  {value:"mobile", label:"Mobile only"},
  {value:"desktop", label:"Desktop only"},
  {value:"studio", label:"Studio / professional mic"},
];
const WIZ_SAMPLE_RATES = [
  {value:"8khz", label:"8 kHz (telephone)"},
  {value:"16khz", label:"16 kHz (standard speech)"},
  {value:"44khz", label:"44.1 kHz (CD)"},
  {value:"48khz", label:"48 kHz (professional)"},
];

// ── Mock "DB defaults" — in production these would be fetched ──
// Base rate (USD per task) defaults, keyed by destination stage + language.
// Any (stage, lang) not listed falls back to a sensible floor.
const WIZ_DEFAULT_BASE_RATES = {
  audioReview:          {Spanish:0.10, French:0.11, Arabic:0.14, Mandarin:0.13, Portuguese:0.10, Swahili:0.12, Hausa:0.12, Yoruba:0.12, Igbo:0.12, Amharic:0.14, Zulu:0.12, Somali:0.14},
  transcription:        {Spanish:0.35, French:0.38, Arabic:0.55, Mandarin:0.50, Portuguese:0.36, Swahili:0.45, Hausa:0.48, Yoruba:0.48, Igbo:0.48, Amharic:0.60, Zulu:0.45, Somali:0.58},
  transcriptionReview:  {Spanish:0.18, French:0.20, Arabic:0.28, Mandarin:0.26, Portuguese:0.18, Swahili:0.23, Hausa:0.25, Yoruba:0.25, Igbo:0.25, Amharic:0.30, Zulu:0.23, Somali:0.30},
  complete:             {}, // no work at complete
};
const getDefaultBaseRate=(stage,lang)=>WIZ_DEFAULT_BASE_RATES[stage]?.[lang]??0.10;
const WIZ_STEP_LABELS = ["Basics","Languages & Hours","Recording Specs","Flow","Review & Launch"];

// ── Stepper ─────────────────────────────────────────────────────────────────
function WizStepper({current,onJump}){
  return(<div style={{display:"flex",alignItems:"center",padding:"16px 28px",borderBottom:`1px solid ${BORDER}`,background:SURFACE_SUNKEN,gap:4}}>
    {WIZ_STEP_LABELS.map((label,i)=>{
      const step=i+1;
      const isDone=step<current;
      const isActive=step===current;
      const canJump=step<current;
      return(<div key={label} style={{display:"flex",alignItems:"center",flex:i===WIZ_STEP_LABELS.length-1?"0 0 auto":1}}>
        <div onClick={()=>canJump&&onJump(step)} style={{display:"flex",alignItems:"center",gap:8,cursor:canJump?"pointer":"default"}}>
          <div style={{width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,background:isActive?P:(isDone?SUCCESS:SURFACE),color:(isActive||isDone)?SURFACE:TEXT_SUBTLE,border:`1px solid ${isActive?P:(isDone?SUCCESS:BORDER_STRONG)}`,flexShrink:0}}>
            {isDone?"✓":step}
          </div>
          <span style={{fontSize:12,fontWeight:isActive?600:500,color:isActive?TEXT_PRIMARY:(isDone?TEXT_SECONDARY:TEXT_SUBTLE),whiteSpace:"nowrap"}}>{label}</span>
        </div>
        {i<WIZ_STEP_LABELS.length-1&&<div style={{flex:1,height:1,background:isDone?SUCCESS_LIGHT:BORDER,margin:"0 12px",minWidth:20}}/>}
      </div>);
    })}
  </div>);
}

// ── Step 1: Basics ──────────────────────────────────────────────────────────
function WizStepBasics({data,onChange}){
  return(<div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:620}}>
    <div>
      <div style={WIZ_LABEL}>PROJECT NAME <span style={{color:DANGER}}>*</span></div>
      <input value={data.name} onChange={e=>onChange({name:e.target.value})} placeholder="e.g. Spanish Medical Training" style={WIZ_INPUT}/>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
      <div>
        <div style={WIZ_LABEL}>CUSTOMER</div>
        <input value={data.customer} onChange={e=>onChange({customer:e.target.value})} placeholder="e.g. Acme Health" style={WIZ_INPUT}/>
      </div>
      <div>
        <div style={WIZ_LABEL}>OVERALL DEADLINE <span style={{color:DANGER}}>*</span></div>
        <input type="date" value={data.deadline} onChange={e=>onChange({deadline:e.target.value})} style={WIZ_INPUT}/>
      </div>
    </div>
    <div>
      <div style={WIZ_LABEL}>DESCRIPTION</div>
      <textarea value={data.description} onChange={e=>onChange({description:e.target.value})} rows={3} placeholder="What is this project collecting or validating?" style={{...WIZ_INPUT,resize:"vertical",lineHeight:1.5}}/>
    </div>
  </div>);
}

// ── Step 2: Languages & Hours ───────────────────────────────────────────────
function WizStepLanguages({data,onChange}){
  const used=data.languages.map(l=>l.lang);
  const available=LANGS.filter(l=>!used.includes(l));
  const totalHours=data.languages.reduce((s,l)=>s+(parseFloat(l.hours)||0),0);

  const updateLang=(idx,patch)=>{
    const next=[...data.languages];
    next[idx]={...next[idx],...patch};
    onChange({languages:next});
  };
  const removeLang=idx=>onChange({languages:data.languages.filter((_,i)=>i!==idx)});
  const addLang=()=>{
    if(available.length===0)return;
    onChange({languages:[...data.languages,{lang:available[0],hours:"",maxPerContributor:"",combinationMax:""}]});
  };

  return(<div style={{display:"flex",flexDirection:"column",gap:14,maxWidth:820}}>
    <p style={{fontSize:13,color:TEXT_MUTED,margin:0,lineHeight:1.5}}>
      Add each language you need to collect, with the target hours and per-contributor caps. Caps only apply to recording batches.
    </p>

    {/* Column headers */}
    {data.languages.length>0&&<div style={{display:"grid",gridTemplateColumns:"1.4fr 1fr 1fr 1.1fr 32px",gap:10,padding:"0 4px"}}>
      <div style={WIZ_LABEL}>LANGUAGE</div>
      <div style={WIZ_LABEL}>HOURS <span style={{color:DANGER}}>*</span></div>
      <div style={WIZ_LABEL}>MAX / CONTRIBUTOR</div>
      <div style={WIZ_LABEL}>MAX COMBINATION HRS</div>
      <div/>
    </div>}

    {data.languages.map((l,i)=>(
      <div key={i} style={{display:"grid",gridTemplateColumns:"1.4fr 1fr 1fr 1.1fr 32px",gap:10,alignItems:"center"}}>
        <select value={l.lang} onChange={e=>updateLang(i,{lang:e.target.value})} style={WIZ_INPUT}>
          <option value={l.lang}>{l.lang}</option>
          {available.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <input type="number" min="1" value={l.hours} onChange={e=>updateLang(i,{hours:e.target.value})} placeholder="40" style={WIZ_INPUT}/>
        <input type="number" min="0" value={l.maxPerContributor} onChange={e=>updateLang(i,{maxPerContributor:e.target.value})} placeholder="5" style={WIZ_INPUT}/>
        <input type="number" min="0" step="0.5" value={l.combinationMax} onChange={e=>updateLang(i,{combinationMax:e.target.value})} placeholder="Optional" style={WIZ_INPUT}/>
        <button onClick={()=>removeLang(i)} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:6,width:28,height:28,cursor:"pointer",color:TEXT_MUTED,fontSize:14,padding:0}} title="Remove">×</button>
      </div>
    ))}

    {data.languages.length===0&&(
      <div style={{padding:"20px 16px",background:SURFACE_SUNKEN,border:`1px dashed ${BORDER_STRONG}`,borderRadius:10,textAlign:"center",fontSize:13,color:TEXT_MUTED}}>
        No languages added yet. Add at least one language to continue.
      </div>
    )}

    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:4}}>
      <button onClick={addLang} disabled={available.length===0} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:13,fontWeight:500,cursor:available.length===0?"default":"pointer",opacity:available.length===0?0.4:1}}>+ Add language</button>
      {data.languages.length>0&&<div style={{fontSize:13,color:TEXT_SECONDARY}}>Total: <strong style={{color:TEXT_PRIMARY}}>{totalHours} hours</strong> across {data.languages.length} language{data.languages.length!==1?"s":""}</div>}
    </div>
  </div>);
}

// ── Step 3: Recording Batches ───────────────────────────────────────────────
// Per-language recording batch config. Each language selected in step 2
// produces one recording batch, and each batch can have its own specs.
// A "Copy from [first language]" button on non-first cards reduces repetition.
function WizStepRecordingBatches({data,onChange}){
  const external=data.audioExternal;
  const toggleExternal=()=>onChange({audioExternal:!external});
  const firstLang=data.languages[0]?.lang;

  const setBatchField=(lang,field,val)=>{
    const curr=data.perLangRecording[lang]||{};
    onChange({perLangRecording:{...data.perLangRecording,[lang]:{...curr,[field]:val}}});
  };
  const copyFromFirst=(targetLang)=>{
    if(!firstLang)return;
    const source=data.perLangRecording[firstLang]||{};
    onChange({perLangRecording:{...data.perLangRecording,[targetLang]:{...source}}});
  };

  return(<div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:820}}>
    {/* Audio source toggle */}
    <div style={{border:`1px solid ${external?WARNING_BORDER:BORDER}`,borderRadius:10,padding:"12px 14px",background:external?WARNING_SURFACE:SURFACE}}>
      <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
        <input type="checkbox" checked={external} onChange={toggleExternal} style={{marginTop:3,accentColor:P}}/>
        <div>
          <div style={{fontWeight:600,fontSize:13,color:external?WARNING_DEEP:TEXT_PRIMARY}}>Audio will be sourced externally</div>
          <div style={{fontSize:12,color:TEXT_MUTED,lineHeight:1.5,marginTop:2}}>Check this if the client is providing existing audio files. No recording batches will be created, and the Audio Creation stage will be disabled in the flow.</div>
        </div>
      </label>
    </div>

    {external?(
      <div style={{padding:"14px 16px",background:INFO_SURFACE,border:`1px solid ${INFO_BORDER}`,borderRadius:10,fontSize:13,color:INFO_DEEP,lineHeight:1.5}}>
        Recording batch specs don't apply to this project. Audio files can be uploaded from the project detail screen after creation.
      </div>
    ):data.languages.length===0?(
      <div style={{padding:"20px 16px",background:SURFACE_SUNKEN,border:`1px dashed ${BORDER_STRONG}`,borderRadius:10,textAlign:"center",fontSize:13,color:TEXT_MUTED}}>
        Go back to step 2 and add at least one language before configuring recording batches.
      </div>
    ):(<>
      <p style={{fontSize:13,color:TEXT_MUTED,margin:0,lineHeight:1.5}}>
        One recording batch will be created per language. Fill in the first language's specs, then use <em>Copy from {firstLang}</em> on subsequent cards to reuse values, tweaking per language as needed.
      </p>

      {data.languages.map((l,idx)=>{
        const b=data.perLangRecording[l.lang]||{};
        const isFirst=idx===0;
        return(<div key={l.lang} style={{border:`1px solid ${BORDER}`,borderRadius:12,padding:"14px 16px",background:SURFACE}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{display:"flex",alignItems:"baseline",gap:10}}>
              <span style={{fontWeight:600,fontSize:14,color:TEXT_PRIMARY}}>{l.lang}</span>
              <span style={{fontSize:12,color:TEXT_MUTED}}>{l.hours} hours goal</span>
            </div>
            {!isFirst&&(
              <button onClick={()=>copyFromFirst(l.lang)} style={{background:"none",border:`1px solid ${BRAND_SOFT}`,borderRadius:6,padding:"4px 10px",color:P,fontSize:11,fontWeight:600,cursor:"pointer"}}>Copy from {firstLang}</button>
            )}
          </div>

          {/* Row 1: script, participants, device */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div style={WIZ_LABEL}>SCRIPT TYPE <span style={{color:DANGER}}>*</span></div>
              <select value={b.scripted||""} onChange={e=>setBatchField(l.lang,"scripted",e.target.value)} style={WIZ_INPUT}>
                <option value="">Select…</option>
                <option value="unscripted">Unscripted</option>
                <option value="scripted">Scripted</option>
              </select>
            </div>
            <div>
              <div style={WIZ_LABEL}>PARTICIPANTS <span style={{color:DANGER}}>*</span></div>
              <select value={b.numParticipants||""} onChange={e=>setBatchField(l.lang,"numParticipants",parseInt(e.target.value))} style={WIZ_INPUT}>
                <option value="">Select…</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
              </select>
            </div>
            <div>
              <div style={WIZ_LABEL}>DEVICE <span style={{color:DANGER}}>*</span></div>
              <select value={b.device||""} onChange={e=>setBatchField(l.lang,"device",e.target.value)} style={WIZ_INPUT}>
                <option value="">Select…</option>
                {WIZ_DEVICE_OPTIONS.map(d=><option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: sample rate, rate/min */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div style={WIZ_LABEL}>SAMPLE RATE <span style={{color:TEXT_SUBTLE,fontWeight:400}}>(optional)</span></div>
              <select value={b.sampleRate||""} onChange={e=>setBatchField(l.lang,"sampleRate",e.target.value)} style={WIZ_INPUT}>
                <option value="">Any / not specified</option>
                {WIZ_SAMPLE_RATES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <div style={WIZ_LABEL}>RATE PER MINUTE (USD) <span style={{color:DANGER}}>*</span></div>
              <input type="number" min="0" step="0.01" value={b.ratePerMinute||""} onChange={e=>setBatchField(l.lang,"ratePerMinute",e.target.value)} placeholder="0.20" style={WIZ_INPUT}/>
            </div>
          </div>

          {/* Row 3: duration range */}
          <div style={{marginBottom:10}}>
            <div style={WIZ_LABEL}>DURATION RANGE (MINUTES) <span style={{color:DANGER}}>*</span></div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input type="number" min="1" value={b.durationMin||""} onChange={e=>setBatchField(l.lang,"durationMin",e.target.value)} placeholder="Min" style={{...WIZ_INPUT,flex:1}}/>
              <span style={{color:TEXT_SUBTLE,fontSize:13}}>to</span>
              <input type="number" min="1" value={b.durationMax||""} onChange={e=>setBatchField(l.lang,"durationMax",e.target.value)} placeholder="Max" style={{...WIZ_INPUT,flex:1}}/>
              <span style={{color:TEXT_SUBTLE,fontSize:13,minWidth:56}}>minutes</span>
            </div>
          </div>

          {/* Topics */}
          <MultiTypeahead
            label="TOPICS"
            required
            options={TOPIC_OPTIONS.filter(t=>t!=="Any")}
            selected={b.topics||[]}
            onAdd={t=>setBatchField(l.lang,"topics",[...(b.topics||[]),t])}
            onRemove={t=>setBatchField(l.lang,"topics",(b.topics||[]).filter(x=>x!==t))}
            placeholder="Type to search or add a topic…"
            allowCustom
          />

          {/* Special instructions — project-specific, shown to the recorder */}
          <div style={{marginTop:12}}>
            <div style={WIZ_LABEL}>SPECIAL INSTRUCTIONS <span style={{color:TEXT_SUBTLE,fontWeight:400}}>(optional)</span></div>
            <textarea
              value={b.specialInstructions||""}
              onChange={e=>setBatchField(l.lang,"specialInstructions",e.target.value)}
              rows={3}
              placeholder="Notes the recorder should see when picking up this task — e.g. 'Speak slowly, avoid English loanwords.'"
              style={{...WIZ_INPUT,resize:"vertical",lineHeight:1.5,fontFamily:"inherit"}}
            />
          </div>

          {/* Scripts — only when scripted */}
          {b.scripted==="scripted"&&(
            <div style={{marginTop:12}}>
              <div style={WIZ_LABEL}>SCRIPTS <span style={{color:DANGER}}>*</span></div>
              <div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:8,lineHeight:1.5}}>Upload a file with one script per row. Each script becomes one engagement.</div>
              <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                <label style={{display:"inline-block",padding:"6px 12px",borderRadius:7,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:12,fontWeight:500,cursor:"pointer"}}>
                  Upload .txt / .csv
                  <input type="file" accept=".txt,.csv" style={{display:"none"}} onChange={e=>{
                    const file=e.target.files[0];
                    if(!file)return;
                    const reader=new FileReader();
                    reader.onload=ev=>{
                      const content=ev.target.result;
                      // Split on blank lines (2+ newlines) — common convention for script collections
                      const parts=content.split(/\n\s*\n+/).map(s=>s.trim()).filter(Boolean);
                      setBatchField(l.lang,"scripts",parts);
                      setBatchField(l.lang,"scriptsSource",file.name);
                    };
                    reader.readAsText(file);
                  }}/>
                </label>
                <button onClick={()=>{/* no-op stub for prototype */}} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:12,fontWeight:500,cursor:"pointer"}}>Download script template</button>
                {(b.scripts||[]).length>0&&(
                  <button onClick={()=>{setBatchField(l.lang,"scripts",[]);setBatchField(l.lang,"scriptsSource",null);}} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:12,cursor:"pointer"}}>Clear scripts</button>
                )}
              </div>
              <div style={{fontSize:12,color:(b.scripts||[]).length>0?SUCCESS:TEXT_SUBTLE}}>
                {(b.scripts||[]).length>0
                  ?<><strong>{b.scripts.length}</strong> script{b.scripts.length!==1?"s":""} loaded{b.scriptsSource?` from ${b.scriptsSource}`:""} — will create {b.scripts.length} engagement{b.scripts.length!==1?"s":""}</>
                  :"No scripts loaded yet"}
              </div>
            </div>
          )}
        </div>);
      })}
    </>)}
  </div>);
}

// ── Helper: unique path id ──
let _pathIdCounter=0;
const newPathId=()=>`path${++_pathIdCounter}_${Math.random().toString(36).slice(2,6)}`;

// ── Helper: construct a default path out of a stage ──
const makePath=(destination)=>({
  id:newPathId(),
  langs:[], // empty array === "applies to all languages"
  selection:{type:"all"}, // {type:"all"} | {type:"sample", pct:N} | {type:"remainder"}
  destination,
  worker:{type:"human",assignment:"pool",who:null},
  rates:{},
});

// ── Helper: stages downstream of a given stage (respecting flow's included stages) ──
const downstreamStages=(stageKey,flowStages)=>{
  const order=ORCH_STAGES.map(s=>s.key);
  const idx=order.indexOf(stageKey);
  if(idx===-1)return[];
  return order.slice(idx+1).filter(s=>flowStages.includes(s));
};

// ── Helper: compute per-language coverage of a stage's paths ──
// Returns {[lang]: {fixedPct, remainderCount}}
const coverageByLang=(paths,allLangs)=>{
  const out={};
  allLangs.forEach(l=>{out[l]={fixedPct:0,remainderCount:0};});
  (paths||[]).forEach(p=>{
    const pLangs=(p.langs||[]).length===0?allLangs:p.langs;
    pLangs.forEach(l=>{
      if(!out[l])out[l]={fixedPct:0,remainderCount:0};
      if(p.selection.type==="all")out[l].fixedPct+=100;
      else if(p.selection.type==="sample")out[l].fixedPct+=Number(p.selection.pct)||0;
      else if(p.selection.type==="remainder")out[l].remainderCount+=1;
    });
  });
  return out;
};

// ── Helper: language chip multi-select (All + individual languages) ──
function WizLangChips({options,selected,onChange}){
  const toggle=l=>onChange(selected.includes(l)?selected.filter(x=>x!==l):[...selected,l]);
  return(<div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
    {options.map(l=>{
      const on=selected.includes(l);
      return(<button key={l} onClick={()=>toggle(l)} style={{padding:"3px 9px",borderRadius:999,border:`1px solid ${on?P:BORDER}`,background:on?PL:SURFACE,color:on?P:TEXT_SECONDARY,fontSize:11,fontWeight:on?600:500,cursor:"pointer"}}>{on?"✓ ":""}{l}</button>);
    })}
  </div>);
}

// ── Helper: read-only preview of a legacy (gate+routing) flow ──
// Used when user selects an existing flow from the dropdown. Only "Clone and edit"
// can actually use the flow — inline overlay editing was removed when we
// switched to the DAG/path model.
function WizFlowPreview({flow,onClone}){
  const pairs=buildTransitionPairs(flow.includedStages);
  const statusLabel=statusStyle[flow.status]?.label||flow.status;
  return(<div style={{border:`1px solid ${BORDER_STRONG}`,borderRadius:12,background:SURFACE,overflow:"hidden"}}>
    <div style={{padding:"12px 16px",background:SURFACE_SUNKEN,borderBottom:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div>
        <div style={{fontWeight:600,fontSize:13,color:TEXT_PRIMARY}}>{flow.name}</div>
        <div style={{fontSize:11,color:TEXT_MUTED,marginTop:2}}>{statusLabel} · {flow.includedStages.length} stages · Created {flow.createdDate}</div>
      </div>
      <button onClick={onClone} style={{background:P,border:"none",borderRadius:7,padding:"7px 14px",color:SURFACE,fontSize:12,fontWeight:600,cursor:"pointer"}}>Clone and edit →</button>
    </div>
    <div style={{padding:"12px 16px",display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
        {flow.includedStages.map((sk,i)=>{
          const s=STAGE_BY_KEY[sk];
          return(<div key={sk} style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{padding:"3px 10px",borderRadius:6,background:s.bg,color:s.text,fontSize:11,fontWeight:600}}>{s.label}</span>
            {i<flow.includedStages.length-1&&<span style={{color:TEXT_SUBTLE,fontSize:11}}>→</span>}
          </div>);
        })}
      </div>
      {pairs.map(p=>{
        const cfg=flow.transitions[p.key]||defaultTransitionConfig();
        const g=describeGate(cfg.gate);
        const r=cfg.routing.default;
        const rText=r.type==="pool"?"Open pool":`${r.type.charAt(0).toUpperCase()+r.type.slice(1)}: ${r.who||"—"}`;
        const routingOverrides=(cfg.routing.overrides||[]).length;
        return(<div key={p.key} style={{borderTop:`1px solid ${BORDER_SUBTLE}`,paddingTop:10}}>
          <div style={{fontSize:12,fontWeight:600,color:TEXT_SECONDARY,marginBottom:4}}>
            {STAGE_BY_KEY[p.from].label} → {STAGE_BY_KEY[p.to].label}
          </div>
          <div style={{display:"flex",gap:14,fontSize:11,color:TEXT_MUTED,flexWrap:"wrap"}}>
            <span><strong style={{color:TEXT_SECONDARY}}>Gate:</strong> {g.text}</span>
            <span><strong style={{color:TEXT_SECONDARY}}>Routing:</strong> {rText}{routingOverrides>0?` (+${routingOverrides} override${routingOverrides!==1?"s":""})`:""}</span>
          </div>
        </div>);
      })}
      <div style={{marginTop:4,padding:"8px 12px",background:WARNING_SURFACE,border:`1px solid ${WARNING_BORDER}`,borderRadius:7,fontSize:11,color:WARNING_DEEP,lineHeight:1.5}}>
        Legacy flow shape. Cloning converts each transition to a single 100% path; sampling/gate rules are dropped and must be recreated in the DAG editor.
      </div>
    </div>
  </div>);
}

// ── PATH EDITOR: one path out of a stage ──
// Captures filter (which langs), selection (all/sample/remainder),
// destination (downstream stage), worker (type + assignment + who),
// and per-language rates (for humans).
function WizPathEditor({path,idx,possibleDestinations,allLangs,allPathsInStage,onChange,onDelete,canDelete,onAddRemainder}){
  const update=patch=>onChange({...path,...patch});
  const appliesToAll=(path.langs||[]).length===0;
  const sel=path.selection||{type:"all"};
  const worker=path.worker||{type:"human",assignment:"pool",who:null};
  const rates=path.rates||{};

  const setSel=patch=>update({selection:{...sel,...patch}});
  const setWorker=patch=>update({worker:{...worker,...patch}});
  const setWorkerType=t=>{
    if(t==="human")update({worker:{type:"human",assignment:"pool",who:null}});
    else if(t==="machine")update({worker:{type:"machine",assignment:null,who:ORCH_MACHINES[0]}});
    else if(t==="vendor")update({worker:{type:"vendor",assignment:null,who:ORCH_VENDORS[0]}});
  };

  const destStage=STAGE_BY_KEY[path.destination];
  const effectiveLangs=appliesToAll?allLangs:path.langs;

  return(<div style={{border:`1px solid ${BORDER}`,borderRadius:10,background:SURFACE,overflow:"hidden"}}>
    {/* Header */}
    <div style={{padding:"9px 14px",background:SURFACE_SUNKEN,borderBottom:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div style={{fontSize:12,fontWeight:600,color:TEXT_SECONDARY}}>Path {idx+1}</div>
      <button onClick={onDelete} disabled={!canDelete} style={{background:"none",border:"none",color:canDelete?TEXT_MUTED:TEXT_DISABLED,fontSize:13,cursor:canDelete?"pointer":"default",padding:0}}>Remove</button>
    </div>

    <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:14}}>

      {/* Filter: languages */}
      <div>
        <div style={{...WIZ_LABEL,marginBottom:6}}>APPLIES TO</div>
        <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
          <button onClick={()=>update({langs:[]})} style={{padding:"3px 10px",borderRadius:999,border:`1px solid ${appliesToAll?P:BORDER}`,background:appliesToAll?PL:SURFACE,color:appliesToAll?P:TEXT_SECONDARY,fontSize:11,fontWeight:appliesToAll?600:500,cursor:"pointer"}}>{appliesToAll?"✓ ":""}All languages</button>
          <span style={{color:TEXT_SUBTLE,fontSize:11,margin:"0 3px"}}>or</span>
          {allLangs.map(l=>{
            const on=!appliesToAll&&(path.langs||[]).includes(l);
            return(<button key={l} onClick={()=>{
              const curr=path.langs||[];
              if(curr.includes(l))update({langs:curr.filter(x=>x!==l)});
              else update({langs:[...curr,l]});
            }} style={{padding:"3px 9px",borderRadius:999,border:`1px solid ${on?P:BORDER}`,background:on?PL:SURFACE,color:on?P:TEXT_SECONDARY,fontSize:11,fontWeight:on?600:500,cursor:"pointer"}}>{on?"✓ ":""}{l}</button>);
          })}
        </div>
      </div>

      {/* Selection: how much of the filtered set */}
      <div>
        <div style={{...WIZ_LABEL,marginBottom:6}}>SELECTION</div>
        <div style={{display:"flex",gap:6,marginBottom:6}}>
          {[{v:"all",l:"All (100%)"},{v:"sample",l:"Sample %"},{v:"remainder",l:"Remainder"}].map(o=>(
            <button key={o.v} onClick={()=>setSel({type:o.v,pct:o.v==="sample"?(sel.pct||10):undefined})} style={{flex:1,padding:"7px 10px",borderRadius:7,border:`1px solid ${sel.type===o.v?P:BORDER}`,background:sel.type===o.v?PL:SURFACE,color:sel.type===o.v?P:TEXT_SECONDARY,fontSize:12,fontWeight:sel.type===o.v?600:500,cursor:"pointer"}}>{o.l}</button>
          ))}
        </div>
        {sel.type==="sample"&&(
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:TEXT_SECONDARY}}>Percent:</span>
            <input type="number" min="1" max="100" value={sel.pct||""} onChange={e=>setSel({pct:parseInt(e.target.value)||0})} style={{...WIZ_INPUT,width:80,padding:"5px 8px",fontSize:12}}/>
            <span style={{fontSize:11,color:TEXT_SUBTLE}}>of engagements in the filtered languages</span>
          </div>
        )}
        {sel.type==="remainder"&&(() => {
          // Compute per-language remainder from sibling paths
          const siblings=(allPathsInStage||[]).filter(sp=>sp.id!==path.id);
          const covPerLang={};
          effectiveLangs.forEach(l=>{covPerLang[l]=Math.max(0,100-coverageByLang(siblings,[l])[l].fixedPct);});
          return(
            <div>
              <div style={{fontSize:11,color:TEXT_SUBTLE,fontStyle:"italic",marginBottom:effectiveLangs.length>0?6:0}}>Catches whatever isn't routed by sibling paths in this stage (per language).</div>
              {effectiveLangs.length>0&&(
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {effectiveLangs.map(l=>{
                    const pct=covPerLang[l];
                    const isZero=pct===0;
                    return(<span key={l} style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:999,background:isZero?WARNING_ACCENT_SURFACE:BRAND_LIGHT,color:isZero?WARNING_ACCENT:P}}>{l}: {pct}% remaining{isZero?" (redundant)":""}</span>);
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Destination */}
      <div>
        <div style={{...WIZ_LABEL,marginBottom:6}}>DESTINATION</div>
        {possibleDestinations.length===0?(
          <div style={{fontSize:12,color:DANGER,fontStyle:"italic"}}>No downstream stages available.</div>
        ):(
          <select value={path.destination||""} onChange={e=>update({destination:e.target.value})} style={WIZ_INPUT}>
            <option value="">Choose destination…</option>
            {possibleDestinations.map(d=>{
              const s=STAGE_BY_KEY[d];
              return(<option key={d} value={d}>{s.label}</option>);
            })}
          </select>
        )}
      </div>

      {/* Nested: work happening at the destination */}
      {destStage&&(
        <div style={{border:`1px solid ${BORDER_SUBTLE}`,borderLeft:`3px solid ${destStage.ring}`,borderRadius:8,background:SURFACE_SUNKEN,padding:"12px 14px",display:"flex",flexDirection:"column",gap:14}}>
          <div style={{fontSize:11,fontWeight:700,color:TEXT_SECONDARY,letterSpacing:"0.06em",display:"flex",alignItems:"center",gap:8}}>
            <span>AT</span>
            <span style={{padding:"3px 9px",borderRadius:5,background:destStage.bg,color:destStage.text,fontSize:11,fontWeight:700}}>{destStage.label}</span>
            <span style={{color:TEXT_SUBTLE,fontWeight:500}}>— who does the work and what they're paid</span>
          </div>

          {/* Worker */}
          <div>
            <div style={{...WIZ_LABEL,marginBottom:6}}>WORKER</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:8}}>
              {[{k:"human",l:"Human"},{k:"machine",l:"Machine"},{k:"vendor",l:"Vendor"}].map(t=>(
                <button key={t.k} onClick={()=>setWorkerType(t.k)} style={{padding:"10px 6px",border:`1px solid ${worker.type===t.k?P:BORDER}`,background:worker.type===t.k?PL:SURFACE,color:worker.type===t.k?P:TEXT_SECONDARY,borderRadius:7,fontSize:12,fontWeight:worker.type===t.k?600:500,cursor:"pointer"}}>{t.l}</button>
              ))}
            </div>

            {worker.type==="human"&&(<div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div>
                <div style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:600,letterSpacing:"0.06em",marginBottom:4}}>ASSIGNMENT MODE</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                  {[{k:"pool",l:"Open pool (claimable)"},{k:"specific",l:"Directly assigned"}].map(a=>(
                    <button key={a.k} onClick={()=>setWorker({assignment:a.k,who:null})} style={{padding:"8px 10px",border:`1px solid ${worker.assignment===a.k?P:BORDER}`,background:worker.assignment===a.k?PL:SURFACE,color:worker.assignment===a.k?P:TEXT_SECONDARY,borderRadius:7,fontSize:12,fontWeight:worker.assignment===a.k?600:500,cursor:"pointer"}}>{a.l}</button>
                  ))}
                </div>
              </div>
              {worker.assignment==="specific"&&(
                <div>
                  <div style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:600,letterSpacing:"0.06em",marginBottom:4}}>WHO</div>
                  <select value={worker.who||""} onChange={e=>setWorker({who:e.target.value||null})} style={WIZ_INPUT}>
                    <option value="">Assign later</option>
                    {ORCH_HUMANS.map(h=><option key={h} value={h}>{h}</option>)}
                  </select>
                  {!worker.who&&<div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:4,fontStyle:"italic"}}>Tasks will be created unassigned; an ops manager can assign them later.</div>}
                </div>
              )}
            </div>)}

            {worker.type==="machine"&&(
              <div>
                <div style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:600,letterSpacing:"0.06em",marginBottom:4}}>MODEL</div>
                <select value={worker.who||""} onChange={e=>setWorker({who:e.target.value})} style={WIZ_INPUT}>
                  {ORCH_MACHINES.map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
            )}

            {worker.type==="vendor"&&(
              <div>
                <div style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:600,letterSpacing:"0.06em",marginBottom:4}}>VENDOR</div>
                <select value={worker.who||""} onChange={e=>setWorker({who:e.target.value})} style={WIZ_INPUT}>
                  {ORCH_VENDORS.map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Rates (human only) */}
          {worker.type==="human"&&(<>
            <div>
              <div style={{...WIZ_LABEL,marginBottom:6}}>TASK RATE (USD) <span style={{color:DANGER}}>*</span></div>
              {effectiveLangs.length===0?(
                <div style={{fontSize:12,color:TEXT_MUTED,fontStyle:"italic"}}>Select languages in step 2 first.</div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {effectiveLangs.map(l=>{
                    const langRate=rates[l]||{};
                    const defaultBase=getDefaultBaseRate(path.destination,l);
                    // Distinguish "value is actually 0" from "user hasn't provided one" via undefined
                    const base=langRate.base!==undefined?langRate.base:defaultBase;
                    const baseNum=parseFloat(base)||0;
                    const setField=(field,val)=>update({rates:{...rates,[l]:{...langRate,[field]:val}}});

                    return(<div key={l} style={{border:`1px solid ${BORDER}`,borderRadius:8,padding:"10px 12px",background:SURFACE}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <span style={{fontSize:12,fontWeight:600,color:TEXT_PRIMARY}}>{l}</span>
                        <span style={{fontSize:11,color:TEXT_MUTED}}>Rate: <strong style={{color:TEXT_PRIMARY}}>${baseNum.toFixed(2)}</strong></span>
                      </div>
                      <div>
                        <div style={{fontSize:10,color:TEXT_SUBTLE,fontWeight:600,letterSpacing:"0.05em",marginBottom:3}}>BASE RATE</div>
                        <input type="number" min="0" step="0.01" value={base} onChange={e=>setField("base",e.target.value)} style={{...WIZ_INPUT,padding:"5px 8px",fontSize:12}}/>
                      </div>
                    </div>);
                  })}
                </div>
              )}
            </div>

            {/* Special instructions — shown to the human doing this task */}
            <div>
              <div style={{...WIZ_LABEL,marginBottom:6}}>SPECIAL INSTRUCTIONS <span style={{color:TEXT_SUBTLE,fontWeight:400}}>(optional)</span></div>
              <textarea
                value={path.specialInstructions||""}
                onChange={e=>update({specialInstructions:e.target.value})}
                rows={3}
                placeholder="Notes the reviewer should see when working a task at this stage — e.g. 'Flag any medical jargon mistranscribed as common words.'"
                style={{...WIZ_INPUT,resize:"vertical",lineHeight:1.5,fontFamily:"inherit",fontSize:12}}
              />
            </div>
          </>)}
        </div>
      )}

      {/* Inline add-remainder */}
      {onAddRemainder&&sel.type!=="remainder"&&(() => {
        // Only offer when this path leaves a gap for some language in its filter
        const siblings=(allPathsInStage||[]).filter(sp=>sp.id!==path.id);
        const fillableLangs=effectiveLangs.filter(l=>{
          const covered=coverageByLang(siblings,[l])[l].fixedPct+(path.selection.type==="all"?100:path.selection.type==="sample"?(path.selection.pct||0):0);
          return covered<100;
        });
        // Also check that no sibling remainder already covers those langs
        const alreadyHasRemainder=siblings.some(sp=>{
          if(sp.selection.type!=="remainder")return false;
          const sLangs=(sp.langs||[]).length===0?allLangs:sp.langs;
          return fillableLangs.some(l=>sLangs.includes(l));
        });
        if(fillableLangs.length===0||alreadyHasRemainder)return null;
        return(<div style={{paddingTop:4}}>
          <button onClick={()=>onAddRemainder(path)} style={{background:"none",border:`1px dashed ${P}`,borderRadius:7,padding:"7px 12px",color:P,fontSize:12,fontWeight:500,cursor:"pointer",width:"100%"}}>
            + Add remainder path for {effectiveLangs.length===allLangs.length?"these languages":effectiveLangs.join(", ")}
          </button>
        </div>);
      })()}

    </div>
  </div>);
}

// ── Helper: describe coverage issues for a single language ──
// Returns array of {type, detail, severity, canAutoFix}. Empty array = fully covered.
const coverageIssuesForLang=c=>{
  const issues=[];
  if(c.fixedPct>100)issues.push({type:"over",detail:`${c.fixedPct}% over-routed — reduce sample %s below`,severity:"error"});
  if(c.remainderCount>1)issues.push({type:"dupRemainder",detail:`${c.remainderCount} remainder paths — only one is allowed per language`,severity:"error"});
  if(c.fixedPct===100&&c.remainderCount>=1)issues.push({type:"redundantRemainder",detail:"remainder path is redundant (already 100% covered)",severity:"warn"});
  if(c.fixedPct<100&&c.remainderCount===0)issues.push({type:"gap",detail:`${100-c.fixedPct}% unrouted`,severity:"error",canAutoFix:true});
  return issues;
};

// ── Helper: is a whole stage's coverage valid? ──
const isStageCoverageValid=(paths,allLangs)=>{
  if(allLangs.length===0)return (paths||[]).length>0;
  const cov=coverageByLang(paths||[],allLangs);
  return allLangs.every(l=>coverageIssuesForLang(cov[l]).filter(i=>i.severity==="error").length===0);
};

// ── STAGE EXIT EDITOR: all paths out of one stage ──
// Renders inside a tab — doesn't draw its own stage header chip.
function WizStageExitEditor({stageKey,paths,includedStages,allLangs,onPathsChange}){
  const possibleDests=downstreamStages(stageKey,includedStages);
  const coverage=coverageByLang(paths||[],allLangs);

  const addPath=()=>{
    const defaultDest=possibleDests[0]||"complete";
    onPathsChange([...(paths||[]),makePath(defaultDest)]);
  };
  // Add a remainder path covering a specific language.
  // Destination and worker default to the first sibling path's values for
  // that language — common case is "same stage, different worker."
  const addRemainderForLang=lang=>{
    const siblings=(paths||[]).filter(p=>{
      const pLangs=p.langs||[];
      return pLangs.length===0||pLangs.includes(lang);
    });
    const first=siblings[0];
    const dest=first?.destination||possibleDests[0]||"complete";
    const newPath={
      ...makePath(dest),
      langs:[lang],
      selection:{type:"remainder"},
    };
    onPathsChange([...(paths||[]),newPath]);
  };
  // Inline add-remainder: creates a sibling with the source path's language filter
  // (or all-languages if that's what the source is). Destination and worker inherit
  // from the source path so the common "80% same stage, different worker" pattern
  // is a one-click setup.
  const addRemainderFromPath=sourcePath=>{
    const newPath={
      ...makePath(sourcePath.destination||possibleDests[0]||"complete"),
      langs:[...(sourcePath.langs||[])],
      selection:{type:"remainder"},
      worker:JSON.parse(JSON.stringify(sourcePath.worker||{type:"human",assignment:"pool",who:null})),
    };
    onPathsChange([...(paths||[]),newPath]);
  };
  const updatePath=(idx,newPath)=>{
    const next=[...paths];
    next[idx]=newPath;
    onPathsChange(next);
  };
  const deletePath=idx=>onPathsChange(paths.filter((_,i)=>i!==idx));

  // Coverage panel: per-language rows
  const anyIssues=allLangs.some(l=>coverageIssuesForLang(coverage[l]).length>0);

  return(<div style={{display:"flex",flexDirection:"column",gap:14}}>

    {/* Coverage panel */}
    {allLangs.length>0&&(
      <div style={{border:`1px solid ${anyIssues?WARNING_BORDER:SUCCESS_LIGHT}`,borderRadius:10,background:anyIssues?WARNING_SURFACE:SUCCESS_SURFACE,padding:"12px 14px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:anyIssues||allLangs.length>0?10:0}}>
          <div style={{fontSize:11,fontWeight:700,color:anyIssues?WARNING_DEEP:SUCCESS,letterSpacing:"0.08em"}}>
            {anyIssues?"COVERAGE ISSUES":"COVERAGE COMPLETE"}
          </div>
          <button onClick={addPath} style={{background:SURFACE,border:`1px solid ${P}`,borderRadius:7,padding:"5px 12px",color:P,fontSize:11,fontWeight:600,cursor:"pointer"}}>+ Add path</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {allLangs.map(l=>{
            const c=coverage[l];
            const issues=coverageIssuesForLang(c);
            const hasError=issues.some(i=>i.severity==="error");
            const ok=issues.length===0;
            const fixable=issues.find(i=>i.canAutoFix);
            return(<div key={l} style={{display:"flex",alignItems:"center",gap:10,fontSize:12}}>
              <span style={{minWidth:80,fontWeight:600,color:TEXT_PRIMARY}}>{l}</span>
              <span style={{flex:1,color:ok?SUCCESS:(hasError?DANGER:WARNING_DEEP)}}>
                {ok?`${c.fixedPct}% routed${c.remainderCount===1?" (+ remainder path)":""}`:issues.map(i=>i.detail).join(" · ")}
              </span>
              {fixable&&(
                <button onClick={()=>addRemainderForLang(l)} style={{background:SURFACE,border:`1px solid ${P}`,borderRadius:6,padding:"3px 9px",color:P,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>+ Remainder path</button>
              )}
            </div>);
          })}
        </div>
      </div>
    )}

    {/* Path cards */}
    {(paths||[]).length===0?(
      <div style={{padding:"18px",background:SURFACE_SUNKEN,border:`1px dashed ${BORDER_STRONG}`,borderRadius:10,textAlign:"center",fontSize:12,color:TEXT_MUTED}}>
        No paths yet. Click <strong>+ Add path</strong> to route engagements out of this stage.
      </div>
    ):(paths.map((p,i)=>(
      <WizPathEditor
        key={p.id}
        path={p}
        idx={i}
        possibleDestinations={possibleDests}
        allLangs={allLangs}
        allPathsInStage={paths}
        onChange={np=>updatePath(i,np)}
        onDelete={()=>deletePath(i)}
        canDelete={paths.length>1}
        onAddRemainder={addRemainderFromPath}
      />
    )))}

  </div>);
}

// ── Step 4: Flow ────────────────────────────────────────────────────────────
function WizStepFlow({data,onChange,existingFlows}){
  const mode=data.flowMode;
  const setMode=m=>onChange({flowMode:m});
  const flowCfg=data.newFlow;
  const setFlowCfg=patch=>onChange({newFlow:{...flowCfg,...patch}});
  const allLangs=data.languages.map(l=>l.lang);

  const ALL_STAGES=ORCH_STAGES;
  const stageOrder=ALL_STAGES.map(s=>s.key);
  const toggleable=new Set(["audioCreation","audioReview","transcription","transcriptionReview"]);

  // Active stage tab within step 4's new-mode view — lets the user focus on
  // one stage's exit paths at a time. Defaults to the first exit stage.
  const[activeExitStage,setActiveExitStage]=useState(null);

  // Keep audioCreation in sync with step 3's audioExternal toggle
  useEffect(()=>{
    const shouldInclude=!data.audioExternal;
    const hasAC=flowCfg.includedStages.includes("audioCreation");
    if(shouldInclude&&!hasAC){
      const next=[...flowCfg.includedStages,"audioCreation"].sort((a,b)=>stageOrder.indexOf(a)-stageOrder.indexOf(b));
      setFlowCfg({includedStages:next});
    }
    if(!shouldInclude&&hasAC){
      setFlowCfg({includedStages:flowCfg.includedStages.filter(k=>k!=="audioCreation")});
    }
  },[data.audioExternal]); // eslint-disable-line

  // When includedStages changes, prune exit entries whose stage isn't in the flow,
  // and seed default exits for newly-included non-terminal stages.
  useEffect(()=>{
    const next={...flowCfg.exits};
    let changed=false;
    // Remove stale keys
    Object.keys(next).forEach(k=>{
      if(!flowCfg.includedStages.includes(k)||k==="complete"){
        delete next[k];
        changed=true;
      }
    });
    // Seed new stages
    flowCfg.includedStages.forEach(k=>{
      if(k==="complete")return;
      if(!next[k]){
        const dests=downstreamStages(k,flowCfg.includedStages);
        const defaultDest=dests[0]||"complete";
        next[k]=[makePath(defaultDest)];
        changed=true;
      }
    });
    if(changed)setFlowCfg({exits:next});
  },[flowCfg.includedStages.join("|")]); // eslint-disable-line

  // Keep activeExitStage valid as stages toggle on/off
  useEffect(()=>{
    const validStages=flowCfg.includedStages.filter(k=>k!=="complete");
    if(validStages.length===0){
      if(activeExitStage!==null)setActiveExitStage(null);
    }else if(!validStages.includes(activeExitStage)){
      setActiveExitStage(validStages[0]);
    }
  },[flowCfg.includedStages.join("|")]); // eslint-disable-line

  const toggleStage=key=>{
    if(!toggleable.has(key))return;
    if(key==="audioCreation"&&data.audioExternal)return;
    const has=flowCfg.includedStages.includes(key);
    const next=has?flowCfg.includedStages.filter(k=>k!==key):[...flowCfg.includedStages,key].sort((a,b)=>stageOrder.indexOf(a)-stageOrder.indexOf(b));
    setFlowCfg({includedStages:next});
  };

  const availableFlows=existingFlows.filter(f=>f.status!=="paused");
  const selectedFlow=existingFlows.find(f=>f.id===data.existingFlowId);

  // Clone an existing legacy flow → new path-based model.
  // Each adjacent transition becomes one "all (100%)" path using the
  // old routing's default worker. Gate/sampling info is dropped.
  const cloneSelected=()=>{
    if(!selectedFlow)return;
    const pairs=buildTransitionPairs(selectedFlow.includedStages);
    const exits={};
    pairs.forEach(p=>{
      const cfg=selectedFlow.transitions[p.key]||defaultTransitionConfig();
      const r=cfg.routing.default;
      const workerType=r.type==="pool"?"human":r.type; // pool is a human assignment mode, not a worker type
      const worker=workerType==="human"
        ?{type:"human",assignment:r.type==="pool"?"pool":"specific",who:r.type==="pool"?null:r.who}
        :{type:workerType,assignment:null,who:r.who};
      const path={
        id:newPathId(),
        langs:[],
        selection:{type:"all"},
        destination:p.to,
        worker,
        rates:{},
      };
      if(!exits[p.from])exits[p.from]=[];
      exits[p.from].push(path);
    });
    setFlowCfg({
      name:`Copy of ${selectedFlow.name}`,
      includedStages:[...selectedFlow.includedStages],
      exits,
      clonedFromExisting:true,
    });
    onChange({flowMode:"new"});
  };

  const exitStages=flowCfg.includedStages.filter(k=>k!=="complete");

  return(<div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:820}}>
    <p style={{fontSize:13,color:TEXT_MUTED,margin:0,lineHeight:1.5}}>
      Build the pipeline as a DAG: each stage routes engagements out via one or more <strong>paths</strong>, where each path says who handles which subset of files and where they go next.
    </p>

    {/* Mode selector */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
      {[
        {v:"existing",t:"Use existing flow",s:"Browse saved flows. Only Clone & edit is supported."},
        {v:"new",t:"Create new flow",s:"Build from scratch using paths."},
        {v:"skip",t:"Skip for now",s:"Save as Draft. Add a flow later."},
      ].map(o=>(
        <button key={o.v} onClick={()=>setMode(o.v)} style={{padding:"12px 14px",borderRadius:10,border:`1px solid ${mode===o.v?P:BORDER}`,background:mode===o.v?PL:SURFACE,textAlign:"left",cursor:"pointer"}}>
          <div style={{fontSize:13,fontWeight:600,color:mode===o.v?P:TEXT_PRIMARY,marginBottom:3}}>{o.t}</div>
          <div style={{fontSize:11,color:TEXT_MUTED,lineHeight:1.4}}>{o.s}</div>
        </button>
      ))}
    </div>

    {/* EXISTING */}
    {mode==="existing"&&(<>
      <div>
        <div style={WIZ_LABEL}>SELECT FLOW <span style={{color:DANGER}}>*</span></div>
        <select value={data.existingFlowId} onChange={e=>onChange({existingFlowId:e.target.value})} style={WIZ_INPUT}>
          <option value="">Choose a flow…</option>
          {availableFlows.map(f=><option key={f.id} value={f.id}>{f.name} — {f.includedStages.length} stages, {statusStyle[f.status]?.label||f.status}</option>)}
        </select>
        {availableFlows.length===0&&<div style={{fontSize:12,color:TEXT_MUTED,marginTop:6}}>No existing flows available. Switch to Create new.</div>}
      </div>
      {selectedFlow&&<WizFlowPreview flow={selectedFlow} onClone={cloneSelected}/>}
      <div style={{padding:"10px 14px",background:INFO_SURFACE,border:`1px solid ${INFO_BORDER}`,borderRadius:8,fontSize:12,color:INFO_DEEP,lineHeight:1.5}}>
        Existing flows use a legacy gate+routing shape. To attach one here, click <strong>Clone and edit</strong> — the flow will be copied into the new path model and you can tune it before launch.
      </div>
    </>)}

    {/* NEW */}
    {mode==="new"&&(<>
      <div>
        <div style={WIZ_LABEL}>FLOW NAME <span style={{color:DANGER}}>*</span></div>
        <input value={flowCfg.name} onChange={e=>setFlowCfg({name:e.target.value})} placeholder="e.g. Standard Pipeline v2" style={WIZ_INPUT}/>
        {flowCfg.clonedFromExisting&&<div style={{fontSize:11,color:TEXT_MUTED,marginTop:4,fontStyle:"italic"}}>Cloned from existing flow — sampling and gate rules were dropped and must be recreated using paths below.</div>}
      </div>

      <div>
        <div style={WIZ_LABEL}>INCLUDED STAGES</div>
        <div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:8}}>Complete is always the terminal stage. {data.audioExternal&&<span>Audio Creation is locked off (step 3 marks audio as externally sourced).</span>}</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {ALL_STAGES.map(s=>{
            const on=flowCfg.includedStages.includes(s.key);
            const locked=!toggleable.has(s.key)||(s.key==="audioCreation"&&data.audioExternal);
            if(locked){
              return(<span key={s.key} style={{padding:"6px 12px",borderRadius:8,background:on?SURFACE:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,fontSize:12,color:on?TEXT_SECONDARY:TEXT_SUBTLE,fontWeight:500,opacity:on?1:0.6}}>{on?"🔒 ":""}{s.label}{!on?" (off)":""}</span>);
            }
            return(<button key={s.key} onClick={()=>toggleStage(s.key)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${on?P:BORDER}`,background:on?PL:SURFACE,color:on?P:TEXT_SECONDARY,fontSize:12,fontWeight:on?600:500,cursor:"pointer"}}>{on?"✓ ":""}{s.label}</button>);
          })}
        </div>
      </div>

      {/* Per-stage exit editors — tabbed */}
      {exitStages.length>0&&(<>
        <div>
          <div style={{...WIZ_LABEL,marginBottom:8}}>STAGE EXITS <span style={{color:TEXT_SUBTLE,fontWeight:400}}>— click a tab to focus on one stage's paths</span></div>

          {/* Tab strip */}
          <div style={{display:"flex",gap:4,borderBottom:`1px solid ${BORDER}`,marginBottom:16,overflowX:"auto"}}>
            {exitStages.map(k=>{
              const s=STAGE_BY_KEY[k];
              const paths=flowCfg.exits[k]||[];
              const cov=coverageByLang(paths,allLangs);
              const hasError=allLangs.some(l=>coverageIssuesForLang(cov[l]).some(i=>i.severity==="error"));
              const hasWarn=allLangs.some(l=>coverageIssuesForLang(cov[l]).some(i=>i.severity==="warn"));
              const ok=!hasError&&!hasWarn&&allLangs.length>0;
              const icon=hasError?"":(hasWarn?"!":(ok?"✓":""));
              const iconColor=hasError?DANGER:(hasWarn?WARNING_ACCENT:(ok?SUCCESS:TEXT_SUBTLE));
              const active=activeExitStage===k;
              return(<button key={k} onClick={()=>setActiveExitStage(k)} style={{
                padding:"10px 16px",
                border:"none",
                borderBottom:`3px solid ${active?P:"transparent"}`,
                marginBottom:-1,
                background:"transparent",
                color:active?P:TEXT_SECONDARY,
                fontSize:12,
                fontWeight:active?600:500,
                cursor:"pointer",
                display:"flex",
                alignItems:"center",
                gap:6,
                whiteSpace:"nowrap",
              }}>
                <span style={{padding:"2px 7px",borderRadius:4,background:s.bg,color:s.text,fontSize:10,fontWeight:700}}>{s.label}</span>
                <span style={{color:iconColor,fontSize:12,fontWeight:700}}>{icon}</span>
              </button>);
            })}
          </div>

          {/* Active stage content */}
          {activeExitStage&&flowCfg.includedStages.includes(activeExitStage)&&(
            <WizStageExitEditor
              stageKey={activeExitStage}
              paths={flowCfg.exits[activeExitStage]||[]}
              includedStages={flowCfg.includedStages}
              allLangs={allLangs}
              onPathsChange={newPaths=>setFlowCfg({exits:{...flowCfg.exits,[activeExitStage]:newPaths}})}
            />
          )}

          {/* Prev/next stage nav */}
          {exitStages.length>1&&activeExitStage&&(
            <div style={{display:"flex",justifyContent:"space-between",marginTop:16,paddingTop:12,borderTop:`1px solid ${BORDER_SUBTLE}`}}>
              {(() => {
                const idx=exitStages.indexOf(activeExitStage);
                const prev=idx>0?exitStages[idx-1]:null;
                const next=idx<exitStages.length-1?exitStages[idx+1]:null;
                return(<>
                  <button onClick={()=>prev&&setActiveExitStage(prev)} disabled={!prev} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:7,padding:"6px 12px",color:prev?TEXT_SECONDARY:TEXT_DISABLED,fontSize:12,cursor:prev?"pointer":"default"}}>{prev?`← ${STAGE_BY_KEY[prev].label}`:"← Previous stage"}</button>
                  <button onClick={()=>next&&setActiveExitStage(next)} disabled={!next} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:7,padding:"6px 12px",color:next?TEXT_SECONDARY:TEXT_DISABLED,fontSize:12,cursor:next?"pointer":"default"}}>{next?`${STAGE_BY_KEY[next].label} →`:"Next stage →"}</button>
                </>);
              })()}
            </div>
          )}
        </div>
      </>)}
    </>)}

    {/* SKIP */}
    {mode==="skip"&&(
      <div style={{padding:"14px 16px",background:WARNING_SURFACE,border:`1px solid ${WARNING_BORDER}`,borderRadius:10,fontSize:13,color:WARNING_DEEP,lineHeight:1.5}}>
        Project will be saved as <strong>Draft</strong>. No batches will be generated until a flow is attached.
      </div>
    )}
  </div>);
}

// ── DAG View: visual summary of the flow ─────────────────────────────────
// Horizontal row of stage boxes with curved SVG arrows between them for
// each path. Arrows are numbered and a legend below explains each one.
// Handles skips (paths whose destination isn't the next adjacent stage)
// via lane assignment so arrows don't overlap.
function WizDagView({includedStages,exits,allLangs}){
  const[expanded,setExpanded]=useState(false);
  const stages=includedStages;
  const N=stages.length;
  if(N===0)return null;

  // Collect paths across all stages (except Complete, which has no exit)
  const pathList=[];
  stages.forEach(stageKey=>{
    if(stageKey==="complete")return;
    const srcIdx=stages.indexOf(stageKey);
    const paths=exits[stageKey]||[];
    paths.forEach(p=>{
      const destIdx=stages.indexOf(p.destination);
      if(destIdx===-1)return; // skip paths whose destination isn't in the flow
      pathList.push({path:p,srcIdx,destIdx,stageKey});
    });
  });

  // Lane assignment — paths that overlap horizontally need distinct lanes
  const laneBuckets=[];
  pathList.forEach(pp=>{
    for(let l=0;;l++){
      if(!laneBuckets[l])laneBuckets[l]=[];
      const conflict=laneBuckets[l].some(other=>pp.srcIdx<other.destIdx&&other.srcIdx<pp.destIdx);
      if(!conflict){
        laneBuckets[l].push(pp);
        pp.lane=l;
        break;
      }
    }
  });
  const maxLane=pathList.length===0?0:Math.max(...pathList.map(p=>p.lane));

  // Layout constants
  const boxW=115,boxH=54,hGap=55,laneH=30,laneOffset=32,topPad=6;
  const svgW=N*boxW+(N-1)*hGap;
  const svgH=topPad+boxH+laneOffset+(maxLane+1)*laneH+20;

  const describePath=pp=>{
    const p=pp.path;
    const langs=(p.langs||[]).length===0?"All langs":p.langs.join(", ");
    let sel;
    if(p.selection.type==="all")sel="100%";
    else if(p.selection.type==="sample")sel=`${p.selection.pct||0}%`;
    else sel="remainder";
    let worker;
    if(p.worker.type==="human")worker=`Human · ${p.worker.assignment==="pool"?"pool":`assigned: ${p.worker.who||"later"}`}`;
    else if(p.worker.type==="machine")worker=`Machine: ${p.worker.who||"—"}`;
    else worker=`Vendor: ${p.worker.who||"—"}`;
    return{langs,sel,worker,from:STAGE_BY_KEY[pp.stageKey]?.label,to:STAGE_BY_KEY[p.destination]?.label};
  };

  // Core diagram content — reused inline and in fullscreen
  const diagramContent=(
    <>
      <div style={{overflowX:"auto",paddingBottom:4}}>
        <svg width={svgW} height={svgH} style={{display:"block",margin:expanded?"0 auto":undefined}}>
          <defs>
            <marker id="wizDagArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={P}/>
            </marker>
          </defs>

          {/* Stages */}
          {stages.map((key,i)=>{
            const s=STAGE_BY_KEY[key];
            const x=i*(boxW+hGap);
            return(<g key={key}>
              <rect x={x} y={topPad} width={boxW} height={boxH} fill={s.bg} stroke={s.ring} strokeWidth={1.5} rx={8}/>
              <text x={x+boxW/2} y={topPad+boxH/2+4} textAnchor="middle" fontSize={11} fontWeight={700} fill={s.text}>{s.label}</text>
            </g>);
          })}

          {/* Paths */}
          {pathList.map((pp,i)=>{
            const srcX=pp.srcIdx*(boxW+hGap)+boxW/2;
            const destX=pp.destIdx*(boxW+hGap)+boxW/2;
            const srcY=topPad+boxH;
            const laneY=topPad+boxH+laneOffset+pp.lane*laneH;
            const midX=(srcX+destX)/2;
            const d=`M ${srcX} ${srcY} C ${srcX} ${laneY}, ${destX} ${laneY}, ${destX} ${srcY}`;
            const isRemainder=pp.path.selection.type==="remainder";
            return(<g key={i}>
              <path d={d} fill="none" stroke={P} strokeWidth={1.5} strokeDasharray={isRemainder?"4 3":"none"} markerEnd="url(#wizDagArrow)"/>
              <circle cx={midX} cy={laneY} r={11} fill={SURFACE} stroke={P} strokeWidth={1.5}/>
              <text x={midX} y={laneY+4} textAnchor="middle" fontSize={10} fontWeight={700} fill={P}>{i+1}</text>
            </g>);
          })}
        </svg>
      </div>

      {/* Legend */}
      {pathList.length>0&&(
        <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:7,paddingTop:12,borderTop:`1px solid ${BORDER_SUBTLE}`}}>
          {pathList.map((pp,i)=>{
            const f=describePath(pp);
            const destLabel=STAGE_BY_KEY[pp.path.destination]?.label||"—";
            const isSkip=pp.destIdx-pp.srcIdx>1;
            return(<div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,fontSize:12}}>
              <span style={{minWidth:22,height:22,borderRadius:"50%",background:P,color:SURFACE,fontSize:11,fontWeight:700,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</span>
              <div style={{flex:1,color:TEXT_SECONDARY,lineHeight:1.5}}>
                <span style={{color:TEXT_PRIMARY,fontWeight:600}}>{f.from}</span>
                <span style={{color:TEXT_SUBTLE,margin:"0 5px"}}>→</span>
                <span style={{color:TEXT_PRIMARY,fontWeight:600}}>{destLabel}</span>
                {isSkip&&<span style={{marginLeft:6,fontSize:10,fontWeight:600,color:WARNING_ACCENT,background:WARNING_ACCENT_SURFACE,padding:"1px 6px",borderRadius:4}}>SKIP</span>}
                <span style={{color:TEXT_MUTED,marginLeft:6}}>· {f.langs} ({f.sel}) · {f.worker}</span>
              </div>
            </div>);
          })}
        </div>
      )}

      {pathList.length===0&&(
        <div style={{padding:"12px",fontSize:12,color:TEXT_MUTED,fontStyle:"italic",textAlign:"center"}}>No paths configured yet.</div>
      )}
    </>
  );

  return(<>
    {/* Inline card */}
    <div style={{border:`1px solid ${BORDER}`,borderRadius:12,background:SURFACE,padding:"16px 18px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:P,letterSpacing:"0.08em"}}>FLOW DIAGRAM</div>
        <button onClick={()=>setExpanded(true)} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:7,padding:"5px 12px",color:TEXT_SECONDARY,fontSize:11,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
          Expand
        </button>
      </div>
      {diagramContent}
    </div>

    {/* Fullscreen overlay */}
    {expanded&&(
      <div onClick={()=>setExpanded(false)} style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:24}}>
        <div onClick={e=>e.stopPropagation()} style={{background:SURFACE,borderRadius:14,width:"96%",maxWidth:1600,height:"92vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{padding:"16px 24px",borderBottom:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:P,letterSpacing:"0.1em",marginBottom:3}}>FLOW DIAGRAM</div>
              <div style={{fontSize:15,fontWeight:600,color:TEXT_PRIMARY}}>{stages.length} stages · {pathList.length} path{pathList.length!==1?"s":""}</div>
            </div>
            <button onClick={()=>setExpanded(false)} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:14,color:TEXT_MUTED}}>✕</button>
          </div>
          <div style={{flex:1,overflow:"auto",padding:"28px 32px"}}>
            {diagramContent}
          </div>
        </div>
      </div>
    )}
  </>);
}

// ── Step 5: Review & Launch ─────────────────────────────────────────────────
function WizStepReview({data,existingFlows,onJump}){
  const totalHours=data.languages.reduce((s,l)=>s+(parseFloat(l.hours)||0),0);
  const firstBatch=data.languages[0]?data.perLangRecording[data.languages[0].lang]:null;
  const avgDuration=firstBatch?((parseFloat(firstBatch.durationMin)||0)+(parseFloat(firstBatch.durationMax)||0))/2:0;
  const estEngagements=(!data.audioExternal&&avgDuration>0)?Math.ceil(totalHours*60/avgDuration):(data.audioExternal?"TBD":0);

  // Stages in flow (for batch count)
  let stagesInFlow=[];
  if(data.flowMode==="existing"){
    const f=existingFlows.find(x=>x.id===data.existingFlowId);
    if(f)stagesInFlow=f.includedStages;
  }else if(data.flowMode==="new"){
    stagesInFlow=data.newFlow.includedStages;
  }
  const batchStages=stagesInFlow.filter(k=>k!=="complete");
  const batchCount=batchStages.length*data.languages.length;

  const section=(title,stepIdx,children)=>(
    <div style={{border:`1px solid ${BORDER}`,borderRadius:10,padding:"14px 16px",background:SURFACE}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontWeight:600,fontSize:13,color:TEXT_PRIMARY}}>{title}</div>
        <button onClick={()=>onJump(stepIdx)} style={{background:"none",border:"none",color:P,fontSize:12,fontWeight:500,cursor:"pointer",padding:0}}>Edit</button>
      </div>
      {children}
    </div>
  );
  const kv=(k,v)=>(<div style={{display:"flex",fontSize:12,padding:"3px 0"}}><span style={{color:TEXT_MUTED,minWidth:160}}>{k}</span><span style={{color:TEXT_PRIMARY}}>{v||<em style={{color:TEXT_SUBTLE}}>—</em>}</span></div>);

  const flowLabel=data.flowMode==="existing"?(existingFlows.find(f=>f.id===data.existingFlowId)?.name||"Not selected"):data.flowMode==="new"?(data.newFlow.name||"New flow (unnamed)"):"None (save as draft)";

  // Describe a path compactly
  const describePath=p=>{
    const langs=(p.langs||[]).length===0?"all langs":p.langs.join(", ");
    let sel;
    if(p.selection.type==="all")sel="100%";
    else if(p.selection.type==="sample")sel=`${p.selection.pct||0}% sample`;
    else sel="remainder";
    const destLabel=STAGE_BY_KEY[p.destination]?.label||"—";
    let workerText;
    if(p.worker.type==="human")workerText=`Human (${p.worker.assignment==="pool"?"pool":`assigned: ${p.worker.who||"later"}`})`;
    else if(p.worker.type==="machine")workerText=`Machine: ${p.worker.who||"—"}`;
    else workerText=`Vendor: ${p.worker.who||"—"}`;
    return`${langs} · ${sel} → ${destLabel} · ${workerText}`;
  };

  return(<div style={{display:"flex",flexDirection:"column",gap:14,maxWidth:820}}>
    <div style={{border:`1px solid ${BRAND_SOFT}`,borderRadius:12,padding:"16px 18px",background:PL}}>
      <div style={{fontSize:11,fontWeight:700,color:P,letterSpacing:"0.08em",marginBottom:8}}>WILL BE CREATED</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:TEXT_PRIMARY}}>{estEngagements||"—"}</div>
          <div style={{fontSize:11,color:TEXT_MUTED}}>Est. engagements</div>
        </div>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:TEXT_PRIMARY}}>{batchCount||"—"}</div>
          <div style={{fontSize:11,color:TEXT_MUTED}}>Auto-generated batches</div>
        </div>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:TEXT_PRIMARY}}>{totalHours}h</div>
          <div style={{fontSize:11,color:TEXT_MUTED}}>Total recording goal</div>
        </div>
      </div>
    </div>

    {section("Basics",1,<>
      {kv("Name",data.name)}
      {kv("Customer",data.customer)}
      {kv("Deadline",data.deadline)}
      {kv("Description",data.description)}
    </>)}

    {section("Languages & Hours",2,
      data.languages.length===0?<div style={{fontSize:12,color:TEXT_SUBTLE,fontStyle:"italic"}}>No languages added</div>:
      <div style={{display:"flex",flexDirection:"column",gap:4}}>
        {data.languages.map(l=>(<div key={l.lang} style={{display:"flex",fontSize:12,padding:"2px 0"}}>
          <span style={{color:TEXT_PRIMARY,minWidth:120,fontWeight:500}}>{l.lang}</span>
          <span style={{color:TEXT_MUTED}}>{l.hours}h · max {l.maxPerContributor||"∞"}/contributor{l.combinationMax?` · ${l.combinationMax}h combo cap`:""}</span>
        </div>))}
      </div>
    )}

    {section("Recording Batches",3,
      data.audioExternal?<div style={{fontSize:12,color:TEXT_MUTED,fontStyle:"italic"}}>Audio sourced externally — no recording batches.</div>:
      data.languages.length===0?<div style={{fontSize:12,color:TEXT_SUBTLE,fontStyle:"italic"}}>No languages to configure</div>:
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {data.languages.map(l=>{
          const b=data.perLangRecording[l.lang]||{};
          const parts=[];
          if(b.scripted)parts.push(b.scripted.charAt(0).toUpperCase()+b.scripted.slice(1));
          if(b.numParticipants)parts.push(`${b.numParticipants}p`);
          if(b.durationMin&&b.durationMax)parts.push(`${b.durationMin}–${b.durationMax}min`);
          if(b.ratePerMinute)parts.push(`$${b.ratePerMinute}/min`);
          if(b.scripted==="scripted"){
            const n=(b.scripts||[]).length;
            parts.push(`${n} script${n!==1?"s":""}`);
          }
          return(<div key={l.lang} style={{display:"flex",fontSize:12,padding:"2px 0"}}>
            <span style={{color:TEXT_PRIMARY,minWidth:120,fontWeight:500}}>{l.lang}</span>
            <span style={{color:TEXT_MUTED}}>{parts.join(" · ")||<em>incomplete</em>}</span>
          </div>);
        })}
      </div>
    )}

    {section("Flow",4,<>
      {kv("Flow",flowLabel)}
      {stagesInFlow.length>0&&kv("Stages",stagesInFlow.map(k=>STAGE_BY_KEY[k]?.label).join(" → "))}
      {data.flowMode==="new"&&stagesInFlow.length>0&&(
        <div style={{marginTop:12}}>
          <WizDagView includedStages={data.newFlow.includedStages} exits={data.newFlow.exits||{}} allLangs={data.languages.map(l=>l.lang)}/>
        </div>
      )}
    </>)}
  </div>);
}

// ── Wizard Parent ───────────────────────────────────────────────────────────
function NewProjectWizard({onClose,onCreate,existingFlows}){
  const[step,setStep]=useState(1);
  const[data,setData]=useState({
    // Step 1
    name:"",customer:"",description:"",deadline:"",
    // Step 2
    languages:[],
    // Step 3
    audioExternal:false,
    perLangRecording:{},
    // Step 4
    flowMode:"new",
    existingFlowId:"",
    newFlow:{
      name:"",
      includedStages:["audioCreation","audioReview","transcription","transcriptionReview","complete"],
      exits:{}, // {stageKey: [path, path, ...]} — seeded by effect when stages change
      clonedFromExisting:false,
    },
  });
  const update=patch=>setData(d=>({...d,...patch}));

  // Validation per step
  const stepValid=(()=>{
    if(step===1)return data.name.trim().length>0&&data.deadline.trim().length>0;
    if(step===2)return data.languages.length>0&&data.languages.every(l=>l.lang&&parseFloat(l.hours)>0);
    if(step===3){
      if(data.audioExternal)return true;
      if(data.languages.length===0)return false;
      return data.languages.every(l=>{
        const b=data.perLangRecording[l.lang]||{};
        const base=b.scripted&&b.numParticipants&&b.device&&(b.topics||[]).length>0&&parseFloat(b.durationMin)>0&&parseFloat(b.durationMax)>0&&parseFloat(b.ratePerMinute)>=0;
        if(!base)return false;
        if(b.scripted==="scripted"&&(b.scripts||[]).length===0)return false;
        return true;
      });
    }
    if(step===4){
      if(data.flowMode==="skip")return true;
      if(data.flowMode==="existing")return !!data.existingFlowId;
      if(data.flowMode==="new"){
        if(!data.newFlow.name.trim())return false;
        const needStages=data.newFlow.includedStages.filter(k=>k!=="complete");
        const langs=data.languages.map(l=>l.lang);
        return needStages.every(k=>{
          const paths=data.newFlow.exits[k]||[];
          if(paths.length===0)return false;
          if(!paths.every(p=>p.destination))return false;
          if(!isStageCoverageValid(paths,langs))return false;
          // Every human path must have a non-zero base rate for each of its effective languages
          return paths.every(p=>{
            if(p.worker?.type!=="human")return true;
            const pLangs=(p.langs||[]).length===0?langs:p.langs;
            return pLangs.every(l=>{
              const r=p.rates?.[l];
              // A non-set entry falls back to DB default — treat as valid. Only reject if user explicitly cleared it.
              if(!r)return true;
              const base=r.base!==undefined?parseFloat(r.base):getDefaultBaseRate(p.destination,l);
              return base>0;
            });
          });
        });
      }
    }
    return true;
  })();

  const buildProject=(status)=>{
    const projectId=`proj${Date.now()}`;
    const pipeline={total:0,audioCreation:0,audioReview:0,transcription:0,transcriptionReview:0,complete:0};
    const languages=data.languages.map(l=>({
      lang:l.lang,
      hoursGoal:parseFloat(l.hours)||0,
      hoursRecorded:0,
      maxPerContributor:parseFloat(l.maxPerContributor)||null,
      combinationMax:parseFloat(l.combinationMax)||null,
    }));
    const perLangRecording={};
    Object.entries(data.perLangRecording).forEach(([k,v])=>{
      perLangRecording[k]={
        ...v,
        durationMin:parseFloat(v.durationMin)||0,
        durationMax:parseFloat(v.durationMax)||0,
        ratePerMinute:parseFloat(v.ratePerMinute)||0,
        numParticipants:parseInt(v.numParticipants)||2,
      };
    });
    return{
      id:projectId,
      name:data.name.trim(),
      customer:data.customer.trim(),
      description:data.description.trim(),
      deadline:data.deadline,
      status,
      createdAt:"Apr 2026",
      languages,
      audioExternal:data.audioExternal,
      perLangRecording,
      flowMode:data.flowMode,
      flowId:data.flowMode==="existing"?data.existingFlowId:(data.flowMode==="new"?`flow${Date.now()}`:null),
      newFlowDraft:data.flowMode==="new"?data.newFlow:null,
      engagementsTotal:0,
      batchIds:[],
      pipeline,
    };
  };

  const canLaunch=data.flowMode!=="skip"&&stepValid;

  const renderStep=()=>{
    if(step===1)return <WizStepBasics data={data} onChange={update}/>;
    if(step===2)return <WizStepLanguages data={data} onChange={update}/>;
    if(step===3)return <WizStepRecordingBatches data={data} onChange={update}/>;
    if(step===4)return <WizStepFlow data={data} onChange={update} existingFlows={existingFlows}/>;
    if(step===5)return <WizStepReview data={data} existingFlows={existingFlows} onJump={setStep}/>;
    return null;
  };

  return(<div style={{position:"fixed",inset:0,background:OVERLAY_STRONG,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}>
    <div style={{background:SURFACE,borderRadius:14,width:"94%",maxWidth:980,height:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Header */}
      <div style={{padding:"18px 28px",borderBottom:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:P,letterSpacing:"0.1em",marginBottom:3}}>NEW PROJECT</div>
          <div style={{fontSize:17,fontWeight:600,color:TEXT_PRIMARY}}>{WIZ_STEP_LABELS[step-1]}</div>
        </div>
        <button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:30,height:30,cursor:"pointer",fontSize:13,color:TEXT_MUTED}}>✕</button>
      </div>

      {/* Stepper */}
      <WizStepper current={step} onJump={setStep}/>

      {/* Content */}
      <div style={{flex:1,overflowY:"auto",padding:"24px 28px",background:SURFACE}}>
        {renderStep()}
      </div>

      {/* Footer */}
      <div style={{padding:"14px 28px",borderTop:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:SURFACE_SUNKEN,flexShrink:0}}>
        <button onClick={step===1?onClose:()=>setStep(step-1)} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer",color:TEXT_SECONDARY,fontWeight:500}}>
          {step===1?"Cancel":"← Back"}
        </button>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{fontSize:12,color:TEXT_SUBTLE}}>Step {step} of {WIZ_STEP_LABELS.length}</div>
          {step<WIZ_STEP_LABELS.length?(
            <button onClick={()=>stepValid&&setStep(step+1)} disabled={!stepValid} style={{padding:"9px 22px",borderRadius:8,border:"none",background:stepValid?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:600,cursor:stepValid?"pointer":"default"}}>Next →</button>
          ):(<>
            <button onClick={()=>onCreate(buildProject("draft"))} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER_STRONG}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:13,fontWeight:500,cursor:"pointer"}}>Save as Draft</button>
            <button onClick={()=>canLaunch&&onCreate(buildProject("active"))} disabled={!canLaunch} style={{padding:"9px 22px",borderRadius:8,border:"none",background:canLaunch?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:600,cursor:canLaunch?"pointer":"default"}}>Launch Project</button>
          </>)}
        </div>
      </div>
    </div>
  </div>);
}

// ── ORCHESTRATION SCREEN ──────────────────────────────────────────────────────
// Mock per-task pipeline data. In production this would come from the API,
// keyed by task/engagement id, with a `stage` (current stage the task sits in)
// and a `ready` flag (stage work is done and task can advance to next stage).
const ORCH_STAGES=[
  {key:"audioCreation",      label:"Audio Creation",       bg:RED_TINT, text:"#991b1b", ring:"#f87171"},
  {key:"audioReview",        label:"Audio Review",         bg:BRAND_TINT_2, text:"#6b21a8", ring:"#c084fc"},
  {key:"transcription",      label:"Transcription",        bg:INFO_BORDER, text:INFO_DEEP, ring:"#60a5fa"},
  {key:"transcriptionReview",label:"Transcription Review", bg:"#fef08a", text:WARNING_DEEP, ring:"#facc15"},
  {key:"complete",           label:"Complete",             bg:"#bbf7d0", text:"#14532d", ring:"#4ade80"},
];
const STAGE_BY_KEY=Object.fromEntries(ORCH_STAGES.map(s=>[s.key,s]));

const orchProjects=[
  {id:"proj1",name:"Spanish Medical Training",lang:"Spanish"},
  {id:"proj2",name:"French Healthcare Dialogues",lang:"French"},
  {id:"proj3",name:"Arabic Business Scripted Content",lang:"Arabic"},
];

// Each task has: id, project, lang, stage (current stage), ready (done at current stage & ready to advance),
// assignee (who's currently working it), assigneeType (human/machine/vendor/pool), since (when it entered stage).
const initialOrchTasks=[
  // Spanish Medical Training
  {id:"E-1001",project:"proj1",lang:"Spanish",stage:"audioCreation",ready:false,assignee:"Sofia Reyes",assigneeType:"human",since:"Apr 14"},
  {id:"E-1002",project:"proj1",lang:"Spanish",stage:"audioCreation",ready:true, assignee:"Lucas Oliveira",assigneeType:"human",since:"Apr 13"},
  {id:"E-1003",project:"proj1",lang:"Spanish",stage:"audioCreation",ready:true, assignee:"Maria González",assigneeType:"human",since:"Apr 12"},
  {id:"E-1004",project:"proj1",lang:"Spanish",stage:"audioReview",  ready:false,assignee:"Kwame Boateng",assigneeType:"human",since:"Apr 11"},
  {id:"E-1005",project:"proj1",lang:"Spanish",stage:"audioReview",  ready:true, assignee:"Amara Osei",assigneeType:"human",since:"Apr 10"},
  {id:"E-1006",project:"proj1",lang:"Spanish",stage:"audioReview",  ready:true, assignee:"Zara Mensah",assigneeType:"human",since:"Apr 10"},
  {id:"E-1007",project:"proj1",lang:"Spanish",stage:"audioReview",  ready:true, assignee:"Kwame Boateng",assigneeType:"human",since:"Apr 9"},
  {id:"E-1008",project:"proj1",lang:"Spanish",stage:"transcription",ready:false,assignee:"ASR-v3.2",assigneeType:"machine",since:"Apr 9"},
  {id:"E-1009",project:"proj1",lang:"Spanish",stage:"transcription",ready:false,assignee:"Lingua Partners",assigneeType:"vendor",since:"Apr 8"},
  {id:"E-1010",project:"proj1",lang:"Spanish",stage:"transcription",ready:true, assignee:"ASR-v3.2",assigneeType:"machine",since:"Apr 7"},
  {id:"E-1011",project:"proj1",lang:"Spanish",stage:"transcription",ready:true, assignee:"Priya Nair",assigneeType:"human",since:"Apr 7"},
  {id:"E-1012",project:"proj1",lang:"Spanish",stage:"transcriptionReview",ready:false,assignee:"Zara Mensah",assigneeType:"human",since:"Apr 6"},
  {id:"E-1013",project:"proj1",lang:"Spanish",stage:"transcriptionReview",ready:true, assignee:"Amara Osei",assigneeType:"human",since:"Apr 5"},
  // Complete (20 total; show a few sample rows)
  {id:"E-1014",project:"proj1",lang:"Spanish",stage:"complete",ready:false,assignee:"—",assigneeType:"human",since:"Apr 4"},
  {id:"E-1015",project:"proj1",lang:"Spanish",stage:"complete",ready:false,assignee:"—",assigneeType:"human",since:"Apr 3"},

  // French Healthcare Dialogues
  {id:"E-2001",project:"proj2",lang:"French",stage:"audioCreation",ready:false,assignee:"Priya Nair",assigneeType:"human",since:"Apr 14"},
  {id:"E-2002",project:"proj2",lang:"French",stage:"audioCreation",ready:true, assignee:"Maria González",assigneeType:"human",since:"Apr 13"},
  {id:"E-2003",project:"proj2",lang:"French",stage:"audioReview",  ready:false,assignee:"Kwame Boateng",assigneeType:"human",since:"Apr 12"},
  {id:"E-2004",project:"proj2",lang:"French",stage:"audioReview",  ready:true, assignee:"Amara Osei",assigneeType:"human",since:"Apr 11"},
  {id:"E-2005",project:"proj2",lang:"French",stage:"audioReview",  ready:true, assignee:null,assigneeType:"pool",since:"Apr 10"},
  {id:"E-2006",project:"proj2",lang:"French",stage:"transcription",ready:false,assignee:"ASR-v3.2",assigneeType:"machine",since:"Apr 10"},
  {id:"E-2007",project:"proj2",lang:"French",stage:"transcription",ready:true, assignee:"Lingua Partners",assigneeType:"vendor",since:"Apr 9"},
  {id:"E-2008",project:"proj2",lang:"French",stage:"transcription",ready:true, assignee:"ASR-v3.2",assigneeType:"machine",since:"Apr 9"},
  {id:"E-2009",project:"proj2",lang:"French",stage:"transcriptionReview",ready:false,assignee:"Zara Mensah",assigneeType:"human",since:"Apr 8"},
  {id:"E-2010",project:"proj2",lang:"French",stage:"transcriptionReview",ready:true, assignee:"Kwame Boateng",assigneeType:"human",since:"Apr 7"},
  {id:"E-2011",project:"proj2",lang:"French",stage:"transcriptionReview",ready:true, assignee:"Priya Nair",assigneeType:"human",since:"Apr 6"},

  // Arabic Business
  {id:"E-3001",project:"proj3",lang:"Arabic",stage:"audioCreation",ready:false,assignee:"Fatima Al-Rashid",assigneeType:"human",since:"Apr 14"},
  {id:"E-3002",project:"proj3",lang:"Arabic",stage:"audioCreation",ready:true, assignee:"Chen Wei",assigneeType:"human",since:"Apr 13"},
  {id:"E-3003",project:"proj3",lang:"Arabic",stage:"audioReview",  ready:false,assignee:"Amara Osei",assigneeType:"human",since:"Apr 12"},
  {id:"E-3004",project:"proj3",lang:"Arabic",stage:"audioReview",  ready:true, assignee:"Zara Mensah",assigneeType:"human",since:"Apr 11"},
  {id:"E-3005",project:"proj3",lang:"Arabic",stage:"transcription",ready:false,assignee:"ASR-v3.2",assigneeType:"machine",since:"Apr 10"},
  {id:"E-3006",project:"proj3",lang:"Arabic",stage:"transcription",ready:true, assignee:"Lingua Partners",assigneeType:"vendor",since:"Apr 9"},
  {id:"E-3007",project:"proj3",lang:"Arabic",stage:"transcriptionReview",ready:false,assignee:"Priya Nair",assigneeType:"human",since:"Apr 8"},
  {id:"E-3008",project:"proj3",lang:"Arabic",stage:"transcriptionReview",ready:true, assignee:"Kwame Boateng",assigneeType:"human",since:"Apr 7"},
];

const AssigneeCell=({assignee,assigneeType})=>{
  if(assigneeType==="pool")return <span style={{fontSize:12,color:TEXT_MUTED,fontStyle:"italic"}}>In pool</span>;
  const typeLabel=assigneeType==="machine"?"Machine":(assigneeType==="vendor"?"Vendor":"");
  return(<div style={{display:"flex",alignItems:"center",gap:6}}>
    {typeLabel&&<span style={{fontSize:10,fontWeight:700,color:TEXT_SUBTLE,letterSpacing:"0.06em",textTransform:"uppercase"}}>{typeLabel}:</span>}
    <span style={{fontSize:12,color:TEXT_SECONDARY}}>{assignee||"—"}</span>
  </div>);
};

function AdvanceAssignmentModal({count,fromStage,toStage,onClose,onConfirm}){
  const[choice,setChoice]=useState(null);
  const[specificHuman,setSpecificHuman]=useState("");
  const[machineModel,setMachineModel]=useState("ASR-v3.2");
  const[vendor,setVendor]=useState("Lingua Partners");
  const HUMANS=["Kwame Boateng","Amara Osei","Zara Mensah","Priya Nair","Lucas Oliveira","Fatima Al-Rashid"];
  const MACHINES=["ASR-v3.2","ASR-v3.1","Whisper-large-v3"];
  const VENDORS=["Lingua Partners","GlobalVoice Co","Translate+"];

  const canConfirm=choice==="pool"||(choice==="human"&&specificHuman)||(choice==="machine"&&machineModel)||(choice==="vendor"&&vendor);

  const doConfirm=()=>{
    let assignee=null,assigneeType=choice;
    if(choice==="human")assignee=specificHuman;
    else if(choice==="machine")assignee=machineModel;
    else if(choice==="vendor")assignee=vendor;
    onConfirm({assigneeType,assignee});
  };

  const options=[
    {key:"pool",title:"Open to Pool",desc:"Add to the claim queue for any eligible human to pick up."},
    {key:"human",title:"Assign to Specific Human",desc:"Directly assign to a named reviewer or creator."},
    {key:"machine",title:"Assign to Machine",desc:"Run through an automated model (e.g. ASR, auto-QA)."},
    {key:"vendor",title:"Send to Vendor",desc:"Route to an external vendor partner."},
  ];

  return(<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}>
    <div style={{background:SURFACE,borderRadius:14,width:520,maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
      <div style={{padding:"20px 24px 14px",borderBottom:`1px solid ${SURFACE_HOVER}`}}>
        <div style={{fontWeight:600,fontSize:16,marginBottom:6}}>Advance {count} task{count!==1?"s":""}</div>
        <div style={{fontSize:12,color:TEXT_MUTED}}>
          Moving from <span style={{color:STAGE_BY_KEY[fromStage]?.text,fontWeight:600}}>{STAGE_BY_KEY[fromStage]?.label}</span>
          {" → "}
          <span style={{color:STAGE_BY_KEY[toStage]?.text,fontWeight:600}}>{STAGE_BY_KEY[toStage]?.label}</span>
        </div>
      </div>
      <div style={{padding:"18px 24px"}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>WHO WILL DO THE NEXT STAGE?</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {options.map(o=>(
            <div key={o.key} onClick={()=>setChoice(o.key)} style={{border:`1px solid ${choice===o.key?P:BORDER}`,background:choice===o.key?PL:SURFACE,borderRadius:10,padding:"12px 14px",cursor:"pointer"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:choice===o.key?P:TEXT_PRIMARY,marginBottom:2}}>{o.title}</div>
                <div style={{fontSize:12,color:TEXT_MUTED,lineHeight:1.5}}>{o.desc}</div>
                {choice===o.key&&o.key==="human"&&(
                  <select value={specificHuman} onChange={e=>setSpecificHuman(e.target.value)} style={{marginTop:10,width:"100%",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",background:SURFACE}}>
                    <option value="">— Select reviewer —</option>
                    {HUMANS.map(h=><option key={h}>{h}</option>)}
                  </select>
                )}
                {choice===o.key&&o.key==="machine"&&(
                  <select value={machineModel} onChange={e=>setMachineModel(e.target.value)} style={{marginTop:10,width:"100%",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",background:SURFACE}}>
                    {MACHINES.map(m=><option key={m}>{m}</option>)}
                  </select>
                )}
                {choice===o.key&&o.key==="vendor"&&(
                  <select value={vendor} onChange={e=>setVendor(e.target.value)} style={{marginTop:10,width:"100%",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",background:SURFACE}}>
                    {VENDORS.map(v=><option key={v}>{v}</option>)}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{padding:"14px 24px 20px",display:"flex",justifyContent:"flex-end",gap:8,borderTop:`1px solid ${SURFACE_HOVER}`}}>
        <button onClick={onClose} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
        <button onClick={doConfirm} disabled={!canConfirm} style={{padding:"8px 18px",borderRadius:8,border:"none",background:canConfirm?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:600,cursor:canConfirm?"pointer":"default"}}>Advance →</button>
      </div>
    </div>
  </div>);
}

// ── FLOW BUILDER CONSTANTS & HELPERS ──
const ORCH_HUMANS=["Kwame Boateng","Amara Osei","Zara Mensah","Priya Nair","Lucas Oliveira","Fatima Al-Rashid","Chen Wei"];
const ORCH_MACHINES=["ASR-v3.2","ASR-v3.1","Whisper-large-v3"];
const ORCH_VENDORS=["Lingua Partners","GlobalVoice Co","Translate+"];
const ORCH_LANGS=["Amharic","Arabic","English","French","Hausa","Igbo","Mandarin","Portuguese","Somali","Spanish","Swahili","Yoruba","Zulu"];
const TOGGLEABLE_STAGES=new Set(["audioReview","transcription","transcriptionReview"]);

// Given a set of included stage keys, return the ordered transitions between consecutive ones.
const buildTransitionPairs=(includedStages)=>{
  const ordered=ORCH_STAGES.filter(s=>includedStages.includes(s.key));
  const pairs=[];
  for(let i=0;i<ordered.length-1;i++){
    pairs.push({from:ordered[i].key,to:ordered[i+1].key,key:`${ordered[i].key}->${ordered[i+1].key}`});
  }
  return pairs;
};

const defaultTransitionConfig=()=>({
  gate:{mode:"auto",samplePct:10,langOverrides:[]},
  routing:{default:{type:"pool",who:null},overrides:[]}
});

const routingTypeIcon={pool:"P",human:"H",machine:"M",vendor:"V"};
const describeRouting=r=>r.type==="pool"?"Open pool":(r.who||"—");
const describeGate=g=>{
  if(g.mode==="auto")return{icon:"",text:"Auto-advance",color:SUCCESS,bg:SUCCESS_BG};
  if(g.mode==="pause")return{icon:"",text:"Pause for review",color:WARNING_ACCENT,bg:WARNING_ACCENT_SURFACE};
  if(g.mode==="sample"){
    const hasOver=(g.langOverrides||[]).length>0;
    return{icon:"",text:hasOver?`Sample (${g.samplePct}% + overrides)`:`Sample ${g.samplePct}%`,color:INFO,bg:INFO_BG};
  }
  return{icon:"",text:"",color:TEXT_MUTED,bg:SURFACE_HOVER};
};

// ── Routing picker (Pool / Specific Human / Machine / Vendor) ──
function RoutingPicker({value,onChange}){
  const types=[
    {key:"pool",icon:"P",label:"Open Pool"},
    {key:"human",icon:"H",label:"Specific"},
    {key:"machine",icon:"M",label:"Machine"},
    {key:"vendor",icon:"V",label:"Vendor"},
  ];
  const defaultWho=t=>t==="human"?ORCH_HUMANS[0]:t==="machine"?ORCH_MACHINES[0]:t==="vendor"?ORCH_VENDORS[0]:null;
  const listFor=t=>t==="human"?ORCH_HUMANS:t==="machine"?ORCH_MACHINES:t==="vendor"?ORCH_VENDORS:[];
  return(<div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
      {types.map(t=>(
        <button key={t.key} onClick={()=>onChange({type:t.key,who:defaultWho(t.key)})} style={{
          padding:"10px 6px",
          border:`1px solid ${value.type===t.key?P:BORDER}`,
          background:value.type===t.key?PL:SURFACE,
          color:value.type===t.key?P:TEXT_SECONDARY,
          borderRadius:8,fontSize:12,fontWeight:value.type===t.key?600:500,cursor:"pointer",
          display:"flex",flexDirection:"column",alignItems:"center",gap:4
        }}>
          <span style={{fontSize:16}}>{t.icon}</span>{t.label}
        </button>
      ))}
    </div>
    {value.type!=="pool"&&(
      <select value={value.who||""} onChange={e=>onChange({type:value.type,who:e.target.value})}
        style={{marginTop:8,width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:13,outline:"none",background:SURFACE}}>
        {listFor(value.type).map(o=><option key={o}>{o}</option>)}
      </select>
    )}
  </div>);
}

// ── Language add-input ──
function LangAddInput({existing,onAdd}){
  const[q,setQ]=useState(""),[open,setOpen]=useState(false);
  const ref=useRef(null);
  useEffect(()=>{
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[]);
  const available=ORCH_LANGS.filter(l=>!existing.includes(l)&&l.toLowerCase().includes(q.toLowerCase()));
  return(<div ref={ref} style={{position:"relative"}}>
    <input value={q} onChange={e=>{setQ(e.target.value);setOpen(true);}} onFocus={()=>setOpen(true)} placeholder={existing.length>0?"Add another language…":"Choose languages…"}
      style={{width:"100%",padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:7,fontSize:12,outline:"none",boxSizing:"border-box",background:SURFACE}}/>
    {open&&available.length>0&&(
      <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,boxShadow:"0 4px 14px rgba(0,0,0,0.1)",zIndex:60,maxHeight:200,overflowY:"auto"}}>
        {available.map(l=>(
          <div key={l} onClick={()=>{onAdd(l);setQ("");setOpen(false);}}
            style={{padding:"7px 12px",fontSize:13,cursor:"pointer"}}
            onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>
            {l}
          </div>
        ))}
      </div>
    )}
  </div>);
}

// ── Language chip list (used for both routing & sample overrides) ──
function LangChips({langs,onRemove}){
  if(langs.length===0)return null;
  return(<div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>
    {langs.map(l=>(
      <span key={l} style={{display:"flex",alignItems:"center",gap:4,background:SURFACE,color:P,fontSize:12,fontWeight:600,padding:"3px 8px 3px 10px",borderRadius:999,border:`1px solid ${PL}`}}>
        {l}<span onClick={()=>onRemove(l)} style={{cursor:"pointer",fontSize:14,lineHeight:1,color:BRAND_SOFT}}>×</span>
      </span>
    ))}
  </div>);
}

// ── Gate editor (inline) — with language-specific sample % ──
function GateEditor({gate,onChange,fromStage,toStage}){
  const gateOptions=[
    {mode:"auto",icon:"",title:"Auto-advance",desc:`When a task finishes ${fromStage.label}, it moves to ${toStage.label} automatically.`},
    {mode:"sample",icon:"",title:"Auto-advance with sampling",desc:"Auto-advance most tasks, but hold a random % for manual review."},
    {mode:"pause",icon:"",title:"Pause for manual decision",desc:`All tasks wait at ${fromStage.label} for an ops manager to approve advancement.`},
  ];
  const setMode=m=>onChange({...gate,mode:m,samplePct:gate.samplePct||10,langOverrides:m==="sample"?(gate.langOverrides||[]):[]});
  const addOv=()=>onChange({...gate,langOverrides:[...(gate.langOverrides||[]),{langs:[],pct:20}]});
  const removeOv=i=>onChange({...gate,langOverrides:(gate.langOverrides||[]).filter((_,idx)=>idx!==i)});
  const updateOv=(i,patch)=>onChange({...gate,langOverrides:(gate.langOverrides||[]).map((o,idx)=>idx===i?{...o,...patch}:o)});

  return(<div style={{display:"flex",flexDirection:"column",gap:8}}>
    {gateOptions.map(o=>(
      <div key={o.mode} onClick={()=>setMode(o.mode)}
        style={{border:`1px solid ${gate.mode===o.mode?P:BORDER}`,background:gate.mode===o.mode?PL:SURFACE,borderRadius:10,padding:"11px 13px",cursor:"pointer",display:"flex",gap:12,alignItems:"flex-start"}}>
        <div style={{width:28,height:28,borderRadius:7,background:gate.mode===o.mode?SURFACE:SURFACE_SUNKEN,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{o.icon}</div>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:600,color:gate.mode===o.mode?P:TEXT_PRIMARY,marginBottom:2}}>{o.title}</div>
          <div style={{fontSize:12,color:TEXT_MUTED,lineHeight:1.5}}>{o.desc}</div>

          {gate.mode===o.mode&&o.mode==="sample"&&(
            <div style={{marginTop:10}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:TEXT_SECONDARY,marginBottom:10}}>
                Default sample rate:
                <input type="number" min="1" max="100" value={gate.samplePct||10}
                  onChange={e=>onChange({...gate,samplePct:Math.max(1,Math.min(100,parseInt(e.target.value)||0))})}
                  style={{width:60,padding:"5px 7px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,outline:"none"}}/>
                <span>% of tasks</span>
              </div>

              {(gate.langOverrides||[]).map((ov,i)=>(
                <div key={i} style={{border:`1px solid ${PL}`,background:SURFACE,borderRadius:8,padding:10,marginBottom:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div style={{fontSize:10,fontWeight:600,color:P,letterSpacing:"0.05em"}}>LANGUAGE-SPECIFIC RATE #{i+1}</div>
                    <button onClick={()=>removeOv(i)} style={{background:"none",border:"none",color:DANGER,fontSize:11,cursor:"pointer"}}>Remove</button>
                  </div>
                  <LangChips langs={ov.langs} onRemove={l=>updateOv(i,{langs:ov.langs.filter(x=>x!==l)})}/>
                  <LangAddInput existing={ov.langs} onAdd={l=>updateOv(i,{langs:[...ov.langs,l]})}/>
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:TEXT_SECONDARY,marginTop:8}}>
                    Sample:
                    <input type="number" min="1" max="100" value={ov.pct}
                      onChange={e=>updateOv(i,{pct:Math.max(1,Math.min(100,parseInt(e.target.value)||0))})}
                      style={{width:55,padding:"4px 6px",border:`1px solid ${BORDER}`,borderRadius:6,fontSize:12,outline:"none"}}/>
                    <span>% of tasks in these languages</span>
                  </div>
                </div>
              ))}

              <button onClick={addOv} style={{width:"100%",padding:"7px",border:`1px dashed ${P}`,borderRadius:8,background:"transparent",color:P,fontSize:12,fontWeight:500,cursor:"pointer"}}>+ Add language-specific rate</button>
            </div>
          )}
        </div>
      </div>
    ))}
  </div>);
}

// ── Routing editor (default + language overrides) ──
function RoutingEditor({routing,onChange,toStage}){
  const setDefault=r=>onChange({...routing,default:r});
  const addOv=()=>onChange({...routing,overrides:[...routing.overrides,{langs:[],type:"pool",who:null}]});
  const removeOv=i=>onChange({...routing,overrides:routing.overrides.filter((_,idx)=>idx!==i)});
  const updateOv=(i,patch)=>onChange({...routing,overrides:routing.overrides.map((o,idx)=>idx===i?{...o,...patch}:o)});

  return(<div>
    <div style={{fontSize:12,color:TEXT_MUTED,marginBottom:10}}>Who performs <strong style={{color:TEXT_SECONDARY}}>{toStage.label}</strong>?</div>
    <div style={{border:`1px solid ${BORDER}`,borderRadius:10,padding:14,marginBottom:10,background:SURFACE_ALT}}>
      <div style={{fontSize:11,fontWeight:600,color:TEXT_MUTED,letterSpacing:"0.05em",marginBottom:8}}>DEFAULT · APPLIES TO ALL LANGUAGES UNLESS OVERRIDDEN</div>
      <RoutingPicker value={routing.default} onChange={setDefault}/>
    </div>

    {routing.overrides.map((ov,i)=>(
      <div key={i} style={{border:`1px solid ${PL}`,borderRadius:10,padding:14,marginBottom:10,background:BRAND_LIGHTER}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:11,fontWeight:600,color:P,letterSpacing:"0.05em"}}>LANGUAGE-SPECIFIC ROUTING #{i+1}</div>
          <button onClick={()=>removeOv(i)} style={{background:"none",border:"none",color:DANGER,fontSize:12,cursor:"pointer",fontWeight:500}}>Remove</button>
        </div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>FOR LANGUAGES</div>
        <LangChips langs={ov.langs} onRemove={l=>updateOv(i,{langs:ov.langs.filter(x=>x!==l)})}/>
        <LangAddInput existing={ov.langs} onAdd={l=>updateOv(i,{langs:[...ov.langs,l]})}/>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",margin:"10px 0 6px"}}>ROUTE TO</div>
        <RoutingPicker value={{type:ov.type,who:ov.who}} onChange={r=>updateOv(i,r)}/>
      </div>
    ))}

    <button onClick={addOv} style={{width:"100%",padding:"10px",border:`1px dashed ${P}`,borderRadius:10,background:"transparent",color:P,fontSize:13,fontWeight:500,cursor:"pointer"}}>+ Add language-specific rule</button>
  </div>);
}

// ── Single transition rule card (one per pair in a flow) ──
function TransitionRuleCard({transition,config,onChange,index}){
  const fromStage=STAGE_BY_KEY[transition.from];
  const toStage=STAGE_BY_KEY[transition.to];
  const isTerminal=transition.to==="complete";
  return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:14,marginBottom:16,overflow:"hidden"}}>
    <div style={{padding:"14px 18px",borderBottom:`1px solid ${SURFACE_HOVER}`,background:SURFACE_ALT,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <div style={{width:26,height:26,borderRadius:"50%",background:SURFACE,border:`1.5px solid ${P}`,color:P,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{index}</div>
      <span style={{color:fromStage.text,background:fromStage.bg,padding:"2px 9px",borderRadius:6,fontSize:12,fontWeight:600}}>{fromStage.label}</span>
      <span style={{color:TEXT_SUBTLE}}>→</span>
      <span style={{color:toStage.text,background:toStage.bg,padding:"2px 9px",borderRadius:6,fontSize:12,fontWeight:600}}>{toStage.label}</span>
    </div>
    <div style={{padding:"18px 18px",display:"grid",gridTemplateColumns:isTerminal?"1fr":"1fr 1fr",gap:24}}>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>GATE BEHAVIOR</div>
        <GateEditor gate={config.gate} onChange={g=>onChange({...config,gate:g})} fromStage={fromStage} toStage={toStage}/>
      </div>
      {!isTerminal&&(
        <div>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>ROUTING</div>
          <RoutingEditor routing={config.routing} onChange={r=>onChange({...config,routing:r})} toStage={toStage}/>
        </div>
      )}
    </div>
  </div>);
}

// ── Seed flow data ──
const initialFlows=[
  {
    id:"flow1",name:"Standard Pipeline",projectId:"proj1",status:"active",createdDate:"Apr 10, 2026",
    includedStages:["audioCreation","audioReview","transcription","transcriptionReview","complete"],
    transitions:{
      "audioCreation->audioReview":{gate:{mode:"sample",samplePct:15,langOverrides:[{langs:["Arabic"],pct:30}]},routing:{default:{type:"pool",who:null},overrides:[]}},
      "audioReview->transcription":{gate:{mode:"auto",samplePct:10,langOverrides:[]},routing:{default:{type:"machine",who:"ASR-v3.2"},overrides:[{langs:["Arabic","Amharic"],type:"vendor",who:"Lingua Partners"}]}},
      "transcription->transcriptionReview":{gate:{mode:"pause",samplePct:10,langOverrides:[]},routing:{default:{type:"pool",who:null},overrides:[{langs:["Spanish","French"],type:"human",who:"Amara Osei"}]}},
      "transcriptionReview->complete":{gate:{mode:"auto",samplePct:10,langOverrides:[]},routing:{default:{type:"pool",who:null},overrides:[]}},
    }
  },
  {
    id:"flow2",name:"French Dialogues — Draft v1",projectId:"proj2",status:"draft",createdDate:"Apr 18, 2026",
    includedStages:["audioCreation","transcription","complete"],
    transitions:{
      "audioCreation->transcription":{gate:{mode:"auto",samplePct:10,langOverrides:[]},routing:{default:{type:"machine",who:"Whisper-large-v3"},overrides:[]}},
      "transcription->complete":{gate:{mode:"auto",samplePct:10,langOverrides:[]},routing:{default:{type:"pool",who:null},overrides:[]}},
    }
  }
];

const statusStyle={active:{bg:SUCCESS_BG,c:SUCCESS,label:"Active"},draft:{bg:SLATE_BG,c:SLATE,label:"Draft"},paused:{bg:WARNING_BG,c:WARNING,label:"Paused"}};

// ── Flow list view ──
function FlowListView({flows,projects,onCreate,onEdit,onView,onClone,onDelete,onToggleStatus}){
  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>Orchestration</h1>
    </div>
    <p style={{fontSize:13,color:TEXT_MUTED,margin:"0 0 22px",maxWidth:760,lineHeight:1.5}}>
      View flows currently attached to your projects and their status. New flows are created as part of project setup.
    </p>

    {flows.length===0?(
      <div style={{background:SURFACE,border:`1px dashed ${TEXT_DISABLED}`,borderRadius:14,padding:"60px 24px",textAlign:"center"}}>
        <div style={{fontSize:15,fontWeight:600,color:TEXT_SECONDARY,marginBottom:6}}>No flows yet</div>
        <p style={{fontSize:13,color:TEXT_MUTED,maxWidth:440,margin:"0 auto"}}>Flows are created when you set up a project. Head to Projects to get started.</p>
      </div>
    ):(
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:`1px solid ${BORDER}`,background:SURFACE_ALT}}>
            {["Flow Name","Project","Stages","Status","Created","Actions"].map(h=>(
              <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {flows.map(f=>{
              const proj=projects.find(p=>p.id===f.projectId);
              const st=statusStyle[f.status]||statusStyle.draft;
              const activeStages=f.includedStages.length;
              return(<tr key={f.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`}}>
                <td style={{padding:"12px 14px",fontSize:13,color:P,fontWeight:600,cursor:"pointer"}} onClick={()=>onView(f)}>{f.name}</td>
                <td style={{padding:"12px 14px",fontSize:13,color:proj?TEXT_SECONDARY:TEXT_SUBTLE,fontStyle:proj?"normal":"italic"}}>{proj?.name||"Unattached"}</td>
                <td style={{padding:"12px 14px",fontSize:12,color:TEXT_MUTED}}>{activeStages} of {ORCH_STAGES.length} stages</td>
                <td style={{padding:"12px 14px"}}><span style={{background:st.bg,color:st.c,fontSize:11,fontWeight:600,padding:"2px 9px",borderRadius:999}}>{st.label}</span></td>
                <td style={{padding:"12px 14px",fontSize:12,color:TEXT_MUTED}}>{f.createdDate}</td>
                <td style={{padding:"12px 14px"}}>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={()=>onView(f)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:12,color:TEXT_SECONDARY,cursor:"pointer"}}>View</button>
                    {f.status==="active"&&<button onClick={()=>onToggleStatus(f.id)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${WARNING_BORDER}`,background:WARNING_SURFACE,color:WARNING,fontSize:12,cursor:"pointer"}}>Pause</button>}
                    {f.status==="paused"&&<button onClick={()=>onToggleStatus(f.id)} style={{padding:"4px 10px",borderRadius:6,border:"none",background:P,color:SURFACE,fontSize:12,fontWeight:500,cursor:"pointer"}}>Resume</button>}
                  </div>
                </td>
              </tr>);
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>);
}

// ── Flow builder (create/edit) ──
function FlowBuilderView({existingFlow,sourceFlow,projectsAvailable,onCancel,onSave}){
  const isEdit=!!existingFlow;
  const isClone=!existingFlow&&!!sourceFlow;
  const seed=existingFlow||sourceFlow;
  // When cloning, prepend "Copy of " to the name and drop the project (clone starts unattached so we don't collide with the original's project)
  const[name,setName]=useState(isClone?`Copy of ${seed.name}`:(seed?.name||""));
  const[projectId,setProjectId]=useState(isClone?"":(seed?.projectId||""));
  const[includedStages,setIncludedStages]=useState(seed?.includedStages||["audioCreation","complete"]);
  const[transitions,setTransitions]=useState(seed?.transitions||{});

  const pairs=buildTransitionPairs(includedStages);
  // Look up a config for a pair, with fallback so render never breaks on missing keys
  const getConfig=p=>transitions[p.key]||defaultTransitionConfig();

  const toggleStage=key=>{
    if(!TOGGLEABLE_STAGES.has(key))return;
    setIncludedStages(prev=>prev.includes(key)?prev.filter(k=>k!==key):[...prev,key].sort((a,b)=>ORCH_STAGES.findIndex(s=>s.key===a)-ORCH_STAGES.findIndex(s=>s.key===b)));
  };

  const updateTransition=(key,cfg)=>setTransitions(prev=>({...prev,[key]:cfg}));

  const nameOk=name.trim().length>0;
  const canSave=nameOk&&pairs.length>0;

  const save=(status)=>{
    if(!canSave)return;
    // Only save configs for currently-included transitions
    const activeTransitions={};
    pairs.forEach(p=>{activeTransitions[p.key]=transitions[p.key]||defaultTransitionConfig();});
    onSave({
      ...(existingFlow||{}),
      id:existingFlow?.id,
      name:name.trim(),
      projectId,
      status,
      includedStages,
      transitions:activeTransitions,
    });
  };

  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    <button onClick={onCancel} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:14,padding:0}}>← Back to Orchestration</button>
    <h1 style={{fontSize:21,fontWeight:600,margin:"0 0 4px"}}>{isEdit?`Edit Flow: ${existingFlow.name}`:(isClone?`Clone Flow: ${sourceFlow.name}`:"Create Flow")}</h1>
    <p style={{fontSize:13,color:TEXT_MUTED,margin:"0 0 22px",maxWidth:760,lineHeight:1.5}}>
      Configure a complete end-to-end flow, then save as a draft or activate it right away.
    </p>

    {/* Step 1: Details */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:14,marginBottom:16,overflow:"hidden"}}>
      <div style={{padding:"14px 18px",borderBottom:`1px solid ${SURFACE_HOVER}`,display:"flex",alignItems:"center",gap:10,background:SURFACE_ALT}}>
        <div style={{width:26,height:26,borderRadius:"50%",background:P,color:SURFACE,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>1</div>
        <div style={{fontSize:14,fontWeight:600}}>Flow details</div>
      </div>
      <div style={{padding:"18px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>FLOW NAME <span style={{color:DANGER}}>*</span></div>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Spanish Medical — Standard QA"
            style={{width:"100%",padding:"9px 11px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>ATTACH TO PROJECT <span style={{color:TEXT_SUBTLE,fontWeight:500,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
          <select value={projectId} onChange={e=>setProjectId(e.target.value)} disabled={isEdit}
            style={{width:"100%",padding:"9px 11px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",background:isEdit?SURFACE_SUNKEN:SURFACE,color:isEdit?TEXT_MUTED:TEXT_PRIMARY,cursor:isEdit?"not-allowed":"pointer"}}>
            <option value="">— No project attached —</option>
            {projectsAvailable.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {isEdit&&projectId&&<div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:4}}>Project cannot be changed after the flow is created.</div>}
          {!isEdit&&<div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:4}}>You can save a flow without attaching a project and link it later.</div>}
        </div>
      </div>
    </div>

    {/* Step 2: Included stages */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:14,marginBottom:16,overflow:"hidden"}}>
      <div style={{padding:"14px 18px",borderBottom:`1px solid ${SURFACE_HOVER}`,display:"flex",alignItems:"center",gap:10,background:SURFACE_ALT}}>
        <div style={{width:26,height:26,borderRadius:"50%",background:P,color:SURFACE,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>2</div>
        <div style={{fontSize:14,fontWeight:600}}>Which steps does this flow include?</div>
      </div>
      <div style={{padding:"18px"}}>
        <p style={{fontSize:12,color:TEXT_MUTED,margin:"0 0 16px"}}>Audio Creation and Complete are always part of every flow. Toggle the middle stages on or off depending on what this flow needs.</p>
        <div style={{display:"flex",alignItems:"stretch",gap:10,overflowX:"auto"}}>
          {ORCH_STAGES.map((s,i)=>{
            const included=includedStages.includes(s.key);
            const toggleable=TOGGLEABLE_STAGES.has(s.key);
            const isLast=i===ORCH_STAGES.length-1;
            return(<div key={s.key} style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{
                minWidth:140,
                background:included?s.bg:SURFACE_SUNKEN,
                border:`2px ${included?"solid":"dashed"} ${included?s.ring:TEXT_DISABLED}`,
                opacity:included?1:0.55,
                borderRadius:12,padding:"14px 14px 12px",textAlign:"center",
                position:"relative",
                cursor:toggleable?"pointer":"default"
              }} onClick={()=>toggleable&&toggleStage(s.key)}>
                {toggleable&&(
                  <div title={included?"Included":"Excluded"}
                    style={{position:"absolute",top:6,right:6,width:32,height:18,borderRadius:999,background:included?P:TEXT_DISABLED,padding:0,display:"flex",alignItems:"center",transition:"background 0.15s"}}>
                    <span style={{display:"block",width:14,height:14,borderRadius:"50%",background:SURFACE,marginLeft:included?16:2,transition:"margin-left 0.15s",boxShadow:"0 1px 2px rgba(0,0,0,0.2)"}}/>
                  </div>
                )}
                <div style={{fontSize:13,fontWeight:600,color:included?s.text:TEXT_SUBTLE,marginBottom:2,marginTop:toggleable?4:0}}>{s.label}</div>
                <div style={{fontSize:10,color:included?s.text:TEXT_SUBTLE,opacity:0.7,fontStyle:included?"normal":"italic"}}>
                  {!toggleable?(i===0?"Always first":"Always last"):(included?"Included":"Excluded")}
                </div>
              </div>
              {!isLast&&<span style={{color:TEXT_SUBTLE,fontSize:16}}>→</span>}
            </div>);
          })}
        </div>
        <div style={{marginTop:12,fontSize:12,color:TEXT_MUTED}}>This flow will move tasks through <strong style={{color:P}}>{pairs.length} handoff{pairs.length!==1?"s":""}</strong>.</div>
      </div>
    </div>

    {/* Step 3: Configure transitions */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:14,marginBottom:16,overflow:"hidden"}}>
      <div style={{padding:"14px 18px",borderBottom:`1px solid ${SURFACE_HOVER}`,display:"flex",alignItems:"center",gap:10,background:SURFACE_ALT}}>
        <div style={{width:26,height:26,borderRadius:"50%",background:P,color:SURFACE,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>3</div>
        <div style={{fontSize:14,fontWeight:600}}>Configure the rules for each handoff</div>
      </div>
      <div style={{padding:"18px",background:SURFACE_ALT}}>
        {pairs.map((p,idx)=>{
          const cfg=getConfig(p);
          return <TransitionRuleCard key={p.key} transition={p} config={cfg} onChange={c=>updateTransition(p.key,c)} index={idx+1}/>;
        })}
      </div>
    </div>

    {/* Footer actions */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,padding:"12px 0",marginTop:4}}>
      <button onClick={onCancel} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>save("draft")} disabled={!canSave} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${canSave?P:BORDER}`,background:SURFACE,color:canSave?P:TEXT_SUBTLE,fontSize:13,fontWeight:500,cursor:canSave?"pointer":"default"}}>Save as Draft</button>
        <button onClick={()=>save("active")} disabled={!canSave} style={{padding:"9px 22px",borderRadius:8,border:"none",background:canSave?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:600,cursor:canSave?"pointer":"default"}}>{isEdit?"Save & Activate":"Activate Flow"}</button>
      </div>
    </div>
  </div>);
}

// ── Flow detail view (live pipeline for one flow) ──
function FlowDetailView({flow,projects,tasks,setTasks,onBack,onEdit,onToggleStatus}){
  const[drill,setDrill]=useState(null);
  const[selected,setSelected]=useState(new Set());
  const[showAdvance,setShowAdvance]=useState(false);

  const project=projects.find(p=>p.id===flow.projectId);
  const projectTasks=tasks.filter(t=>t.project===flow.projectId);
  const stagesInFlow=ORCH_STAGES.filter(s=>flow.includedStages.includes(s.key));
  const pairs=buildTransitionPairs(flow.includedStages);

  const stageCounts={},readyCounts={};
  stagesInFlow.forEach(s=>{
    stageCounts[s.key]=projectTasks.filter(t=>t.stage===s.key).length;
    readyCounts[s.key]=projectTasks.filter(t=>t.stage===s.key&&t.ready).length;
  });

  const drillTasks=drill
    ? projectTasks.filter(t=>t.stage===drill.stage&&(drill.readyOnly?t.ready:true))
    : [];

  const toggleSelect=id=>setSelected(prev=>{const n=new Set(prev);if(n.has(id))n.delete(id);else n.add(id);return n;});
  const toggleSelectAll=()=>{if(selected.size===drillTasks.length)setSelected(new Set());else setSelected(new Set(drillTasks.map(t=>t.id)));};
  const closeDrill=()=>{setDrill(null);setSelected(new Set());};
  const openStage=(stageKey,readyOnly=false)=>{setDrill({stage:stageKey,readyOnly});setSelected(new Set());};

  const doAdvance=({assigneeType,assignee})=>{
    if(!drill)return;
    const curIdx=stagesInFlow.findIndex(s=>s.key===drill.stage);
    const nextStage=stagesInFlow[curIdx+1]?.key;
    if(!nextStage)return;
    setTasks(prev=>prev.map(t=>{
      if(!selected.has(t.id))return t;
      return{...t,stage:nextStage,ready:false,assignee:assigneeType==="pool"?null:assignee,assigneeType,since:"Today"};
    }));
    setShowAdvance(false);
    setSelected(new Set());
  };

  // Drill view
  if(drill){
    const stage=STAGE_BY_KEY[drill.stage];
    const nextStageIdx=stagesInFlow.findIndex(s=>s.key===drill.stage)+1;
    const nextStage=stagesInFlow[nextStageIdx];
    const canAdvance=selected.size>0&&!!nextStage;

    return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {showAdvance&&<AdvanceAssignmentModal count={selected.size} fromStage={drill.stage} toStage={nextStage?.key} onClose={()=>setShowAdvance(false)} onConfirm={doAdvance}/>}
      <div style={{padding:"22px 28px 0",flexShrink:0}}>
        <button onClick={closeDrill} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:14,padding:0}}>← Back to {flow.name}</button>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:16}}>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
              <div style={{width:12,height:12,borderRadius:3,background:stage.bg,border:"1px solid rgba(0,0,0,0.08)"}}/>
              <h1 style={{fontSize:21,fontWeight:600,margin:0}}>{stage.label}</h1>
              {drill.readyOnly&&<Badge label="Ready to advance" color="green"/>}
            </div>
            <div style={{fontSize:13,color:TEXT_MUTED}}>
              {drillTasks.length} task{drillTasks.length!==1?"s":""} {drill.readyOnly?"ready to move to the next stage":`currently at ${stage.label.toLowerCase()}`} · {project?.name||"Unattached"}
            </div>
          </div>
          {selected.size>0&&nextStage&&(
            <div style={{display:"flex",alignItems:"center",gap:10,background:SURFACE,border:`1px solid ${P}`,borderRadius:10,padding:"8px 12px"}}>
              <span style={{fontSize:12,color:TEXT_MUTED}}>{selected.size} selected</span>
              <button onClick={()=>setShowAdvance(true)} disabled={!canAdvance} style={{padding:"7px 14px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:canAdvance?"pointer":"default"}}>Advance to {nextStage.label} →</button>
            </div>
          )}
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"0 28px 22px"}}>
        <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{borderBottom:`1px solid ${BORDER}`,background:SURFACE_ALT}}>
              <th style={{padding:"9px 13px",width:32}}>
                {nextStage&&<input type="checkbox" style={{accentColor:P}} checked={drillTasks.length>0&&selected.size===drillTasks.length} onChange={toggleSelectAll}/>}
              </th>
              {["ID","Language","Currently With","Ready","In Stage Since"].map(h=>(
                <th key={h} style={{padding:"9px 13px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{drillTasks.length?drillTasks.map(t=>(
              <tr key={t.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:selected.has(t.id)?BRAND_LIGHTER:SURFACE}}>
                <td style={{padding:"10px 13px"}}>
                  {nextStage&&<input type="checkbox" style={{accentColor:P}} checked={selected.has(t.id)} onChange={()=>toggleSelect(t.id)}/>}
                </td>
                <td style={{padding:"10px 13px",color:P,fontWeight:600,fontSize:13}}>{t.id}</td>
                <td style={{padding:"10px 13px"}}><Badge label={t.lang} color="purple"/></td>
                <td style={{padding:"10px 13px"}}><AssigneeCell assignee={t.assignee} assigneeType={t.assigneeType}/></td>
                <td style={{padding:"10px 13px"}}>{t.ready?<Badge label="Ready" color="green"/>:<Badge label="In progress" color="gray"/>}</td>
                <td style={{padding:"10px 13px",fontSize:12,color:TEXT_MUTED}}>{t.since}</td>
              </tr>
            )):<tr><td colSpan={6} style={{padding:"28px",textAlign:"center",fontSize:13,color:TEXT_SUBTLE}}>No tasks match this view.</td></tr>}</tbody>
          </table>
        </div>
      </div>
    </div>);
  }

  // Detail main view
  const st=statusStyle[flow.status]||statusStyle.draft;
  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:14,padding:0}}>← Back to Orchestration</button>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:16,flexWrap:"wrap"}}>
      <div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
          <h1 style={{fontSize:21,fontWeight:600,margin:0}}>{flow.name}</h1>
          <span style={{background:st.bg,color:st.c,fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:999}}>{st.label}</span>
        </div>
        <div style={{fontSize:13,color:TEXT_MUTED}}>{project?.name||<em style={{color:TEXT_SUBTLE,fontStyle:"italic"}}>Unattached</em>} · {flow.includedStages.length} of {ORCH_STAGES.length} stages · Created {flow.createdDate}</div>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={onEdit} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Edit Flow</button>
        {flow.status==="active"&&<button onClick={onToggleStatus} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${WARNING_BORDER}`,background:WARNING_SURFACE,color:WARNING,fontSize:13,cursor:"pointer"}}>Pause</button>}
        {flow.status==="paused"&&<button onClick={onToggleStatus} style={{padding:"7px 14px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Resume</button>}
      </div>
    </div>
    <p style={{fontSize:13,color:TEXT_MUTED,margin:"14px 0 22px",maxWidth:760,lineHeight:1.5}}>
      Live pipeline for this flow. Click a stage to view its tasks; click "Edit Flow" to change routing or gating rules.
    </p>

    {/* Flow visualization */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:14,padding:"24px 20px",marginBottom:22,overflowX:"auto"}}>
      <div style={{display:"flex",alignItems:"stretch",minWidth:stagesInFlow.length*150+pairs.length*160}}>
        {stagesInFlow.map((s,i)=>{
          const isLast=i===stagesInFlow.length-1;
          const n=stageCounts[s.key];
          const ready=readyCounts[s.key];
          const transition=pairs[i];
          const tConfig=transition?flow.transitions[transition.key]:null;
          const gate=tConfig?describeGate(tConfig.gate):null;

          return(<div key={s.key} style={{display:"contents"}}>
            <div style={{flex:"1 1 0",minWidth:150,display:"flex",flexDirection:"column",alignItems:"stretch",padding:"0 4px"}}>
              <div onClick={()=>openStage(s.key,false)} style={{
                width:"100%",background:s.bg,border:`2px solid ${s.ring}`,borderRadius:12,padding:"14px 10px",textAlign:"center",
                cursor:"pointer",minHeight:90,display:"flex",flexDirection:"column",justifyContent:"center"
              }}>
                <div style={{fontSize:28,fontWeight:700,color:s.text,lineHeight:1,marginBottom:4}}>{n}</div>
                <div style={{fontSize:11,fontWeight:600,color:s.text,opacity:0.85}}>{s.label}</div>
              </div>
              <div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:4,textAlign:"center"}}>{n===1?"task":"tasks"} at stage{ready>0?` · ${ready} ready`:""}</div>
            </div>

            {!isLast&&tConfig&&(
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-start",paddingTop:8,flexShrink:0,width:160}}>
                <div style={{width:"100%",background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"8px 10px",display:"flex",flexDirection:"column",alignItems:"center",gap:5,marginBottom:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:4,padding:"2px 8px",background:gate.bg,color:gate.color,borderRadius:999,fontSize:11,fontWeight:600}}>
                    <span>{gate.text}</span>
                  </div>
                  {transition.to!=="complete"&&(
                    <div style={{fontSize:11,color:TEXT_SECONDARY,textAlign:"center",lineHeight:1.3}}>
                      <span>{describeRouting(tConfig.routing.default)}</span>
                    </div>
                  )}
                  {tConfig.routing.overrides.length>0&&(
                    <div style={{fontSize:10,color:P,fontWeight:600,background:PL,padding:"1px 6px",borderRadius:999}}>+{tConfig.routing.overrides.length} routing override{tConfig.routing.overrides.length>1?"s":""}</div>
                  )}
                </div>
                <svg width="100%" height="14" viewBox="0 0 100 14" preserveAspectRatio="none" style={{display:"block"}}>
                  <defs><marker id={`arrow-${s.key}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill={TEXT_SUBTLE}/></marker></defs>
                  <line x1="0" y1="7" x2="92" y2="7" stroke={TEXT_SUBTLE} strokeWidth="1.5" markerEnd={`url(#arrow-${s.key})`}/>
                </svg>
                {ready>0&&tConfig.gate.mode!=="auto"&&(
                  <button onClick={()=>openStage(s.key,true)} style={{marginTop:6,padding:"3px 10px",borderRadius:999,border:`1px solid ${P}`,background:PL,color:P,fontSize:10,fontWeight:600,cursor:"pointer"}}>
                    {ready} waiting →
                  </button>
                )}
              </div>
            )}
          </div>);
        })}
      </div>
    </div>

    {/* Summary */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:14}}>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:4}}>PIPELINE SUMMARY</div>
        <div style={{fontSize:13,color:TEXT_SECONDARY}}>
          <strong>{projectTasks.length}</strong> total tasks in flight ·{" "}
          <strong style={{color:P}}>{stagesInFlow.slice(0,-1).reduce((sum,s)=>sum+readyCounts[s.key],0)}</strong> paused or sampled ·{" "}
          <strong style={{color:SUCCESS}}>{stageCounts.complete||0}</strong> complete
        </div>
      </div>
      <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
        {[{icon:"H",label:"Human",c:P,bg:PL},{icon:"M",label:"Machine",c:INFO,bg:INFO_BG},{icon:"V",label:"Vendor",c:SLATE,bg:SLATE_BG},{icon:"P",label:"Pool",c:TEXT_MUTED,bg:SURFACE_HOVER}].map(l=>(
          <div key={l.label} style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{width:18,height:18,borderRadius:"50%",background:l.bg,color:l.c,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9}}>{l.icon}</span>
            <span style={{fontSize:11,color:TEXT_MUTED}}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>);
}

// ── Main Orchestration screen (routes between list, create, edit, detail) ──
// ── Error boundary so runtime errors in the flow builder surface on screen
//     instead of silently blanking the artifact ──
class OrchErrorBoundary extends Component{
  constructor(p){super(p);this.state={err:null};}
  static getDerivedStateFromError(err){return{err};}
  componentDidCatch(err,info){console.error("Orchestration error:",err,info);}
  render(){
    if(this.state.err){
      return(<div style={{flex:1,padding:"32px 28px",overflowY:"auto",background:SURFACE}}>
        <h1 style={{fontSize:20,fontWeight:600,color:DANGER,marginBottom:8}}>Orchestration crashed</h1>
        <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:14}}>Something threw during render. The message is below — copy-paste it back so we can fix it.</p>
        <pre style={{background:"#fef2f2",border:`1px solid ${DANGER_BORDER}`,borderRadius:8,padding:14,fontSize:12,color:"#991b1b",whiteSpace:"pre-wrap",wordBreak:"break-word",fontFamily:"monospace",marginBottom:12}}>{String(this.state.err&&(this.state.err.stack||this.state.err.message||this.state.err))}</pre>
        <button onClick={()=>this.setState({err:null})} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Try again</button>
      </div>);
    }
    return this.props.children;
  }
}

function OrchestrationScreen(){
  return(<OrchErrorBoundary><OrchestrationScreenInner/></OrchErrorBoundary>);
}

function OrchestrationScreenInner(){
  const[flows,setFlows]=useState(initialFlows);
  const[tasks,setTasks]=useState(initialOrchTasks);
  const[view,setView]=useState({type:"list"});

  const projectsWithoutFlow=orchProjects.filter(p=>!flows.some(f=>f.projectId===p.id));
  const selectedFlow=(view.type==="edit"||view.type==="detail")?flows.find(f=>f.id===view.flowId):null;
  const sourceFlow=view.type==="create"&&view.sourceFlowId?flows.find(f=>f.id===view.sourceFlowId):null;

  const saveFlow=data=>{
    if(view.type==="edit"){
      setFlows(prev=>prev.map(f=>f.id===view.flowId?{...f,...data}:f));
    }else{
      const newFlow={...data,id:`flow${Date.now()}`,createdDate:"Today"};
      setFlows(prev=>[...prev,newFlow]);
    }
    setView({type:"list"});
  };
  const deleteFlow=flowId=>setFlows(prev=>prev.filter(f=>f.id!==flowId));
  const toggleFlowStatus=flowId=>setFlows(prev=>prev.map(f=>f.id===flowId?{...f,status:f.status==="active"?"paused":"active"}:f));
  const cloneFlow=f=>setView({type:"create",sourceFlowId:f.id});

  if(view.type==="list")return <FlowListView flows={flows} projects={orchProjects} onCreate={()=>setView({type:"create"})} onEdit={f=>setView({type:"edit",flowId:f.id})} onView={f=>setView({type:"detail",flowId:f.id})} onClone={cloneFlow} onDelete={deleteFlow} onToggleStatus={toggleFlowStatus}/>;
  if(view.type==="create")return <FlowBuilderView sourceFlow={sourceFlow} projectsAvailable={projectsWithoutFlow} onCancel={()=>setView({type:"list"})} onSave={saveFlow}/>;
  if(view.type==="edit"&&selectedFlow)return <FlowBuilderView existingFlow={selectedFlow} projectsAvailable={orchProjects} onCancel={()=>setView({type:"list"})} onSave={saveFlow}/>;
  if(view.type==="detail"&&selectedFlow)return <FlowDetailView flow={selectedFlow} projects={orchProjects} tasks={tasks} setTasks={setTasks} onBack={()=>setView({type:"list"})} onEdit={()=>setView({type:"edit",flowId:selectedFlow.id})} onToggleStatus={()=>toggleFlowStatus(selectedFlow.id)}/>;
  return null;
}

// ── Project detail: Flow card on Overview ──
// Collapsed by default. Expand to show full DAG diagram (reuses WizDagView).
function ProjectFlowCard({flow,languages}){
  const[expanded,setExpanded]=useState(false);
  const stageCount=flow.includedStages.length;
  const pathCount=Object.values(flow.exits||{}).reduce((s,p)=>s+p.length,0);
  return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,marginBottom:20,overflow:"hidden"}}>
    <div onClick={()=>setExpanded(!expanded)} style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
      <div>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:4}}>ATTACHED FLOW</div>
        <div style={{fontSize:14,fontWeight:600,color:TEXT_PRIMARY}}>{flow.name}</div>
        <div style={{fontSize:11,color:TEXT_MUTED,marginTop:2}}>{stageCount} stages · {pathCount} path{pathCount!==1?"s":""}</div>
      </div>
      <button style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:7,padding:"6px 14px",color:TEXT_SECONDARY,fontSize:12,fontWeight:500,cursor:"pointer"}}>
        {expanded?"Collapse":"Expand to see diagram"}
      </button>
    </div>
    {expanded&&(
      <div style={{padding:"0 18px 18px"}}>
        <WizDagView includedStages={flow.includedStages} exits={flow.exits||{}} allLangs={languages}/>
      </div>
    )}
  </div>);
}

// ── Project detail: Engagements tab ─────────────────────────────────────────
// One row per engagement (recording + downstream tasks). Expand a row to see
// task-level breakdown. Filterable by language and current stage.
function ProjectEngagementsTab({engagements,languages,initialLangFilter,initialStageFilter}){
  const[expandedId,setExpandedId]=useState(null);
  const[langFilter,setLangFilter]=useState(initialLangFilter||"all");
  const[stageFilter,setStageFilter]=useState(initialStageFilter||"all");

  // When the parent pre-sets filters (via clicking on Overview pipeline), apply them.
  useEffect(()=>{if(initialLangFilter)setLangFilter(initialLangFilter);},[initialLangFilter]);
  useEffect(()=>{if(initialStageFilter)setStageFilter(initialStageFilter);},[initialStageFilter]);

  const STAGE_LABEL={audioCreation:"Audio Creation",audioReview:"Audio Review",transcription:"Transcription",transcriptionReview:"Transcription Review",complete:"Complete"};
  const STAGE_COLOR={audioCreation:{bg:RED_TINT,text:"#991b1b"},audioReview:{bg:BRAND_TINT_2,text:"#6b21a8"},transcription:{bg:INFO_BORDER,text:INFO_DEEP},transcriptionReview:{bg:"#fef08a",text:WARNING_DEEP},complete:{bg:"#bbf7d0",text:"#14532d"}};
  const TASK_STATUS_COLOR={completed:{bg:SUCCESS_BG,c:SUCCESS,label:"Completed"},in_progress:{bg:INFO_BG,c:INFO,label:"In progress"},pending:{bg:SURFACE_HOVER,c:TEXT_MUTED,label:"Pending"}};

  const filtered=engagements.filter(e=>{
    if(langFilter!=="all"&&e.lang!==langFilter)return false;
    if(stageFilter!=="all"&&e.currentStage!==stageFilter)return false;
    return true;
  });

  const uniqueLangs=[...new Set(engagements.map(e=>e.lang))];
  const uniqueStages=[...new Set(engagements.map(e=>e.currentStage))];

  return(<div>
    {/* Filters */}
    <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <span style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.06em"}}>LANGUAGE:</span>
        <button onClick={()=>setLangFilter("all")} style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${langFilter==="all"?P:BORDER}`,background:langFilter==="all"?PL:SURFACE,color:langFilter==="all"?P:TEXT_SECONDARY,fontSize:12,fontWeight:langFilter==="all"?600:400,cursor:"pointer"}}>All</button>
        {uniqueLangs.map(l=>(
          <button key={l} onClick={()=>setLangFilter(l)} style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${langFilter===l?P:BORDER}`,background:langFilter===l?PL:SURFACE,color:langFilter===l?P:TEXT_SECONDARY,fontSize:12,fontWeight:langFilter===l?600:400,cursor:"pointer"}}>{l}</button>
        ))}
      </div>
      <div style={{width:1,height:20,background:BORDER}}/>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <span style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.06em"}}>STAGE:</span>
        <button onClick={()=>setStageFilter("all")} style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${stageFilter==="all"?P:BORDER}`,background:stageFilter==="all"?PL:SURFACE,color:stageFilter==="all"?P:TEXT_SECONDARY,fontSize:12,fontWeight:stageFilter==="all"?600:400,cursor:"pointer"}}>All</button>
        {uniqueStages.map(s=>(
          <button key={s} onClick={()=>setStageFilter(s)} style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${stageFilter===s?P:BORDER}`,background:stageFilter===s?PL:SURFACE,color:stageFilter===s?P:TEXT_SECONDARY,fontSize:12,fontWeight:stageFilter===s?600:400,cursor:"pointer"}}>{STAGE_LABEL[s]||s}</button>
        ))}
      </div>
    </div>

    {/* Engagements table */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${BORDER}`,background:SURFACE_ALT}}>
          <th style={{padding:"10px 14px",width:30}}></th>
          {["ID","Language","Topic","Duration","Current Stage","Progress","Created"].map(h=>(
            <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {filtered.length===0?(
            <tr><td colSpan={8} style={{padding:"24px",textAlign:"center",fontSize:13,color:TEXT_SUBTLE}}>No engagements match the current filters.</td></tr>
          ):filtered.map(e=>{
            const isExpanded=expandedId===e.id;
            const stageColor=STAGE_COLOR[e.currentStage]||STAGE_COLOR.audioCreation;
            const completedTasks=e.tasks.filter(t=>t.status==="completed").length;
            const totalTasks=e.tasks.length;
            return(<Fragment key={e.id}>
              <tr onClick={()=>setExpandedId(isExpanded?null:e.id)} style={{borderBottom:`1px solid ${SURFACE_HOVER}`,cursor:"pointer",background:isExpanded?SURFACE_SUNKEN:SURFACE}}>
                <td style={{padding:"10px 14px",color:TEXT_SUBTLE,fontSize:11}}>{isExpanded?"▼":"▶"}</td>
                <td style={{padding:"10px 14px",fontSize:13,color:P,fontWeight:600}}>{e.id}</td>
                <td style={{padding:"10px 14px"}}><Badge label={e.lang} color="purple"/></td>
                <td style={{padding:"10px 14px",fontSize:12,color:TEXT_SECONDARY}}>{e.topic}</td>
                <td style={{padding:"10px 14px",fontSize:12,color:TEXT_MUTED}}>{e.durationMins?`${e.durationMins} min`:"—"}</td>
                <td style={{padding:"10px 14px"}}>
                  <span style={{padding:"3px 9px",borderRadius:5,background:stageColor.bg,color:stageColor.text,fontSize:11,fontWeight:600}}>{STAGE_LABEL[e.currentStage]}</span>
                </td>
                <td style={{padding:"10px 14px",fontSize:12,color:TEXT_MUTED}}>{completedTasks}/{totalTasks} tasks</td>
                <td style={{padding:"10px 14px",fontSize:12,color:TEXT_MUTED}}>{e.createdAt}</td>
              </tr>
              {isExpanded&&(
                <tr style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:SURFACE_SUNKEN}}>
                  <td colSpan={8} style={{padding:"14px 28px"}}>
                    <div style={{display:"flex",gap:20,marginBottom:12,fontSize:12,color:TEXT_MUTED}}>
                      <span><strong style={{color:TEXT_SECONDARY}}>Participants:</strong> {(e.participants||[]).length>0?e.participants.join(", "):"—"}</span>
                    </div>
                    <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.06em",marginBottom:8}}>TASK BREAKDOWN</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {e.tasks.map((t,idx)=>{
                        const stage=STAGE_COLOR[t.stage]||STAGE_COLOR.audioCreation;
                        const status=TASK_STATUS_COLOR[t.status]||TASK_STATUS_COLOR.pending;
                        const isComplete=t.status==="completed";
                        return(<div key={idx} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:SURFACE,borderRadius:8,border:`1px solid ${BORDER}`,flexWrap:"wrap"}}>
                          <span style={{padding:"3px 8px",borderRadius:5,background:stage.bg,color:stage.text,fontSize:11,fontWeight:600,minWidth:130,textAlign:"center"}}>{STAGE_LABEL[t.stage]}</span>
                          <span style={{padding:"2px 8px",borderRadius:999,background:status.bg,color:status.c,fontSize:11,fontWeight:600}}>{status.label}</span>
                          <span style={{fontSize:12,color:TEXT_SECONDARY,flex:1,minWidth:120}}>{t.assignee||<em style={{color:TEXT_SUBTLE}}>unassigned</em>}</span>
                          {isComplete&&t.qa&&(
                            <span style={{padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700,background:t.qa==="passed"?SUCCESS_BG:t.qa==="flagged"?WARNING_BG:DANGER_BG,color:t.qa==="passed"?SUCCESS:t.qa==="flagged"?WARNING:DANGER}}>QA: {t.qa}</span>
                          )}
                          {isComplete&&t.paid!==undefined&&(
                            <span style={{padding:"2px 8px",borderRadius:999,fontSize:10,fontWeight:700,background:t.paid?SUCCESS_BG:SURFACE_HOVER,color:t.paid?SUCCESS:TEXT_MUTED}}>{t.paid?"Paid":"Unpaid"}</span>
                          )}
                          <span style={{fontSize:11,color:TEXT_SUBTLE}}>
                            {t.completedAt?`Completed ${t.completedAt}`:t.startedAt?`Started ${t.startedAt}`:""}
                          </span>
                        </div>);
                      })}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>);
          })}
        </tbody>
      </table>
    </div>
  </div>);
}

// ── Project detail: Activity tab ────────────────────────────────────────────
// Chronological log of events on the project. Grouped visually by date.
// ── Project detail: Recorders tab ───────────────────────────────────────────
// Aggregates participants from engagements into a per-recorder, per-language table
// showing total hours toward each contributor cap.
function ProjectRecordersTab({engagements,languages}){
  // Build {recorder: {lang: hours}} from completed audio creation tasks
  const totals={};
  engagements.forEach(e=>{
    const audioTask=e.tasks.find(t=>t.stage==="audioCreation"&&t.status==="completed");
    if(!audioTask)return;
    const hours=(e.durationMins||0)/60;
    (e.participants||[]).forEach(p=>{
      if(!totals[p])totals[p]={};
      if(!totals[p][e.lang])totals[p][e.lang]=0;
      totals[p][e.lang]+=hours;
    });
  });

  const langMax={};
  (languages||[]).forEach(l=>{langMax[l.lang]=l.maxPerContributor||null;});

  const rows=Object.entries(totals).map(([name,byLang])=>{
    const totalHours=Object.values(byLang).reduce((s,h)=>s+h,0);
    return{name,byLang,totalHours};
  }).sort((a,b)=>b.totalHours-a.totalHours);

  if(rows.length===0){
    return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"40px 20px",textAlign:"center",fontSize:13,color:TEXT_MUTED}}>
      No recorders yet. Hours will appear here once recordings start being completed.
    </div>);
  }

  // All languages that appear across all rows, ordered by project's language list
  const seenLangs=new Set();
  rows.forEach(r=>Object.keys(r.byLang).forEach(l=>seenLangs.add(l)));
  const orderedLangs=(languages||[]).map(l=>l.lang).filter(l=>seenLangs.has(l));
  // Add any langs not in the project list (defensive)
  Array.from(seenLangs).forEach(l=>{if(!orderedLangs.includes(l))orderedLangs.push(l);});

  return(<div>
    <div style={{fontSize:12,color:TEXT_MUTED,marginBottom:12}}>{rows.length} recorder{rows.length!==1?"s":""} — hours from completed recordings, with per-language contributor cap progress.</div>
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${BORDER}`,background:SURFACE_ALT}}>
          <th style={{padding:"10px 14px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE,minWidth:200}}>Recorder</th>
          {orderedLangs.map(l=>(
            <th key={l} style={{padding:"10px 14px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>
              {l}
              {langMax[l]!=null&&<span style={{fontWeight:400,color:TEXT_MUTED}}> · cap {langMax[l]}h</span>}
            </th>
          ))}
          <th style={{padding:"10px 14px",textAlign:"right",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>Total</th>
        </tr></thead>
        <tbody>
          {rows.map(r=>{
            const anyAtCap=Object.entries(r.byLang).some(([lang,h])=>{
              const max=langMax[lang];
              return max&&h>=max;
            });
            return(<tr key={r.name} style={{borderBottom:`1px solid ${SURFACE_HOVER}`,background:anyAtCap?WARNING_SURFACE:SURFACE}}>
              <td style={{padding:"14px 14px",verticalAlign:"middle"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:13,color:TEXT_PRIMARY,fontWeight:500}}>{r.name}</span>
                  {anyAtCap&&<span style={{padding:"2px 8px",borderRadius:999,background:WARNING_ACCENT_SURFACE,color:WARNING_ACCENT,fontSize:10,fontWeight:700,letterSpacing:"0.06em"}}>AT LIMIT</span>}
                </div>
              </td>
              {orderedLangs.map(l=>{
                const h=r.byLang[l]||0;
                if(h===0)return(<td key={l} style={{padding:"14px 14px",fontSize:12,color:TEXT_DISABLED,verticalAlign:"middle"}}>—</td>);
                const max=langMax[l];
                const pct=max?Math.min(100,h/max*100):null;
                const atCap=max&&h>=max;
                return(<td key={l} style={{padding:"12px 14px",minWidth:170,verticalAlign:"middle"}}>
                  <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:max?4:0}}>
                    <span style={{fontSize:13,fontWeight:600,color:atCap?WARNING_ACCENT:TEXT_PRIMARY}}>{h.toFixed(2)}h</span>
                    {max&&<span style={{fontSize:11,color:atCap?WARNING_ACCENT:TEXT_SUBTLE,fontWeight:atCap?700:400}}>{Math.round(pct)}%{atCap?" · at cap":""}</span>}
                  </div>
                  {max&&(
                    <div style={{height:7,background:BORDER,borderRadius:999,overflow:"hidden"}}>
                      <div style={{width:`${pct}%`,height:"100%",background:atCap?WARNING_ACCENT:P,borderRadius:999,transition:"width 0.3s"}}/>
                    </div>
                  )}
                </td>);
              })}
              <td style={{padding:"14px 14px",textAlign:"right",fontSize:13,fontWeight:600,color:TEXT_PRIMARY,verticalAlign:"middle"}}>{r.totalHours.toFixed(2)}h</td>
            </tr>);
          })}
        </tbody>
      </table>
    </div>
  </div>);
}

function ProjectActivityTab({activity}){
  if(activity.length===0){
    return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"40px 20px",textAlign:"center",fontSize:13,color:TEXT_MUTED}}>
      No activity yet.
    </div>);
  }

  const TYPE_STYLE={
    project_created:{label:"Project Created",bg:BRAND_TINT_2,c:"#6b21a8"},
    project_launched:{label:"Project Launched",bg:BRAND_TINT_2,c:"#6b21a8"},
    engagement_created:{label:"Engagement Created",bg:INFO_BG,c:INFO},
    engagement_complete:{label:"Engagement Complete",bg:SUCCESS_BG,c:SUCCESS},
    task_started:{label:"Task Started",bg:INFO_BG,c:INFO},
    task_completed:{label:"Task Completed",bg:SUCCESS_BG,c:SUCCESS},
    task_assigned:{label:"Task Assigned",bg:WARNING_BG,c:WARNING},
    task_auto:{label:"Auto-completed",bg:SLATE_BG,c:SLATE},
  };

  return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"18px 22px",maxWidth:900}}>
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {activity.map((ev,i)=>{
        const style=TYPE_STYLE[ev.type]||{label:ev.type,bg:SURFACE_HOVER,c:TEXT_MUTED};
        return(<div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",paddingBottom:10,borderBottom:i<activity.length-1?`1px solid ${BORDER_SUBTLE}`:"none"}}>
          <div style={{fontSize:11,color:TEXT_SUBTLE,width:110,flexShrink:0,paddingTop:2}}>{ev.at}</div>
          <div style={{minWidth:130,flexShrink:0}}>
            <span style={{padding:"2px 8px",borderRadius:5,background:style.bg,color:style.c,fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>{style.label}</span>
          </div>
          <div style={{flex:1,fontSize:13,color:TEXT_SECONDARY,lineHeight:1.5}}>
            <strong style={{color:TEXT_PRIMARY,fontWeight:600}}>{ev.actor}</strong> {ev.desc}
          </div>
        </div>);
      })}
    </div>
  </div>);
}

function OpsProjectsScreen({recordingBatches}){
  const[projects,setProjects]=useState(initialOpsProjectsList);
  const[showNew,setShowNew]=useState(false);
  const[detail,setDetail]=useState(null);
  const allBatches={recording:recordingBatches||initialOpsProjects,reviewer:reviewBatches};

  const getBatch=id=>{
    const r=allBatches.recording.find(b=>b.id===id);
    if(r)return{...r,kind:"recording"};
    const v=allBatches.reviewer.find(b=>b.id===id);
    if(v)return{...v,kind:"reviewer"};
    return null;
  };

  const[detailTab,setDetailTab]=useState("overview");
  const[engagementsInit,setEngagementsInit]=useState({lang:null,stage:null});
  // Helper used by the Overview pipeline card to drill into a specific (lang, stage) cell
  const drillIntoEngagements=(lang,stage)=>{
    setEngagementsInit({lang,stage});
    setDetailTab("engagements");
  };

  if(detail){
    const proj=detail;
    const linkedBatches=proj.batchIds.map(getBatch).filter(Boolean);
    const engagements=sampleEngagements[proj.id]||[];
    const activity=sampleActivity[proj.id]||[];

    return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
      <button onClick={()=>{setDetail(null);setDetailTab("overview");}} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",marginBottom:18,padding:0}}>← Back to Projects</button>
      <h1 style={{fontSize:21,fontWeight:600,marginBottom:4}}>{proj.name}</h1>
      <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:18,maxWidth:600}}>{proj.description}</p>
      <div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:16}}>Created {proj.createdAt} · {proj.batchIds.length} linked batch{proj.batchIds.length!==1?"es":""}</div>

      {/* Tab strip */}
      <div style={{display:"flex",gap:4,borderBottom:`1px solid ${BORDER}`,marginBottom:20}}>
        {[
          {k:"overview",l:"Overview"},
          {k:"engagements",l:`Engagements (${engagements.length})`},
          {k:"recorders",l:"Recorders"},
        ].map(t=>{
          const active=detailTab===t.k;
          return(<button key={t.k} onClick={()=>setDetailTab(t.k)} style={{
            padding:"10px 16px",
            border:"none",
            borderBottom:`3px solid ${active?P:"transparent"}`,
            marginBottom:-1,
            background:"transparent",
            color:active?P:TEXT_SECONDARY,
            fontSize:13,
            fontWeight:active?600:500,
            cursor:"pointer",
            whiteSpace:"nowrap",
          }}>{t.l}</button>);
        })}
      </div>

      {detailTab==="overview"&&(<>
      {/* Attach Flow banner — shown when project has no flow attached */}
      {(!proj.flowId||proj.flowMode==="skip")&&(
        <div style={{background:WARNING_SURFACE,border:`1px solid ${WARNING_BORDER}`,borderRadius:12,padding:"14px 18px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:WARNING_DEEP,marginBottom:3}}>This project is in Draft</div>
            <div style={{fontSize:12,color:WARNING_DEEP,opacity:0.85,lineHeight:1.5,maxWidth:560}}>No flow is attached yet. Attach one to start auto-generating batches and engagements. The project will move from Draft to Active.</div>
          </div>
          <button onClick={()=>{/* stub: would open flow editor */}} style={{padding:"8px 18px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>Attach Flow →</button>
        </div>
      )}
      {/* Hours by Language — moved above pipeline since it's more important */}
      {proj.languages&&proj.languages.length>0&&(()=>{
        const totalGoal=proj.languages.reduce((s,l)=>s+(l.hoursGoal||0),0);
        const totalRec=proj.languages.reduce((s,l)=>s+(l.hoursRecorded||0),0);
        const overall=totalGoal>0?Math.round(totalRec/totalGoal*100):0;
        return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"18px 20px",marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
            <span style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>HOURS BY LANGUAGE</span>
            <span style={{fontSize:12,color:TEXT_MUTED}}>{totalRec.toFixed(1)} of {totalGoal}h recorded · {overall}%</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {proj.languages.map(l=>{
              const pct=l.hoursGoal>0?Math.min(100,(l.hoursRecorded||0)/l.hoursGoal*100):0;
              const complete=(l.hoursRecorded||0)>=l.hoursGoal;
              return(<div key={l.lang}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:13,fontWeight:600,color:TEXT_PRIMARY}}>{l.lang}</span>
                    <span style={{fontSize:11,fontWeight:600,color:complete?SUCCESS:P}}>{Math.round(pct)}%</span>
                  </div>
                  <span style={{fontSize:12,color:TEXT_MUTED}}>{(l.hoursRecorded||0).toFixed(1)}h / {l.hoursGoal}h</span>
                </div>
                <div style={{height:7,background:BORDER,borderRadius:999,overflow:"hidden"}}>
                  <div style={{width:`${pct}%`,height:"100%",background:complete?SUCCESS:P,borderRadius:999,transition:"width 0.3s"}}/>
                </div>
              </div>);
            })}
          </div>
        </div>);
      })()}

      {/* Engagements Pipeline — by language only, with per-stage counts + shared legend */}
      {proj.languages&&proj.languages.some(l=>l.pipeline)&&(()=>{
        const STAGE_DEFS=[
          {key:"notStarted",         label:"Not Started",          bg:BORDER, text:TEXT_MUTED},
          {key:"audioCreation",      label:"Audio Creation",       bg:RED_TINT, text:"#991b1b"},
          {key:"audioReview",        label:"Audio Review",         bg:BRAND_TINT_2, text:"#6b21a8"},
          {key:"transcription",      label:"Transcription",        bg:INFO_BORDER, text:INFO_DEEP},
          {key:"transcriptionReview",label:"Transcription Review", bg:"#fef08a", text:WARNING_DEEP},
          {key:"complete",           label:"Complete",             bg:"#bbf7d0", text:"#14532d"},
        ];
        const buildStages=pl=>{
          const inFlight=pl.audioCreation+pl.audioReview+pl.transcription+pl.transcriptionReview+pl.complete;
          const unstarted=Math.max(0,pl.total-inFlight);
          return STAGE_DEFS.map(d=>({...d,n:d.key==="notStarted"?unstarted:pl[d.key]}));
        };
        const PipelineBar=({pl,height=16,onSegmentClick})=>{
          const stages=buildStages(pl);
          return(<div style={{display:"flex",height,borderRadius:6,overflow:"hidden",gap:1}}>
            {stages.filter(s=>s.n>0).map(s=>(
              <div key={s.key} onClick={onSegmentClick&&s.key!=="notStarted"?()=>onSegmentClick(s.key):undefined} style={{flex:s.n,background:s.bg,minWidth:s.n>0?3:0,cursor:onSegmentClick&&s.key!=="notStarted"?"pointer":"default"}} title={`${s.label}: ${s.n}${onSegmentClick&&s.key!=="notStarted"?" — click to view":""}`}/>
            ))}
          </div>);
        };
        const pl=proj.pipeline;
        const totalComplete=proj.languages.reduce((s,l)=>s+(l.pipeline?.complete||0),0);
        const totalEngagements=proj.languages.reduce((s,l)=>s+(l.pipeline?.total||0),0);

        return(<div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"18px 20px",marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
            <span style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>ENGAGEMENTS PIPELINE</span>
            <span style={{fontSize:12,color:TEXT_MUTED}}>{totalComplete} of {totalEngagements} complete · {totalEngagements>0?Math.round(totalComplete/totalEngagements*100):0}%</span>
          </div>

          {/* Shared legend */}
          <div style={{display:"flex",flexWrap:"wrap",gap:"6px 14px",marginBottom:16,paddingBottom:14,borderBottom:`1px solid ${BORDER_SUBTLE}`}}>
            {STAGE_DEFS.map(s=>(
              <div key={s.key} style={{display:"flex",alignItems:"center",gap:5}}>
                <div style={{width:10,height:10,borderRadius:2,background:s.bg,border:"1px solid rgba(0,0,0,0.08)",flexShrink:0}}/>
                <span style={{fontSize:11,color:TEXT_SECONDARY}}>{s.label}</span>
              </div>
            ))}
          </div>

          <div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:10,fontStyle:"italic"}}>Click any stage segment or count to view those engagements.</div>

          {/* Per-language rows with stage counts */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {proj.languages.filter(l=>l.pipeline).map(l=>{
              const lp=l.pipeline;
              const stages=buildStages(lp);
              const pct=lp.total>0?Math.round(lp.complete/lp.total*100):0;
              return(<div key={l.lang}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                  <span style={{fontSize:13,fontWeight:600,color:TEXT_PRIMARY}}>{l.lang}</span>
                  <span style={{fontSize:11,color:TEXT_MUTED}}>{lp.complete}/{lp.total} complete · {pct}%</span>
                </div>
                <PipelineBar pl={lp} height={16} onSegmentClick={stage=>drillIntoEngagements(l.lang,stage)}/>
                <div style={{display:"flex",flexWrap:"wrap",gap:"4px 14px",marginTop:6}}>
                  {stages.filter(s=>s.n>0).map(s=>{
                    const isClickable=s.key!=="notStarted";
                    return(<div key={s.key} onClick={isClickable?()=>drillIntoEngagements(l.lang,s.key):undefined} style={{display:"flex",alignItems:"center",gap:4,cursor:isClickable?"pointer":"default",padding:"2px 4px",borderRadius:4}} onMouseEnter={isClickable?(e=>e.currentTarget.style.background=SURFACE_HOVER):undefined} onMouseLeave={isClickable?(e=>e.currentTarget.style.background="transparent"):undefined}>
                      <div style={{width:8,height:8,borderRadius:2,background:s.bg,flexShrink:0}}/>
                      <span style={{fontSize:11,color:TEXT_MUTED}}>{s.label}:</span>
                      <span style={{fontSize:11,fontWeight:700,color:isClickable?P:TEXT_PRIMARY}}>{s.n}</span>
                    </div>);
                  })}
                </div>
              </div>);
            })}
          </div>
        </div>);
      })()}

      {/* Flow diagram — collapsed by default, expand to see DAG */}
      {(()=>{
        const flow=sampleProjectFlows[proj.id];
        if(!flow)return null;
        return(<ProjectFlowCard flow={flow} languages={(proj.languages||[]).map(l=>l.lang)}/>);
      })()}

      <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>LINKED BATCHES</div>
      <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:680}}>
        {linkedBatches.map(b=>{
          const isRec=b.kind==="recording";
          const pct=isRec?Math.round((b.hoursUsed||0)/b.hours*100):Math.round(b.done/b.tasks*100);
          const complete=isRec?(b.hoursUsed||0)>=b.hours:b.done>=b.tasks;
          return(<div key={b.id} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"16px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div>
                <div style={{fontWeight:500,fontSize:13,marginBottom:5}}>{b.title||b.name}</div>
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  <Badge label={isRec?"Recording Batch":"Reviewer Batch"} color={isRec?"teal":"purple"}/>
                  {isRec&&<Badge label={b.status} color={b.status==="Active"?"green":b.status==="Full"?"red":"gray"}/>}
                  {!isRec&&<Badge label={b.type} color="blue"/>}
                  {!isRec&&<Badge label={b.lang} color="purple"/>}
                </div>
              </div>
              <div style={{fontSize:11,color:TEXT_SUBTLE,textAlign:"right",whiteSpace:"nowrap",marginLeft:12}}>
                <div style={{fontWeight:600,fontSize:13,color:complete?SUCCESS:P}}>{pct}%</div>
                <div>Deadline: {b.deadline}</div>
              </div>
            </div>
            {/* Progress bar */}
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1,height:7,background:BORDER,borderRadius:999,overflow:"hidden"}}>
                <div style={{width:`${pct}%`,height:"100%",background:complete?SUCCESS:P,borderRadius:999,transition:"width 0.3s"}}/>
              </div>
              <span style={{fontSize:12,color:TEXT_MUTED,whiteSpace:"nowrap"}}>
                {isRec?`${b.hoursUsed||0}h / ${b.hours}h`:`${b.done} / ${b.tasks} tasks`}
              </span>
            </div>
          </div>);
        })}
        {linkedBatches.length===0&&<div style={{fontSize:13,color:TEXT_SUBTLE,padding:"20px",textAlign:"center",background:SURFACE_SUNKEN,borderRadius:10}}>No batches linked yet.</div>}
      </div>
      </>)}

      {detailTab==="engagements"&&(<ProjectEngagementsTab engagements={engagements} languages={proj.languages||[]} initialLangFilter={engagementsInit.lang} initialStageFilter={engagementsInit.stage}/>)}

      {detailTab==="recorders"&&(<ProjectRecordersTab engagements={engagements} languages={proj.languages||[]}/>)}
    </div>);
  }

  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    {showNew&&<NewProjectWizard onClose={()=>setShowNew(false)} onCreate={p=>{setProjects(prev=>[...prev,p]);setShowNew(false);}} existingFlows={initialFlows}/>}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
      <h1 style={{fontSize:21,fontWeight:600,margin:0}}>Projects</h1>
      <button onClick={()=>setShowNew(true)} style={{padding:"8px 14px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>+ New Project</button>
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {projects.map(p=>{
        const batches=p.batchIds.map(getBatch).filter(Boolean);
        return(<div key={p.id} onClick={()=>setDetail(p)} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"16px 18px",cursor:"pointer",transition:"box-shadow 0.15s"}} onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 14px rgba(0,0,0,0.07)"} onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>{p.name}</div>
              <p style={{fontSize:12,color:TEXT_MUTED,margin:"0 0 10px",lineHeight:1.5,maxWidth:520}}>{p.description}</p>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {batches.map(b=>(
                  <span key={b.id} style={{fontSize:11,background:SURFACE_HOVER,color:TEXT_SECONDARY,padding:"2px 8px",borderRadius:5,fontWeight:500}}>{b.title||b.name}</span>
                ))}
              </div>
            </div>
            <div style={{fontSize:11,color:TEXT_SUBTLE,whiteSpace:"nowrap",marginLeft:16,textAlign:"right"}}>
              <div>{p.batchIds.length} batch{p.batchIds.length!==1?"es":""}</div>
              <div>{p.createdAt}</div>
            </div>
          </div>
        </div>);
      })}
    </div>
  </div>);
}

// ── REVIEWER SCREEN ───────────────────────────────────────────────────────────
const reviewTasks=[
  {id:"c185e1a1-dbfc-4c1c-ae29-c02c48888c01",type:"Full Transcript Review",project:"Spanish Conversational Audio – Batch 3",lang:"Spanish",batch:"Batch 3",deadline:"May 12",status:"Available"},
  {id:"64d93bb9-f9b8-4ad7-bce8-cc6f4046b216",type:"Audio Review",project:"Arabic Business Scripted",lang:"Arabic",expectedVariant:"Arabic (MSA)",batch:"Batch 1",deadline:"Jun 5",status:"Available"},
  {id:"a721fbe3-5e0d-4165-b02d-669ed84e2218",type:"Post-Model Audio Review",project:"Spanish Conversational Audio",lang:"Spanish",expectedVariant:"Spanish (Mexico)",batch:"Batch 5",deadline:"Jun 8",status:"Available"},
  {id:"fe003d80-85b7-4808-aa5c-b578aaafd673",type:"Transcript Creation",project:"Spanish Medical Training",lang:"Spanish",batch:"Batch 1",deadline:"May 25",status:"Available"},
  {id:"4ae58b32-3a12-4b6f-b462-338f6f40ad17",type:"Diarization Review",project:"French Medical Dialogues",lang:"French",batch:"Batch 2",deadline:"May 28",status:"Available"},
  {id:"d1ae3c1c-4f74-4099-82b5-913a1b1e1650",type:"Diarization Creation",project:"Arabic Business Scripted",lang:"Arabic",batch:"Batch 1",deadline:"Jun 10",status:"In Progress"},
  {id:"45d11cc2-318a-4cfb-92d1-0ae3ab550b8a",type:"NER/PII Recognition",project:"Spanish Medical Training",lang:"Spanish",batch:"Batch 2",deadline:"Jun 15",status:"Available"},
  {id:"9941e101-f8c3-4853-be8b-4ea756f21e13",type:"topic",project:"French Healthcare Dialogues",lang:"French",batch:"Batch 1",deadline:"Jun 12",status:"Available"},
  {id:"fe6e9cb2-351d-44b6-b4e0-7f1556cfd2c9",type:"sentiment",project:"Swahili Daily Conversations",lang:"Swahili",batch:"Batch 1",deadline:"Jun 18",status:"Available"},
  {id:"27c18599-201e-438f-bbbf-68c4d60aecf8",type:"topic + sentiment",project:"Spanish Medical Training",lang:"Spanish",batch:"Batch 3",deadline:"Jun 22",status:"Available"},
  {id:"0820e79e-359d-4602-ab4d-d7c6c3d37882",type:"Full Transcript Review",project:"Arabic Business Scripted",lang:"Arabic",batch:"Batch 2",deadline:"Jul 1",status:"In Progress"},
];

// QA reviewer's task list — all tasks are either Full Transcript Review QA or Transcript Creation QA.
// Each carries originalReviewerId/originalReviewer so the QA screen knows whose work to load.
const qaReviewTasks=[
  {id:"cb33ab3e-10be-4dec-bf41-095355a3a807",type:"Full Transcript Review QA",project:"Spanish Conversational Audio – Batch 3",lang:"Spanish",batch:"Batch 3",deadline:"May 13",status:"Available",originalReviewerId:"c185e1a1-dbfc-4c1c-ae29-c02c48888c01",originalReviewer:"Amara Osei"},
  {id:"65668d3b-8d1c-48d3-99db-ccba34c18319",type:"Transcript Creation QA",project:"Spanish Medical Training",lang:"Spanish",batch:"Batch 1",deadline:"May 26",status:"Available",originalReviewerId:"fe003d80-85b7-4808-aa5c-b578aaafd673",originalReviewer:"Kwame Boateng"},
  {id:"c5482628-3803-453e-9dff-55b142474697",type:"Full Transcript Review QA",project:"French Medical Dialogues",lang:"French",batch:"Batch 1",deadline:"May 1",status:"In Progress",originalReviewerId:"9499ae26-b7fa-44f6-86d4-5ca0edc04906",originalReviewer:"Amara Osei"},
  {id:"13c80456-adb5-4b91-9d69-70c15715054c",type:"Transcript Creation QA",project:"Arabic Business Scripted",lang:"Arabic",batch:"Batch 1",deadline:"Jun 11",status:"Available",originalReviewerId:"ab15d009-03dc-440c-adbd-17e38aee1720",originalReviewer:"Kwame Boateng"},
  {id:"c83251d2-a8b4-408a-9511-192fd0f2185b",type:"Full Transcript Review QA",project:"French Healthcare Dialogues",lang:"French",batch:"Batch 1",deadline:"Jun 13",status:"Available",originalReviewerId:"21bbb5a4-879f-408e-96b6-ee68cc055f21",originalReviewer:"Zara Mensah"},
  {id:"66e7a097-058a-431c-966e-3309557a03c6",type:"Transcript Creation QA",project:"Spanish Conversational Audio – Batch 3",lang:"Spanish",batch:"Batch 3",deadline:"May 14",status:"Available",originalReviewerId:"6c739cc0-5a92-4ed8-950d-698fc6f7f08f",originalReviewer:"Amara Osei"},
  {id:"47639bde-c8e1-41fe-9439-aff30a1ba2a4",type:"Full Transcript Review QA",project:"Swahili Daily Conversations",lang:"Swahili",batch:"Batch 1",deadline:"Jun 19",status:"Available",originalReviewerId:"210ae711-8142-4561-9ebd-2be2882135cc",originalReviewer:"Kwame Boateng"},
  {id:"ed4d68fd-8d2c-40e7-ae27-83334e4e20e5",type:"Transcript Creation QA",project:"Spanish Medical Training",lang:"Spanish",batch:"Batch 3",deadline:"Jun 23",status:"In Progress",originalReviewerId:"1b7d8d18-770c-41c2-8003-2a4fa4f33103",originalReviewer:"Amara Osei"},
];
// ── Transcript task screens: shared helpers ──────────────────────────────────
const SPEAKER_COLORS=[
  {bg:INFO_BG,c:INFO},  // blue
  {bg:SUCCESS_BG,c:SUCCESS},  // green
  {bg:WARNING_BG,c:WARNING},  // amber
  {bg:PINK_BG,c:PINK_DEEP},  // pink
  {bg:INFO_ACCENT_TINT,c:INFO_ACCENT_2},  // indigo
];
const speakerStyle=(speakers,id)=>{const i=speakers.indexOf(id);return SPEAKER_COLORS[i%SPEAKER_COLORS.length]||SPEAKER_COLORS[0];};

const TranscriptTaskHeader=({task,title,onBack,crumb="Task Review"})=>(
  <>
    <div style={{padding:"12px 28px",borderBottom:`1px solid ${BORDER}`,background:SURFACE_SUNKEN,flexShrink:0,display:"flex",alignItems:"center",gap:14}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",padding:0}}>← My Tasks</button>
      {crumb ? (<>
        <span style={{color:TEXT_DISABLED}}>|</span>
        <span style={{fontSize:14,fontWeight:500,color:TEXT_PRIMARY}}>{crumb}</span>
      </>) : null}
    </div>
    <div style={{padding:"26px 28px 12px",flexShrink:0}}>
      <div style={{fontSize:18,fontWeight:700,color:TEXT_PRIMARY,marginBottom:4}}>{title}</div>
      <div style={{fontSize:12,color:TEXT_SUBTLE}}>{task.id} · {task.project||task.batch||""} · Due {task.deadline}</div>
    </div>
  </>
);

const SpeakersPanel=({speakers})=>(
  <div style={{margin:"0 28px 14px",border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 16px",background:SURFACE}}>
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
      
      <span style={{fontSize:13,fontWeight:600,color:TEXT_PRIMARY}}>Speakers</span>
    </div>
    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
      {speakers.map(s=>{const st=speakerStyle(speakers,s);return(<span key={s} style={{background:st.bg,color:st.c,fontSize:12,fontFamily:"monospace",padding:"4px 10px",borderRadius:6,fontWeight:500}}>{s}</span>);})}
    </div>
  </div>
);

const ProgressPanel=({done,total})=>{
  const pct=total>0?Math.round((done/total)*100):0;
  return(<div style={{margin:"0 28px 14px",border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 16px",background:SURFACE}}>
    <div style={{fontSize:13,color:TEXT_PRIMARY,marginBottom:8}}><strong>Progress:</strong> {done} / {total} segments transcribed</div>
    <div style={{height:8,background:SURFACE_HOVER,borderRadius:4,overflow:"hidden"}}>
      <div style={{height:"100%",width:`${pct}%`,background:SUCCESS,transition:"width 0.2s"}}/>
    </div>
  </div>);
};

// Fake waveform that fills the allotted width
const WaveformMock=({startTime,endTime})=>{
  const hasAudio=endTime>startTime;
  const barCount=hasAudio?14:0;
  return(<div style={{position:"relative",height:80,border:`1px solid ${BORDER}`,borderRadius:6,background:SURFACE,padding:"6px 8px",boxSizing:"border-box"}}>
    {/* Start/End time pills */}
    <div style={{position:"absolute",top:-8,left:8,background:SUCCESS_BG,color:SUCCESS,fontSize:11,fontWeight:600,padding:"1px 8px",borderRadius:4}}>Start: {startTime.toFixed(1)}s</div>
    <div style={{position:"absolute",top:-8,right:8,background:DANGER_BG,color:DANGER,fontSize:11,fontWeight:600,padding:"1px 8px",borderRadius:4}}>End: {endTime.toFixed(1)}s</div>
    {/* Left red marker */}
    <div style={{position:"absolute",left:8,top:16,bottom:20,width:3,background:DANGER_SOFT,borderRadius:2}}/>
    {/* Bars */}
    {hasAudio?(
      <div style={{display:"flex",alignItems:"center",gap:3,marginLeft:8,height:48,marginTop:8}}>
        {Array.from({length:barCount}).map((_,i)=>(<div key={i} style={{flex:1,height:`${40+Math.sin(i*1.3)*18+Math.cos(i*0.7)*10}%`,background:"#c7d2fe",borderRadius:2}}/>))}
      </div>
    ):(
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:48,marginTop:8,color:TEXT_SUBTLE,fontSize:12}}>No audio in range</div>
    )}
    {/* Time axis */}
    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:TEXT_SUBTLE,marginTop:4}}>
      <span>{Math.floor(startTime/60)}:{String(Math.floor(startTime%60)).padStart(2,"0")}</span>
      <span>{Math.floor(endTime/60)}:{String(Math.floor(endTime%60)).padStart(2,"0")}</span>
    </div>
  </div>);
};

// Segment editor used by both Create and Edit screens
const SegmentEditor=({segment,speakers,total,index,onChange,onDelete,onDone})=>{
  const charCount=(segment.text||"").length;
  const textRef=useRef(null);
  const insertTag=(tag)=>{
    const el=textRef.current;
    const cur=segment.text||"";
    if(el&&document.activeElement===el){
      const start=el.selectionStart;
      const end=el.selectionEnd;
      const before=cur.slice(0,start);
      const after=cur.slice(end);
      // Pad with spaces so the tag doesn't fuse to surrounding text
      const needsLeadSpace=before.length>0&&!/\s$/.test(before);
      const needsTrailSpace=after.length>0&&!/^\s/.test(after);
      const inserted=`${needsLeadSpace?" ":""}${tag}${needsTrailSpace?" ":""}`;
      const next=before+inserted+after;
      onChange({text:next});
      // Restore caret after the inserted tag
      requestAnimationFrame(()=>{
        if(textRef.current){
          const pos=before.length+inserted.length;
          textRef.current.focus();
          textRef.current.setSelectionRange(pos,pos);
        }
      });
    } else {
      // No active cursor — append with a leading space if needed
      const needsLeadSpace=cur.length>0&&!/\s$/.test(cur);
      onChange({text:`${cur}${needsLeadSpace?" ":""}${tag}`});
    }
  };
  return(<div style={{border:`2px solid ${P}`,borderRadius:10,padding:"16px 18px",background:SURFACE}}>
    <div style={{display:"flex",gap:32,marginBottom:14,flexWrap:"wrap"}}>
      <div style={{flex:"0 0 auto"}}>
        <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Speaker</div>
        <select value={segment.speakerId} onChange={e=>onChange({speakerId:e.target.value})}
          style={{padding:"6px 10px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:13,outline:"none",background:SURFACE_SUNKEN,fontFamily:"monospace",minWidth:180}}>
          {speakers.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Timing</div>
        <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:TEXT_MUTED}}>
          <span>Start:</span>
          <input type="number" step="0.1" value={segment.startTime} onChange={e=>onChange({startTime:parseFloat(e.target.value)||0})}
            style={{width:60,padding:"5px 7px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:5,fontSize:13,outline:"none"}}/>
          <span>s</span>
          <span style={{marginLeft:8}}>End:</span>
          <input type="number" step="0.1" value={segment.endTime} onChange={e=>onChange({endTime:parseFloat(e.target.value)||0})}
            style={{width:60,padding:"5px 7px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:5,fontSize:13,outline:"none"}}/>
          <span>s</span>
          <span style={{marginLeft:10}}>Duration: {(segment.endTime-segment.startTime).toFixed(1)}s</span>
        </div>
      </div>
    </div>
    <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Audio Playback</div>
    <WaveformMock startTime={segment.startTime} endTime={segment.endTime}/>
    <div style={{marginTop:10,marginBottom:14}}>
      <button style={{padding:"7px 22px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:7}}>
        <span style={{fontSize:11}}>▶</span> Play
      </button>
    </div>
    <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Transcript Text</div>
    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
      <span style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:500,letterSpacing:"0.04em"}}>INSERT TAG:</span>
      {["[laugh]","[sigh]","[scream]"].map(tag=>(
        <button key={tag} type="button" onMouseDown={e=>e.preventDefault()} onClick={()=>insertTag(tag)}
          style={{padding:"3px 9px",borderRadius:999,border:`1px solid ${BORDER}`,background:SURFACE_SUNKEN,color:TEXT_SECONDARY,fontSize:11,fontFamily:"monospace",cursor:"pointer"}}>
          {tag}
        </button>
      ))}
    </div>
    <textarea ref={textRef} value={segment.text} onChange={e=>onChange({text:e.target.value})} rows={5}
      style={{width:"100%",padding:"10px 12px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit",lineHeight:1.5,boxSizing:"border-box"}}/>
    <div style={{fontSize:12,color:TEXT_SUBTLE,marginTop:6,marginBottom:14}}>Segment {index+1} of {total} · {charCount} characters</div>
    <div style={{display:"flex",gap:8}}>
      <button onClick={onDelete} style={{padding:"8px 14px",borderRadius:7,border:"none",background:DANGER,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>
        Delete
      </button>
      <button onClick={onDone} style={{padding:"8px 14px",borderRadius:7,border:"none",background:NEUTRAL_DARK,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>
        Done Editing
      </button>
    </div>
  </div>);
};

const InsertButton=({label,onClick})=>(
  <div style={{display:"flex",justifyContent:"center",padding:"8px 0"}}>
    <button onClick={onClick} style={{padding:"6px 16px",borderRadius:999,border:`1px solid ${P}`,background:"transparent",color:P,fontSize:13,fontWeight:500,cursor:"pointer"}}>
      + {label}
    </button>
  </div>
);

// ── Create Transcript screen ─────────────────────────────────────────────────
function CreateTranscriptScreen({task,onBack}){
  const DEFAULT_SPEAKERS=["speaker-1","speaker-2"];
  const [speakers]=useState(DEFAULT_SPEAKERS);
  const [segments,setSegments]=useState([{id:1,speakerId:DEFAULT_SPEAKERS[0],startTime:0,endTime:0,text:""}]);
  const [nextId,setNextId]=useState(2);

  const doneCount=segments.filter(s=>s.text.trim().length>0).length;
  const updateSegment=(id,patch)=>setSegments(prev=>prev.map(s=>s.id===id?{...s,...patch}:s));
  const deleteSegment=(id)=>{
    if(segments.length===1){updateSegment(id,{startTime:0,endTime:0,text:"",speakerId:speakers[0]});return;}
    setSegments(prev=>prev.filter(s=>s.id!==id));
  };
  const insertBefore=(id)=>{const idx=segments.findIndex(s=>s.id===id);const newSeg={id:nextId,speakerId:speakers[0],startTime:0,endTime:0,text:""};setSegments(prev=>[...prev.slice(0,idx),newSeg,...prev.slice(idx)]);setNextId(nextId+1);};
  const insertAfter=(id)=>{const idx=segments.findIndex(s=>s.id===id);const newSeg={id:nextId,speakerId:speakers[0],startTime:0,endTime:0,text:""};setSegments(prev=>[...prev.slice(0,idx+1),newSeg,...prev.slice(idx+1)]);setNextId(nextId+1);};

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <TranscriptTaskHeader task={task} title="Transcript Creation" onBack={onBack}/>
    <div style={{flex:1,overflowY:"auto",paddingBottom:28}}>
      <SpeakersPanel speakers={speakers}/>
      <ProgressPanel done={doneCount} total={segments.length}/>
      <div style={{padding:"0 28px"}}>
        {segments.map((s,i)=>(<div key={s.id}>
          <InsertButton label="Insert utterance before" onClick={()=>insertBefore(s.id)}/>
          <SegmentEditor segment={s} speakers={speakers} total={segments.length} index={i}
            onChange={patch=>updateSegment(s.id,patch)} onDelete={()=>deleteSegment(s.id)} onDone={()=>{}}/>
          {i===segments.length-1&&<InsertButton label="Insert utterance after" onClick={()=>insertAfter(s.id)}/>}
        </div>))}
      </div>
      <div style={{padding:"14px 28px",display:"flex",justifyContent:"flex-end",gap:8}}>
        <button style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
        <button style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Submit Transcript →</button>
      </div>
    </div>
  </div>);
}

// ── Full Transcript Review screen ────────────────────────────────────────────
const SAMPLE_EDIT_SEGMENTS=[
  {id:1,speakerId:"speaker-1",startTime:2.0,endTime:56.0,text:"Bueno, em, con estos climas me ha habilitado, por lo menos a mí, trapear más seguido, que no lo hacía en invierno, viste que no se puede abrir toda la casa que, el frío, las las nenas que se enferman. Entonces ahora como se puede abrir todo, me da más para trapear más seguido. No obstante eso, tenemos una serie de árboles en toda la vereda que en esta época que están floreciendo, juntan una florcita blanca muy chiquitita, pero así como brota, cae y ensucia todo el frente y si dejas mucho tiempo la puerta abierta se te llena todo adentro también. Así que, porque es una zona ventosa, estamos a 1100 metros de altura acá, y y suele haber bastante viento. Eh, entonces bueno, estoy ahí, y trapeo, que cierro la puerta, que se llena todo de florcitas. Y hay mucha tierra, somos de tener calles de tierra acá y la tierra inunda la casa, así que, sí."},
  {id:2,speakerId:"speaker-2",startTime:56.0,endTime:76.0,text:"Claro, sí, sí. A mí me pasó también, que ayer me puse a limpiar toda la casa. Y y lavé el piso y todo y también como vos decís, en este en esta época se desprende demasiadas cosas de los de los árboles, entonces es como que por más que estés barriendo, es el lugarcito donde se hace como un remolinito te manda todo adentro."},
  {id:3,speakerId:"speaker-1",startTime:76.0,endTime:76.0,text:"aham."},
  {id:4,speakerId:"speaker-2",startTime:76.0,endTime:92.0,text:"Así que terminé de limpiar y me puse a barrer. Jajajaja, Re fanática. Pero para sacar todo los la, como esos panaderitos, ¿viste? Como que es como que es una pelucita. Y sí, hay un montón de de tierra o barro cuando llueve."},
  {id:5,speakerId:"speaker-1",startTime:92.0,endTime:93.0,text:"Mirá."},
  {id:6,speakerId:"speaker-2",startTime:93.0,endTime:110.0,text:"Y tenemos muchos árboles también alrededor, así que sí. En otoño es lo mismo, te llenas de hojas, hay que juntar hojas todos los días."},
  {id:7,speakerId:"speaker-1",startTime:110.0,endTime:118.0,text:"Uf, sí. Yo tengo uno grande, grande al frente, así que bueno, se me llena todo."},
  {id:8,speakerId:"speaker-2",startTime:118.0,endTime:130.0,text:"Claro. Y acá también, porque estamos rodeados de árboles. Me encanta igual, eh, pero hay que limpiar seguido."},
];
// Pad out to 20 segments for the realistic "20/20" display
for(let i=SAMPLE_EDIT_SEGMENTS.length+1;i<=20;i++){
  const lastEnd=SAMPLE_EDIT_SEGMENTS[SAMPLE_EDIT_SEGMENTS.length-1].endTime;
  SAMPLE_EDIT_SEGMENTS.push({id:i,speakerId:i%2===1?"speaker-1":"speaker-2",startTime:lastEnd+(i-SAMPLE_EDIT_SEGMENTS.length)*8,endTime:lastEnd+(i-SAMPLE_EDIT_SEGMENTS.length)*8+6,text:`Segment ${i} content — placeholder for the remainder of this conversation. Click to edit the transcript text, speaker, or timing.`});
}

const CHANNEL_OPTIONS=["Channel 1","Channel 2","Unassigned"];
// Playback source options: full mix or a single channel.
const PLAYBACK_CHANNELS=["Multi-channel","Channel 1","Channel 2"];
// Label a playback option with the speakers currently mapped to that channel.
const channelLabel=(channel,speakers,channelMap)=>{
  if(channel==="Multi-channel")return channel;
  const inCh=(speakers||[]).filter(s=>(channelMap&&channelMap[s]||"Unassigned")===channel);
  return inCh.length?`${channel} (${inCh.join(", ")})`:`${channel} (none)`;
};

// Labeled identifier field for the screen header (UUIDs render monospace).
const MetaField=({label,value,mono})=>(
  <div style={{display:"flex",flexDirection:"column",gap:1}}>
    <span style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.06em",textTransform:"uppercase"}}>{label}</span>
    <span style={{fontSize:12,color:TEXT_SECONDARY,fontFamily:mono?"monospace":"inherit"}}>{value}</span>
  </div>
);

// Read-only reviewer instructions (set by Ops). Collapsible to save vertical space.
const TranscriptSpecialInstructions=({text})=>{
  const [open,setOpen]=useState(true);
  return (<div style={{margin:"16px 28px 10px",border:`1px solid ${WARNING_BORDER}`,borderRadius:10,background:WARNING_BG,overflow:"hidden"}}>
    <div onClick={()=>setOpen(v=>!v)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",cursor:"pointer"}}>
      <span style={{fontSize:16,color:WARNING_DEEP,width:16,display:"inline-block",lineHeight:1}}>{open?"▾":"▸"}</span>
      <span style={{fontSize:12,fontWeight:700,color:WARNING_DEEP,letterSpacing:"0.04em"}}>SPECIAL INSTRUCTIONS</span>
    </div>
    {open ? (
      <div style={{padding:"0 16px 14px 42px",fontSize:13,color:WARNING_DEEP,lineHeight:1.6}}>
        {text||"This is a reminder to the devs that this copy can change per task. It is custom."}
      </div>
    ) : null}
  </div>);
};

// Reviewer settings as a top-right gear that opens a small popover.
const SettingsPopover=({settings,onChange})=>{
  const [open,setOpen]=useState(false);
  const row=(key,label)=>(
    <label style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:13,color:TEXT_SECONDARY,cursor:"pointer",lineHeight:1.4}}>
      <input type="checkbox" checked={!!settings[key]} onChange={e=>onChange({...settings,[key]:e.target.checked})} style={{cursor:"pointer",marginTop:2}}/>
      <span>{label}</span>
    </label>
  );
  return (<div style={{position:"relative"}}>
    <button onClick={()=>setOpen(v=>!v)} title="Settings" aria-label="Settings"
      style={{width:30,height:30,borderRadius:7,border:`1px solid ${open?P:BORDER}`,background:open?BRAND_LIGHTER:SURFACE,color:open?P:TEXT_MUTED,fontSize:15,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>⚙</button>
    {open ? (<>
      {/* click-away backdrop */}
      <div onClick={()=>setOpen(false)} style={{position:"fixed",inset:0,zIndex:40}}/>
      <div style={{position:"absolute",top:36,right:0,zIndex:41,width:300,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,boxShadow:"0 12px 32px rgba(0,0,0,0.18)",padding:"14px 16px"}}>
        <div style={{fontSize:11,fontWeight:700,color:TEXT_SUBTLE,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:12}}>Settings</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {row("autoplayOnSelect","Autoplay audio when a new turn is selected")}
          {row("autoplayOnSliderMove","Autoplay audio when a timestamp slider is moved")}
        </div>
      </div>
    </>) : null}
  </div>);
};

// Per-channel waveform — fully functional like the global one (play/pause, speed,
// Bar heights for a waveform: tall and varied where a speaker segment is present,
// near-flat in the gaps — so activity vs. silence reads at a glance.
function segWaveBars(segs,totalS,count,seed){
  segs=segs||[];const sd=seed||0;
  return Array.from({length:count},(_,i)=>{
    const t=(i/count)*totalS;
    const inSeg=segs.some(s=>t>=s.startTime&&t<=s.endTime);
    if(inSeg){
      const h=48+Math.sin(t*2.3+sd)*24+Math.cos(t*0.9+sd*1.7)*16+Math.sin(t*6.1+sd)*12;
      return Math.max(18,Math.min(100,h));
    }
    const h=7+Math.sin(t*3.5+sd)*2.5;
    return Math.max(4,Math.min(13,h));
  });
}
// time, clickable segments), full width, showing this channel's speaker segments.
const ChannelWaveform=({duration,segments,speakers,seed,onSegmentClick})=>{
  const [playing,setPlaying]=useState(false);
  const [speed,setSpeed]=useState(1);
  const totalS=Math.max(1,duration);
  const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;
  const segs=segments||[];
  const bars=segWaveBars(segs,totalS,160,seed);
  return (<div style={{marginTop:10}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <button onClick={()=>setPlaying(p=>!p)} style={{padding:"6px 16px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:7,minWidth:78,justifyContent:"center"}}>
          <span style={{fontSize:10}}>{playing?"❚❚":"▶"}</span> {playing?"Pause":"Play"}
        </button>
        <select value={speed} onChange={e=>setSpeed(parseFloat(e.target.value))}
          style={{padding:"5px 9px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:12,outline:"none",background:SURFACE,cursor:"pointer"}}>
          <option value="0.5">0.5x</option>
          <option value="1">1x</option>
          <option value="1.25">1.25x</option>
          <option value="1.5">1.5x</option>
          <option value="2">2x</option>
        </select>
      </div>
      <div style={{fontSize:11,color:TEXT_MUTED,fontFamily:"monospace"}}>0:00 / {fmt(totalS)}</div>
    </div>
    <div style={{position:"relative",height:48,background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:6,overflow:"hidden"}}>
      {segs.map(s=>{
        const sp=speakerStyle(speakers,s.speakerId);
        const left=(s.startTime/totalS)*100;
        const width=Math.max(0.5,((s.endTime-s.startTime)/totalS)*100);
        return <div key={s.id} onClick={e=>{e.stopPropagation();onSegmentClick&&onSegmentClick(s.id);}}
          title={`${s.speakerId} · ${s.startTime.toFixed(1)}s – ${s.endTime.toFixed(1)}s`}
          style={{position:"absolute",top:0,bottom:0,left:`${left}%`,width:`${width}%`,background:sp.bg,opacity:0.6,borderLeft:`2px solid ${sp.c}`,borderRight:`2px solid ${sp.c}`,cursor:"pointer"}}/>;
      })}
      <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",height:"100%",padding:"0 6px",gap:1,pointerEvents:"none"}}>
        {bars.map((h,i)=><div key={i} style={{flex:1,height:`${h}%`,background:TEXT_SUBTLE,opacity:0.45,borderRadius:1}}/>)}
      </div>
      <div style={{position:"absolute",left:0,top:0,bottom:0,width:2,background:P,zIndex:2}}/>
    </div>
    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:TEXT_SUBTLE,marginTop:4}}>
      <span>0:00</span><span>{fmt(totalS)}</span>
    </div>
  </div>);
};

// Small "?" affordance that explains a section on hover or click.
const InfoTip=({text})=>{
  const [open,setOpen]=useState(false);
  return (<span style={{position:"relative",display:"inline-flex"}}
    onMouseEnter={()=>setOpen(true)} onMouseLeave={()=>setOpen(false)}>
    <button onClick={e=>{e.stopPropagation();setOpen(o=>!o);}} aria-label="What is this?"
      style={{width:16,height:16,borderRadius:"50%",border:`1px solid ${TEXT_DISABLED}`,background:SURFACE,color:TEXT_MUTED,fontSize:10,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",lineHeight:1,padding:0}}>?</button>
    {open ? (
      <span style={{position:"absolute",top:22,left:0,zIndex:30,width:280,background:NEUTRAL_DARK,color:SURFACE,fontSize:12,lineHeight:1.5,fontWeight:400,padding:"9px 11px",borderRadius:7,boxShadow:"0 8px 20px rgba(0,0,0,0.25)"}}>{text}</span>
    ) : null}
  </span>);
};

// Compact, collapsible speaker control: rename inline, map each speaker to an audio
// channel, add speakers, and merge. Kept low-profile to preserve space for the task.
const ChannelSpeakerBar=({speakers,channelMap,counts,segments,duration,onAddSpeaker,onRenameSpeaker,onChannelChange,onDeleteSpeaker,onMergeClick,onSegmentClick})=>{
  const [open,setOpen]=useState(true);
  const [editingId,setEditingId]=useState(null);
  const [draft,setDraft]=useState("");
  const [pendingDelete,setPendingDelete]=useState(null);
  const [dragOver,setDragOver]=useState(null);
  const startEdit=id=>{setEditingId(id);setDraft(id);};
  const commitEdit=()=>{
    const trimmed=draft.trim();
    if(trimmed&&trimmed!==editingId&&!speakers.includes(trimmed)){onRenameSpeaker(editingId,trimmed);}
    setEditingId(null);
  };
  const mapped=speakers.filter(s=>channelMap[s]&&channelMap[s]!=="Unassigned").length;
  const deleteTarget=pendingDelete?speakers.find(s=>s!==pendingDelete):null;
  return (<div style={{margin:"0 28px 10px",border:`1px solid ${BORDER}`,borderRadius:8,background:SURFACE}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px"}}>
      <div onClick={()=>setOpen(v=>!v)} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
        <span style={{fontSize:16,color:TEXT_MUTED,width:16,display:"inline-block",lineHeight:1}}>{open?"▾":"▸"}</span>
        <span style={{fontSize:12,fontWeight:600,color:TEXT_PRIMARY}}>Speakers &amp; channels</span>
        <InfoTip text="Map each speaker to the audio channel they were recorded on. Drag a speaker chip into a channel to reassign it, click a name to rename, and use ✕ to remove a speaker. Add or merge speakers with the buttons on the right."/>
        <span style={{fontSize:11,color:TEXT_SUBTLE}}>· {speakers.length} speaker{speakers.length!==1?"s":""}, {mapped} mapped</span>
      </div>
      <div style={{display:"flex",gap:6}}>
        <button onClick={onAddSpeaker} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${SUCCESS_SOFT}`,background:SURFACE,color:SUCCESS,fontSize:11,fontWeight:600,cursor:"pointer"}}>+ Add speaker</button>
        {speakers.length>=2 ? <button onClick={onMergeClick} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${BRAND_SOFTER}`,background:SURFACE,color:P,fontSize:11,fontWeight:600,cursor:"pointer"}}>Merge speakers</button> : null}
      </div>
    </div>
    {open ? (
      <div style={{padding:"0 14px 12px"}}>
        <div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:8}}>Drag a speaker up or down to move it between channels.</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {["Channel 1","Channel 2"].concat(speakers.some(s=>(channelMap[s]||"Unassigned")==="Unassigned")?["Unassigned"]:[]).map(ch=>{
            const inChannel=speakers.filter(s=>(channelMap[s]||"Unassigned")===ch);
            const isUnassigned=ch==="Unassigned";
            const isOver=dragOver===ch;
            return (<div key={ch}
              onDragOver={e=>{e.preventDefault();if(dragOver!==ch)setDragOver(ch);}}
              onDragLeave={()=>setDragOver(o=>o===ch?null:o)}
              onDrop={e=>{e.preventDefault();const sp=e.dataTransfer.getData("text/plain");if(sp)onChannelChange(sp,ch);setDragOver(null);}}
              style={{width:"100%",border:isOver?`2px dashed ${P}`:`1px solid ${BORDER}`,borderRadius:8,background:isOver?BRAND_LIGHTER:(isUnassigned?SURFACE:SURFACE_SUNKEN),padding:isOver?"9px 11px":"10px 12px"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span style={{fontSize:11,fontWeight:700,color:isUnassigned?TEXT_SUBTLE:TEXT_SECONDARY,letterSpacing:"0.04em",textTransform:"uppercase",flexShrink:0}}>{ch}</span>
                <span style={{fontSize:10,color:TEXT_SUBTLE,flexShrink:0}}>{inChannel.length}</span>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",flex:1}}>
                  {inChannel.length===0 ? (
                    <span style={{fontSize:11,color:TEXT_SUBTLE,fontStyle:"italic"}}>Drop a speaker here</span>
                  ) : inChannel.map(s=>{
                    const st=speakerStyle(speakers,s);
                    const editingThis=editingId===s;
                    return (<div key={s} draggable={!editingThis}
                      onDragStart={e=>{e.dataTransfer.setData("text/plain",s);e.dataTransfer.effectAllowed="move";}}
                      style={{display:"flex",alignItems:"center",gap:6,border:`1px solid ${st.c}`,borderRadius:8,padding:"4px 6px 4px 8px",background:st.bg,cursor:editingThis?"default":"grab"}}>
                      <span style={{fontSize:12,color:st.c,opacity:0.5,lineHeight:1}}>⠿</span>
                      {editingThis ? (
                        <input autoFocus value={draft} onChange={e=>setDraft(e.target.value)} onBlur={commitEdit}
                          onKeyDown={e=>{if(e.key==="Enter")commitEdit();if(e.key==="Escape")setEditingId(null);}}
                          style={{background:SURFACE,color:st.c,fontSize:12,fontFamily:"monospace",padding:"3px 6px",borderRadius:5,fontWeight:600,border:`1px solid ${st.c}`,outline:"none",width:96}}/>
                      ) : (
                        <span onClick={()=>startEdit(s)} title="Click to rename" style={{fontSize:12,fontFamily:"monospace",fontWeight:600,color:st.c,cursor:"text"}}>{s}</span>
                      )}
                      {speakers.length>1 ? (
                        <button onClick={()=>setPendingDelete(s)} title="Delete speaker" style={{width:18,height:18,borderRadius:"50%",border:"none",background:"transparent",color:st.c,fontSize:13,cursor:"pointer",lineHeight:1,opacity:0.7,padding:0}}>✕</button>
                      ) : null}
                    </div>);
                  })}
                </div>
              </div>
              {isUnassigned?null:<ChannelWaveform duration={duration} speakers={speakers} seed={ch==="Channel 1"?1:2} onSegmentClick={onSegmentClick}
                segments={(segments||[]).filter(s=>((channelMap&&channelMap[s.speakerId])||"Unassigned")===ch)}/>}
            </div>);
          })}
        </div>
        {pendingDelete ? (
          <div style={{marginTop:10,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",background:DANGER_BG,border:`1px solid ${DANGER_BORDER}`,borderRadius:7,padding:"8px 12px"}}>
            <span style={{fontSize:12,color:DANGER}}>Delete <strong style={{fontFamily:"monospace"}}>{pendingDelete}</strong>? Its {counts?.[pendingDelete]||0} turn{(counts?.[pendingDelete]||0)===1?"":"s"} will be reassigned to <strong style={{fontFamily:"monospace"}}>{deleteTarget}</strong>.</span>
            <div style={{flex:1}}/>
            <button onClick={()=>setPendingDelete(null)} style={{padding:"5px 11px",borderRadius:6,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={()=>{onDeleteSpeaker(pendingDelete);setPendingDelete(null);}} style={{padding:"5px 11px",borderRadius:6,border:"none",background:DANGER,color:SURFACE,fontSize:12,fontWeight:600,cursor:"pointer"}}>Delete speaker</button>
          </div>
        ) : null}
      </div>
    ) : null}
  </div>);
};

// Collapsed turn row with a reviewed/not-reviewed state indicator.
const TranscriptTurnRow=({segment,speakers,channelMap,index,onClick})=>{
  const st=speakerStyle(speakers,segment.speakerId);
  const reviewed=segment.reviewed;
  const channel=(channelMap&&channelMap[segment.speakerId])||"Unassigned";
  return (<div id={"turn-"+segment.id} onClick={onClick}
    style={{border:`1px solid ${BORDER}`,borderLeft:reviewed?`4px solid ${SUCCESS_SOFT}`:`4px solid ${BORDER_STRONG}`,borderRadius:10,padding:"14px 18px",marginBottom:10,background:reviewed?SUCCESS_SURFACE:SURFACE,cursor:"pointer",display:"flex",gap:14,alignItems:"flex-start"}}
    onMouseEnter={e=>e.currentTarget.style.borderColor=P}
    onMouseLeave={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.borderLeftColor=reviewed?SUCCESS_SOFT:BORDER_STRONG;}}>
    <div style={{width:26,height:26,borderRadius:"50%",background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>{index+1}</div>
    <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0,marginTop:2,alignItems:"flex-start"}}>
      <span style={{background:st.bg,color:st.c,fontSize:12,fontFamily:"monospace",padding:"3px 10px",borderRadius:6,fontWeight:500}}>{segment.speakerId}</span>
      <span style={{fontSize:10,color:channel==="Unassigned"?TEXT_SUBTLE:TEXT_MUTED,border:`1px solid ${BORDER}`,borderRadius:5,padding:"1px 6px"}}>{channel}</span>
    </div>
    <div style={{flex:1,minWidth:0}}>
      <p style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.6,margin:0,marginBottom:6}}>{segment.text||<em style={{color:TEXT_SUBTLE}}>(empty)</em>}</p>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:11,color:TEXT_SUBTLE}}>{segment.startTime.toFixed(1)}s – {segment.endTime.toFixed(1)}s</span>
        {reviewed ? (
          <span style={{fontSize:11,fontWeight:600,color:SUCCESS,background:SUCCESS_BG,padding:"1px 8px",borderRadius:999}}>✓ Reviewed</span>
        ) : (
          <span style={{fontSize:11,fontWeight:500,color:TEXT_MUTED,background:SURFACE_HOVER,padding:"1px 8px",borderRadius:999}}>Not reviewed</span>
        )}
      </div>
    </div>
  </div>);
};

// Expanded turn editor. Edits are buffered in a local draft so the reviewer can
// Cancel (discard) or Save explicitly — nothing reaches the transcript until save.
const TranscriptTurnEditor=({segment,speakers,channelMap,total,index,duration,autoplayOnSelect,autoplayOnSliderMove,onSave,onDelete,onCancel,onMarkReviewed})=>{
  const [draft,setDraft]=useState(segment);
  const [playing,setPlaying]=useState(!!autoplayOnSelect);
  const [speed,setSpeed]=useState(1);
  const [playChannel,setPlayChannel]=useState("Multi-channel");
  const [zoom,setZoom]=useState(1);
  const [confirmDelete,setConfirmDelete]=useState(false);
  const patch=p=>setDraft(d=>({...d,...p}));
  const dirty=draft.speakerId!==segment.speakerId||draft.startTime!==segment.startTime||draft.endTime!==segment.endTime||draft.text!==segment.text;
  return (<div id={"turn-"+segment.id} style={{border:`2px solid ${P}`,borderRadius:10,padding:"16px 18px",background:SURFACE}}>
    <div style={{display:"flex",gap:24,marginBottom:14,flexWrap:"wrap",alignItems:"flex-start"}}>
      <div style={{width:30,height:30,borderRadius:"50%",background:P,color:SURFACE,fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:22}}>{index+1}</div>
      <div style={{flex:"0 0 auto"}}>
        <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Speaker</div>
        <select value={draft.speakerId} onChange={e=>patch({speakerId:e.target.value})}
          style={{padding:"6px 10px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:13,outline:"none",background:SURFACE_SUNKEN,fontFamily:"monospace",minWidth:180}}>
          {speakers.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:5}}>Channel: <span style={{color:TEXT_SECONDARY,fontWeight:500}}>{(channelMap&&channelMap[draft.speakerId])||"Unassigned"}</span></div>
      </div>
      <div style={{flex:"1 1 auto"}}>
        <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Timing</div>
        <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:TEXT_MUTED,flexWrap:"wrap"}}>
          <span>Start</span>
          <input type="number" step="0.1" value={draft.startTime} onChange={e=>patch({startTime:Math.max(0,Math.min(draft.endTime-0.1,parseFloat(e.target.value)||0))})}
            style={{width:64,padding:"5px 7px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:5,fontSize:13,outline:"none"}}/>
          <span>s</span>
          <span style={{marginLeft:8}}>End</span>
          <input type="number" step="0.1" value={draft.endTime} onChange={e=>patch({endTime:Math.max(draft.startTime+0.1,Math.min(duration,parseFloat(e.target.value)||0))})}
            style={{width:64,padding:"5px 7px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:5,fontSize:13,outline:"none"}}/>
          <span>s</span>
          <span style={{marginLeft:8,color:TEXT_SUBTLE}}>Duration: {(draft.endTime-draft.startTime).toFixed(1)}s</span>
        </div>
      </div>
    </div>

    <SegmentWaveform segment={draft} duration={duration} onChange={patch} zoom={zoom} hideHint onScrubEnd={()=>{if(autoplayOnSliderMove)setPlaying(true);}}/>
    <div style={{marginTop:10,marginBottom:14,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <button onClick={()=>setPlaying(p=>!p)} style={{padding:"7px 22px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:7}}>
        <span style={{fontSize:11}}>{playing?"❚❚":"▶"}</span> {playing?"Pause":"Play"}
      </button>
      <select value={speed} onChange={e=>setSpeed(parseFloat(e.target.value))}
        style={{padding:"6px 10px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:12,outline:"none",background:SURFACE,cursor:"pointer"}}>
        <option value="0.5">0.5x</option>
        <option value="1">1x</option>
        <option value="1.25">1.25x</option>
        <option value="1.5">1.5x</option>
        <option value="2">2x</option>
      </select>
      <select value={playChannel} onChange={e=>setPlayChannel(e.target.value)} title="Playback source"
        style={{padding:"6px 10px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:12,outline:"none",background:SURFACE,cursor:"pointer"}}>
        {PLAYBACK_CHANNELS.map(c=><option key={c} value={c}>{channelLabel(c,speakers,channelMap)}</option>)}
      </select>
      <div style={{flex:1}}/>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        <span style={{fontSize:11,color:TEXT_SUBTLE}}>Zoom</span>
        <button onClick={()=>setZoom(z=>Math.max(0.5,Math.round((z-0.5)*10)/10))} disabled={zoom<=0.5}
          style={{width:24,height:24,borderRadius:5,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:15,cursor:zoom<=0.5?"default":"pointer",lineHeight:1,opacity:zoom<=0.5?0.4:1}}>−</button>
        <span style={{fontSize:12,color:TEXT_SECONDARY,minWidth:36,textAlign:"center",fontFamily:"monospace"}}>{zoom.toFixed(1)}×</span>
        <button onClick={()=>setZoom(z=>Math.min(4,Math.round((z+0.5)*10)/10))} disabled={zoom>=4}
          style={{width:24,height:24,borderRadius:5,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:15,cursor:zoom>=4?"default":"pointer",lineHeight:1,opacity:zoom>=4?0.4:1}}>+</button>
      </div>
    </div>

    <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Transcript text</div>
    <textarea value={draft.text} onChange={e=>patch({text:e.target.value})} rows={4}
      style={{width:"100%",padding:"10px 12px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit",lineHeight:1.5,boxSizing:"border-box"}}/>
    <div style={{fontSize:12,color:TEXT_SUBTLE,marginTop:6,marginBottom:14}}>Turn {index+1} of {total} · {(draft.text||"").length} characters</div>

    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
      <div>
        {confirmDelete ? (
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:DANGER,fontWeight:500}}>Delete this turn?</span>
            <button onClick={()=>setConfirmDelete(false)} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:12,cursor:"pointer"}}>Cancel</button>
            <button onClick={onDelete} style={{padding:"6px 12px",borderRadius:7,border:"none",background:DANGER,color:SURFACE,fontSize:12,fontWeight:600,cursor:"pointer"}}>Yes, delete</button>
          </div>
        ) : (
          <button onClick={()=>setConfirmDelete(true)} style={{padding:"8px 14px",borderRadius:7,border:`1px solid ${DANGER_BORDER}`,background:SURFACE,color:DANGER,fontSize:13,fontWeight:500,cursor:"pointer"}}>Delete</button>
        )}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        {dirty ? <span style={{fontSize:11,color:WARNING_ACCENT,fontWeight:500}}>Unsaved changes</span> : null}
        <button onClick={onCancel} style={{padding:"8px 14px",borderRadius:7,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_MUTED,fontSize:13,fontWeight:500,cursor:"pointer"}}>Cancel</button>
        <button onClick={()=>onSave(draft)} style={{padding:"8px 16px",borderRadius:7,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:13,fontWeight:600,cursor:"pointer"}}>Save &amp; close</button>
        <button onClick={()=>onMarkReviewed(draft)} style={{padding:"8px 18px",borderRadius:7,border:"none",background:SUCCESS,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>{segment.reviewed?"✓ Save & keep reviewed":"Save & mark reviewed"}</button>
      </div>
    </div>
  </div>);
};

function EditTranscriptScreen({task,onBack}){
  const [speakers,setSpeakers]=useState(["speaker-1","speaker-2","speaker-3"]);
  const [channelMap,setChannelMap]=useState({"speaker-1":"Channel 1","speaker-2":"Channel 1","speaker-3":"Channel 2"});
  const [segments,setSegments]=useState(SAMPLE_EDIT_SEGMENTS.map((s,i)=>({...s,reviewed:false,speakerId:i%3===2?"speaker-3":s.speakerId})));
  const [editingId,setEditingId]=useState(null);
  const [nextId,setNextId]=useState(100);
  const [filter,setFilter]=useState("all");
  const [pinned,setPinned]=useState(true);
  const [playing,setPlaying]=useState(false);
  const [currentTime]=useState(0);
  const [speed,setSpeed]=useState(1);
  const [playbackChannel,setPlaybackChannel]=useState("Multi-channel");
  const [showMerge,setShowMerge]=useState(false);
  const [submitAttempted,setSubmitAttempted]=useState(false);
  const [submitted,setSubmitted]=useState(false);
  const [settings,setSettings]=useState({autoplayOnSelect:false,autoplayOnSliderMove:false});
  const [lastSavedAt,setLastSavedAt]=useState(()=>Date.now());
  const [now,setNow]=useState(()=>Date.now());
  const markSaved=()=>setLastSavedAt(Date.now());

  const active=segments.filter(s=>!s.deleted);
  const audioDuration=Math.max(60,...active.map(s=>s.endTime));
  // Header identifiers. Real values come from the task/backend; mocked here.
  const engagementId=task.engagementId||"a3f1c9e2-7b44-4d80-9f12-6c0e5a8b21d4";
  const transcriptId=task.transcriptId||"d57e0b18-2c9a-4f63-8a01-3e9b7c4d6f55";
  const langVariant=task.langVariant||task.lang||"—";
  const reviewedCount=active.filter(s=>s.reviewed).length;
  const counts=active.reduce((acc,s)=>{acc[s.speakerId]=(acc[s.speakerId]||0)+1;return acc;},{all:active.length});
  const visible=filter==="all"?segments:segments.filter(s=>s.speakerId===filter);

  // Relative "last saved" label, refreshed by a ticking clock.
  const savedLabel=(()=>{
    const diff=Math.max(0,Math.round((now-lastSavedAt)/1000));
    if(diff<45)return "Saved just now";
    if(diff<90)return "Saved 1 minute ago";
    if(diff<3600)return `Saved ${Math.round(diff/60)} minutes ago`;
    const h=Math.round(diff/3600);
    return `Saved ${h} hour${h===1?"":"s"} ago`;
  })();
  useEffect(()=>{
    const t=setInterval(()=>setNow(Date.now()),15000);
    return ()=>clearInterval(t);
  },[]);

  const updateSegment=(id,patch)=>setSegments(prev=>prev.map(s=>s.id===id?{...s,...patch}:s));
  const deleteSegment=(id)=>{setSegments(prev=>prev.map(s=>s.id===id?{...s,deleted:true}:s));setEditingId(null);requestAnimationFrame(()=>{const el=document.getElementById("turn-"+id);if(el)el.scrollIntoView({behavior:"smooth",block:"start"});});};
  const undoDelete=(id)=>setSegments(prev=>prev.map(s=>s.id===id?{...s,deleted:false}:s));
  const dismissDelete=(id)=>setSegments(prev=>prev.filter(s=>s.id!==id));
  const openTurn=(id)=>{
    const seg=segments.find(s=>s.id===id);
    if(seg&&filter!=="all"&&seg.speakerId!==filter)setFilter("all");
    setEditingId(id);
    requestAnimationFrame(()=>{const el=document.getElementById("turn-"+id);if(el)el.scrollIntoView({behavior:"smooth",block:"center"});});
  };

  const addSpeaker=()=>{
    let n=speakers.length+1;
    let name=`speaker-${n}`;
    while(speakers.includes(name)){n++;name=`speaker-${n}`;}
    setSpeakers(prev=>[...prev,name]);
    setChannelMap(prev=>({...prev,[name]:"Unassigned"}));
  };
  const renameSpeaker=(oldId,newId)=>{
    setSpeakers(prev=>prev.map(s=>s===oldId?newId:s));
    setSegments(prev=>prev.map(s=>s.speakerId===oldId?{...s,speakerId:newId}:s));
    setChannelMap(prev=>{const next={...prev};next[newId]=next[oldId];delete next[oldId];return next;});
    if(filter===oldId)setFilter(newId);
  };
  // A speaker maps to exactly one channel (the single-select enforces this).
  // Channels may be shared by multiple speakers, so no exclusivity bumping.
  const changeChannel=(id,channel)=>setChannelMap(prev=>({...prev,[id]:channel}));
  const deleteSpeaker=(id)=>{
    if(speakers.length<=1)return;
    const target=speakers.find(s=>s!==id);
    setSegments(prev=>prev.map(s=>s.speakerId===id?{...s,speakerId:target}:s));
    setSpeakers(prev=>prev.filter(s=>s!==id));
    setChannelMap(prev=>{const next={...prev};delete next[id];return next;});
    if(filter===id)setFilter("all");
  };
  const mergeSpeakers=(source,target)=>{
    setSegments(prev=>prev.map(s=>s.speakerId===source?{...s,speakerId:target}:s));
    setSpeakers(prev=>prev.filter(s=>s!==source));
    setChannelMap(prev=>{const next={...prev};delete next[source];return next;});
    if(filter===source)setFilter(target);
    setShowMerge(false);
  };

  // Turn editor save semantics. Edits live in the editor's local draft until one
  // of these commits them; Cancel discards.
  const saveTurn=(id,draft)=>{setSegments(prev=>prev.map(s=>s.id===id?{...draft}:s));setEditingId(null);markSaved();};
  const saveAndReview=(id,draft)=>{setSegments(prev=>prev.map(s=>s.id===id?{...draft,reviewed:true}:s));setEditingId(null);markSaved();};
  const cancelTurn=()=>setEditingId(null);

  const goToFirstUnreviewed=()=>{
    const seg=active.find(s=>!s.reviewed);
    if(seg)openTurn(seg.id);
  };
  const onSubmit=()=>{
    setSubmitAttempted(true);
    if(reviewedCount===active.length&&active.length>0)setSubmitted(true);
    else setSubmitted(false);
  };

  const insertBefore=(id)=>{const idx=segments.findIndex(s=>s.id===id);const seg={id:nextId,speakerId:speakers[0],startTime:segments[idx].startTime,endTime:segments[idx].startTime,text:"",reviewed:false};setSegments(prev=>[...prev.slice(0,idx),seg,...prev.slice(idx)]);setNextId(nextId+1);setEditingId(nextId);};
  const insertAfter=(id)=>{const idx=segments.findIndex(s=>s.id===id);const seg={id:nextId,speakerId:speakers[0],startTime:segments[idx].endTime,endTime:segments[idx].endTime,text:"",reviewed:false};setSegments(prev=>[...prev.slice(0,idx+1),seg,...prev.slice(idx+1)]);setNextId(nextId+1);setEditingId(nextId);};

  return (<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    {/* Slim, always-visible back bar */}
    <div style={{padding:"10px 28px",borderBottom:`1px solid ${BORDER}`,background:SURFACE_SUNKEN,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between",gap:14}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",padding:0}}>← My Tasks</button>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:12,color:TEXT_SUBTLE}}>{savedLabel}</span>
        <SettingsPopover settings={settings} onChange={setSettings}/>
      </div>
    </div>
    <div style={{flex:1,overflowY:"auto",paddingBottom:28,position:"relative",background:SURFACE_SUNKEN}}>
      {/* Title scrolls away to free up vertical space */}
      <div style={{padding:"16px 28px 0"}}>
        <div style={{fontSize:18,fontWeight:700,color:TEXT_PRIMARY}}>Full Transcript Review</div>
        <div style={{fontSize:12,color:TEXT_SUBTLE,marginTop:2}}>{task.project||task.batch||""} · Due {task.deadline}</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:"8px 24px",marginTop:12}}>
          <MetaField label="Task ID" value={task.id} mono/>
          <MetaField label="Engagement ID" value={engagementId} mono/>
          <MetaField label="Transcript ID" value={transcriptId} mono/>
          <MetaField label="Language variant" value={langVariant}/>
        </div>
      </div>
      <TranscriptSpecialInstructions/>
      <ChannelSpeakerBar speakers={speakers} channelMap={channelMap} counts={counts} segments={active} duration={audioDuration} onSegmentClick={openTurn}
        onAddSpeaker={addSpeaker} onRenameSpeaker={renameSpeaker} onChannelChange={changeChannel} onDeleteSpeaker={deleteSpeaker} onMergeClick={()=>setShowMerge(true)}/>

      <div style={pinned?{position:"sticky",top:0,zIndex:5,background:SURFACE,boxShadow:"0 6px 14px -8px rgba(0,0,0,0.35)",borderBottom:`1px solid ${BORDER}`,paddingTop:14}:{paddingTop:14}}>
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 28px 12px"}}>
          <span style={{width:10,height:10,borderRadius:2,background:P,flexShrink:0}}/>
          <span style={{fontSize:12,fontWeight:700,color:TEXT_PRIMARY,letterSpacing:"0.04em",textTransform:"uppercase"}}>Full recording</span>
        </div>
        <FullWaveform duration={audioDuration} currentTime={currentTime} playing={playing}
          onTogglePlay={()=>setPlaying(v=>!v)} speed={speed} onSpeedChange={setSpeed}
          playbackChannel={playbackChannel} onPlaybackChannelChange={setPlaybackChannel} channelMap={channelMap}
          segments={active} speakers={speakers} onSegmentClick={openTurn} showToolbar={false}/>
        {/* Compact control row: pin · filter · reviewed counter */}
        <div style={{padding:"6px 28px 6px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <button onClick={()=>setPinned(v=>!v)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${pinned?P:BORDER}`,background:pinned?BRAND_LIGHTER:SURFACE,color:pinned?P:TEXT_MUTED,fontSize:11,fontWeight:600,cursor:"pointer"}}>{pinned?"Pinned to top":"Pin to top"}</button>
          <div style={{width:1,height:18,background:BORDER}}/>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {[{key:"all",label:"All"}].concat(speakers.map(s=>({key:s,label:s}))).map(t=>{
              const active=filter===t.key;
              return (<button key={t.key} onClick={()=>setFilter(t.key)}
                style={{padding:"4px 11px",borderRadius:999,border:active?`1px solid ${P}`:`1px solid ${BORDER}`,background:active?P:SURFACE,color:active?SURFACE:TEXT_SECONDARY,fontSize:12,fontWeight:active?600:500,cursor:"pointer",fontFamily:t.key==="all"?"inherit":"monospace"}}>
                {t.label} <span style={{opacity:0.7}}>({counts[t.key]||0})</span>
              </button>);
            })}
          </div>
          <div style={{flex:1}}/>
          <span style={{fontSize:12,fontWeight:500,color:active.length>0&&reviewedCount===active.length?SUCCESS:TEXT_MUTED}}>{reviewedCount} of {active.length} reviewed</span>
        </div>
      </div>

      <div style={{padding:"12px 28px 0"}}>
        {visible.length===0 ? (
          <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:"28px 20px",textAlign:"center",fontSize:13,color:TEXT_MUTED}}>No turns for this speaker.</div>
        ) : visible.map(s=>{
          if(s.deleted){
            return (<div key={s.id} id={"turn-"+s.id} style={{border:`1px dashed ${BORDER_STRONG}`,borderRadius:10,padding:"12px 18px",marginBottom:10,background:SURFACE_SUNKEN,display:"flex",alignItems:"center",gap:12,scrollMarginTop:pinned?210:14}}>
              <span style={{fontSize:13,color:TEXT_MUTED,fontStyle:"italic",flex:1}}>Turn deleted — <span style={{fontFamily:"monospace",fontStyle:"normal"}}>{s.speakerId}</span></span>
              <button onClick={()=>undoDelete(s.id)} style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:12,fontWeight:600,cursor:"pointer"}}>Undo</button>
              <button onClick={()=>dismissDelete(s.id)} style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_MUTED,fontSize:12,fontWeight:500,cursor:"pointer"}}>Dismiss</button>
            </div>);
          }
          const trueIndex=active.findIndex(x=>x.id===s.id);
          if(editingId===s.id){
            return (<div key={s.id}>
              <InsertButton label="Insert turn before" onClick={()=>insertBefore(s.id)}/>
              <TranscriptTurnEditor segment={s} speakers={speakers} channelMap={channelMap} total={active.length} index={trueIndex} duration={audioDuration}
                autoplayOnSelect={settings.autoplayOnSelect} autoplayOnSliderMove={settings.autoplayOnSliderMove}
                onSave={draft=>saveTurn(s.id,draft)} onDelete={()=>deleteSegment(s.id)} onCancel={cancelTurn} onMarkReviewed={draft=>saveAndReview(s.id,draft)}/>
              <InsertButton label="Insert turn after" onClick={()=>insertAfter(s.id)}/>
            </div>);
          }
          return <TranscriptTurnRow key={s.id} segment={s} speakers={speakers} channelMap={channelMap} index={trueIndex} onClick={()=>openTurn(s.id)}/>;
        })}
      </div>

      <div style={{padding:"16px 28px 0",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:10}}>
        {submitAttempted&&reviewedCount<active.length ? (
          <div style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",background:DANGER_BG,border:`1px solid ${DANGER_BORDER}`,borderRadius:8,padding:"10px 14px"}}>
            <span style={{fontSize:13,color:DANGER,fontWeight:500}}>{active.length-reviewedCount} of {active.length} turns still need review before you can submit.</span>
            <button onClick={goToFirstUnreviewed} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${DANGER}`,background:SURFACE,color:DANGER,fontSize:12,fontWeight:600,cursor:"pointer"}}>Go to first unreviewed</button>
          </div>
        ) : null}
        {submitted ? (
          <div style={{width:"100%",background:SUCCESS_BG,border:`1px solid ${SUCCESS_SOFT}`,borderRadius:8,padding:"10px 14px",fontSize:13,color:SUCCESS,fontWeight:500}}>Review submitted. (Demo — no backend.)</div>
        ) : null}
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={markSaved} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
          <button onClick={onSubmit} style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Submit Review →</button>
        </div>
      </div>

      {showMerge ? <MergeSpeakersModal speakers={speakers} segments={active} duration={audioDuration} onClose={()=>setShowMerge(false)} onMerge={mergeSpeakers}/> : null}
    </div>
  </div>);
}
// ── Audio Review screen ──────────────────────────────────────────────────────
// Reviewer listens to reference audio for each expected speaker, listens to the
// file under review, marks any quality flags (scripted / synthetic / pre-recorded),
// and confirms or flags each expected speaker.

// Visual-only audio player. No real playback — toggles a "playing" state for UI.
const AudioPlayerMock=({duration,compact})=>{
  const[playing,setPlaying]=useState(false);
  const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;
  // Deterministic fake waveform — bars of varying height
  const barCount=compact?28:56;
  const bars=Array.from({length:barCount},(_,i)=>0.25+Math.abs(Math.sin(i*0.5))*0.5+Math.abs(Math.cos(i*0.31))*0.25);
  return <div style={{display:"flex",alignItems:"center",gap:10,padding:compact?"7px 9px":"10px 14px",background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:8}}>
    <button onClick={()=>setPlaying(p=>!p)} aria-label={playing?"Pause":"Play"}
      style={{width:compact?26:34,height:compact?26:34,borderRadius:"50%",border:"none",background:P,color:SURFACE,fontSize:compact?10:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontWeight:600}}>
      {playing?"❚❚":"▶"}
    </button>
    <div style={{display:"flex",gap:1,alignItems:"center",flex:1,height:compact?22:30}}>
      {bars.map((h,i)=><div key={i} style={{flex:1,height:`${h*100}%`,background:BRAND_SOFT,borderRadius:1,minWidth:1,opacity:playing?1:0.65}}/>)}
    </div>
    <div style={{fontSize:11,color:TEXT_MUTED,fontVariantNumeric:"tabular-nums",flexShrink:0}}>0:00 / {fmt(duration)}</div>
  </div>;
};

// Per-variant config for AudioReviewScreen. The "Post-Model" variant adds model
// scores per voice: identity-match confidence + per-flag likelihood.
const AUDIO_REVIEW_VARIANTS={
  "Audio Review":{
    title:"Audio Review",
    showModelOutputs:false,
    expectedSpeakers:[
      {id:"u-401",name:"Sofia Reyes",initials:"SR",role:"Speaker 1",referenceDuration:28},
      {id:"u-402",name:"Carlos Vega",initials:"CV",role:"Speaker 2",referenceDuration:34},
    ],
    audioDuration:187,
  },
  "Post-Model Audio Review":{
    title:"Post-Model Audio Review",
    showModelOutputs:true,
    expectedSpeakers:[
      // High identity confidence, low flag scores — model thinks this is fine.
      {id:"u-411",name:"Maria Salazar",initials:"MS",role:"Speaker 1",referenceDuration:31,
        modelIdentityConfidence:0.94,
        modelFlagScores:{scripted:0.12,synthetic:0.07,prerecorded:0.08}},
      // Ambiguous identity + high "scripted" score — model is flagging this voice.
      {id:"u-412",name:"Diego Morales",initials:"DM",role:"Speaker 2",referenceDuration:38,
        modelIdentityConfidence:0.61,
        modelFlagScores:{scripted:0.85,synthetic:0.04,prerecorded:0.32}},
    ],
    audioDuration:204,
  },
};

// Color helpers — green = good (low concern), yellow = uncertain, red = concerning.
// For identity match: high % = good (it really is them) → green.
// For flag scores: low % = good (voice is NOT scripted/synthetic/pre-recorded) → green.
// Yellow inlined as #ca8a04 because the palette's WARNING token is a dark amber/brown.
const modelIdentityColor=s=>s>=0.8?SUCCESS:s>=0.5?"#ca8a04":DANGER;
const modelScoreColor=s=>s>=0.7?DANGER:s>=0.3?"#ca8a04":SUCCESS;

// Searchable single-select for picking "any other" language variant.
function VariantSearchSelect({value,placeholder,options,onChange}){
  const[open,setOpen]=useState(false);
  const[q,setQ]=useState("");
  const filtered=options.filter(o=>o.toLowerCase().includes(q.toLowerCase()));
  return(<div style={{position:"relative",minWidth:210}}>
    <input
      value={open?q:(value||"")}
      placeholder={placeholder||"Search variants…"}
      onFocus={()=>{setOpen(true);setQ("");}}
      onChange={e=>setQ(e.target.value)}
      onBlur={()=>setTimeout(()=>setOpen(false),120)}
      style={{width:"100%",padding:"5px 10px",border:`1.5px solid ${value&&!open?P:BORDER}`,borderRadius:999,fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box",color:value?P:TEXT_SECONDARY,fontWeight:value?600:500,background:value&&!open?PL:SURFACE}}/>
    {open&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,maxHeight:200,overflowY:"auto",background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:8,zIndex:30,boxShadow:"0 6px 20px rgba(0,0,0,0.12)"}}>
      {filtered.length===0&&<div style={{padding:"8px 10px",fontSize:12,color:TEXT_SUBTLE}}>No matches</div>}
      {filtered.map(o=>(
        <div key={o} onMouseDown={()=>{onChange(o);setOpen(false);}}
          style={{padding:"7px 10px",fontSize:12,color:TEXT_SECONDARY,cursor:"pointer"}}
          onMouseEnter={e=>e.currentTarget.style.background=SURFACE_SUNKEN}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{o}</div>
      ))}
    </div>}
  </div>);
}

function AudioReviewScreen({task,onBack}){
  // Pick the variant based on task type; fall back to plain Audio Review if unrecognized.
  const variant=AUDIO_REVIEW_VARIANTS[task.type]||AUDIO_REVIEW_VARIANTS["Audio Review"];
  const{title,showModelOutputs,expectedSpeakers}=variant;
  const audioUnderReview={filename:`${task.id}_recording.mp3`,duration:variant.audioDuration};
  const expectedVariant=task.expectedVariant||"Unknown";
  const otherVariants=LANGUAGE_VARIANTS.filter(x=>x!==expectedVariant);

  const[flagsBySpeaker,setFlagsBySpeaker]=useState({}); // {speakerId: {scripted, synthetic, prerecorded}}
  const[speakerConfirms,setSpeakerConfirms]=useState({}); // speakerId → "match" | "mismatch"
  const[heritageBySpeaker,setHeritageBySpeaker]=useState({}); // speakerId → "yes" | "no"
  const[sexBySpeaker,setSexBySpeaker]=useState({}); // speakerId → "male" | "female" | "unknown"
  const[variantBySpeaker,setVariantBySpeaker]=useState({}); // speakerId → variant string | "Unknown"
  const[otherIssues,setOtherIssues]=useState({}); // recording-level: {severeAudio, childVoice, copyrighted, other}
  const[otherIssueText,setOtherIssueText]=useState("");

  const toggleFlag=(sid,k)=>setFlagsBySpeaker(p=>({...p,[sid]:{...(p[sid]||{}),[k]:!(p[sid]&&p[sid][k])}}));
  const setConfirm=(sid,v)=>setSpeakerConfirms(p=>({...p,[sid]:v}));
  const setHeritage=(sid,v)=>setHeritageBySpeaker(p=>({...p,[sid]:v}));
  const setSex=(sid,v)=>setSexBySpeaker(p=>({...p,[sid]:v}));
  const setVariant=(sid,v)=>setVariantBySpeaker(p=>({...p,[sid]:v}));
  const toggleOtherIssue=k=>setOtherIssues(p=>({...p,[k]:!p[k]}));
  const speakerFlags=sid=>flagsBySpeaker[sid]||{};
  const hasAnyFlag=sid=>Object.values(speakerFlags(sid)).some(Boolean);

  const flaggedVoiceCount=expectedSpeakers.filter(s=>hasAnyFlag(s.id)).length;
  const mismatchCount=Object.values(speakerConfirms).filter(v=>v==="mismatch").length;
  const heritageCount=Object.values(heritageBySpeaker).filter(v=>v==="yes").length;
  const otherIssueCount=Object.values(otherIssues).filter(Boolean).length;

  const flagOptions=[
    {key:"scripted",    label:"Scripted"},
    {key:"synthetic",   label:"Synthetic"},
    {key:"prerecorded", label:"Pre-recorded"},
  ];

  const recordingIssueOptions=[
    {key:"severeAudio", label:"Severe audio issues"},
    {key:"childVoice",  label:"Child voice"},
    {key:"copyrighted", label:"Potentially copyrighted audio in background"},
    {key:"other",       label:"Other"},
  ];

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <TranscriptTaskHeader task={task} title={title} onBack={onBack}/>

    <div style={{flex:1,overflowY:"auto",padding:"20px 28px 24px"}}>

      {/* Expected speakers + reference audio */}
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:4}}>EXPECTED SPEAKERS</div>
        <p style={{fontSize:12,color:TEXT_MUTED,margin:"0 0 12px",lineHeight:1.5}}>Listen to each reference sample (from prior submissions) to familiarize yourself with the expected voices before reviewing the audio below.</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {expectedSpeakers.map((s,i)=>(
            <div key={s.id} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:14}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:PL,color:P,fontSize:14,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</div>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600,color:TEXT_PRIMARY}}>{s.role}</div>
                  <div style={{fontSize:11,color:TEXT_SUBTLE}}>Reference sample</div>
                </div>
              </div>
              <AudioPlayerMock duration={s.referenceDuration} compact/>
            </div>
          ))}
        </div>
      </div>

      {/* Audio under review */}
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:4}}>AUDIO UNDER REVIEW</div>
        <p style={{fontSize:12,color:TEXT_MUTED,margin:"0 0 12px",lineHeight:1.5}}>Listen to the full recording, then complete the checks below.</p>
        <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10,gap:8,flexWrap:"wrap"}}>
            <span style={{fontSize:13,fontFamily:"monospace",color:TEXT_SECONDARY}}>{audioUnderReview.filename}</span>
            <span style={{fontSize:11,color:TEXT_SUBTLE}}>Duration: {Math.floor(audioUnderReview.duration/60)}:{String(audioUnderReview.duration%60).padStart(2,"0")}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}>
            <span style={{fontSize:11,color:TEXT_SUBTLE}}>Expected variant:</span>
            <span style={{fontSize:12,fontWeight:600,color:TEXT_SECONDARY,padding:"2px 9px",borderRadius:999,background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`}}>{expectedVariant}</span>
          </div>
          <AudioPlayerMock duration={audioUnderReview.duration}/>
        </div>
      </div>

      {/* Speaker verification — per-voice match/mismatch + flag chips */}
      <div style={{marginBottom:24}}>
        <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:4,flexWrap:"wrap"}}>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>SPEAKER AND QUALITY VERIFICATION</div>
          {mismatchCount>0&&<span style={{fontSize:11,color:DANGER,fontWeight:600}}>· {mismatchCount} mismatch{mismatchCount>1?"es":""}</span>}
          {flaggedVoiceCount>0&&<span style={{fontSize:11,color:WARNING_DEEP,fontWeight:600}}>· {flaggedVoiceCount} voice{flaggedVoiceCount>1?"s":""} flagged</span>}
          {heritageCount>0&&<span style={{fontSize:11,color:TEXT_MUTED,fontWeight:600}}>· {heritageCount} heritage</span>}
        </div>
        <p style={{fontSize:12,color:TEXT_MUTED,margin:"0 0 10px",lineHeight:1.5}}>
          For each expected speaker, compare the voice in the recording to the reference sample. Confirm match or mismatch, and flag the voice if it sounds <strong>scripted</strong> (reading text), <strong>synthetic</strong> (AI/cloned/TTS), or <strong>pre-recorded</strong> (from a podcast, broadcast, etc). Also indicate whether each speaker is a <strong>heritage speaker</strong> (raised with the language at home but typically dominant in another language).
          {showModelOutputs&&<><br/><span style={{color:TEXT_SUBTLE}}>Model predictions are shown for context — the final call is yours.</span></>}
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {expectedSpeakers.map((s,i)=>{
            const v=speakerConfirms[s.id];
            const matchSel=v==="match",missSel=v==="mismatch";
            const sf=speakerFlags(s.id);
            const flagged=hasAnyFlag(s.id);
            const heritage=heritageBySpeaker[s.id];
            const borderColor=missSel?DANGER:(flagged?WARNING_DEEP:BORDER);
            const bg=missSel?DANGER_BG:(flagged?WARNING_BG:SURFACE);
            return <div key={s.id} style={{border:`1px solid ${borderColor}`,borderRadius:10,padding:"12px 14px",background:bg}}>
              {/* Top row: speaker identity + match/mismatch */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:TEXT_PRIMARY}}>Channel {i+1}</div>
                    <div style={{fontSize:11,color:TEXT_SUBTLE,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      {showModelOutputs&&s.modelIdentityConfidence!==undefined?(
                        <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"1px 7px",borderRadius:999,background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`}}>
                          <span style={{color:TEXT_MUTED}}>model match: </span>
                          <strong style={{color:modelIdentityColor(s.modelIdentityConfidence)}}>{Math.round(s.modelIdentityConfidence*100)}%</strong>
                        </span>
                      ):(
                        <span>Compare to reference above</span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>setConfirm(s.id,"match")}
                    style={{padding:"6px 14px",borderRadius:6,border:`1.5px solid ${matchSel?SUCCESS:BORDER}`,background:matchSel?SUCCESS_BG:SURFACE,color:matchSel?SUCCESS:TEXT_SECONDARY,fontSize:12,fontWeight:matchSel?600:500,cursor:"pointer"}}>
                    ✓ Match
                  </button>
                  <button onClick={()=>setConfirm(s.id,"mismatch")}
                    style={{padding:"6px 14px",borderRadius:6,border:`1.5px solid ${missSel?DANGER:BORDER}`,background:SURFACE,color:missSel?DANGER:TEXT_SECONDARY,fontSize:12,fontWeight:missSel?600:500,cursor:"pointer"}}>
                    ✗ Doesn't match
                  </button>
                </div>
              </div>
              {/* Channel waveform */}
              <div style={{marginTop:10}}>
                <AudioPlayerMock duration={audioUnderReview.duration} compact/>
              </div>
              {/* Bottom row: per-voice flag chips */}
              <div style={{marginTop:11,paddingTop:10,borderTop:`1px solid ${BORDER}`}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>FLAG THIS VOICE</span>
                  {flagOptions.map(({key,label})=>{
                    const on=!!sf[key];
                    const score=showModelOutputs?(s.modelFlagScores?.[key]):undefined;
                    return <button key={key} onClick={()=>toggleFlag(s.id,key)}
                      style={{padding:"4px 11px",borderRadius:999,border:`1.5px solid ${on?WARNING_DEEP:BORDER}`,background:on?WARNING_BG:SURFACE,color:on?WARNING_DEEP:TEXT_SECONDARY,fontSize:12,fontWeight:on?600:500,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}>
                      {on&&<span style={{fontSize:11}}>⚠</span>}
                      <span>{label}</span>
                      {score!==undefined&&(
                        <span style={{color:on?WARNING_DEEP:modelScoreColor(score),fontWeight:700,fontVariantNumeric:"tabular-nums",fontSize:11,paddingLeft:4,borderLeft:`1px solid ${on?WARNING_DEEP:BORDER}`,opacity:on?1:0.95}}>
                          {Math.round(score*100)}%
                        </span>
                      )}
                    </button>;
                  })}
                </div>
                {/* Heritage speaker designation */}
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:10}}>
                  <span style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>HERITAGE SPEAKER</span>
                  {[{key:"yes",label:"Yes"},{key:"no",label:"No"}].map(({key,label})=>{
                    const on=heritage===key;
                    return <button key={key} onClick={()=>setHeritage(s.id,key)}
                      style={{padding:"4px 16px",borderRadius:999,border:`1.5px solid ${on?P:BORDER}`,background:on?PL:SURFACE,color:on?P:TEXT_SECONDARY,fontSize:12,fontWeight:on?600:500,cursor:"pointer"}}>
                      {label}
                    </button>;
                  })}
                </div>
                {/* Speaker sex */}
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:10}}>
                  <span style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>SEX</span>
                  {[{key:"male",label:"Male"},{key:"female",label:"Female"},{key:"unknown",label:"Unknown"}].map(({key,label})=>{
                    const on=sexBySpeaker[s.id]===key;
                    return <button key={key} onClick={()=>setSex(s.id,key)}
                      style={{padding:"4px 14px",borderRadius:999,border:`1.5px solid ${on?P:BORDER}`,background:on?PL:SURFACE,color:on?P:TEXT_SECONDARY,fontSize:12,fontWeight:on?600:500,cursor:"pointer"}}>
                      {label}
                    </button>;
                  })}
                </div>
                {/* Language variant */}
                <div style={{marginTop:10}}>
                  <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>LANGUAGE VARIANT</div>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    {[{key:expectedVariant,label:expectedVariant+" (expected)"},{key:"Unknown",label:"Unknown"}].map(({key,label})=>{
                      const on=(variantBySpeaker[s.id]||"")===key;
                      return <button key={key} onClick={()=>setVariant(s.id,key)}
                        style={{padding:"4px 14px",borderRadius:999,border:`1.5px solid ${on?P:BORDER}`,background:on?PL:SURFACE,color:on?P:TEXT_SECONDARY,fontSize:12,fontWeight:on?600:500,cursor:"pointer"}}>
                        {label}
                      </button>;
                    })}
                    <VariantSearchSelect
                      value={(variantBySpeaker[s.id]&&variantBySpeaker[s.id]!==expectedVariant&&variantBySpeaker[s.id]!=="Unknown")?variantBySpeaker[s.id]:""}
                      placeholder="Other variant…"
                      options={otherVariants}
                      onChange={val=>setVariant(s.id,val)}/>
                  </div>
                </div>
              </div>
            </div>;
          })}
        </div>
      </div>

      {/* Other issues — recording-level */}
      <div style={{marginBottom:24}}>
        <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:4,flexWrap:"wrap"}}>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>OTHER ISSUES</div>
          {otherIssueCount>0&&<span style={{fontSize:11,color:DANGER,fontWeight:600}}>· {otherIssueCount} flagged</span>}
        </div>
        <p style={{fontSize:12,color:TEXT_MUTED,margin:"0 0 10px",lineHeight:1.5}}>
          Flag any issues with the recording as a whole — these are not tied to a specific speaker.
        </p>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          {recordingIssueOptions.map(({key,label})=>{
            const on=!!otherIssues[key];
            return <button key={key} onClick={()=>toggleOtherIssue(key)}
              style={{padding:"6px 13px",borderRadius:999,border:`1.5px solid ${on?DANGER:BORDER}`,background:on?DANGER_BG:SURFACE,color:on?DANGER:TEXT_SECONDARY,fontSize:12,fontWeight:on?600:500,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}>
              {on&&<span style={{fontSize:11}}>⚠</span>}
              <span>{label}</span>
            </button>;
          })}
        </div>
        {otherIssues.other&&(
          <div style={{marginTop:10}}>
            <textarea value={otherIssueText} onChange={e=>setOtherIssueText(e.target.value)} rows={3}
              placeholder="Describe the issue (required)…"
              style={{width:"100%",padding:"8px 10px",border:`1px solid ${otherIssueText.trim()?BORDER:DANGER_BORDER}`,borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none",lineHeight:1.5,fontFamily:"inherit"}}/>
            {!otherIssueText.trim()&&<div style={{fontSize:11,color:DANGER,marginTop:4}}>This field is required when “Other” is selected.</div>}
          </div>
        )}
      </div>
    </div>

    {/* Submit bar */}
    <div style={{padding:"14px 28px",borderTop:`1px solid ${BORDER}`,display:"flex",justifyContent:"flex-end",gap:8,background:SURFACE}}>
      <button style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
      <button onClick={onBack} style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Submit Review →</button>
    </div>
  </div>);
}

// ── Full Transcript Review QA screen ─────────────────────────────────────────
// The QA reviewer audits the work the original reviewer did. We show:
//   • text diffs per segment (what changed vs the original / pre-review state)
//   • diarization timing changes (start/end deltas)
//   • speaker changes (reassignments + newly-added speakers)
//   • tag insertions ([laugh], [sigh], [scream]) by the reviewer
// The QA can reassign segments to other speakers, edit text, and write feedback.

// Pre-review (raw) state — what the system gave the reviewer.
const QA_ORIGINAL={
  speakers:["speaker-1","speaker-2"],
  segments:[
    {id:1,speakerId:"speaker-1",startTime:0,endTime:5.5,text:"hello there how are you doing today"},
    {id:2,speakerId:"speaker-2",startTime:5.5,endTime:11.0,text:"Im doing alright just got back from the store"},
    {id:3,speakerId:"speaker-1",startTime:11.0,endTime:18.0,text:"thats good did you find what you were looking for"},
    {id:4,speakerId:"speaker-1",startTime:18.0,endTime:24.0,text:"yeah they had everything on sale this week"},
    {id:5,speakerId:"speaker-2",startTime:24.0,endTime:32.0,text:"thats great i should probably head over there too before they run out"},
    {id:6,speakerId:"speaker-1",startTime:32.0,endTime:38.0,text:"yeah you should they had a really good deal on coffee"},
  ],
};

// Post-review state — the original reviewer's submitted work.
const QA_REVIEWED={
  speakers:["speaker-1","speaker-2","speaker-3"], // reviewer added a third speaker
  segments:[
    {id:1,speakerId:"speaker-1",startTime:0,endTime:5.5,text:"Hello there, how are you doing today?"},
    {id:2,speakerId:"speaker-2",startTime:5.5,endTime:11.3,text:"I'm doing alright, just got back from the store [laugh]"},
    {id:3,speakerId:"speaker-1",startTime:11.3,endTime:18.0,text:"That's good. Did you find what you were looking for?"},
    {id:4,speakerId:"speaker-2",startTime:18.0,endTime:24.0,text:"Yeah, they had everything on sale this week."}, // speaker reassigned 1→2
    {id:5,speakerId:"speaker-2",startTime:24.0,endTime:32.0,text:"That's great. I should probably head over there too before they run out."},
    {id:6,speakerId:"speaker-1",startTime:32.0,endTime:38.0,text:"Yeah you should, they had a really good deal on coffee."},
    {id:7,speakerId:"speaker-3",startTime:38.0,endTime:43.0,text:"Did somebody mention coffee?"}, // new segment, new speaker
  ],
};

// Word-level diff via LCS. Returns ordered list of {op, token} where op ∈ {"eq","del","add"}.
// Whitespace is preserved as its own token so the joined output looks natural.
const wordDiff=(oldText,newText)=>{
  const tokenize=t=>(t||"").split(/(\s+)/).filter(Boolean);
  const o=tokenize(oldText),n=tokenize(newText);
  const m=o.length,k=n.length;
  // dp[i][j] = LCS length of o[i..] and n[j..]
  const dp=Array.from({length:m+1},()=>new Array(k+1).fill(0));
  for(let i=m-1;i>=0;i--)for(let j=k-1;j>=0;j--){
    dp[i][j]=o[i]===n[j]?dp[i+1][j+1]+1:Math.max(dp[i+1][j],dp[i][j+1]);
  }
  const ops=[];let i=0,j=0;
  while(i<m&&j<k){
    if(o[i]===n[j]){ops.push({op:"eq",token:o[i]});i++;j++;}
    else if(dp[i+1][j]>=dp[i][j+1]){ops.push({op:"del",token:o[i]});i++;}
    else{ops.push({op:"add",token:n[j]});j++;}
  }
  while(i<m)ops.push({op:"del",token:o[i++]});
  while(j<k)ops.push({op:"add",token:n[j++]});
  return ops;
};

// Renders text with the diff styling. Tags like [laugh] get a chip-style highlight
// when they're additions (they're inherently new content from the reviewer).
const DiffText=({original,current})=>{
  const ops=wordDiff(original,current);
  const isTag=t=>/^\[(laugh|sigh|scream)\]$/.test(t);
  return <span style={{lineHeight:1.7}}>
    {ops.map((o,idx)=>{
      if(o.op==="eq") return <span key={idx}>{o.token}</span>;
      if(o.op==="del") return <span key={idx} style={{textDecoration:"line-through",color:DANGER,background:DANGER_BG,padding:"0 2px",borderRadius:3}}>{o.token}</span>;
      // add
      if(isTag(o.token)) return <span key={idx} style={{display:"inline-block",background:BRAND_LIGHTER,color:P,fontFamily:"monospace",fontSize:12,padding:"1px 7px",borderRadius:999,fontWeight:600,margin:"0 1px",border:`1px solid ${BRAND_SOFT}`}}>{o.token}</span>;
      return <span key={idx} style={{color:SUCCESS,background:SUCCESS_BG,padding:"0 2px",borderRadius:3,fontWeight:500}}>{o.token}</span>;
    })}
  </span>;
};

function FullTranscriptReviewQAScreen({task,onBack}){
  // QA's working state — starts from the reviewer's submitted version. The QA can edit further.
  const[speakers,setSpeakers]=useState(QA_REVIEWED.speakers);
  const[segments,setSegments]=useState(QA_REVIEWED.segments.map(s=>({...s})));
  const[editingId,setEditingId]=useState(null);
  const[feedback,setFeedback]=useState("");

  const updateSegment=(id,patch)=>setSegments(prev=>prev.map(s=>s.id===id?{...s,...patch}:s));
  const reassignSpeaker=(id,newSpeakerId)=>updateSegment(id,{speakerId:newSpeakerId});
  const addSpeaker=()=>setSpeakers(prev=>[...prev,`speaker-${prev.length+1}`]);

  // Per-segment change summary vs original.
  const summarize=(reviewed,original)=>{
    if(!original) return {kind:"new"};
    const changed=[];
    if(reviewed.text!==original.text) changed.push("text");
    if(reviewed.startTime!==original.startTime||reviewed.endTime!==original.endTime) changed.push("timing");
    if(reviewed.speakerId!==original.speakerId) changed.push("speaker");
    return {kind:changed.length?"changed":"unchanged",changed,original};
  };

  const originalById=new Map(QA_ORIGINAL.segments.map(s=>[s.id,s]));
  const newSpeakers=speakers.filter(s=>!QA_ORIGINAL.speakers.includes(s));

  // Diarization timeline — render each segment as a bar; highlight those with timing changes.
  const totalDuration=Math.max(...segments.map(s=>s.endTime),...QA_ORIGINAL.segments.map(s=>s.endTime));
  const speakerColor=(sid)=>{
    const idx=speakers.indexOf(sid);
    return SPEAKER_COLORS[idx%SPEAKER_COLORS.length]||SPEAKER_COLORS[0];
  };

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <TranscriptTaskHeader task={task} title="Full Transcript Review QA" onBack={onBack}/>

    <div style={{flex:1,overflowY:"auto",paddingBottom:28}}>

      {/* Summary banner */}
      <div style={{margin:"16px 28px 12px",padding:"12px 16px",background:PL,border:`1px solid ${BRAND_SOFTER}`,borderRadius:10}}>
        <div style={{fontSize:11,fontWeight:700,color:P,letterSpacing:"0.08em",marginBottom:6}}>REVIEWING WORK BY</div>
        <div style={{fontSize:14,fontWeight:600,color:TEXT_PRIMARY}}>{task.originalReviewer||"Unknown reviewer"} <span style={{fontSize:12,color:TEXT_SUBTLE,fontWeight:400}}>· {task.originalReviewerId||"—"}</span></div>
        <div style={{fontSize:12,color:TEXT_MUTED,marginTop:6,lineHeight:1.5}}>
          Below: the reviewer's submitted transcript with their changes shown inline. <span style={{color:DANGER,background:DANGER_BG,padding:"0 3px",borderRadius:3}}>red strikethrough</span> = removed, <span style={{color:SUCCESS,background:SUCCESS_BG,padding:"0 3px",borderRadius:3}}>green</span> = added. Click any segment to make corrections.
        </div>
      </div>

      {/* Speakers panel — flag any speakers the reviewer added */}
      <div style={{margin:"0 28px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>SPEAKERS</div>
          <button onClick={addSpeaker} style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:11,cursor:"pointer"}}>+ Add Speaker</button>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {speakers.map(s=>{
            const isNew=!QA_ORIGINAL.speakers.includes(s);
            const st=speakerColor(s);
            return <span key={s} style={{display:"inline-flex",alignItems:"center",gap:6,background:st.bg,color:st.c,fontSize:12,fontFamily:"monospace",padding:"4px 10px",borderRadius:6,fontWeight:500,border:isNew?`1.5px solid ${SUCCESS}`:"none"}}>
              {s}
              {isNew&&<span style={{fontSize:9,fontWeight:700,background:SUCCESS,color:SURFACE,padding:"1px 5px",borderRadius:3,letterSpacing:"0.04em"}}>NEW</span>}
            </span>;
          })}
        </div>
      </div>

      {/* Diarization timeline — visualize timing changes */}
      <div style={{margin:"0 28px 18px"}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>DIARIZATION TIMELINE</div>
        <div style={{position:"relative",height:54,background:SURFACE_SUNKEN,borderRadius:8,border:`1px solid ${BORDER}`,overflow:"hidden"}}>
          {/* Reviewer's segments (top half) */}
          <div style={{position:"absolute",top:4,left:0,right:0,height:20}}>
            {segments.map(s=>{
              const sm=summarize(s,originalById.get(s.id));
              const timingChanged=sm.kind==="changed"&&sm.changed.includes("timing");
              const isNew=sm.kind==="new";
              const left=(s.startTime/totalDuration)*100;
              const width=Math.max(0.5,((s.endTime-s.startTime)/totalDuration)*100);
              const st=speakerColor(s.speakerId);
              return <div key={s.id} title={`Reviewed seg ${s.id}: ${s.startTime.toFixed(1)}–${s.endTime.toFixed(1)}s · ${s.speakerId}`}
                style={{position:"absolute",left:`${left}%`,width:`${width}%`,top:0,height:"100%",background:st.bg,border:`${(timingChanged||isNew)?2:1}px solid ${isNew?SUCCESS:timingChanged?WARNING:BORDER}`,borderRadius:3,boxSizing:"border-box"}}/>;
            })}
          </div>
          {/* Original segments (bottom half) */}
          <div style={{position:"absolute",top:30,left:0,right:0,height:20}}>
            {QA_ORIGINAL.segments.map(s=>{
              const left=(s.startTime/totalDuration)*100;
              const width=Math.max(0.5,((s.endTime-s.startTime)/totalDuration)*100);
              const st=speakerColor(s.speakerId);
              return <div key={s.id} title={`Original seg ${s.id}: ${s.startTime.toFixed(1)}–${s.endTime.toFixed(1)}s · ${s.speakerId}`}
                style={{position:"absolute",left:`${left}%`,width:`${width}%`,top:0,height:"100%",background:st.bg,opacity:0.5,border:`1px solid ${BORDER}`,borderRadius:3,boxSizing:"border-box"}}/>;
            })}
          </div>
          {/* Row labels */}
          <div style={{position:"absolute",top:4,left:6,fontSize:9,color:TEXT_SUBTLE,fontWeight:600,letterSpacing:"0.06em",pointerEvents:"none"}}>REVIEWED</div>
          <div style={{position:"absolute",top:30,left:6,fontSize:9,color:TEXT_SUBTLE,fontWeight:600,letterSpacing:"0.06em",pointerEvents:"none"}}>ORIGINAL</div>
        </div>
        <div style={{display:"flex",gap:14,marginTop:6,fontSize:11,color:TEXT_MUTED,flexWrap:"wrap"}}>
          <span><span style={{display:"inline-block",width:10,height:10,verticalAlign:"middle",background:SURFACE,border:`2px solid ${WARNING}`,borderRadius:2,marginRight:5}}/>timing changed</span>
          <span><span style={{display:"inline-block",width:10,height:10,verticalAlign:"middle",background:SURFACE,border:`2px solid ${SUCCESS}`,borderRadius:2,marginRight:5}}/>added by reviewer</span>
        </div>
      </div>

      {/* Per-segment diffs */}
      <div style={{padding:"0 28px"}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>SEGMENTS</div>
        {segments.map((s,i)=>{
          const sm=summarize(s,originalById.get(s.id));
          const st=speakerColor(s.speakerId);
          const original=sm.original;
          const isNew=sm.kind==="new";
          const isEditing=editingId===s.id;

          if(isEditing){
            return <div key={s.id} style={{marginBottom:10}}>
              <SegmentEditor segment={s} speakers={speakers} total={segments.length} index={i}
                onChange={patch=>updateSegment(s.id,patch)}
                onDelete={()=>{setSegments(prev=>prev.filter(x=>x.id!==s.id));setEditingId(null);}}
                onDone={()=>setEditingId(null)}/>
            </div>;
          }

          // Build the "change tags" row
          const changeTags=[];
          if(isNew) changeTags.push({label:"NEW SEGMENT",color:SUCCESS,bg:SUCCESS_BG});
          else {
            if(sm.changed.includes("text")) changeTags.push({label:"text edited",color:P,bg:PL});
            if(sm.changed.includes("timing")) changeTags.push({label:`timing: ${original.startTime.toFixed(1)}–${original.endTime.toFixed(1)}s → ${s.startTime.toFixed(1)}–${s.endTime.toFixed(1)}s`,color:WARNING_DEEP,bg:WARNING_PALE});
            if(sm.changed.includes("speaker")) changeTags.push({label:`speaker: ${original.speakerId} → ${s.speakerId}`,color:INFO,bg:INFO_BG});
          }

          return <div key={s.id} style={{border:`1px solid ${BORDER}`,borderRadius:10,padding:"14px 16px",marginBottom:10,background:SURFACE}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{width:24,height:24,borderRadius:"50%",background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:11,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</div>

              {/* Speaker dropdown for quick reassignment */}
              <select value={s.speakerId} onChange={e=>reassignSpeaker(s.id,e.target.value)}
                style={{background:st.bg,color:st.c,fontSize:12,fontFamily:"monospace",padding:"3px 8px",borderRadius:6,fontWeight:500,border:"none",cursor:"pointer",outline:"none"}}>
                {speakers.map(sp=><option key={sp} value={sp}>{sp}</option>)}
              </select>

              <span style={{fontSize:11,color:TEXT_SUBTLE,fontVariantNumeric:"tabular-nums"}}>{s.startTime.toFixed(1)}s – {s.endTime.toFixed(1)}s</span>

              {/* Change tags */}
              {changeTags.map((c,ci)=>(
                <span key={ci} style={{background:c.bg,color:c.color,fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:999,letterSpacing:"0.04em"}}>{c.label}</span>
              ))}

              <button onClick={()=>setEditingId(s.id)} style={{marginLeft:"auto",fontSize:11,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500}}>Edit →</button>
            </div>

            <div style={{fontSize:13,color:TEXT_SECONDARY,paddingLeft:34}}>
              {isNew
                ? <em style={{color:SUCCESS}}>{s.text}</em>
                : <DiffText original={original.text} current={s.text}/>}
            </div>
          </div>;
        })}
      </div>

      {/* High-level feedback */}
      <div style={{margin:"18px 28px 0"}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>HIGH-LEVEL FEEDBACK FOR THE REVIEWER</div>
        <textarea value={feedback} onChange={e=>setFeedback(e.target.value)} rows={4}
          placeholder="e.g. Great catch on the speaker reassignment in segment 4. Minor: a few places where punctuation could be cleaner."
          style={{width:"100%",padding:"10px 12px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit",lineHeight:1.5,boxSizing:"border-box"}}/>
      </div>
    </div>

    {/* Submit bar */}
    <div style={{padding:"14px 28px",borderTop:`1px solid ${BORDER}`,display:"flex",justifyContent:"flex-end",gap:8,background:SURFACE}}>
      <button style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
      <button onClick={onBack} style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Submit QA Review →</button>
    </div>
  </div>);
}

// ── Reviewer's QA Feedback view ──────────────────────────────────────────────
// Read-only view the ORIGINAL REVIEWER sees from their history. Shows the high-level
// feedback the QA wrote + any edits the QA made on top of the reviewer's submitted work.
function ReviewerQAFeedbackScreen({task,qaReview,onBack}){
  const {qaReviewer,qaCompleted,feedback,reviewerSegments,qaSegments,qaSpeakers}=qaReview;
  const reviewerById=new Map(reviewerSegments.map(s=>[s.id,s]));
  const speakerColor=(sid)=>{
    const idx=qaSpeakers.indexOf(sid);
    return SPEAKER_COLORS[idx%SPEAKER_COLORS.length]||SPEAKER_COLORS[0];
  };
  const summarize=(qa,reviewer)=>{
    if(!reviewer) return {kind:"new"};
    const changed=[];
    if(qa.text!==reviewer.text) changed.push("text");
    if(qa.startTime!==reviewer.startTime||qa.endTime!==reviewer.endTime) changed.push("timing");
    if(qa.speakerId!==reviewer.speakerId) changed.push("speaker");
    return {kind:changed.length?"changed":"unchanged",changed,reviewer};
  };
  const changesCount=qaSegments.filter(s=>summarize(s,reviewerById.get(s.id)).kind!=="unchanged").length;

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <div style={{padding:"14px 28px",borderBottom:`1px solid ${BORDER}`,background:SURFACE,display:"flex",alignItems:"center",gap:12}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",padding:0}}>← Back to History</button>
      <div style={{flex:1}}>
        <div style={{fontWeight:600,fontSize:15}}>QA feedback on {task.id}</div>
        <div style={{fontSize:12,color:TEXT_SUBTLE}}>{task.type} · {task.project}</div>
      </div>
    </div>

    <div style={{flex:1,overflowY:"auto",padding:"20px 28px"}}>

      {/* QA reviewer + high-level feedback */}
      <div style={{background:PL,border:`1px solid ${BRAND_SOFTER}`,borderRadius:10,padding:"14px 18px",marginBottom:18}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
          <div style={{width:32,height:32,borderRadius:"50%",background:SURFACE,color:P,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",border:`1.5px solid ${BRAND_SOFT}`}}>{qaReviewer.split(" ").map(n=>n[0]).join("")}</div>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:TEXT_PRIMARY}}>{qaReviewer}</div>
            <div style={{fontSize:11,color:TEXT_SUBTLE}}>QA reviewer · {qaCompleted}</div>
          </div>
        </div>
        <div style={{fontSize:11,fontWeight:600,color:P,letterSpacing:"0.08em",marginTop:10,marginBottom:5}}>HIGH-LEVEL FEEDBACK</div>
        <p style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.6,margin:0,whiteSpace:"pre-wrap"}}>{feedback||<em style={{color:TEXT_SUBTLE}}>No high-level feedback left.</em>}</p>
      </div>

      {/* Segment-by-segment changes */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>SEGMENTS</div>
        <span style={{fontSize:11,color:TEXT_MUTED}}>· {changesCount} of {qaSegments.length} edited by QA</span>
      </div>
      <div style={{fontSize:12,color:TEXT_MUTED,marginBottom:12,lineHeight:1.5}}>
        Showing your submitted work with QA's changes layered on top. <span style={{color:DANGER,background:DANGER_BG,padding:"0 3px",borderRadius:3}}>red strikethrough</span> = QA removed, <span style={{color:SUCCESS,background:SUCCESS_BG,padding:"0 3px",borderRadius:3}}>green</span> = QA added.
      </div>

      {qaSegments.map((s,i)=>{
        const sm=summarize(s,reviewerById.get(s.id));
        const st=speakerColor(s.speakerId);
        const reviewer=sm.reviewer;
        const isUnchanged=sm.kind==="unchanged";

        const changeTags=[];
        if(!isUnchanged&&reviewer){
          if(sm.changed.includes("text")) changeTags.push({label:"text edited by QA",color:P,bg:PL});
          if(sm.changed.includes("timing")) changeTags.push({label:`timing: ${reviewer.startTime.toFixed(1)}–${reviewer.endTime.toFixed(1)}s → ${s.startTime.toFixed(1)}–${s.endTime.toFixed(1)}s`,color:WARNING_DEEP,bg:WARNING_PALE});
          if(sm.changed.includes("speaker")) changeTags.push({label:`speaker: ${reviewer.speakerId} → ${s.speakerId}`,color:INFO,bg:INFO_BG});
        }

        return <div key={s.id} style={{border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 16px",marginBottom:10,background:isUnchanged?SURFACE_SUNKEN:SURFACE,opacity:isUnchanged?0.7:1}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:11,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</div>
            <span style={{background:st.bg,color:st.c,fontSize:12,fontFamily:"monospace",padding:"3px 8px",borderRadius:6,fontWeight:500}}>{s.speakerId}</span>
            <span style={{fontSize:11,color:TEXT_SUBTLE,fontVariantNumeric:"tabular-nums"}}>{s.startTime.toFixed(1)}s – {s.endTime.toFixed(1)}s</span>
            {isUnchanged
              ? <span style={{fontSize:10,color:TEXT_SUBTLE,letterSpacing:"0.04em",fontWeight:500}}>unchanged</span>
              : changeTags.map((c,ci)=>(
                  <span key={ci} style={{background:c.bg,color:c.color,fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:999,letterSpacing:"0.04em"}}>{c.label}</span>
                ))}
          </div>
          <div style={{fontSize:13,color:TEXT_SECONDARY,paddingLeft:34}}>
            {reviewer
              ? <DiffText original={reviewer.text} current={s.text}/>
              : <em style={{color:SUCCESS}}>{s.text}</em>}
          </div>
        </div>;
      })}
    </div>
  </div>);
}


// ── PII / PHI Redaction screen ───────────────────────────────────────────────
const PII_TYPES=[
  {key:"name",label:"Name",color:DANGER_SOFT,bg:DANGER_BG},
  {key:"phone",label:"Phone Number",color:ORANGE_ACCENT,bg:WARNING_ACCENT_SURFACE},
  {key:"email",label:"Email Address",color:AMBER_ACCENT,bg:WARNING_PALE},
  {key:"dob",label:"Date of Birth",color:SUCCESS_SOFT,bg:SUCCESS_BG},
  {key:"ssn",label:"SSN / Gov ID",color:INFO_ACCENT,bg:INFO_BG},
  {key:"address",label:"Address",color:BRAND_SOFT,bg:BRAND_LIGHT},
  {key:"mrn",label:"Medical Record No.",color:PINK,bg:PINK_BG},
  {key:"insurance",label:"Insurance ID",color:TEAL,bg:TEAL_BG},
  {key:"org",label:"Organization/Facility",color:INFO_ACCENT_SOFT,bg:INFO_ACCENT_TINT},
  {key:"passport",label:"Passport No.",color:DANGER,bg:RED_TINT},
];
const PII_TYPE_MAP=Object.fromEntries(PII_TYPES.map(t=>[t.key,t]));

// Sample turns. `redacted` contains character-offset spans already redacted by the system.
// The reviewer's job is to find any PII / PHI the auto-redactor missed and tag it.
const INITIAL_PII_TURNS=[
  {id:"p1",speaker:"Speaker A",time:"0:00 – 0:14",
    text:"Hi, my name is Abena Mensah and I'm calling about my account. My date of birth is March 14, 1982 and I live at 14 Ringway Estate, East Legon, Accra.",
    redacted:[{start:82,end:96}]}, // "March 14, 1982"
  {id:"p2",speaker:"Speaker B",time:"0:15 – 0:27",
    text:"Sure, can I get your phone number and the last four digits of your national ID?",
    redacted:[]},
  {id:"p3",speaker:"Speaker A",time:"0:28 – 0:42",
    text:"Yes, my number is 0244-783-912. My email is abena.mensah@gmail.com and the account is under AB-55831.",
    redacted:[{start:93,end:101}]}, // "AB-55831"
  {id:"p4",speaker:"Speaker B",time:"0:43 – 0:58",
    text:"Thank you. I can see the policy linked to Korle Bu Teaching Hospital. Is your medical record number still KBT-00482917?",
    redacted:[]},
  {id:"p5",speaker:"Speaker A",time:"0:59 – 1:12",
    text:"Yes, that's correct. My SSN on file is 732-11-8845 and my insurance ID is GHA-INS-774421-B.",
    redacted:[{start:39,end:50}]}, // "732-11-8845"
  {id:"p6",speaker:"Speaker B",time:"1:13 – 1:24",
    text:"Great, I've verified your identity. Is 14 Ringway Estate, East Legon still the best address to send correspondence?",
    redacted:[]},
  {id:"p7",speaker:"Speaker A",time:"1:25 – 1:38",
    text:"Yes, that's still my address. You can also send a copy to my sister Afia at afia.m@example.com if needed.",
    redacted:[]},
  {id:"p8",speaker:"Speaker B",time:"1:39 – 1:52",
    text:"Noted. One last thing — for passport-related documents we have number G1234567 on file. Should we keep that as primary?",
    redacted:[{start:74,end:82}]}, // "G1234567"
  {id:"p9",speaker:"Speaker A",time:"1:53 – 2:04",
    text:"Yes, keep that as primary. Thanks for double-checking everything.",
    redacted:[]},
  {id:"p10",speaker:"Speaker B",time:"2:05 – 2:14",
    text:"Perfect. Is there anything else I can help you with today, Ms. Mensah?",
    redacted:[]},
];
const totalCharsInTurns=INITIAL_PII_TURNS.reduce((s,t)=>s+t.text.length,0);

function PIIRedactionScreen({task,onBack}){
  const [turns]=useState(INITIAL_PII_TURNS);
  const [reviewed,setReviewed]=useState({});           // turnId -> true
  const [userTags,setUserTags]=useState({});           // turnId -> [{id,start,end,text,category}]
  const [selection,setSelection]=useState(null);       // {turnId,start,end,text,top,left}
  const [showInstructions,setShowInstructions]=useState(true);
  const popoverRef=useRef(null);
  const turnRefs=useRef({});

  const reviewedCount=Object.values(reviewed).filter(Boolean).length;
  const totalTags=Object.values(userTags).reduce((s,arr)=>s+(arr||[]).length,0);

  // Close popover on outside click / escape
  useEffect(()=>{
    if(!selection)return;
    const h=e=>{
      if(popoverRef.current&&popoverRef.current.contains(e.target))return;
      setSelection(null);
      window.getSelection()?.removeAllRanges();
    };
    const esc=e=>{if(e.key==="Escape"){setSelection(null);window.getSelection()?.removeAllRanges();}};
    document.addEventListener("mousedown",h);
    document.addEventListener("keydown",esc);
    return()=>{document.removeEventListener("mousedown",h);document.removeEventListener("keydown",esc);};
  },[selection]);

  const applyTag=(categoryKey)=>{
    if(!selection)return;
    const newTag={id:Date.now()+Math.random(),start:selection.start,end:selection.end,text:selection.text,category:categoryKey};
    setUserTags(prev=>({...prev,[selection.turnId]:[...(prev[selection.turnId]||[]),newTag]}));
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const removeTag=(turnId,tagId)=>{
    setUserTags(prev=>({...prev,[turnId]:(prev[turnId]||[]).filter(t=>t.id!==tagId)}));
  };

  const markReviewed=(turnId)=>{
    setReviewed(prev=>({...prev,[turnId]:!prev[turnId]}));
  };

  const jumpToNext=()=>{
    const next=turns.find(t=>!reviewed[t.id]);
    if(!next)return;
    const el=turnRefs.current[next.id];
    if(el)el.scrollIntoView({behavior:"smooth",block:"center"});
  };

  // Handle text selection within a turn body
  const handleTurnMouseUp=(turnId)=>(e)=>{
    setTimeout(()=>{
      const sel=window.getSelection();
      if(!sel||sel.isCollapsed)return;
      const selectedText=sel.toString();
      if(!selectedText.trim())return;
      const range=sel.getRangeAt(0);

      // Find the run-span via closest ancestor with a data-text-start attribute that belongs to this turn
      const findRun=(node)=>{
        let el=node.nodeType===1?node:node.parentElement;
        while(el&&!(el.dataset&&el.dataset.textStart!==undefined&&el.dataset.turnId===turnId)) el=el.parentElement;
        return el;
      };
      const startRun=findRun(range.startContainer);
      const endRun=findRun(range.endContainer);
      if(!startRun||!endRun)return;
      // Must be entirely within a single un-annotated text run
      if(startRun!==endRun)return;

      const runStart=parseInt(startRun.dataset.textStart,10);
      const absStart=runStart+range.startOffset;
      const absEnd=runStart+range.endOffset;
      if(absEnd<=absStart)return;

      // Trim whitespace from the edges of the selection
      const rawText=turns.find(t=>t.id===turnId).text.substring(absStart,absEnd);
      const leading=rawText.length-rawText.trimStart().length;
      const trailing=rawText.length-rawText.trimEnd().length;
      const trimmedStart=absStart+leading;
      const trimmedEnd=absEnd-trailing;
      if(trimmedEnd<=trimmedStart)return;

      const rect=range.getBoundingClientRect();
      setSelection({
        turnId,
        start:trimmedStart,
        end:trimmedEnd,
        text:rawText.trim(),
        top:rect.bottom+6,
        left:rect.left,
      });
    },0);
  };

  // Build the list of rendered parts for a turn by merging redacted spans and user tags
  const buildParts=(turn)=>{
    const anns=[
      ...turn.redacted.map(r=>({...r,kind:"redacted"})),
      ...(userTags[turn.id]||[]).map(t=>({...t,kind:"user"})),
    ].sort((a,b)=>a.start-b.start);
    const parts=[];
    let cursor=0;
    for(const a of anns){
      if(a.start>cursor) parts.push({kind:"text",start:cursor,end:a.start,text:turn.text.substring(cursor,a.start)});
      parts.push({...a,text:turn.text.substring(a.start,a.end)});
      cursor=a.end;
    }
    if(cursor<turn.text.length) parts.push({kind:"text",start:cursor,end:turn.text.length,text:turn.text.substring(cursor)});
    return parts;
  };

  return(<div style={{flex:1,display:"flex",overflow:"hidden"}}>
    {/* Left sidebar */}
    <div style={{width:270,borderRight:`1px solid ${BORDER}`,overflowY:"auto",flexShrink:0,background:SURFACE_ALT,padding:"18px 18px"}}>
      {/* Task info */}
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:"14px 14px",marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
          <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>PII / PHI REDACTION EVAL</div>
          <button onClick={onBack} title="Back to My Tasks" style={{background:SURFACE_HOVER,border:"none",borderRadius:6,width:22,height:22,cursor:"pointer",color:TEXT_MUTED,fontSize:12,padding:0,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>–</button>
        </div>
        <div style={{fontSize:18,fontWeight:700,color:TEXT_PRIMARY,marginBottom:8}}>{task.id}</div>
        <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:6}}>
          {task.project&&<span style={{background:PL,color:P,fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:4}}>{task.project}</span>}
          {task.batch&&<span style={{background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:4}}>{task.batch}</span>}
        </div>
        <div style={{fontSize:12,color:TEXT_SUBTLE}}>Due {task.deadline}</div>
      </div>

      {/* Review progress */}
      <div style={{marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>REVIEW PROGRESS</div>
          <div style={{fontSize:13,color:P,fontWeight:600}}>{reviewedCount}/{turns.length}</div>
        </div>
        <div style={{height:6,background:BRAND_LIGHT,borderRadius:3,overflow:"hidden",marginBottom:6}}>
          <div style={{height:"100%",width:`${(reviewedCount/turns.length)*100}%`,background:P,transition:"width 0.2s"}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:11,color:TEXT_MUTED}}>{totalTags} PII tag{totalTags===1?"":"s"} placed</div>
          <button onClick={jumpToNext} disabled={reviewedCount>=turns.length} style={{background:"none",border:"none",color:reviewedCount>=turns.length?TEXT_DISABLED:P,fontSize:11,cursor:reviewedCount>=turns.length?"default":"pointer",fontWeight:500,padding:0}}>Jump to next →</button>
        </div>
      </div>

      {/* Instructions */}
      <div style={{marginBottom:14,border:`1px solid ${BORDER}`,borderRadius:8,background:SURFACE}}>
        <div onClick={()=>setShowInstructions(v=>!v)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 12px",cursor:"pointer",borderBottom:showInstructions?`1px solid ${SURFACE_HOVER}`:"none"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,color:TEXT_PRIMARY}}>
            <span style={{fontSize:14}}></span> Instructions
          </div>
          <span style={{fontSize:10,color:TEXT_SUBTLE}}>{showInstructions?"▲":"▼"}</span>
        </div>
        {showInstructions&&(
          <div style={{padding:"10px 12px",fontSize:12,color:TEXT_SECONDARY,lineHeight:1.55}}>
            <p style={{margin:"0 0 8px"}}>The transcript has been <strong style={{color:P}}>auto-redacted</strong> — redacted spans appear as <span style={{background:NEUTRAL_DARK,color:SURFACE,fontSize:10,fontWeight:600,padding:"1px 6px",borderRadius:3,fontFamily:"monospace"}}>[REDACTED]</span>.</p>
            <p style={{margin:"0 0 8px"}}>Your job is to catch PII / PHI the system <strong style={{color:P}}>missed</strong>. Highlight any missed span and tag it with a category.</p>
            <p style={{margin:0}}>When you're done with a turn — tagged or not — click <strong style={{color:SUCCESS}}>Mark reviewed</strong>. Every turn must be reviewed before submit.</p>
          </div>
        )}
      </div>

      {/* Categories */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>PII / PHI CATEGORIES</div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {PII_TYPES.map(t=>(
            <div key={t.key} style={{display:"flex",alignItems:"center",gap:8,padding:"3px 2px"}}>
              <span style={{width:10,height:10,borderRadius:"50%",background:t.color,flexShrink:0}}/>
              <span style={{fontSize:12,color:TEXT_SECONDARY}}>{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tagged instances */}
      <div>
        <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>TAGGED INSTANCES ({totalTags})</div>
        {totalTags===0?(
          <div style={{fontSize:11,color:TEXT_SUBTLE,fontStyle:"italic",lineHeight:1.5}}>No tags yet. Highlight missed PII in any turn.</div>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            {turns.flatMap(t=>(userTags[t.id]||[]).map(tag=>{
              const cat=PII_TYPE_MAP[tag.category];
              return(<div key={tag.id} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:6,padding:"6px 8px",display:"flex",alignItems:"center",gap:6}}>
                <span style={{width:8,height:8,borderRadius:"50%",background:cat.color,flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:600,color:cat.color,marginBottom:1}}>{cat.label}</div>
                  <div style={{fontSize:11,color:TEXT_SECONDARY,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>"{tag.text}"</div>
                  <div style={{fontSize:10,color:TEXT_SUBTLE}}>{t.id} · {t.speaker}</div>
                </div>
                <button onClick={()=>removeTag(t.id,tag.id)} style={{background:"none",border:"none",color:TEXT_SUBTLE,fontSize:12,cursor:"pointer",padding:0,lineHeight:1}} title="Remove tag">×</button>
              </div>);
            }))}
          </div>
        )}
      </div>
    </div>

    {/* Right: transcript */}
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Top bar */}
      <div style={{padding:"12px 28px",borderBottom:`1px solid ${BORDER}`,background:SURFACE_SUNKEN,flexShrink:0,display:"flex",alignItems:"center",gap:14}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",padding:0}}>← My Tasks</button>
        <span style={{color:TEXT_DISABLED}}>|</span>
        <span style={{fontSize:14,fontWeight:500,color:TEXT_PRIMARY}}>Task Review</span>
      </div>
      <div style={{padding:"22px 28px 10px",flexShrink:0,display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:16}}>
        <div>
          <div style={{fontSize:16,fontWeight:700,color:TEXT_PRIMARY,marginBottom:4}}>Auto-redacted Transcript</div>
          <div style={{fontSize:12,color:TEXT_MUTED}}>Find missed PII · highlight text to tag · mark each turn reviewed</div>
        </div>
        <div style={{background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:12,fontWeight:500,padding:"5px 12px",borderRadius:6,whiteSpace:"nowrap"}}>{turns.length} turns · {totalCharsInTurns.toLocaleString()} chars</div>
      </div>

      {/* Turns */}
      <div style={{flex:1,overflowY:"auto",padding:"8px 28px 16px"}}>
        {turns.map(turn=>{
          const isReviewed=!!reviewed[turn.id];
          const parts=buildParts(turn);
          const turnTags=userTags[turn.id]||[];
          return(<div key={turn.id} ref={el=>{turnRefs.current[turn.id]=el;}}
            style={{border:`1px solid ${isReviewed?SUCCESS_LIGHT:BORDER}`,borderRadius:10,marginBottom:10,background:SURFACE,overflow:"hidden"}}>
            <div style={{padding:"10px 16px",background:isReviewed?SUCCESS_SURFACE:SURFACE_ALT,borderBottom:`1px solid ${SURFACE_HOVER}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,fontSize:12}}>
                <span style={{color:TEXT_SUBTLE,fontFamily:"monospace"}}>{turn.id}</span>
                <span style={{color:P,fontWeight:600}}>{turn.speaker}</span>
                <span style={{color:TEXT_SUBTLE}}>{turn.time}</span>
              </div>
              <button onClick={()=>markReviewed(turn.id)} style={{
                background:isReviewed?SUCCESS:SUCCESS_BG,
                color:isReviewed?SURFACE:SUCCESS,
                border:`1px solid ${isReviewed?SUCCESS:SUCCESS_LIGHT}`,
                borderRadius:6,padding:"4px 12px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"
              }}>
                {isReviewed?"✓ Reviewed":(turnTags.length>0?`✓ ${turnTags.length} tagged · mark reviewed`:"✓ No missed PII · mark reviewed")}
              </button>
            </div>
            <p onMouseUp={handleTurnMouseUp(turn.id)}
              style={{fontSize:14,color:TEXT_SECONDARY,lineHeight:1.85,margin:0,padding:"14px 16px",userSelect:"text"}}>
              {parts.map((p,i)=>{
                if(p.kind==="text") return(<span key={i} data-turn-id={turn.id} data-text-start={p.start}>{p.text}</span>);
                if(p.kind==="redacted") return(<span key={i} style={{background:NEUTRAL_DARK,color:SURFACE,fontSize:12,fontWeight:600,padding:"2px 10px",borderRadius:4,fontFamily:"monospace",userSelect:"none",margin:"0 2px",display:"inline-block"}}>[REDACTED]</span>);
                // user tag
                const cat=PII_TYPE_MAP[p.category];
                return(<span key={i} onClick={e=>{e.stopPropagation();removeTag(turn.id,p.id);}}
                  style={{background:cat.bg,color:cat.color,fontWeight:600,padding:"1px 5px",borderRadius:3,cursor:"pointer",userSelect:"none"}}
                  title={`${cat.label} — click to remove`}>{p.text}</span>);
              })}
            </p>
          </div>);
        })}

        {/* Footer actions */}
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,paddingTop:6}}>
          <button style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
          <button disabled={reviewedCount<turns.length} style={{padding:"9px 22px",borderRadius:8,border:"none",background:reviewedCount<turns.length?BRAND_SOFTER:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:reviewedCount<turns.length?"default":"pointer"}}>Submit Redaction →</button>
        </div>
      </div>
    </div>

    {/* Floating tag popover */}
    {selection&&(
      <div ref={popoverRef} style={{position:"fixed",top:selection.top,left:selection.left,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,0.18)",zIndex:200,padding:"8px 0",minWidth:220}}>
        <div style={{fontSize:10,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",padding:"4px 14px 8px"}}>TAG AS…</div>
        <div style={{padding:"0 10px 8px"}}>
          <div style={{background:BRAND_LIGHT,color:P,fontSize:12,fontWeight:500,padding:"5px 10px",borderRadius:6,wordBreak:"break-all",textAlign:"center"}}>"{selection.text}"</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:4,padding:"0 8px 6px"}}>
          {PII_TYPES.map(opt=>(
            <div key={opt.key} onClick={()=>applyTag(opt.key)}
              style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",cursor:"pointer",fontSize:12,color:opt.color,fontWeight:500,border:`1px solid ${opt.bg}`,borderRadius:6,background:SURFACE}}
              onMouseEnter={e=>e.currentTarget.style.background=opt.bg}
              onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>
              <span style={{width:8,height:8,borderRadius:"50%",background:opt.color}}/>
              {opt.label}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>);
}


// ── Shared diarization helpers ──────────────────────────────────────────────

// Blue banner notice at the top of both diarization screens
const DiarizationNotice=({onClose})=>(
  <div style={{margin:"0 28px 14px",background:INFO_SURFACE,border:`1px solid ${INFO_BORDER}`,borderRadius:10,padding:"14px 18px",position:"relative"}}>
    <div style={{fontSize:13,fontWeight:700,color:INFO,marginBottom:6}}>Short vocalizations ("mmms," "ahs," etc.)</div>
    <div style={{fontSize:12,color:INFO_DEEP,lineHeight:1.55,paddingRight:24}}>
      It is okay if some of these are missed, as long as they do not impact the context or meaning. For example, if someone asks a question and the response is "Mhm," this should be preserved because it conveys agreement. In contrast, there is more flexibility with filler sounds in the middle of a sentence. Transcribers may use their judgment to include/prioritize these sounds when they meaningfully contribute to the flow or interpretation of the conversation.
    </div>
    <button onClick={onClose} style={{position:"absolute",top:10,right:10,background:"none",border:"none",color:INFO,fontSize:14,cursor:"pointer",padding:0,width:20,height:20,lineHeight:1}}>×</button>
  </div>
);

// Full-audio waveform view. Segments (if any) shown as colored overlay spans
const FullWaveform=({duration,currentTime,playing,onTogglePlay,speed,onSpeedChange,segments,speakers,onSegmentClick,showToolbar=true,playbackChannel,onPlaybackChannelChange,channelMap})=>{
  const totalS=Math.max(1,duration);
  // Bar heights reflect activity: tall/varied inside segments, flat in the gaps.
  const bars=segWaveBars(segments,totalS,180,0);
  const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;
  return(<div style={{margin:"0 28px 14px",border:`1px solid ${BORDER}`,borderRadius:10,padding:"14px 18px",background:SURFACE}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <button onClick={onTogglePlay} style={{padding:"7px 18px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:7,minWidth:84,justifyContent:"center"}}>
          <span style={{fontSize:11}}>{playing?"❚❚":"▶"}</span> {playing?"Pause":"Play"}
        </button>
        <select value={speed} onChange={e=>onSpeedChange(parseFloat(e.target.value))}
          style={{padding:"6px 10px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:12,outline:"none",background:SURFACE,cursor:"pointer"}}>
          <option value="0.5">0.5x</option>
          <option value="1">1x</option>
          <option value="1.25">1.25x</option>
          <option value="1.5">1.5x</option>
          <option value="2">2x</option>
        </select>
        {onPlaybackChannelChange ? (
          <select value={playbackChannel} onChange={e=>onPlaybackChannelChange(e.target.value)} title="Playback source"
            style={{padding:"6px 10px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:12,outline:"none",background:SURFACE,cursor:"pointer"}}>
            {PLAYBACK_CHANNELS.map(c=><option key={c} value={c}>{channelLabel(c,speakers,channelMap)}</option>)}
          </select>
        ) : null}
      </div>
      <div style={{fontSize:12,color:TEXT_MUTED,fontFamily:"monospace"}}>{fmt(currentTime)} / {fmt(totalS)}</div>
    </div>
    {/* Waveform track */}
    <div style={{position:"relative",height:64,background:SURFACE_ALT,borderRadius:6,overflow:"hidden"}}>
      {/* Segment overlays */}
      {(segments||[]).map(seg=>{
        const sp=speakerStyle(speakers,seg.speakerId);
        const left=(seg.startTime/totalS)*100;
        const width=Math.max(0.5,((seg.endTime-seg.startTime)/totalS)*100);
        return(<div key={seg.id} onClick={e=>{e.stopPropagation();onSegmentClick?.(seg.id);}}
          style={{position:"absolute",top:0,bottom:0,left:`${left}%`,width:`${width}%`,background:sp.bg,opacity:0.55,borderLeft:`2px solid ${sp.c}`,borderRight:`2px solid ${sp.c}`,cursor:"pointer"}}
          title={`${seg.speakerId} · ${seg.startTime.toFixed(1)}s – ${seg.endTime.toFixed(1)}s`}/>);
      })}
      {/* Bars */}
      <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",height:"100%",padding:"0 8px",gap:1,pointerEvents:"none"}}>
        {bars.map((h,i)=>(<div key={i} style={{flex:1,height:`${h}%`,background:TEXT_SUBTLE,opacity:0.55,borderRadius:1}}/>))}
      </div>
      {/* Left start marker */}
      <div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:DANGER_SOFT,zIndex:2}}/>
      {/* Playhead */}
      <div style={{position:"absolute",top:0,bottom:0,left:`${(currentTime/totalS)*100}%`,width:2,background:P,zIndex:3}}/>
    </div>
    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:TEXT_SUBTLE,marginTop:4}}>
      <span>0:00</span>
      <span>{fmt(totalS)}</span>
    </div>
    {/* Toolbar row */}
    {showToolbar ? (
    <div style={{marginTop:10,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
      <button style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${WARNING_SOFT}`,background:SURFACE,color:WARNING_ACCENT,fontSize:11,fontWeight:500,cursor:"pointer"}}>
        Channel setup
      </button>
    </div>
    ) : null}
  </div>);
};

// Editable speakers panel (for diarization) — chips with pencil edit, Add/Merge buttons
const EditableSpeakersPanel=({speakers,onAddSpeaker,onRenameSpeaker,onMergeClick,allowMerge})=>{
  const [editingId,setEditingId]=useState(null);
  const [draft,setDraft]=useState("");
  const startEdit=id=>{setEditingId(id);setDraft(id);};
  const commitEdit=()=>{
    const trimmed=draft.trim();
    if(trimmed&&trimmed!==editingId&&!speakers.includes(trimmed)){
      onRenameSpeaker(editingId,trimmed);
    }
    setEditingId(null);
  };
  return(<div style={{margin:"0 28px 14px",border:`1px solid ${BORDER}`,borderRadius:10,padding:"14px 18px",background:SURFACE}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        
        <span style={{fontSize:13,fontWeight:600,color:TEXT_PRIMARY}}>Speakers</span>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={onAddSpeaker} style={{padding:"5px 12px",borderRadius:6,border:"none",background:SUCCESS_SOFT,color:SURFACE,fontSize:12,fontWeight:600,cursor:"pointer"}}>+ Add Speaker</button>
        {allowMerge&&speakers.length>=2&&<button onClick={onMergeClick} style={{padding:"5px 12px",borderRadius:6,border:"none",background:P,color:SURFACE,fontSize:12,fontWeight:600,cursor:"pointer"}}>Merge speakers</button>}
      </div>
    </div>
    {speakers.length===0?(
      <div style={{background:SURFACE_ALT,border:`1px solid ${SURFACE_HOVER}`,borderRadius:6,padding:"12px",textAlign:"center",fontSize:12,color:TEXT_SUBTLE}}>
        No speakers yet. Add a speaker or import a transcript.
      </div>
    ):(
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {speakers.map(s=>{
          const st=speakerStyle(speakers,s);
          if(editingId===s){
            return(<input key={s} autoFocus value={draft} onChange={e=>setDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={e=>{if(e.key==="Enter")commitEdit();if(e.key==="Escape")setEditingId(null);}}
              style={{background:st.bg,color:st.c,fontSize:13,fontFamily:"monospace",padding:"5px 12px",borderRadius:6,fontWeight:600,border:`1.5px solid ${st.c}`,outline:"none",width:140}}/>);
          }
          return(<span key={s} style={{background:st.bg,color:st.c,fontSize:13,fontFamily:"monospace",padding:"5px 12px",borderRadius:6,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
            {s}
            <span onClick={()=>startEdit(s)} style={{cursor:"pointer",opacity:0.7,fontSize:11}} title="Rename">Edit</span>
          </span>);
        })}
      </div>
    )}
  </div>);
};

// Merge speakers modal
const MergeSpeakersModal=({speakers,onClose,onMerge,segments,duration})=>{
  const [selected,setSelected]=useState([]);
  const [keep,setKeep]=useState("");
  const segs=segments||[];
  const totalS=Math.max(1,duration||(segs.length?Math.max(60,...segs.map(s=>s.endTime)):60));
  const bars=Array.from({length:90}).map((_,i)=>35+Math.sin(i*0.4)*18+Math.cos(i*0.17)*13+Math.sin(i*1.1)*9);
  const toggleSelect=s=>setSelected(prev=>{
    if(prev.includes(s)){if(keep===s)setKeep("");return prev.filter(x=>x!==s);}
    return [...prev,s];
  });
  const chooseKeep=s=>{setKeep(s);setSelected(prev=>prev.includes(s)?prev:[...prev,s]);};
  const sources=selected.filter(s=>s!==keep);
  const canMerge=keep&&sources.length>0;
  const confirm=()=>{if(canMerge)sources.forEach(src=>onMerge(src,keep));};
  const keepSt=keep?speakerStyle(speakers,keep):null;
  const combined=segs.filter(s=>selected.includes(s.speakerId));
  const wave=(items,colorId,key)=>{
    const cs=colorId?speakerStyle(speakers,colorId):null;
    return (<div style={{position:"relative",height:30,background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:5,overflow:"hidden"}}>
      {items.map(seg=>{
        const sp=cs||speakerStyle(speakers,seg.speakerId);
        const left=(seg.startTime/totalS)*100;
        const width=Math.max(0.5,((seg.endTime-seg.startTime)/totalS)*100);
        return <div key={key+seg.id} style={{position:"absolute",top:0,bottom:0,left:`${left}%`,width:`${width}%`,background:sp.bg,opacity:0.85,borderLeft:`2px solid ${sp.c}`,borderRight:`2px solid ${sp.c}`,zIndex:2,transition:"background 0.2s,border-color 0.2s"}}/>;
      })}
      <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",height:"100%",padding:"0 5px",gap:1,pointerEvents:"none"}}>
        {bars.map((h,i)=><div key={i} style={{flex:1,height:`${Math.max(10,Math.min(100,h))}%`,background:TEXT_SUBTLE,opacity:0.35,borderRadius:1}}/>)}
      </div>
    </div>);
  };
  return(<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}>
    <div style={{background:SURFACE,borderRadius:12,padding:24,width:560,maxWidth:"calc(100vw - 24px)",maxHeight:"calc(100vh - 48px)",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <div style={{fontSize:16,fontWeight:700}}>Merge speakers</div>
        <button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button>
      </div>
      <p style={{fontSize:12,color:TEXT_MUTED,marginBottom:16}}>Select the speakers to merge, then choose which one remains. The others' segments fold into the speaker that remains.</p>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
        {speakers.map(s=>{
          const st=speakerStyle(speakers,s);
          const isSel=selected.includes(s);
          const mine=segs.filter(x=>x.speakerId===s);
          return (<div key={s} style={{border:`1px solid ${isSel?BORDER_STRONG:BORDER}`,borderRadius:9,padding:"10px 12px",background:isSel?SURFACE_SUNKEN:SURFACE,transition:"background 0.15s"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <input type="checkbox" checked={isSel} onChange={()=>toggleSelect(s)} style={{accentColor:P,width:15,height:15,cursor:"pointer",flexShrink:0}}/>
              <span style={{width:11,height:11,borderRadius:3,background:st.c,flexShrink:0}}/>
              <span style={{fontSize:13,fontFamily:"monospace",fontWeight:600,color:st.c}}>{s}</span>
              <span style={{fontSize:11,color:TEXT_SUBTLE}}>{mine.length} segment{mine.length===1?"":"s"}</span>
              <div style={{flex:1}}/>
              <label style={{display:"flex",alignItems:"center",gap:6,cursor:isSel?"pointer":"default",opacity:isSel?1:0.4}}>
                <input type="radio" name="ms-keep" checked={keep===s} disabled={!isSel} onChange={()=>chooseKeep(s)} style={{accentColor:P,cursor:isSel?"pointer":"default"}}/>
                <span style={{fontSize:12,fontWeight:keep===s?700:500,color:keep===s?P:TEXT_MUTED}}>Remains</span>
              </label>
            </div>
            {wave(mine,null,"r"+s)}
          </div>);
        })}
      </div>
      {selected.length>=2?(
        <div style={{border:`1px solid ${BORDER_STRONG}`,borderRadius:9,padding:"10px 12px",marginBottom:16,background:SURFACE}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
            {keep?<span style={{width:11,height:11,borderRadius:3,background:keepSt.c,flexShrink:0}}/>:null}
            <span style={{fontSize:12,fontWeight:700,color:TEXT_SECONDARY,letterSpacing:"0.03em"}}>RESULT{keep?<> · <span style={{fontFamily:"monospace"}}>{keep}</span></>:""}</span>
            <span style={{fontSize:11,color:TEXT_MUTED}}>{combined.length} segments combined</span>
            {keep?null:<span style={{fontSize:11,color:TEXT_SUBTLE,fontStyle:"italic"}}>· choose which speaker remains</span>}
          </div>
          {wave(combined,null,"res")}
        </div>
      ):null}
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:12,color:canMerge?TEXT_SECONDARY:TEXT_SUBTLE,flex:1}}>{canMerge?<span><span style={{fontFamily:"monospace"}}>{sources.join(", ")}</span> → <span style={{fontFamily:"monospace",fontWeight:600}}>{keep}</span></span>:"Select speakers, then mark which one remains."}</span>
        <button onClick={onClose} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
        <button onClick={confirm} disabled={!canMerge} style={{padding:"8px 18px",borderRadius:8,border:"none",background:canMerge?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:600,cursor:canMerge?"pointer":"default"}}>Merge speakers</button>
      </div>
    </div>
  </div>);
};

// Diarization segment editor — Speaker + timing only (no transcript text)
// Zoomed-in, interactive segment waveform with draggable region handles.
// Shows a window around the segment (segment + padding each side) so the
// reviewer has some context while dragging.
const SegmentWaveform=({segment,duration,onChange,zoom=1,hideHint,onScrubEnd})=>{
  const containerRef=useRef(null);
  const [dragging,setDragging]=useState(null); // "start" | "end" | "region" | null
  const dragStateRef=useRef(null);

  const computeWindow=(seg,z)=>{
    const basePad=Math.max(5,(seg.endTime-seg.startTime)*0.5);
    const pad=Math.max(0.5,basePad/z);
    return {start:Math.max(0,seg.startTime-pad),end:Math.min(duration,seg.endTime+pad)};
  };
  // Frozen visible window. Recomputed only when a new turn is opened or the user
  // zooms — never while dragging — so the bars/ruler stay put and only the
  // region and pucks move within the window.
  const [view,setView]=useState(()=>computeWindow(segment,zoom));
  useEffect(()=>{
    setView(computeWindow(segment,zoom));
    // eslint-disable-next-line
  },[segment.id,zoom,duration]);

  const winStart=view.start;
  const winEnd=view.end;
  const winSpan=Math.max(1,winEnd-winStart);

  // Static bars for the visible window — seeded from winStart so they stay fixed
  const bars=Array.from({length:80}).map((_,i)=>{
    const x=winStart+(i/80)*winSpan;
    return 35+Math.sin(x*0.4)*22+Math.cos(x*0.17)*15+Math.sin(x*1.3)*12;
  });

  const pctForTime=t=>((t-winStart)/winSpan)*100;
  const timeForPx=(px,w)=>winStart+(px/w)*winSpan;

  useEffect(()=>{
    if(!dragging)return;
    const mv=e=>{
      const rect=containerRef.current?.getBoundingClientRect();
      if(!rect)return;
      const px=Math.max(0,Math.min(rect.width,e.clientX-rect.left));
      const rawTime=timeForPx(px,rect.width);
      const t=Math.max(0,Math.min(duration,rawTime));
      if(dragging==="start"){
        const clamped=Math.min(segment.endTime-0.1,t);
        onChange({startTime:Math.round(clamped*10)/10});
      }else if(dragging==="end"){
        const clamped=Math.max(segment.startTime+0.1,t);
        onChange({endTime:Math.round(clamped*10)/10});
      }else if(dragging==="region"){
        // Move both start and end by the delta, keeping length fixed
        const ds=dragStateRef.current;
        if(!ds)return;
        const dt=rawTime-ds.initialTime;
        let newStart=ds.initialStart+dt;
        let newEnd=ds.initialEnd+dt;
        const len=ds.initialEnd-ds.initialStart;
        if(newStart<0){newStart=0;newEnd=len;}
        if(newEnd>duration){newEnd=duration;newStart=duration-len;}
        onChange({startTime:Math.round(newStart*10)/10,endTime:Math.round(newEnd*10)/10});
      }
    };
    const up=()=>{setDragging(null);dragStateRef.current=null;onScrubEnd&&onScrubEnd();};
    document.addEventListener("mousemove",mv);
    document.addEventListener("mouseup",up);
    return()=>{document.removeEventListener("mousemove",mv);document.removeEventListener("mouseup",up);};
    // eslint-disable-next-line
  },[dragging,segment.startTime,segment.endTime,duration,winStart,winEnd]);

  const startHandleMouseDown=e=>{e.preventDefault();e.stopPropagation();setDragging("start");};
  const endHandleMouseDown=e=>{e.preventDefault();e.stopPropagation();setDragging("end");};
  const regionMouseDown=e=>{
    e.preventDefault();e.stopPropagation();
    const rect=containerRef.current?.getBoundingClientRect();
    if(!rect)return;
    const px=e.clientX-rect.left;
    dragStateRef.current={initialTime:timeForPx(px,rect.width),initialStart:segment.startTime,initialEnd:segment.endTime};
    setDragging("region");
  };

  const startPct=pctForTime(segment.startTime);
  const endPct=pctForTime(segment.endTime);

  return(<div>
    {/* Labels row */}
    {hideHint ? null : (
    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:TEXT_MUTED,marginBottom:4}}>
      <span>Audio Playback &amp; Timeline</span>
      <span style={{color:TEXT_SUBTLE}}>Drag pucks or region to adjust timing</span>
    </div>
    )}
    {/* Start / End time pills */}
    <div style={{position:"relative",height:18}}>
      <div style={{position:"absolute",top:0,left:`calc(${startPct}% - 30px)`,background:SUCCESS_SOFT,color:SURFACE,fontSize:11,fontWeight:600,padding:"1px 8px",borderRadius:4,whiteSpace:"nowrap"}}>Start: {segment.startTime.toFixed(1)}s</div>
      <div style={{position:"absolute",top:0,left:`calc(${endPct}% + 2px)`,background:DANGER_SOFT,color:SURFACE,fontSize:11,fontWeight:600,padding:"1px 8px",borderRadius:4,whiteSpace:"nowrap"}}>End: {segment.endTime.toFixed(1)}s</div>
    </div>
    {/* Waveform container */}
    <div ref={containerRef} style={{position:"relative",height:88,background:SURFACE_ALT,borderRadius:6,overflow:"hidden",border:`1px solid ${BORDER}`,userSelect:"none"}}>
      {/* Region highlight */}
      <div onMouseDown={regionMouseDown}
        style={{position:"absolute",top:0,bottom:0,left:`${startPct}%`,width:`${Math.max(0.5,endPct-startPct)}%`,background:"rgba(124,58,237,0.15)",borderLeft:`2px solid ${P}`,borderRight:`2px solid ${P}`,cursor:dragging==="region"?"grabbing":"grab",zIndex:2}}/>
      {/* Bars (behind region) */}
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",padding:"0 4px",gap:1,zIndex:1,pointerEvents:"none"}}>
        {bars.map((h,i)=>{
          const x=winStart+(i/80)*winSpan;
          const inRegion=x>=segment.startTime&&x<=segment.endTime;
          return(<div key={i} style={{flex:1,height:`${h}%`,background:inRegion?P:TEXT_SUBTLE,opacity:inRegion?0.65:0.5,borderRadius:1}}/>);
        })}
      </div>
      {/* Left red edge marker at start of window */}
      <div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:DANGER_SOFT,zIndex:1}}/>
    </div>
    {/* Draggable pucks below the waveform */}
    <div style={{position:"relative",height:28,marginTop:6}}>
      {/* Track line */}
      <div style={{position:"absolute",top:12,left:0,right:0,height:3,background:BORDER,borderRadius:2}}/>
      {/* Region bar on track */}
      <div style={{position:"absolute",top:12,left:`${startPct}%`,width:`${Math.max(0.5,endPct-startPct)}%`,height:3,background:P,borderRadius:2}}/>
      {/* Start puck */}
      <div onMouseDown={startHandleMouseDown}
        style={{position:"absolute",top:0,left:`calc(${startPct}% - 14px)`,width:28,height:28,borderRadius:"50%",background:SUCCESS_SOFT,border:`3px solid ${SURFACE}`,boxShadow:"0 2px 6px rgba(0,0,0,0.25)",cursor:dragging==="start"?"grabbing":"grab",zIndex:3,transition:dragging?"none":"left 0.1s"}}
        title={`Start: ${segment.startTime.toFixed(1)}s — drag to adjust`}/>
      {/* End puck */}
      <div onMouseDown={endHandleMouseDown}
        style={{position:"absolute",top:0,left:`calc(${endPct}% - 14px)`,width:28,height:28,borderRadius:"50%",background:DANGER_SOFT,border:`3px solid ${SURFACE}`,boxShadow:"0 2px 6px rgba(0,0,0,0.25)",cursor:dragging==="end"?"grabbing":"grab",zIndex:3,transition:dragging?"none":"left 0.1s"}}
        title={`End: ${segment.endTime.toFixed(1)}s — drag to adjust`}/>
    </div>
    {/* Timeline ruler */}
    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:TEXT_SUBTLE,marginTop:4}}>
      <span>{winStart.toFixed(1)}s</span>
      <span>{winEnd.toFixed(1)}s</span>
    </div>
  </div>);
};

const DiarizationSegmentEditor=({segment,speakers,duration,index,onChange,onDelete,onDone})=>{
  return(<div style={{border:`2px solid ${P}`,borderRadius:10,padding:"16px 18px",background:SURFACE}}>
    <div style={{display:"flex",gap:32,marginBottom:14,flexWrap:"wrap",alignItems:"flex-start"}}>
      <div style={{width:30,height:30,borderRadius:"50%",background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:22}}>{index+1}</div>
      <div style={{flex:"0 0 auto"}}>
        <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Speaker</div>
        <select value={segment.speakerId} onChange={e=>onChange({speakerId:e.target.value})}
          style={{padding:"6px 10px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:6,fontSize:13,outline:"none",background:SURFACE_SUNKEN,fontFamily:"monospace",minWidth:200}}>
          {speakers.length===0&&<option value="">— Add a speaker first —</option>}
          {speakers.length>0&&!segment.speakerId&&<option value="">— Select Speaker... —</option>}
          {speakers.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div style={{flex:"1 1 auto"}}>
        <div style={{fontSize:13,color:P,fontWeight:500,marginBottom:6}}>Timing</div>
        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:TEXT_MUTED,flexWrap:"wrap"}}>
          <span>Start</span>
          <input type="number" step="0.1" value={segment.startTime.toFixed(1)} onChange={e=>onChange({startTime:Math.max(0,Math.min(segment.endTime-0.1,parseFloat(e.target.value)||0))})}
            style={{width:72,padding:"5px 7px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:5,fontSize:13,outline:"none"}}/>
          <span>s</span>
          <span style={{marginLeft:8}}>End</span>
          <input type="number" step="0.1" value={segment.endTime.toFixed(1)} onChange={e=>onChange({endTime:Math.max(segment.startTime+0.1,Math.min(duration,parseFloat(e.target.value)||0))})}
            style={{width:72,padding:"5px 7px",border:`1px solid ${TEXT_DISABLED}`,borderRadius:5,fontSize:13,outline:"none"}}/>
          <span>s</span>
          <span style={{marginLeft:12,color:TEXT_SUBTLE}}>Duration: {(segment.endTime-segment.startTime).toFixed(1)}s</span>
        </div>
      </div>
    </div>
    <div style={{marginBottom:14}}>
      <SegmentWaveform segment={segment} duration={duration} onChange={onChange}/>
    </div>
    <div style={{display:"flex",gap:8}}>
      <button onClick={onDelete} style={{padding:"8px 14px",borderRadius:7,border:"none",background:DANGER,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>
        Delete
      </button>
      <button onClick={onDone} style={{padding:"8px 14px",borderRadius:7,border:"none",background:NEUTRAL_DARK,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Done Editing</button>
    </div>
  </div>);
};

// Inline non-editing row in the Edit Diarization list
const DiarizationRow=({segment,speakers,index,onClick})=>{
  const st=speakerStyle(speakers,segment.speakerId);
  return(<div onClick={onClick}
    style={{border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 16px",marginBottom:10,background:SURFACE,cursor:"pointer",display:"flex",gap:14,alignItems:"center"}}
    onMouseEnter={e=>e.currentTarget.style.borderColor=P}
    onMouseLeave={e=>e.currentTarget.style.borderColor=BORDER}>
    <div style={{width:26,height:26,borderRadius:"50%",background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{index+1}</div>
    <span style={{background:st.bg,color:st.c,fontSize:12,fontFamily:"monospace",padding:"3px 10px",borderRadius:6,fontWeight:500,flexShrink:0}}>{segment.speakerId||"—"}</span>
    <div style={{flex:1,minWidth:0}}>
      <span style={{fontSize:13,color:TEXT_SUBTLE,fontStyle:"italic"}}>[Not transcribed — click to edit]</span>
    </div>
    <span style={{fontSize:12,color:TEXT_SUBTLE,fontFamily:"monospace",flexShrink:0}}>{segment.startTime.toFixed(1)}s – {segment.endTime.toFixed(1)}s</span>
  </div>);
};

// ── Create Diarization screen ────────────────────────────────────────────────
function CreateDiarizationScreen({task,onBack}){
  const AUDIO_DURATION=910; // 15:10 in seconds (matches screenshot)
  const [speakers,setSpeakers]=useState([]);
  const [segments,setSegments]=useState([]);
  const [editingId,setEditingId]=useState(null);
  const [nextId,setNextId]=useState(1);
  const [showNotice,setShowNotice]=useState(true);
  const [playing,setPlaying]=useState(false);
  const [currentTime,setCurrentTime]=useState(0);
  const [speed,setSpeed]=useState(1);

  const doneCount=segments.filter(s=>s.speakerId&&s.endTime>s.startTime).length;

  const addSpeaker=()=>{
    const next=`speaker-${speakers.length+1}`;
    setSpeakers(prev=>[...prev,next]);
  };
  const renameSpeaker=(oldId,newId)=>{
    setSpeakers(prev=>prev.map(s=>s===oldId?newId:s));
    setSegments(prev=>prev.map(s=>s.speakerId===oldId?{...s,speakerId:newId}:s));
  };

  const addFirstUtterance=()=>{
    const seg={id:nextId,speakerId:speakers[0]||"",startTime:0,endTime:10};
    setSegments([seg]);
    setNextId(nextId+1);
    setEditingId(seg.id);
  };
  const insertBefore=(id)=>{
    const idx=segments.findIndex(s=>s.id===id);
    const refSeg=segments[idx];
    const seg={id:nextId,speakerId:speakers[0]||"",startTime:Math.max(0,refSeg.startTime-10),endTime:refSeg.startTime};
    setSegments(prev=>[...prev.slice(0,idx),seg,...prev.slice(idx)]);
    setNextId(nextId+1);
    setEditingId(seg.id);
  };
  const insertAfter=(id)=>{
    const idx=segments.findIndex(s=>s.id===id);
    const refSeg=segments[idx];
    const seg={id:nextId,speakerId:speakers[0]||"",startTime:refSeg.endTime,endTime:Math.min(AUDIO_DURATION,refSeg.endTime+10)};
    setSegments(prev=>[...prev.slice(0,idx+1),seg,...prev.slice(idx+1)]);
    setNextId(nextId+1);
    setEditingId(seg.id);
  };
  const updateSegment=(id,patch)=>setSegments(prev=>prev.map(s=>s.id===id?{...s,...patch}:s));
  const deleteSegment=(id)=>{setSegments(prev=>prev.filter(s=>s.id!==id));setEditingId(null);};

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <TranscriptTaskHeader task={task} title="Diarization Creation" onBack={onBack}/>
    <div style={{flex:1,overflowY:"auto",paddingBottom:28}}>
      {showNotice&&<DiarizationNotice onClose={()=>setShowNotice(false)}/>}
      <EditableSpeakersPanel speakers={speakers} onAddSpeaker={addSpeaker} onRenameSpeaker={renameSpeaker}/>
      <FullWaveform duration={AUDIO_DURATION} currentTime={currentTime} playing={playing}
        onTogglePlay={()=>setPlaying(v=>!v)} speed={speed} onSpeedChange={setSpeed}
        segments={segments} speakers={speakers}
        onSegmentClick={id=>setEditingId(id)}/>
      <ProgressPanel done={doneCount} total={Math.max(1,segments.length)}/>

      <div style={{padding:"0 28px"}}>
        {segments.length===0?(
          <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,padding:"36px 20px",textAlign:"center"}}>
            <div style={{fontSize:13,color:TEXT_MUTED,marginBottom:14}}>No utterances yet. Use the audio above to listen, then start creating speaker segments manually.</div>
            <button onClick={addFirstUtterance} disabled={speakers.length===0}
              style={{padding:"9px 18px",borderRadius:8,border:"none",background:speakers.length===0?BRAND_SOFTER:SUCCESS_SOFT,color:SURFACE,fontSize:13,fontWeight:600,cursor:speakers.length===0?"default":"pointer"}}
              title={speakers.length===0?"Add a speaker first":""}>
              + Add First Utterance
            </button>
            {speakers.length===0&&<div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:8}}>Add at least one speaker above to start.</div>}
          </div>
        ):segments.map((seg,i)=>{
          const isEditing=editingId===seg.id;
          return(<div key={seg.id}>
            <InsertButton label="Insert utterance before" onClick={()=>insertBefore(seg.id)}/>
            {isEditing?(
              <DiarizationSegmentEditor segment={seg} speakers={speakers} duration={AUDIO_DURATION} index={i}
                onChange={patch=>updateSegment(seg.id,patch)} onDelete={()=>deleteSegment(seg.id)} onDone={()=>setEditingId(null)}/>
            ):(
              <DiarizationRow segment={seg} speakers={speakers} index={i} onClick={()=>setEditingId(seg.id)}/>
            )}
            {i===segments.length-1&&<InsertButton label="Insert utterance after" onClick={()=>insertAfter(seg.id)}/>}
          </div>);
        })}
      </div>

      <div style={{padding:"14px 28px",display:"flex",justifyContent:"flex-end",gap:8}}>
        <button style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
        <button disabled={segments.length===0} style={{padding:"9px 22px",borderRadius:8,border:"none",background:segments.length===0?BRAND_SOFTER:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:segments.length===0?"default":"pointer"}}>Submit Diarization →</button>
      </div>
    </div>
  </div>);
}

// ── Edit Diarization screen ─────────────────────────────────────────────────
// Seed with 9 segments as shown in screenshot, alternating speakers, ~10s each
const INITIAL_DIA_SEGMENTS=[
  {id:1,speakerId:"speaker-1",startTime:0,endTime:10},
  {id:2,speakerId:"speaker-2",startTime:10.5,endTime:20.5},
  {id:3,speakerId:"speaker-1",startTime:21,endTime:31},
  {id:4,speakerId:"speaker-2",startTime:31.5,endTime:41.5},
  {id:5,speakerId:"speaker-1",startTime:42,endTime:52},
  {id:6,speakerId:"speaker-2",startTime:52.5,endTime:62.5},
  {id:7,speakerId:"speaker-1",startTime:63,endTime:73},
  {id:8,speakerId:"speaker-2",startTime:73.5,endTime:83.5},
  {id:9,speakerId:"speaker-1",startTime:84,endTime:94},
];

function EditDiarizationScreen({task,onBack}){
  const AUDIO_DURATION=910;
  const [speakers,setSpeakers]=useState(["speaker-1","speaker-2"]);
  const [segments,setSegments]=useState(INITIAL_DIA_SEGMENTS.map(s=>({...s})));
  const [editingId,setEditingId]=useState(null);
  const [nextId,setNextId]=useState(100);
  const [showNotice,setShowNotice]=useState(true);
  const [playing,setPlaying]=useState(false);
  const [currentTime,setCurrentTime]=useState(0);
  const [speed,setSpeed]=useState(1);
  const [showMerge,setShowMerge]=useState(false);

  const doneCount=segments.filter(s=>s.speakerId&&s.endTime>s.startTime).length;

  const addSpeaker=()=>{
    const next=`speaker-${speakers.length+1}`;
    setSpeakers(prev=>[...prev,next]);
  };
  const renameSpeaker=(oldId,newId)=>{
    setSpeakers(prev=>prev.map(s=>s===oldId?newId:s));
    setSegments(prev=>prev.map(s=>s.speakerId===oldId?{...s,speakerId:newId}:s));
  };
  const mergeSpeakers=(source,target)=>{
    setSegments(prev=>prev.map(s=>s.speakerId===source?{...s,speakerId:target}:s));
    setSpeakers(prev=>prev.filter(s=>s!==source));
    setShowMerge(false);
  };

  const insertBefore=(id)=>{
    const idx=segments.findIndex(s=>s.id===id);
    const refSeg=segments[idx];
    const seg={id:nextId,speakerId:speakers[0]||"",startTime:Math.max(0,refSeg.startTime-5),endTime:refSeg.startTime};
    setSegments(prev=>[...prev.slice(0,idx),seg,...prev.slice(idx)]);
    setNextId(nextId+1);
    setEditingId(seg.id);
  };
  const insertAfter=(id)=>{
    const idx=segments.findIndex(s=>s.id===id);
    const refSeg=segments[idx];
    const seg={id:nextId,speakerId:speakers[0]||"",startTime:refSeg.endTime,endTime:Math.min(AUDIO_DURATION,refSeg.endTime+5)};
    setSegments(prev=>[...prev.slice(0,idx+1),seg,...prev.slice(idx+1)]);
    setNextId(nextId+1);
    setEditingId(seg.id);
  };
  const updateSegment=(id,patch)=>setSegments(prev=>prev.map(s=>s.id===id?{...s,...patch}:s));
  const deleteSegment=(id)=>{setSegments(prev=>prev.filter(s=>s.id!==id));setEditingId(null);};

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <TranscriptTaskHeader task={task} title="Diarization Review" onBack={onBack}/>
    {showMerge&&<MergeSpeakersModal speakers={speakers} segments={segments} onClose={()=>setShowMerge(false)} onMerge={mergeSpeakers}/>}
    <div style={{flex:1,overflowY:"auto",paddingBottom:28}}>
      {showNotice&&<DiarizationNotice onClose={()=>setShowNotice(false)}/>}
      <EditableSpeakersPanel speakers={speakers} onAddSpeaker={addSpeaker} onRenameSpeaker={renameSpeaker}
        onMergeClick={()=>setShowMerge(true)} allowMerge/>
      <FullWaveform duration={AUDIO_DURATION} currentTime={currentTime} playing={playing}
        onTogglePlay={()=>setPlaying(v=>!v)} speed={speed} onSpeedChange={setSpeed}
        segments={segments} speakers={speakers}
        onSegmentClick={id=>setEditingId(id)}/>
      <ProgressPanel done={doneCount} total={segments.length}/>

      <div style={{padding:"0 28px"}}>
        {segments.map((seg,i)=>{
          const isEditing=editingId===seg.id;
          return(<div key={seg.id}>
            {i===0&&isEditing&&<InsertButton label="Insert utterance before" onClick={()=>insertBefore(seg.id)}/>}
            {isEditing?(
              <DiarizationSegmentEditor segment={seg} speakers={speakers} duration={AUDIO_DURATION} index={i}
                onChange={patch=>updateSegment(seg.id,patch)} onDelete={()=>deleteSegment(seg.id)} onDone={()=>setEditingId(null)}/>
            ):(
              <DiarizationRow segment={seg} speakers={speakers} index={i} onClick={()=>setEditingId(seg.id)}/>
            )}
            {isEditing&&<InsertButton label="Insert utterance after" onClick={()=>insertAfter(seg.id)}/>}
          </div>);
        })}
      </div>

      <div style={{padding:"14px 28px",display:"flex",justifyContent:"flex-end",gap:8}}>
        <button style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
        <button style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Submit Diarization →</button>
      </div>
    </div>
  </div>);
}


// ── Topic & Sentiment annotation screen ─────────────────────────────────────
const CONVO_TOPICS=["Aging & Getting Older","Arts & Culture","Best Advice You've Been Given","Books & Reading","Celebrations & Festivals","Celebrities","Childhood Cartoons","Collecting & Memorabilia","Communication & Social Media","Community & Neighborhood Life","Cooking & Recipes","Cultural Traditions vs. Modern Life","Death, Grief & Remembrance","DIY & Home Repairs","Dream Jobs","Dreams & Aspirations","Education & Learning","Entrepreneurship","Environment & Sustainability","Family & Relationships","Fashion & Style","Fears & Phobias","Finance & Money","Fitness & Exercise","Food & Drink","Friendship","Gaming","Gardening","Health & Wellness","History","Hobbies & Pastimes","Holidays & Vacations","Home & Living","Humor & Comedy","Languages & Linguistics","Local News & Events","Marriage & Partnership","Mental Health","Movies & TV","Music","Nature & Wildlife","Parenting","Personal Growth","Pets & Animals","Philosophy & Beliefs","Politics & Current Events","Religion & Spirituality","Science & Discovery","Shopping & Consumerism","Social Issues","Sports","Storytelling & Folklore","Technology & Gadgets","Travel & Adventure","Volunteering & Charity","Weather & Seasons","Work & Career","Writing & Journaling"];
const SENSITIVE_CATEGORIES=["Child Sexual Abuse","Criminal Facilitation","Dangerous Misinformation","Explicit Sexual Content","Graphic Violence & Gore","Hate Speech & Dehumanization","Personal Data Exposure (Doxxing)","Political Sensitivity","Profanity & Vulgarity","Religious Offense & Blasphemy","Sexual Orientation & Gender Identity Content","Sexual Violence / Non-Consensual Sex","Substances & Vice","Suicide & Self-Harm Facilitation","Targeted Harassment","Terrorism & Mass Violence","Extremism & Radicalization"];
const TS_SAMPLE_TURNS=[
  {n:1,speaker:"Speaker A",text:"Honestly, I don't give a damn about either of those things anymore.",time:"0:00 – 0:18"},
  {n:2,speaker:"Speaker B",text:"Yeah. My brother spent half his paycheck on it. He's got a drinking problem.",time:"0:19 – 0:36"},
  {n:3,speaker:"Speaker A",text:"That's rough. My cousin's been smoking since he was fifteen.",time:"0:37 – 0:53"},
  {n:4,speaker:"Speaker B",text:"People cope however they can, I guess.",time:"0:54 – 1:08"},
  {n:5,speaker:"Speaker A",text:"True. Anyway, did you catch the game last night?",time:"1:09 – 1:22"},
  {n:6,speaker:"Speaker B",text:"Of course. Best match of the season.",time:"1:23 – 1:35"},
];

// Draggable floating window (move by its header).
const FloatingWindow=({title,onClose,initial,width,children})=>{
  const [pos,setPos]=useState(initial||{x:420,y:150});
  const onHeaderDown=(e)=>{
    const sx=e.clientX,sy=e.clientY,ox=pos.x,oy=pos.y;
    const mv=ev=>setPos({x:Math.max(8,ox+ev.clientX-sx),y:Math.max(8,oy+ev.clientY-sy)});
    const up=()=>{document.removeEventListener("mousemove",mv);document.removeEventListener("mouseup",up);};
    document.addEventListener("mousemove",mv);document.addEventListener("mouseup",up);
  };
  return (<div style={{position:"fixed",left:pos.x,top:pos.y,zIndex:60,width:width||440,maxWidth:"calc(100vw - 24px)",background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,boxShadow:"0 18px 48px rgba(0,0,0,0.22)",overflow:"hidden"}}>
    <div onMouseDown={onHeaderDown} style={{display:"flex",alignItems:"center",gap:8,padding:"11px 14px",borderBottom:`1px solid ${BORDER}`,cursor:"move",userSelect:"none"}}>
      <span style={{fontSize:13,color:TEXT_SUBTLE,opacity:0.6}}>⠿</span>
      <span style={{width:9,height:9,borderRadius:2,background:P,flexShrink:0}}/>
      <span style={{fontSize:10,fontWeight:700,color:TEXT_SUBTLE,letterSpacing:"0.06em"}}>GLOBAL</span>
      <span style={{fontSize:12,fontWeight:700,color:TEXT_PRIMARY,letterSpacing:"0.04em",textTransform:"uppercase"}}>{title}</span>
      <div style={{flex:1}}/>
      <button onClick={onClose} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:18,cursor:"pointer",lineHeight:1,padding:0}}>×</button>
    </div>
    <div style={{padding:"14px 16px"}}>{children}</div>
  </div>);
};

// Multi-select picker window (used for Topic and Sensitive Content).
const PickerWindow=({title,subtitle,options,noun,initialSelected,initial,onApply,onClose})=>{
  const [sel,setSel]=useState(initialSelected||[]);
  const [q,setQ]=useState("");
  const toggle=o=>setSel(prev=>prev.includes(o)?prev.filter(x=>x!==o):[...prev,o]);
  const filtered=options.filter(o=>o.toLowerCase().includes(q.trim().toLowerCase()));
  return (<FloatingWindow title={title} onClose={onClose} initial={initial} width={460}>
    <div style={{fontSize:13,color:TEXT_SECONDARY,marginBottom:10,lineHeight:1.5}}>{subtitle}</div>
    <input value={q} onChange={e=>setQ(e.target.value)} placeholder={`Search ${noun}…`}
      style={{width:"100%",padding:"9px 11px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box",marginBottom:10}}/>
    <div style={{fontSize:12,color:TEXT_SUBTLE,marginBottom:10}}>{options.length} {noun} · {sel.length} selected</div>
    <div style={{display:"flex",flexWrap:"wrap",gap:8,maxHeight:300,overflowY:"auto",marginBottom:14}}>
      {filtered.map(o=>{
        const on=sel.includes(o);
        return (<button key={o} onClick={()=>toggle(o)}
          style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:999,border:on?`1px solid ${P}`:`1px solid ${BORDER}`,background:on?BRAND_LIGHTER:SURFACE,color:on?P:TEXT_SECONDARY,fontSize:12,fontWeight:on?600:500,cursor:"pointer"}}>
          {on?<span style={{fontSize:12,lineHeight:1}}>×</span>:null}{o}
        </button>);
      })}
      {filtered.length===0?<span style={{fontSize:12,color:TEXT_SUBTLE,fontStyle:"italic"}}>No matches.</span>:null}
    </div>
    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
      <button onClick={onClose} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:13,fontWeight:500,cursor:"pointer"}}>Back</button>
      <button onClick={()=>onApply(sel)} style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Apply</button>
    </div>
  </FloatingWindow>);
};

// Sentiment window with a two-handle weight slider.
const SentimentWindow=({initialSentiment,initial,onApply,onClose})=>{
  const [b1,setB1]=useState(initialSentiment?initialSentiment.neg:33);
  const [b2,setB2]=useState(initialSentiment?initialSentiment.neg+initialSentiment.neu:66);
  const barRef=useRef(null);
  const startDrag=(which)=>(e)=>{
    e.preventDefault();
    const mv=ev=>{
      const r=barRef.current.getBoundingClientRect();
      let pct=Math.round(((ev.clientX-r.left)/r.width)*100);
      pct=Math.max(0,Math.min(100,pct));
      if(which===1)setB1(Math.min(pct,b2-2<2?b2:b2-2));
      else setB2(Math.max(pct,b1+2>98?b1:b1+2));
    };
    const up=()=>{document.removeEventListener("mousemove",mv);document.removeEventListener("mouseup",up);};
    document.addEventListener("mousemove",mv);document.addEventListener("mouseup",up);
  };
  const neg=Math.round(b1),pos=Math.round(100-b2),neu=100-neg-pos;
  const dot=(c)=><span style={{width:9,height:9,borderRadius:"50%",background:c,display:"inline-block",marginRight:5}}/>;
  return (<FloatingWindow title="Edit Sentiment" onClose={onClose} initial={initial} width={460}>
    <div style={{fontSize:13,color:TEXT_SECONDARY,marginBottom:14,lineHeight:1.5}}>Score the overall tone of the participants in the conversation. Drag a handle to shift weight between adjacent sentiments.</div>
    <div ref={barRef} style={{position:"relative",height:26,borderRadius:999,overflow:"visible",display:"flex",marginBottom:16}}>
      <div style={{width:`${b1}%`,background:"#fca5a5",borderTopLeftRadius:999,borderBottomLeftRadius:999}}/>
      <div style={{width:`${b2-b1}%`,background:"#fcd34d"}}/>
      <div style={{width:`${100-b2}%`,background:"#86efac",borderTopRightRadius:999,borderBottomRightRadius:999}}/>
      <div onMouseDown={startDrag(1)} style={{position:"absolute",top:"50%",left:`${b1}%`,transform:"translate(-50%,-50%)",width:22,height:22,borderRadius:"50%",background:SURFACE,border:`2px solid ${TEXT_DISABLED}`,boxShadow:"0 1px 4px rgba(0,0,0,0.2)",cursor:"ew-resize",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:TEXT_MUTED}}>⟨⟩</div>
      <div onMouseDown={startDrag(2)} style={{position:"absolute",top:"50%",left:`${b2}%`,transform:"translate(-50%,-50%)",width:22,height:22,borderRadius:"50%",background:SURFACE,border:`2px solid ${TEXT_DISABLED}`,boxShadow:"0 1px 4px rgba(0,0,0,0.2)",cursor:"ew-resize",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:TEXT_MUTED}}>⟨⟩</div>
    </div>
    <div style={{display:"flex",gap:18,marginBottom:16,fontSize:13,color:TEXT_SECONDARY}}>
      <span>{dot("#ef4444")}Negative <strong>{neg}%</strong></span>
      <span>{dot("#eab308")}Neutral <strong>{neu}%</strong></span>
      <span>{dot("#22c55e")}Positive <strong>{pos}%</strong></span>
    </div>
    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
      <button onClick={onClose} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:13,fontWeight:500,cursor:"pointer"}}>Back</button>
      <button onClick={()=>onApply({neg,neu,pos})} style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Apply</button>
    </div>
  </FloatingWindow>);
};

const TS_SENTI_DOT={Negative:"#ef4444",Neutral:"#eab308",Positive:"#22c55e"};

function TopicSentimentScreen({task,onBack}){
  const type=task.type||"topic + sentiment";
  const showTopic=type.indexOf("topic")>=0;
  const showSentiment=type.indexOf("sentiment")>=0;
  const title="Annotate: "+(showTopic&&showSentiment?"Topic & Sentiment":showTopic?"Topic":"Sentiment");

  const [topics,setTopics]=useState(["Aging & Getting Older","Arts & Culture","Celebrities"]);
  const [sensitive,setSensitive]=useState([]);
  const [sentiment,setSentiment]=useState({neg:19,neu:23,pos:58});
  const [openWin,setOpenWin]=useState(null); // "topic" | "sensitive" | "sentiment" | null
  const [lastSavedAt,setLastSavedAt]=useState(()=>Date.now());
  const [now,setNow]=useState(()=>Date.now());
  const [submitted,setSubmitted]=useState(false);
  const markSaved=()=>setLastSavedAt(Date.now());
  useEffect(()=>{const t=setInterval(()=>setNow(Date.now()),15000);return ()=>clearInterval(t);},[]);
  const savedLabel=(()=>{
    const diff=Math.max(0,Math.round((now-lastSavedAt)/1000));
    if(diff<45)return "Saved just now";
    if(diff<90)return "Saved 1 minute ago";
    if(diff<3600)return `Saved ${Math.round(diff/60)} minutes ago`;
    const h=Math.round(diff/3600);
    return `Saved ${h} hour${h===1?"":"s"} ago`;
  })();
  // Annotation changes autosave.
  const applyTopics=s=>{setTopics(s);markSaved();};
  const applySensitive=s=>{setSensitive(s);markSaved();};
  const applySentiment=v=>{setSentiment(v);markSaved();};
  const removeTopic=t=>{setTopics(prev=>prev.filter(x=>x!==t));markSaved();};
  const removeSensitive=t=>{setSensitive(prev=>prev.filter(x=>x!==t));markSaved();};

  const engagementId=task.engagementId||"b91c4e07-5a3d-42f9-8c1b-0e6a7d2f9b34";
  const transcriptId=task.transcriptId||"5c8a1f23-6d40-4e91-b7a2-3f1e9c0d6b85";
  const langVariant=task.langVariant||(task.lang?`${task.lang} (AR)`:"—");

  const sectionLabel=(t,onClear)=>(
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
      <span style={{fontSize:11,fontWeight:700,color:TEXT_SUBTLE,letterSpacing:"0.06em"}}>{t}</span>
      {onClear?<button onClick={onClear} title="Clear" style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",lineHeight:1,padding:0}}>×</button>:null}
    </div>
  );

  return (<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <div style={{padding:"10px 28px",borderBottom:`1px solid ${BORDER}`,background:SURFACE_SUNKEN,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between",gap:14}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:TEXT_MUTED,fontSize:13,cursor:"pointer",padding:0}}>← My Tasks</button>
      <span style={{fontSize:12,color:TEXT_SUBTLE}}>{savedLabel}</span>
    </div>
    <div style={{flex:1,overflowY:"auto",paddingBottom:40,background:SURFACE_SUNKEN}}>
      {/* Title + IDs — full page, no surrounding card */}
      <div style={{padding:"20px 28px 0"}}>
        <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <h1 style={{fontSize:22,fontWeight:700,color:TEXT_PRIMARY,margin:0}}>{title}</h1>
          <span style={{display:"flex",alignItems:"center",gap:5,fontSize:12,fontWeight:600,color:WARNING_ACCENT,background:WARNING_ACCENT_SURFACE,padding:"3px 11px",borderRadius:999}}>⏱ Due today</span>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:"8px 28px",marginTop:14}}>
          <MetaField label="Task ID" value={task.id} mono/>
          <MetaField label="Engagement ID" value={engagementId} mono/>
          <MetaField label="Transcript ID" value={transcriptId} mono/>
          <MetaField label="Language variant" value={langVariant}/>
          <MetaField label="Due date" value={task.deadline||"Jun 18"}/>
        </div>
      </div>

      {/* Instructions — consistent with transcript review */}
      <TranscriptSpecialInstructions text="Hello Jeenie developers. This copy is not the same on every topic & sentiment task — these are custom instructions that an Ops Manager can write each time they create a topic & sentiment task."/>

      {/* Global annotation controls */}
      <div style={{margin:"22px 28px 0"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <span style={{width:10,height:10,borderRadius:"50%",background:P}}/>
          <span style={{fontSize:12,fontWeight:700,color:TEXT_SECONDARY,letterSpacing:"0.04em"}}>GLOBAL</span>
          <span style={{fontSize:12,color:TEXT_SUBTLE}}>· Applies to the entire conversation</span>
        </div>
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:18}}>
          {showTopic?<button onClick={()=>setOpenWin("topic")} style={{flex:"1 1 200px",padding:"14px",borderRadius:10,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_PRIMARY,fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>✎ Edit Topic</button>:null}
          <button onClick={()=>setOpenWin("sensitive")} style={{flex:"1 1 200px",padding:"14px",borderRadius:10,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>+ Add Sensitive Content</button>
          {showSentiment?<button onClick={()=>setOpenWin("sentiment")} style={{flex:"1 1 200px",padding:"14px",borderRadius:10,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_PRIMARY,fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>✎ Edit Sentiment</button>:null}
        </div>

        {showTopic?<div style={{marginBottom:16}}>
          {sectionLabel("TOPIC",topics.length?()=>{setTopics([]);markSaved();}:null)}
          {topics.length===0?<span style={{fontSize:13,color:TEXT_SUBTLE,fontStyle:"italic"}}>No topics added yet</span>:(
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {topics.map(t=><span key={t} style={{display:"flex",alignItems:"center",gap:5,background:BRAND_LIGHTER,color:P,fontSize:12,fontWeight:600,padding:"4px 8px 4px 11px",borderRadius:999}}>{t}<span onClick={()=>removeTopic(t)} style={{cursor:"pointer",fontSize:13,lineHeight:1}}>×</span></span>)}
            </div>
          )}
        </div>:null}

        <div style={{marginBottom:16}}>
          {sectionLabel("SENSITIVE CONTENT",sensitive.length?()=>{setSensitive([]);markSaved();}:null)}
          {sensitive.length===0?<span style={{fontSize:13,color:TEXT_SUBTLE,fontStyle:"italic"}}>No sensitive content added yet</span>:(
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {sensitive.map(t=><span key={t} style={{display:"flex",alignItems:"center",gap:5,background:DANGER_BG,color:DANGER,fontSize:12,fontWeight:600,padding:"4px 8px 4px 11px",borderRadius:999}}>{t}<span onClick={()=>removeSensitive(t)} style={{cursor:"pointer",fontSize:13,lineHeight:1}}>×</span></span>)}
            </div>
          )}
        </div>

        {showSentiment?<div style={{marginBottom:8}}>
          {sectionLabel("SENTIMENT",null)}
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            <span style={{display:"flex",alignItems:"center",gap:6,background:DANGER_BG,color:"#b91c1c",fontSize:12,fontWeight:600,padding:"4px 12px",borderRadius:999}}><span style={{width:8,height:8,borderRadius:"50%",background:TS_SENTI_DOT.Negative}}/>Negative {sentiment.neg}%</span>
            <span style={{display:"flex",alignItems:"center",gap:6,background:"#fef9c3",color:"#854d0e",fontSize:12,fontWeight:600,padding:"4px 12px",borderRadius:999}}><span style={{width:8,height:8,borderRadius:"50%",background:TS_SENTI_DOT.Neutral}}/>Neutral {sentiment.neu}%</span>
            <span style={{display:"flex",alignItems:"center",gap:6,background:SUCCESS_BG,color:"#166534",fontSize:12,fontWeight:600,padding:"4px 12px",borderRadius:999}}><span style={{width:8,height:8,borderRadius:"50%",background:TS_SENTI_DOT.Positive}}/>Positive {sentiment.pos}%</span>
          </div>
        </div>:null}
      </div>

      {/* Read-only transcript */}
      <div style={{margin:"24px 28px 0",borderTop:`1px solid ${BORDER}`,paddingTop:18}}>
        {TS_SAMPLE_TURNS.map(t=>{
          const a=t.speaker==="Speaker A";
          return (<div key={t.n} style={{display:"flex",gap:14,alignItems:"flex-start",padding:"14px 4px",borderBottom:`1px solid ${SURFACE_HOVER}`}}>
            <div style={{width:26,height:26,borderRadius:"50%",background:SURFACE_HOVER,color:TEXT_MUTED,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{t.n}</div>
            <span style={{background:a?BRAND_LIGHTER:SUCCESS_SURFACE,color:a?P:"#166534",fontSize:12,fontWeight:600,padding:"3px 10px",borderRadius:6,flexShrink:0}}>{t.speaker}</span>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:14,color:TEXT_PRIMARY,lineHeight:1.6,margin:0,marginBottom:5}}>{t.text}</p>
              <span style={{fontSize:11,color:TEXT_SUBTLE}}>{t.time}</span>
            </div>
          </div>);
        })}
      </div>

      <div style={{padding:"20px 28px 0",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:10}}>
        {submitted ? (
          <div style={{width:"100%",background:SUCCESS_BG,border:`1px solid ${SUCCESS_SOFT}`,borderRadius:8,padding:"10px 14px",fontSize:13,color:SUCCESS,fontWeight:500}}>Annotations submitted. (Demo — no backend.)</div>
        ) : null}
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={markSaved} style={{padding:"9px 18px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Save Draft</button>
          <button onClick={()=>{markSaved();setSubmitted(true);}} style={{padding:"9px 22px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Submit →</button>
        </div>
      </div>
    </div>

    {openWin==="topic"?<PickerWindow title="Edit Topic" subtitle="Select the subjects that were the main topics of conversation." options={CONVO_TOPICS} noun="topics" initialSelected={topics} initial={{x:360,y:150}} onClose={()=>setOpenWin(null)} onApply={s=>{applyTopics(s);setOpenWin(null);}}/>:null}
    {openWin==="sensitive"?<PickerWindow title="Add Sensitive Content" subtitle="Select all the sensitive content identified in the conversation." options={SENSITIVE_CATEGORIES} noun="sensitive content categories" initialSelected={sensitive} initial={{x:520,y:120}} onClose={()=>setOpenWin(null)} onApply={s=>{applySensitive(s);setOpenWin(null);}}/>:null}
    {openWin==="sentiment"?<SentimentWindow initialSentiment={sentiment} initial={{x:600,y:200}} onClose={()=>setOpenWin(null)} onApply={v=>{applySentiment(v);setOpenWin(null);}}/>:null}
  </div>);
}

function ReviewerScreen({profile,tasks}){
  const taskList=tasks||reviewTasks;
  const[sel,setSel]=useState(null);
  if(sel){
    if(sel.type==="Transcript Creation") return <CreateTranscriptScreen task={sel} onBack={()=>setSel(null)}/>;
    if(sel.type==="Full Transcript Review") return <EditTranscriptScreen task={sel} onBack={()=>setSel(null)}/>;
    if(sel.type==="Full Transcript Review QA") return <FullTranscriptReviewQAScreen task={sel} onBack={()=>setSel(null)}/>;
    if(sel.type==="Audio Review"||sel.type==="Post-Model Audio Review") return <AudioReviewScreen task={sel} onBack={()=>setSel(null)}/>;
    if(sel.type==="NER/PII Recognition") return <PIIRedactionScreen task={sel} onBack={()=>setSel(null)}/>;
    if(sel.type==="Diarization Creation") return <CreateDiarizationScreen task={sel} onBack={()=>setSel(null)}/>;
    if(sel.type==="Diarization Review") return <EditDiarizationScreen task={sel} onBack={()=>setSel(null)}/>;
    if(sel.type==="topic"||sel.type==="sentiment"||sel.type==="topic + sentiment") return <TopicSentimentScreen task={sel} onBack={()=>setSel(null)}/>;
    return <ReviewPanel task={sel} onBack={()=>setSel(null)}/>;
  }
  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:4}}>My Tasks</h1>
    <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:18}}>Tasks matching your approved capabilities.</p>
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead><tr style={{borderBottom:`1px solid ${BORDER}`}}>{["Task ID","Type","Batch","Language","Deadline","Status",""].map(h=><th key={h} style={{padding:"9px 13px",textAlign:"left",fontSize:12,fontWeight:500,color:TEXT_SUBTLE}}>{h}</th>)}</tr></thead>
        <tbody>{taskList.map(t=>(
          <tr key={t.id} style={{borderBottom:`1px solid ${SURFACE_HOVER}`}}>
            <td style={{padding:"10px 13px",color:P,fontWeight:500,fontSize:13}}>{t.id}</td>
            <td style={{padding:"10px 13px",fontSize:13}}>{t.type}</td>
            <td style={{padding:"10px 13px",fontSize:13,color:TEXT_MUTED}}>{t.project}</td>
            <td style={{padding:"10px 13px"}}><Badge label={t.lang} color="purple"/></td>
            <td style={{padding:"10px 13px",fontSize:13}}>{t.deadline}</td>
            <td style={{padding:"10px 13px"}}><Badge label={t.status} color={t.status==="Available"?"green":"orange"}/></td>
            <td style={{padding:"10px 13px"}}><button onClick={()=>setSel(t)} style={{fontSize:12,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500}}>Open →</button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  </div>);
}

// ── CREATOR SCREEN ────────────────────────────────────────────────────────────
const creatorProjects=[
  {id:"cp1",title:"Spanish Conversational Audio – Batch 3",langs:["Spanish"],scripted:false,topics:["Daily Life","Food","Family","Sports","Humor","Travel","Culture"],hours:40,hoursUsed:28,maxPerUser:5,deadline:"May 10, 2025",claimable:true,durationRange:"20–45 min"},
  {id:"cp3",title:"Arabic Business Scripted",langs:["Arabic"],scripted:true,solo:true,topics:["Business","Finance","Technology"],hours:30,hoursUsed:10,maxPerUser:4,deadline:"Jun 1, 2025",claimable:true,durationRange:"30–60 min"},
  {id:"cp5",title:"Hausa Daily Life",langs:["Hausa"],scripted:false,topics:["Daily Life","Family","Food"],hours:15,hoursUsed:8,maxPerUser:3,deadline:"May 20, 2025",claimable:true,limitReached:true,durationRange:"20–40 min"},
  {id:"cp2",title:"French Medical Dialogues",langs:["French"],scripted:false,topics:["Healthcare","Pharmacy","Mental Health"],hours:20,hoursUsed:20,maxPerUser:3,deadline:"Apr 30, 2025",claimable:false,durationRange:"30–45 min"},
  {id:"cp6",title:"Swahili Family Conversations",langs:["Swahili"],scripted:false,topics:["Family","Daily Life","Culture"],hours:25,hoursUsed:12,maxPerUser:3,deadline:"May 25, 2025",claimable:false,limitReached:true,durationRange:"20–40 min"},
  {id:"cp4",title:"Spanish Pilot – Season 1",langs:["Spanish"],scripted:false,topics:["Culture","History","Storytelling"],hours:10,hoursUsed:10,maxPerUser:2,deadline:"Mar 1, 2025",claimable:false,closedHoursAgo:18,durationRange:"20–35 min"},
];

function CreatorScreen({profile,onClaim,projects}){
  const visibleProjects=(projects||creatorProjects).map(p=>({
    ...p,
    claimable:p.claimable&&!p.limitReached,
  })).sort((a,b)=>{
    const rank=p=>{
      if(p.claimable&&!p.limitReached)return 0;
      if(p.limitReached)return 1;
      if(!p.claimable&&!p.closedHoursAgo)return 2;
      return 3; // closed/expired
    };
    return rank(a)-rank(b);
  });
  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:4}}>Available Batches</h1>
    <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:18}}>Batches matching your approved languages.</p>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
      {visibleProjects.map(p=><ProjectCard key={p.id} p={p} approvedLangs={profile.approvedLangs} onClaim={onClaim}/>)}
    </div>
  </div>);
}

// ── MY RECORDINGS SCREEN (Creator / Reviewer & Creator) ──────────────────────

const initialWindowsByProject={
  cp1:[
    {id:"mw1",date:"2025-04-14",start:"10:00",end:"10:45"},
    {id:"mw2",date:"2025-04-15",start:"09:00",end:"09:45"},
  ],
  cp3:[
    {id:"mw3",date:"2025-04-17",start:"14:00",end:"14:45"},
  ],
};

const projectNameById={
  cp1:"Spanish Conversational Audio – Batch 3",
  cp2:"French Medical Dialogues",
  cp3:"Arabic Business Scripted",
};

const initialInvitations=[
  {id:"INV-001",from:"Fatima Al-Rashid",fromInitials:"FA",fromEmail:"fatima.al-rashid@example.com",project:"Spanish Conversational Audio – Batch 3",projId:"cp1",topic:"Food",duration:"20–45 min",deadline:"May 8, 2025",direction:"received",numParticipants:2},
  {id:"INV-002",from:"Lucas Oliveira",fromInitials:"LO",fromEmail:"lucas.oliveira@example.com",project:"Hausa Daily Life",projId:"cp5",topic:"Family",duration:"20–40 min",deadline:"May 20, 2025",direction:"received",numParticipants:2},
  {id:"INV-005",from:"Priya Nair",fromInitials:"PN",fromEmail:"priya.nair@example.com",project:"French Medical Dialogues",projId:"cp2",topic:"Healthcare",duration:"30–45 min",deadline:"Apr 30, 2025",direction:"received",numParticipants:2},
  {id:"INV-006",from:"Chen Wei",fromInitials:"CW",fromEmail:"chen.wei@example.com",alsoFrom:"Diego Fernández",alsoFromStatus:"Pending",project:"Spanish Family Conversations – 3-Person",projId:"p6",topic:"Daily Life",duration:"25–45 min",deadline:"Jun 20, 2025",direction:"received",numParticipants:3},
  {id:"INV-003",to:"Priya Nair",toInitials:"PN",project:"Spanish Conversational Audio – Batch 3",projId:"cp1",topic:"Daily Life",duration:"20–45 min",deadline:"May 8, 2025",direction:"sent",sentStatus:"Pending",numParticipants:2},
  {id:"INV-004",to:"Chen Wei",toInitials:"CW",project:"French Medical Dialogues",projId:"cp2",topic:"Healthcare",duration:"30–45 min",deadline:"Apr 30, 2025",direction:"sent",sentStatus:"Declined",numParticipants:2},
  {id:"INV-007",to:"Kwame Boateng",toInitials:"KB",toStatus:"Accepted",alsoTo:"Zara Mensah",alsoToInitials:"ZM",alsoToStatus:"Pending",project:"Spanish Family Conversations – 3-Person",projId:"p6",topic:"Family",duration:"25–45 min",deadline:"Jun 20, 2025",direction:"sent",sentStatus:"Partial",numParticipants:3},
];

function MyRecordingsScreen({onContact,taskList,invitations,setInvitations,acceptedInv,setAcceptedInv}){
  const tasks=taskList||[];
  const[windowsByProject,setWindowsByProject]=useState(initialWindowsByProject);
  const[manageProj,setManageProj]=useState(null);
  const fmtDate=d=>{const dt=new Date(d+"T00:00:00");return dt.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});};
  const fmtTime=t=>{const[h,m]=t.split(":");const hr=parseInt(h);return `${hr>12?hr-12:hr||12}:${m} ${hr>=12?"PM":"AM"}`;};
  const fmtDeadline=d=>{
    if(!d)return"—";
    if(d.includes("T")){const dt=new Date(d);return dt.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});}
    return d;
  };
  const setProjectWindows=(projId,ws)=>setWindowsByProject(prev=>({...prev,[projId]:ws}));
  const managingWindows=manageProj?windowsByProject[manageProj]||[]:null;
  const acceptInvitation=id=>setAcceptedInv(prev=>({...prev,[id]:true}));
  const rejectInvitation=id=>setInvitations(prev=>prev.filter(i=>i.id!==id));

  const confirmed=tasks.filter(t=>t.status==="Ready to Record");
  const solo=tasks.filter(t=>t.status==="Solo");
  const awaiting=tasks.filter(t=>t.status==="Awaiting Buddy");

  const TaskCard=({t,showRecord})=>(
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:16}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
            <span style={{fontWeight:600,fontSize:13,color:P}}>{t.id}</span>
            <Badge label={t.status} color={t.status==="Ready to Record"||t.status==="Solo"?"green":"orange"}/>
            {t.lang&&<Badge label={t.lang} color="purple"/>}
            {t.scripted&&<Badge label="Scripted" color="blue"/>}
          </div>
          <div style={{fontSize:13,color:TEXT_SECONDARY,marginBottom:4}}>{t.project} · {t.topic}</div>
          {t.partner&&!t.partners&&<div style={{fontSize:12,color:TEXT_MUTED,marginBottom:4}}>{shortName(t.partner)}</div>}
          {t.partners&&t.partners.length>0&&<div style={{fontSize:12,color:TEXT_MUTED,marginBottom:4}}>{t.partners.map(p=>p?shortName(p):"Awaiting partner").join(" · ")}</div>}
          {t.duration&&<div style={{fontSize:12,color:TEXT_MUTED,marginBottom:4}}>⏱ {t.duration}</div>}
          {t.window&&<div style={{fontSize:12,color:P,fontWeight:500,marginBottom:4}}>
            {fmtDate(t.window.date)}, {fmtTime(t.window.start)}–{fmtTime(t.window.end)} CT
          </div>}
          <div style={{display:"inline-flex",alignItems:"center",gap:5,background:`#fff7ed`,border:"1px solid #fed7aa",borderRadius:6,padding:"3px 8px"}}>
            <span style={{fontSize:11,fontWeight:600,color:WARNING_ACCENT}}>Batch deadline: {fmtDeadline(t.deadline)}</span>
          </div>
        </div>
        {showRecord&&<button onClick={()=>onContact(t)} style={{padding:"8px 16px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer",flexShrink:0,marginTop:2}}>→ Record</button>}
      </div>
      {/* Inline windows for awaiting tasks */}
      {t.status==="Awaiting Buddy"&&windowsByProject[t.projId]&&windowsByProject[t.projId].length>0&&<div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${SURFACE_HOVER}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.06em"}}>YOUR POSTED WINDOWS ({windowsByProject[t.projId].length})</span>
          <button onClick={()=>setManageProj(t.projId)} style={{fontSize:11,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500}}>Manage →</button>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
          {windowsByProject[t.projId].slice(0,4).map(w=>(
            <span key={w.id} style={{fontSize:11,background:PL,color:P,padding:"2px 8px",borderRadius:5,fontWeight:500}}>
              {fmtDate(w.date)}, {fmtTime(w.start)}
            </span>
          ))}
          {windowsByProject[t.projId].length>4&&<span style={{fontSize:11,color:TEXT_SUBTLE}}>+{windowsByProject[t.projId].length-4} more</span>}
        </div>
      </div>}
      {t.status==="Awaiting Buddy"&&(!windowsByProject[t.projId]||windowsByProject[t.projId].length===0)&&<div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${SURFACE_HOVER}`}}>
        <button onClick={()=>setManageProj(t.projId)} style={{fontSize:12,color:P,background:"none",border:`1px solid ${P}`,borderRadius:6,padding:"5px 12px",cursor:"pointer",fontWeight:500}}>+ Post availability windows</button>
      </div>}
    </div>
  );

  return(<div style={{flex:1,padding:"22px 28px",overflowY:"auto"}}>
    {manageProj&&<WindowsModal
      onClose={()=>setManageProj(null)}
      projTitle={projectNameById[manageProj]||manageProj}
      windows={managingWindows}
      setWindows={ws=>setProjectWindows(manageProj,ws)}
    />}
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:18}}>My Recordings</h1>

    {(invitations.length>0||Object.keys(acceptedInv).length>0)&&<>
      {/* Received invitations */}
      {invitations.filter(i=>i.direction==="received"||acceptedInv[i.id]).length>0&&<>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>INVITED YOU</div>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24}}>
          {invitations.filter(i=>i.direction==="received").map(inv=>{
            const accepted=acceptedInv[inv.id];
            return(<div key={inv.id} style={{background:SURFACE,border:`1px solid ${accepted?SUCCESS_LIGHT:BORDER}`,borderRadius:12,padding:16}}>
              {accepted
                ?<div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                  <div style={{width:40,height:40,borderRadius:"50%",background:SUCCESS_BG,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>✓</div>
                  <div>
                    <div style={{fontWeight:600,fontSize:13,color:SUCCESS,marginBottom:6}}>Accepted — {inv.project} · {inv.topic}</div>
                    <p style={{fontSize:13,color:TEXT_SECONDARY,margin:"0 0 8px",lineHeight:1.5}}>{inv.numParticipants===3?"You can all email each other to set up a time.":"You two can email each other to set up a time."}</p>
                    <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"#f5f3ff",border:`1px solid ${BRAND_LIGHT}`,borderRadius:7,padding:"6px 12px"}}>
                      <span style={{fontSize:12,color:TEXT_MUTED}}>{shortName(inv.from)}:</span>
                      <a href={`mailto:${inv.fromEmail}`} style={{fontSize:13,fontWeight:600,color:P,textDecoration:"none"}}>{inv.fromEmail}</a>
                    </div>
                  </div>
                </div>
                :<div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                  <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                    <div style={{width:40,height:40,borderRadius:"50%",background:PL,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,color:P}}>{inv.fromInitials}</div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13,marginBottom:inv.alsoFrom?4:2}}>{shortName(inv.from)}</div>
                    {inv.alsoFrom&&<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                      <span style={{fontSize:12,color:TEXT_MUTED}}>{shortName(inv.alsoFrom)}</span>
                      <span style={{fontSize:10,background:inv.alsoFromStatus==="Pending"?WARNING_BG:SUCCESS_BG,color:inv.alsoFromStatus==="Pending"?WARNING:SUCCESS,padding:"1px 6px",borderRadius:999,fontWeight:600}}>{inv.alsoFromStatus||"Pending"}</span>
                    </div>}
                    {inv.numParticipants===3&&!inv.alsoFrom&&<div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:4}}>{inv.numParticipants} participants per recording</div>}
                    <div style={{fontSize:13,color:TEXT_SECONDARY,marginBottom:2}}>{inv.project} · {inv.topic}</div>
                    <div style={{fontSize:12,color:TEXT_MUTED,marginBottom:12}}>⏱ {inv.duration} · Deadline: {inv.deadline}</div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>acceptInvitation(inv.id)} style={{padding:"7px 18px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:12,fontWeight:500,cursor:"pointer"}}>Accept</button>
                      <button onClick={()=>rejectInvitation(inv.id)} style={{padding:"7px 14px",borderRadius:7,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_SECONDARY,fontSize:12,cursor:"pointer"}}>Decline</button>
                    </div>
                  </div>
                </div>
              }
            </div>);
          })}
        </div>
      </>}
      {/* Sent invitations */}
      {invitations.filter(i=>i.direction==="sent").length>0&&<>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>INVITED BY YOU</div>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24}}>
          {invitations.filter(i=>i.direction==="sent").map(inv=>{
            const statusColor=inv.sentStatus==="Pending"?"amber":inv.sentStatus==="Declined"?"gray":"green";
            return(<div key={inv.id} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:16,opacity:inv.sentStatus==="Declined"?0.7:1}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:SURFACE_HOVER,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,color:TEXT_MUTED,flexShrink:0}}>{inv.toInitials}</div>
                <div style={{flex:1}}>
                  <div style={{marginBottom:inv.alsoTo?6:2}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:inv.alsoTo?4:0}}>
                      <span style={{fontWeight:600,fontSize:13}}>{shortName(inv.to)}</span>
                      {inv.toStatus
                        ?<span style={{fontSize:10,background:inv.toStatus==="Accepted"?SUCCESS_BG:WARNING_BG,color:inv.toStatus==="Accepted"?SUCCESS:WARNING,padding:"1px 6px",borderRadius:999,fontWeight:600}}>{inv.toStatus}</span>
                        :<Badge label={inv.sentStatus} color={statusColor}/>
                      }
                    </div>
                    {inv.alsoTo&&<div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:13,color:TEXT_SECONDARY}}>{shortName(inv.alsoTo)}</span>
                      <span style={{fontSize:10,background:inv.alsoToStatus==="Accepted"?SUCCESS_BG:WARNING_BG,color:inv.alsoToStatus==="Accepted"?SUCCESS:WARNING,padding:"1px 6px",borderRadius:999,fontWeight:600}}>{inv.alsoToStatus||"Pending"}</span>
                    </div>}
                  </div>
                  <div style={{fontSize:13,color:TEXT_SECONDARY,marginBottom:2}}>{inv.project} · {inv.topic}</div>
                  <div style={{fontSize:12,color:TEXT_MUTED}}>⏱ {inv.duration} · Deadline: {inv.deadline}</div>
                </div>
              </div>
            </div>);
          })}
        </div>
      </>}
    </>}

    {confirmed.length>0&&<>
      <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>CONFIRMED MEETINGS</div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24}}>
        {confirmed.map(t=><TaskCard key={t.id} t={t} showRecord/>)}
      </div>
    </>}

    {solo.length>0&&<>
      <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>SOLO RECORDINGS</div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24}}>
        {solo.map(t=><TaskCard key={t.id} t={t} showRecord/>)}
      </div>
    </>}

    {awaiting.length>0&&<>
      <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>AWAITING BUDDY</div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24}}>
        {awaiting.map(t=><TaskCard key={t.id} t={t} showRecord={false}/>)}
      </div>
    </>}
  </div>);
}

// ── RECORD AGAIN SCREEN ───────────────────────────────────────────────────────
function RecordAgainScreen({task,batchTopics,onRecordAgain,onDone}){
  const[requested,setRequested]=useState(false);
  const[selectedTopic,setSelectedTopic]=useState(task.topic);
  const isSolo=!!task.solo;
  const hasTopics=(batchTopics||[]).length>1;

  if(!isSolo&&requested){
    return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",background:SURFACE_ALT}}>
      <div style={{width:"100%",maxWidth:440,textAlign:"center"}}>
        <div style={{width:56,height:56,borderRadius:"50%",background:SUCCESS_BG,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,margin:"0 auto 16px"}}>✓</div>
        <div style={{fontSize:20,fontWeight:700,marginBottom:8}}>Request sent!</div>
        <p style={{fontSize:14,color:TEXT_MUTED,marginBottom:28,lineHeight:1.6}}><strong>{shortName(task.partner)}</strong> will need to accept before you can record together again. We'll notify you when they respond.</p>
        <button onClick={onDone} style={{width:"100%",padding:"12px",borderRadius:9,border:"none",background:P,color:SURFACE,fontSize:14,fontWeight:600,cursor:"pointer"}}>Done →</button>
      </div>
    </div>);
  }

  return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",background:SURFACE_ALT}}>
    <div style={{width:"100%",maxWidth:460}}>
      <div style={{textAlign:"center",marginBottom:28}}>
        <div style={{width:64,height:64,borderRadius:"50%",background:SUCCESS_BG,display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,margin:"0 auto 16px"}}>✓</div>
        <div style={{fontSize:22,fontWeight:700,marginBottom:6}}>Recording submitted!</div>
        <div style={{fontSize:14,color:TEXT_MUTED}}>{task.project} · {task.topic}</div>
      </div>
      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:14,padding:24,marginBottom:16}}>
        <div style={{fontWeight:600,fontSize:15,marginBottom:8}}>Record again?</div>
        <p style={{fontSize:13,color:TEXT_MUTED,lineHeight:1.6,marginBottom:hasTopics?16:20}}>
          {isSolo
            ?"You can start another recording for this batch right away."
            :`You can request another session with ${shortName(task.partner)}. They'll need to accept before you can record together again.`}
        </p>
        {hasTopics&&<div style={{marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>TOPIC</div>
          <select value={selectedTopic} onChange={e=>setSelectedTopic(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,outline:"none",color:TEXT_PRIMARY}}>
            {(batchTopics||[]).map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>}
        <div style={{display:"flex",gap:10}}>
          {isSolo
            ?<button onClick={()=>onRecordAgain(selectedTopic)} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Record again</button>
            :<button onClick={()=>setRequested(true)} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Request another session →</button>
          }
          <button onClick={onDone} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>No thanks</button>
        </div>
      </div>
    </div>
  </div>);
}

// ── MY BUDDIES SCREEN ─────────────────────────────────────────────────────────
const initialPartners=[
  {id:"p1",name:"Maria González",initials:"MG",email:"maria.gonzalez@example.com",recordingsTogether:3,lastRecorded:"Apr 14, 2025",blocked:false,reported:false},
  {id:"p2",name:"Kwame Boateng",initials:"KB",email:"k.boateng@example.com",recordingsTogether:1,lastRecorded:"Mar 28, 2025",blocked:false,reported:false},
  {id:"p3",name:"Zara Mensah",initials:"ZM",email:"z.mensah@example.com",recordingsTogether:2,lastRecorded:"Feb 10, 2025",blocked:false,reported:false},
];

function MyPartnersScreen({buddyRequests:extBuddyRequests,onBuddyRequestsChange,discoverable,onGoToProfile}){
  const[partners,setPartners]=useState(initialPartners);
  const[buddyRequests,setBuddyRequests]=useState(extBuddyRequests||initialBuddyRequests);
  const[reportingPartner,setReportingPartner]=useState(null);
  const[reportReasons,setReportReasons]=useState([]);
  const[reportText,setReportText]=useState("");
  const[reportOtherText,setReportOtherText]=useState("");
  const[reportSent,setReportSent]=useState(false);
  const[showFirstVisitModal,setShowFirstVisitModal]=useState(!discoverable);
  const[hasSeenModal,setHasSeenModal]=useState(false);
  // Add-by-email state
  const[addEmail,setAddEmail]=useState("");
  const[addSent,setAddSent]=useState(false);
  const[pendingEmails,setPendingEmails]=useState([]);
  // Browse discoverable state
  const[browseTZFilter,setBrowseTZFilter]=useState([]);
  const[tzDropOpen,setTzDropOpen]=useState(false);
  const tzDropRef=useRef(null);
  useEffect(()=>{const h=e=>{if(tzDropRef.current&&!tzDropRef.current.contains(e.target))setTzDropOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);
  const[browseTopicFilter,setBrowseTopicFilter]=useState([]);
  const[topicDropOpen,setTopicDropOpen]=useState(false);
  const topicDropRef=useRef(null);
  useEffect(()=>{const h=e=>{if(topicDropRef.current&&!topicDropRef.current.contains(e.target))setTopicDropOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);
  const[sentRequests,setSentRequests]=useState({});
  const REPORT_REASONS=["Verbal abuse","Inappropriate behavior","Suspected fraud","Other"];
  const otherSelected=reportReasons.includes("Other");
  const reportValid=(reportReasons.filter(r=>r!=="Other").length>0||(otherSelected&&reportOtherText.trim().length>0));
  const toggleBlock=id=>setPartners(prev=>prev.map(p=>p.id===id?{...p,blocked:!p.blocked}:p));
  const submitReport=()=>{setPartners(prev=>prev.map(p=>p.id===reportingPartner.id?{...p,reported:true}:p));setReportSent(true);};
  const closeReport=()=>{setReportingPartner(null);setReportReasons([]);setReportText("");setReportOtherText("");setReportSent(false);};
  const acceptRequest=id=>{const req=buddyRequests.find(r=>r.id===id);if(req){setPartners(prev=>[...prev,{id:"p"+Date.now(),name:req.name,initials:req.initials,email:req.email,recordingsTogether:req.mutualRecordings||0,lastRecorded:"—",blocked:false,reported:false}]);}const updated=buddyRequests.filter(r=>r.id!==id);setBuddyRequests(updated);if(onBuddyRequestsChange)onBuddyRequestsChange(updated);};
  const declineRequest=id=>{const updated=buddyRequests.filter(r=>r.id!==id);setBuddyRequests(updated);if(onBuddyRequestsChange)onBuddyRequestsChange(updated);};
  const sendBuddyByEmail=()=>{
    const trimmed=addEmail.trim().toLowerCase();
    if(!trimmed)return;
    if(pendingEmails.includes(trimmed)){return;}
    setPendingEmails(prev=>[...prev,trimmed]);
    setAddSent(true);
    setAddEmail("");
  };

  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    {/* First-visit undiscoverable modal */}
    {showFirstVisitModal&&!hasSeenModal&&<div style={{position:"fixed",inset:0,background:OVERLAY_MAX,display:"flex",alignItems:"center",justifyContent:"center",zIndex:400}}>
      <div style={{background:SURFACE,borderRadius:14,padding:28,width:440,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
        <div style={{fontWeight:700,fontSize:16,textAlign:"center",marginBottom:8}}>You're currently undiscoverable</div>
        <p style={{fontSize:13,color:TEXT_MUTED,lineHeight:1.6,marginBottom:20,textAlign:"center"}}>Other creators can't find you as a potential recording partner right now. You can turn on discoverability from your <strong>My Profile</strong> page — only your first name, last initial, and time zone will be visible to others.</p>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>{setShowFirstVisitModal(false);setHasSeenModal(true);}} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Stay hidden for now</button>
          <button onClick={()=>{setShowFirstVisitModal(false);setHasSeenModal(true);if(onGoToProfile)onGoToProfile();}} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Go to My Profile →</button>
        </div>
      </div>
    </div>}
    {/* Report modal */}
    {reportingPartner&&<div style={{position:"fixed",inset:0,background:OVERLAY_STRONGER,display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}>
      <div style={{background:SURFACE,borderRadius:12,padding:28,width:440,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
        {!reportSent?<>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}><div style={{fontWeight:600,fontSize:16}}>Report {shortName(reportingPartner.name)}</div><button onClick={closeReport} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:26,height:26,cursor:"pointer",fontSize:12,color:TEXT_MUTED}}>✕</button></div>
          <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:14,lineHeight:1.5}}>Reports are reviewed by our team. Please describe what happened.</p>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:8}}>REASON (select all that apply)</div>
            {REPORT_REASONS.map(r=>(
              <div key={r}>
                <label style={{display:"flex",alignItems:"center",gap:9,padding:"7px 10px",borderRadius:7,cursor:"pointer",marginBottom:4,background:reportReasons.includes(r)?PL:SURFACE_SUNKEN,border:`1px solid ${reportReasons.includes(r)?P:BORDER}`}}>
                  <input type="checkbox" checked={reportReasons.includes(r)} onChange={()=>setReportReasons(s=>s.includes(r)?s.filter(x=>x!==r):[...s,r])} style={{accentColor:P}}/>
                  <span style={{fontSize:13,color:reportReasons.includes(r)?P:TEXT_SECONDARY,fontWeight:reportReasons.includes(r)?500:400}}>{r}</span>
                </label>
                {r==="Other"&&reportReasons.includes("Other")&&<textarea value={reportOtherText} onChange={e=>setReportOtherText(e.target.value)} rows={3} placeholder="Please explain…" style={{width:"100%",padding:"8px 10px",border:`1px solid ${reportOtherText.trim()?BORDER:DANGER_BORDER}`,borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none",lineHeight:1.5,marginBottom:4}}/>}
              </div>
            ))}
          </div>
          <div style={{marginBottom:18}}>
            <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>ADDITIONAL DETAILS</div>
            <textarea value={reportText} onChange={e=>setReportText(e.target.value)} rows={2} placeholder="Any other context…" style={{width:"100%",padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none",lineHeight:1.5}}/>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
            <button onClick={closeReport} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Cancel</button>
            <button onClick={submitReport} disabled={!reportValid} style={{padding:"8px 16px",borderRadius:8,border:"none",background:reportValid?DANGER:DANGER_BORDER,color:SURFACE,fontSize:13,fontWeight:500,cursor:reportValid?"pointer":"default"}}>Submit Report</button>
          </div>
        </>:<div style={{textAlign:"center",padding:"10px 0 6px"}}>
          <div style={{width:48,height:48,borderRadius:"50%",background:SUCCESS_BG,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,margin:"0 auto 14px"}}>✓</div>
          <div style={{fontWeight:600,fontSize:16,marginBottom:6}}>Report submitted</div>
          <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:20}}>Our team will review your report and follow up if needed.</p>
          <button onClick={closeReport} style={{padding:"8px 20px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Done</button>
        </div>}
      </div>
    </div>}
    <h1 style={{fontSize:21,fontWeight:600,marginBottom:20}}>My Buddies</h1>

    {/* 1. BUDDY REQUESTS — top */}
    {buddyRequests.length>0&&<div style={{marginBottom:28}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em"}}>BUDDY REQUESTS</div>
        <span style={{background:DANGER,color:SURFACE,fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:999}}>{buddyRequests.length}</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8,maxWidth:580}}>
        {buddyRequests.map(req=>(
          <div key={req.id} style={{background:SURFACE,border:"1.5px solid "+P,borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:14}}>
            <div style={{width:42,height:42,borderRadius:"50%",background:PL,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,color:P,flexShrink:0}}>{req.initials}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:14,marginBottom:2}}>{shortName(req.name)}</div>
              <div style={{fontSize:12,color:TEXT_SUBTLE}}>{req.email}{req.mutualRecordings>0?" · "+req.mutualRecordings+" recording"+(req.mutualRecordings!==1?"s":"")+" together":""}</div>
            </div>
            <div style={{display:"flex",gap:7,flexShrink:0}}>
              <button onClick={()=>acceptRequest(req.id)} style={{padding:"7px 16px",borderRadius:7,border:"none",background:P,color:SURFACE,fontSize:12,fontWeight:600,cursor:"pointer"}}>Accept</button>
              <button onClick={()=>declineRequest(req.id)} style={{padding:"7px 14px",borderRadius:7,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_MUTED,fontSize:12,cursor:"pointer"}}>Decline</button>
            </div>
          </div>
        ))}
      </div>
    </div>}

    {/* 2. SAVED BUDDIES */}
    <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:12}}>SAVED BUDDIES</div>
    {partners.length===0&&pendingEmails.length===0&&<div style={{fontSize:13,color:TEXT_SUBTLE,padding:"16px 0 24px",textAlign:"center",maxWidth:580}}>You haven't recorded with anyone yet.</div>}
    <div style={{display:"flex",flexDirection:"column",gap:10,maxWidth:580,marginBottom:28}}>
      {partners.map(p=>(
        <div key={p.id} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"16px 20px",display:"flex",alignItems:"center",gap:16,opacity:p.blocked?0.65:1}}>
          <div style={{width:44,height:44,borderRadius:"50%",background:p.blocked?SURFACE_HOVER:PL,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:15,color:p.blocked?TEXT_SUBTLE:P,flexShrink:0}}>{p.initials}</div>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
              <div style={{fontWeight:600,fontSize:14}}>{shortName(p.name)}</div>
              {p.blocked&&<Badge label="Blocked" color="gray"/>}
              {p.reported&&<Badge label="Reported" color="red"/>}
            </div>
            <div style={{fontSize:12,color:TEXT_SUBTLE}}>{p.recordingsTogether} recording{p.recordingsTogether!==1?"s":""} together{p.lastRecorded!=="—"?" · Last: "+p.lastRecorded:""}</div>
          </div>
          <div style={{display:"flex",gap:8,flexShrink:0}}>
            <button onClick={()=>toggleBlock(p.id)} style={{padding:"6px 14px",borderRadius:7,border:"1px solid "+(p.blocked?BORDER:DANGER_BORDER),background:SURFACE,color:p.blocked?TEXT_SECONDARY:DANGER,fontSize:12,fontWeight:500,cursor:"pointer"}}>
              {p.blocked?"Unblock":"Block"}
            </button>
            <button onClick={()=>{if(!p.reported){setReportingPartner(p);}}} disabled={p.reported} style={{padding:"6px 14px",borderRadius:7,border:`1px solid ${BORDER}`,background:p.reported?SURFACE_SUNKEN:SURFACE,color:p.reported?TEXT_SUBTLE:TEXT_SECONDARY,fontSize:12,fontWeight:500,cursor:p.reported?"default":"pointer",opacity:p.reported?0.7:1}}>
              {p.reported?"Reported":"Report"}
            </button>
          </div>
        </div>
      ))}
      {/* Pending email requests */}
      {pendingEmails.map(email=>(
        <div key={email} style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"16px 20px",display:"flex",alignItems:"center",gap:16}}>
          <div style={{width:44,height:44,borderRadius:"50%",background:SURFACE_HOVER,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:TEXT_SUBTLE,flexShrink:0}}>Mail</div>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
              <div style={{fontWeight:500,fontSize:14,color:TEXT_SECONDARY}}>{email}</div>
              <Badge label="Pending" color="amber"/>
            </div>
            <div style={{fontSize:12,color:TEXT_SUBTLE}}>Buddy Request sent</div>
          </div>
        </div>
      ))}
    </div>

    {/* 3. ADD A BUDDY BY EMAIL */}
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:"18px 20px",marginBottom:24,maxWidth:580}}>
      <div style={{fontSize:12,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:10}}>ADD A BUDDY BY EMAIL</div>
      {!addSent?<>
        <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:12,lineHeight:1.5}}>If they're already on Jeenius, they'll receive your Buddy Request directly. If not, they'll get an email inviting them to join.</p>
        <div style={{display:"flex",gap:8}}>
          <input type="email" value={addEmail} onChange={e=>{setAddEmail(e.target.value);}} placeholder="their@email.com" style={{flex:1,padding:"8px 10px",border:`1px solid ${pendingEmails.includes(addEmail.trim().toLowerCase())?DANGER_BORDER:BORDER}`,borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          <button onClick={sendBuddyByEmail} disabled={!addEmail.trim()||pendingEmails.includes(addEmail.trim().toLowerCase())} style={{padding:"8px 16px",borderRadius:8,border:"none",background:(addEmail.trim()&&!pendingEmails.includes(addEmail.trim().toLowerCase()))?P:BRAND_SOFTER,color:SURFACE,fontSize:13,fontWeight:500,cursor:(addEmail.trim()&&!pendingEmails.includes(addEmail.trim().toLowerCase()))?"pointer":"default",whiteSpace:"nowrap"}}>Send Request →</button>
        </div>
        {pendingEmails.includes(addEmail.trim().toLowerCase())&&addEmail.trim()&&<p style={{fontSize:12,color:DANGER,marginTop:6,margin:"6px 0 0"}}>A Buddy Request to this address is already pending.</p>}
      </>:<>
        <div style={{display:"flex",alignItems:"center",gap:10,background:SUCCESS_SURFACE,border:`1px solid ${SUCCESS_LIGHT}`,borderRadius:8,padding:"10px 14px",marginBottom:10}}>
          <span style={{fontSize:18}}>✓</span>
          <p style={{fontSize:13,color:SUCCESS,margin:0,lineHeight:1.5}}>Request sent. If they're already on Jeenius, they'll see it in their Buddy Requests. If not, they'll receive an email inviting them to join.</p>
        </div>
        <button onClick={()=>setAddSent(false)} style={{fontSize:12,color:P,background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:500}}>Send another →</button>
      </>}
    </div>

    {/* 4. BROWSE DISCOVERABLE CREATORS */}
    {(()=>{
      const tzOptions=Array.from(new Set(discoverableCreators.map(dc=>dc.timezone))).sort();
      const topicOptions=Array.from(new Set(discoverableCreators.flatMap(dc=>dc.favoriteTopics||[]))).sort();
      const browseResults=discoverableCreators.filter(dc=>{
        if(browseTZFilter.length>0&&!browseTZFilter.includes(dc.timezone))return false;
        if(browseTopicFilter.length>0&&!(dc.favoriteTopics||[]).some(t=>browseTopicFilter.includes(t)))return false;
        return true;
      });
      const toggleTZ=tz=>setBrowseTZFilter(prev=>prev.includes(tz)?prev.filter(x=>x!==tz):[...prev,tz]);
      const toggleTopic=t=>setBrowseTopicFilter(prev=>prev.includes(t)?prev.filter(x=>x!==t):[...prev,t]);
      const tzLabel=browseTZFilter.length===0?"All time zones":browseTZFilter.length===1?browseTZFilter[0]:`${browseTZFilter.length} time zones selected`;
      const topicLabel=browseTopicFilter.length===0?"All favorite topics":browseTopicFilter.length===1?browseTopicFilter[0]:`${browseTopicFilter.length} topics selected`;
      const activeFilters=browseTZFilter.length+browseTopicFilter.length;
      return(<div style={{maxWidth:580}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>BROWSE DISCOVERABLE CREATORS</div>
        <p style={{fontSize:13,color:TEXT_MUTED,marginBottom:12,lineHeight:1.5}}>
          Adding someone as a buddy means you can invite them directly to future recordings — no searching required. It's worth building your list now so you have partners ready when a new batch opens.
        </p>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <span style={{fontSize:12,color:TEXT_SECONDARY,fontWeight:500,flexShrink:0,minWidth:110}}>Time zone:</span>
          <div ref={tzDropRef} style={{position:"relative",flex:1}}>
            <button onClick={()=>setTzDropOpen(v=>!v)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 10px",border:`1px solid ${browseTZFilter.length>0?P:BORDER}`,borderRadius:7,background:browseTZFilter.length>0?PL:SURFACE,color:browseTZFilter.length>0?P:TEXT_SECONDARY,fontSize:13,cursor:"pointer",outline:"none"}}>
              <span style={{fontWeight:browseTZFilter.length>0?500:400}}>{tzLabel}</span>
              <span style={{fontSize:10,color:browseTZFilter.length>0?P:TEXT_SUBTLE,marginLeft:6}}>▾</span>
            </button>
            {tzDropOpen&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:9,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:60,padding:"6px 0",maxHeight:220,overflowY:"auto"}}>
              {tzOptions.map(tz=>(
                <label key={tz} style={{display:"flex",alignItems:"center",gap:9,padding:"7px 13px",cursor:"pointer",background:browseTZFilter.includes(tz)?PL:SURFACE}} onMouseEnter={e=>!browseTZFilter.includes(tz)&&(e.currentTarget.style.background=SURFACE_SUNKEN)} onMouseLeave={e=>!browseTZFilter.includes(tz)&&(e.currentTarget.style.background=SURFACE)}>
                  <input type="checkbox" checked={browseTZFilter.includes(tz)} onChange={()=>toggleTZ(tz)} style={{accentColor:P,flexShrink:0}}/>
                  <span style={{fontSize:13,color:browseTZFilter.includes(tz)?P:TEXT_PRIMARY,fontWeight:browseTZFilter.includes(tz)?500:400}}>{tz}</span>
                </label>
              ))}
              {browseTZFilter.length>0&&<div style={{borderTop:`1px solid ${SURFACE_HOVER}`,padding:"6px 13px"}}>
                <button onClick={()=>setBrowseTZFilter([])} style={{fontSize:12,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:0}}>Clear all</button>
              </div>}
            </div>}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <span style={{fontSize:12,color:TEXT_SECONDARY,fontWeight:500,flexShrink:0,minWidth:110}}>Favorite topics:</span>
          <div ref={topicDropRef} style={{position:"relative",flex:1}}>
            <button onClick={()=>setTopicDropOpen(v=>!v)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 10px",border:`1px solid ${browseTopicFilter.length>0?P:BORDER}`,borderRadius:7,background:browseTopicFilter.length>0?PL:SURFACE,color:browseTopicFilter.length>0?P:TEXT_SECONDARY,fontSize:13,cursor:"pointer",outline:"none"}}>
              <span style={{fontWeight:browseTopicFilter.length>0?500:400}}>{topicLabel}</span>
              <span style={{fontSize:10,color:browseTopicFilter.length>0?P:TEXT_SUBTLE,marginLeft:6}}>▾</span>
            </button>
            {topicDropOpen&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:9,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:60,padding:"6px 0",maxHeight:260,overflowY:"auto"}}>
              {topicOptions.map(t=>(
                <label key={t} style={{display:"flex",alignItems:"center",gap:9,padding:"7px 13px",cursor:"pointer",background:browseTopicFilter.includes(t)?PL:SURFACE}} onMouseEnter={e=>!browseTopicFilter.includes(t)&&(e.currentTarget.style.background=SURFACE_SUNKEN)} onMouseLeave={e=>!browseTopicFilter.includes(t)&&(e.currentTarget.style.background=SURFACE)}>
                  <input type="checkbox" checked={browseTopicFilter.includes(t)} onChange={()=>toggleTopic(t)} style={{accentColor:P,flexShrink:0}}/>
                  <span style={{fontSize:13,color:browseTopicFilter.includes(t)?P:TEXT_PRIMARY,fontWeight:browseTopicFilter.includes(t)?500:400}}>{t}</span>
                </label>
              ))}
              {browseTopicFilter.length>0&&<div style={{borderTop:`1px solid ${SURFACE_HOVER}`,padding:"6px 13px"}}>
                <button onClick={()=>setBrowseTopicFilter([])} style={{fontSize:12,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:0}}>Clear all</button>
              </div>}
            </div>}
          </div>
        </div>
        {activeFilters>0&&<div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
          {browseTZFilter.map(tz=><span key={`tz-${tz}`} style={{display:"flex",alignItems:"center",gap:4,background:PL,color:P,fontSize:11,fontWeight:600,padding:"3px 8px 3px 10px",borderRadius:999}}>{tz}<span onClick={()=>toggleTZ(tz)} style={{cursor:"pointer",fontSize:13,lineHeight:1,color:BRAND_SOFT,marginLeft:2}}>×</span></span>)}
          {browseTopicFilter.map(t=><span key={`t-${t}`} style={{display:"flex",alignItems:"center",gap:4,background:PL,color:P,fontSize:11,fontWeight:600,padding:"3px 8px 3px 10px",borderRadius:999}}>{t}<span onClick={()=>toggleTopic(t)} style={{cursor:"pointer",fontSize:13,lineHeight:1,color:BRAND_SOFT,marginLeft:2}}>×</span></span>)}
          <button onClick={()=>{setBrowseTZFilter([]);setBrowseTopicFilter([]);}} style={{fontSize:11,color:TEXT_SUBTLE,background:"none",border:"none",cursor:"pointer",padding:"3px 4px"}}>Clear all ×</button>
        </div>}
        <div style={{border:`1px solid ${BORDER}`,borderRadius:10,overflow:"hidden",maxHeight:360,overflowY:"auto"}}>
          {browseResults.length>0?browseResults.map((dc,i)=>{
            const sent=sentRequests[dc.id];
            const favs=dc.favoriteTopics||[];
            return(<div key={dc.id} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 16px",background:SURFACE,borderBottom:i<browseResults.length-1?`1px solid ${SURFACE_HOVER}`:"none"}} onMouseEnter={e=>e.currentTarget.style.background=SURFACE_ALT} onMouseLeave={e=>e.currentTarget.style.background=SURFACE}>
              <div style={{width:36,height:36,borderRadius:"50%",background:SUCCESS_SURFACE,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:12,color:SUCCESS,flexShrink:0}}>{dc.displayName.split(" ").map(n=>n[0]).join("")}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:13,marginBottom:1}}>{dc.displayName}</div>
                <div style={{fontSize:11,color:TEXT_SUBTLE,marginBottom:favs.length>0?6:0}}>{dc.timezone}</div>
                {favs.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                  {favs.map(t=><span key={t} style={{fontSize:10,fontWeight:500,padding:"2px 7px",borderRadius:999,background:browseTopicFilter.includes(t)?PL:SURFACE_HOVER,color:browseTopicFilter.includes(t)?P:TEXT_MUTED,border:browseTopicFilter.includes(t)?`1px solid ${P}`:"1px solid transparent"}}>{t}</span>)}
                </div>}
              </div>
              {sent
                ?<span style={{fontSize:11,color:TEXT_SUBTLE,fontWeight:500,flexShrink:0}}>Request sent</span>
                :<button onClick={()=>setSentRequests(prev=>({...prev,[dc.id]:true}))} style={{padding:"6px 14px",borderRadius:7,border:`1px solid ${P}`,background:SURFACE,color:P,fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>Add Buddy</button>
              }
            </div>);
          }):<div style={{padding:"24px",fontSize:13,color:TEXT_SUBTLE,textAlign:"center"}}>No creators match the selected filters.</div>}
        </div>
        <div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:6}}>{browseResults.length} creator{browseResults.length!==1?"s":""}{activeFilters>0?" matching filters":""}</div>
      </div>);
    })()}

  </div>);
}

// ── NOTIFICATIONS ────────────────────────────────────────────────────────────

const NOTIF_CAT_STYLE={
  Recording:{bg:INFO_BG,c:INFO},
  QA:{bg:SUCCESS_BG,c:SUCCESS},
  Payment:{bg:WARNING_BG,c:WARNING},
  Buddies:{bg:"#f3e8ff",c:BRAND},
  Batch:{bg:TEAL_BG,c:TEAL_DEEP},
  "Recording Batch":{bg:INFO_BG,c:INFO},
  Deadline:{bg:DANGER_BG,c:DANGER},
  "User Report":{bg:WARNING_BG,c:WARNING},
};

function ReportDetailModal({report,onClose}){
  return(<div style={{position:"fixed",inset:0,background:OVERLAY_MAX,display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}>
    <div style={{background:SURFACE,borderRadius:14,padding:28,width:480,boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <div style={{fontWeight:700,fontSize:16}}>User Report Detail</div>
        <button onClick={onClose} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:"50%",width:28,height:28,cursor:"pointer",fontSize:13,color:TEXT_MUTED}}>✕</button>
      </div>
      <div style={{display:"flex",gap:16,marginBottom:18}}>
        <div style={{flex:1,background:WARNING_BG,borderRadius:10,padding:"12px 14px"}}>
          <div style={{fontSize:10,fontWeight:700,color:WARNING,letterSpacing:"0.08em",marginBottom:6}}>REPORTED BY</div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:"#fde68a",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:12,color:WARNING}}>{report.reporterInitials}</div>
            <span style={{fontSize:13,fontWeight:600}}>{report.reporter}</span>
          </div>
        </div>
        <div style={{flex:1,background:DANGER_BG,borderRadius:10,padding:"12px 14px"}}>
          <div style={{fontSize:10,fontWeight:700,color:DANGER,letterSpacing:"0.08em",marginBottom:6}}>REPORTED USER</div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:DANGER_BORDER,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:12,color:DANGER}}>{report.reportedInitials}</div>
            <span style={{fontSize:13,fontWeight:600}}>{report.reported}</span>
          </div>
        </div>
      </div>
      <div style={{background:SURFACE_SUNKEN,borderRadius:9,padding:"12px 14px",marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:4}}>CONTEXT</div>
        <div style={{fontSize:13,color:TEXT_SECONDARY}}>{report.project} · {report.topic}</div>
        <div style={{fontSize:11,color:TEXT_SUBTLE,marginTop:2}}>{report.time}</div>
      </div>
      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>REASON</div>
        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{report.reasons.map(r=><span key={r} style={{background:DANGER_BG,color:DANGER,fontSize:12,fontWeight:600,padding:"3px 9px",borderRadius:999}}>{r}</span>)}</div>
      </div>
      <div style={{marginBottom:22}}>
        <div style={{fontSize:11,fontWeight:600,color:TEXT_SUBTLE,letterSpacing:"0.08em",marginBottom:6}}>DETAILS</div>
        <div style={{fontSize:13,color:TEXT_SECONDARY,lineHeight:1.6,background:SURFACE_SUNKEN,borderRadius:8,padding:"10px 12px"}}>{report.details}</div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button onClick={onClose} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,fontSize:13,cursor:"pointer"}}>Dismiss</button>
        <button style={{padding:"8px 16px",borderRadius:8,border:"none",background:DANGER,color:SURFACE,fontSize:13,fontWeight:500,cursor:"pointer"}}>Take Action</button>
      </div>
    </div>
  </div>);
}

function NotifRow({notif,onMarkRead,onNavigate}){
  const cs=NOTIF_CAT_STYLE[notif.category]||{bg:SURFACE_HOVER,c:TEXT_MUTED};
  const handleArrow=(e)=>{e.stopPropagation();onMarkRead(notif.id);onNavigate(notif);};
  return(
    <div onClick={()=>onMarkRead(notif.id)} style={{display:"flex",alignItems:"center",gap:14,padding:"16px 20px 16px 32px",background:notif.read?SURFACE:BRAND_LIGHTER,cursor:"pointer",position:"relative"}} onMouseEnter={e=>e.currentTarget.style.background=notif.read?SURFACE_ALT:"#f0ebff"} onMouseLeave={e=>e.currentTarget.style.background=notif.read?SURFACE:BRAND_LIGHTER}>
      {!notif.read&&<div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",width:7,height:7,borderRadius:"50%",background:P}}/>}
<div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:3}}>
          <div style={{fontWeight:notif.read?400:600,fontSize:13,color:TEXT_PRIMARY,lineHeight:1.4}}>{notif.title}</div>
          <span style={{fontSize:11,color:TEXT_SUBTLE,whiteSpace:"nowrap",flexShrink:0,marginTop:1}}>{notif.time}</span>
        </div>
        <div style={{fontSize:12,color:TEXT_MUTED,lineHeight:1.5,marginBottom:6}}>{notif.body}</div>
        <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:999,background:cs.bg,color:cs.c}}>{notif.category}</span>
      </div>
      <div onClick={handleArrow} style={{flexShrink:0,width:30,height:30,borderRadius:"50%",background:SURFACE_HOVER,display:"flex",alignItems:"center",justifyContent:"center",color:TEXT_SUBTLE,fontSize:17,cursor:"pointer",marginLeft:4}} onMouseEnter={e=>{e.currentTarget.style.background=PL;e.currentTarget.style.color=P;}} onMouseLeave={e=>{e.currentTarget.style.background=SURFACE_HOVER;e.currentTarget.style.color=TEXT_SUBTLE;}}>›</div>
    </div>
  );
}

function NotificationsScreen({notifs,setNotifs,onNavigate}){
  const[reportDetail,setReportDetail]=useState(null);
  const unreadCount=notifs.filter(n=>!n.read).length;
  const markRead=id=>setNotifs(prev=>prev.map(n=>n.id===id?{...n,read:true}:n));
  const markAllRead=()=>setNotifs(prev=>prev.map(n=>({...n,read:true})));
  const handleNavigate=(notif)=>{
    if(notif.action==="report-detail"&&notif.report){setReportDetail(notif.report);}
    else if(onNavigate){onNavigate(notif.action);}
  };
  return(<div style={{flex:1,padding:"26px 32px",overflowY:"auto"}}>
    {reportDetail&&<ReportDetailModal report={reportDetail} onClose={()=>setReportDetail(null)}/>}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
      <div>
        <h1 style={{fontSize:21,fontWeight:600,margin:0,marginBottom:2}}>Notifications</h1>
        {unreadCount>0&&<p style={{fontSize:13,color:TEXT_MUTED,margin:0}}>{unreadCount} unread</p>}
      </div>
      {unreadCount>0&&<button onClick={markAllRead} style={{fontSize:12,color:P,background:"none",border:"none",cursor:"pointer",fontWeight:500}}>Mark all as read</button>}
    </div>
    <div style={{display:"flex",flexDirection:"column",maxWidth:640,background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:"hidden"}}>
      {notifs.map((notif,i)=>(
        <div key={notif.id} style={{borderBottom:i<notifs.length-1?`1px solid ${SURFACE_HOVER}`:"none"}}>
          <NotifRow notif={notif} onMarkRead={markRead} onNavigate={handleNavigate}/>
        </div>
      ))}
      {notifs.length===0&&<div style={{padding:"40px 20px",textAlign:"center",fontSize:13,color:TEXT_SUBTLE}}>You're all caught up!</div>}
    </div>
  </div>);
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
const sampleScriptSolo=`SCRIPT: Arabic Business Monologue — Finance & Investment
Language: Arabic | Topic: Business | Duration: 30–60 min

Read this script aloud, naturally and at a steady pace. You may pause between sections as a real presenter would.

─────────────────────────────────────────

Good morning, everyone. Thank you for joining us today. I appreciate the time you have all set aside for this quarterly review, and I want to assure you that we will make efficient use of it. My name is Khalid Al-Mansouri, and I serve as the Chief Financial Officer of Meridian Capital Partners. Today I will walk you through our financial performance for the first quarter, highlight the key drivers behind our results, address the challenges we encountered, and outline our strategic priorities for the remainder of the fiscal year.

Let me begin with the top line. Revenue for Q1 came in at four hundred and thirty-two million dirhams, representing a fourteen percent increase year-over-year. This result exceeded our internal forecast by approximately six percent, and I am proud to say it marks the fifth consecutive quarter of double-digit revenue growth. The primary contributors to this outperformance were our Gulf region expansion initiative and the accelerated adoption of our digital advisory platform among retail investors.

Our Gulf region now accounts for twenty-three percent of total revenue, up from fifteen percent in the same period last year. This expansion has been the result of deliberate investment over the past eighteen months — investment in talent, in regulatory compliance infrastructure, and in building local partnerships that give us credibility in markets where trust is paramount. I want to take a moment to recognize the regional leadership team in Riyadh and Abu Dhabi, who have executed this expansion with remarkable discipline.

On the cost side, I am pleased to report that operating expenses remained flat on an absolute basis despite the revenue growth, which means our operating margin expanded from eighteen point two percent in Q1 of last year to twenty point eight percent this quarter. We achieved this through a combination of automation initiatives in our back-office functions, renegotiated vendor contracts, and a hiring freeze we implemented in the second half of last year that is now showing results. Our cost-per-transaction metric declined by eleven percent year-over-year, which is a direct reflection of the efficiency gains we have been working toward.

Now, I do want to speak candidly about an area of concern. Supply chain disruptions in the technology hardware sector created delays in our infrastructure upgrade program. Specifically, we had planned to complete the migration of our core trading systems to the new data center by the end of March. That timeline slipped by approximately six weeks. The delay has not impacted client-facing services, and I want to be very clear about that. Our clients experienced no degradation in system performance or availability during this period. However, the delay does mean that some of the efficiency gains we expected to capture in Q2 will now be realized in Q3.

We have already put mitigation steps in place. We have secured priority allocation agreements with our hardware suppliers, and we have engaged an additional systems integration firm to accelerate the migration. We now expect to complete the infrastructure transition by the end of May, which keeps us on track to meet our full-year technology roadmap.

On the investment performance side, our flagship Meridian Diversified Fund delivered a return of six point four percent in Q1, compared to a benchmark return of four point nine percent. This outperformance of one hundred and fifty basis points was driven primarily by our overweight position in Gulf energy equities and our underweight in European technology, which faced significant headwinds during the quarter. Our risk team maintained a portfolio volatility level of eight point two percent annualized, which is within our target band of seven to nine percent.

Our fixed income strategies performed more modestly. The Meridian Income Fund returned two point one percent in Q1, in line with expectations. Rising yields in the latter part of the quarter created some mark-to-market pressure on longer-duration positions, but we view this as transient, and our duration positioning is appropriate given the current rate environment.

On capital allocation, the Board approved a share buyback program of fifty million dirhams in January, of which we have deployed thirty-one million to date. We expect to complete the buyback by the end of Q2. In addition, our balance sheet remains strong, with net cash of two hundred and fourteen million dirhams and a debt-to-equity ratio of zero point three, well below our internal ceiling of zero point six. This capital strength gives us the flexibility to pursue the two strategic acquisitions we have under evaluation, which I will discuss with the Board in our private session this afternoon.

Looking ahead, we are reaffirming our full-year revenue guidance of one point eight billion dirhams, representing twelve percent growth over the prior year. We expect operating margin to expand to twenty-two percent by year-end as the infrastructure investments mature and the Gulf expansion continues to scale. Our headcount plan calls for net additions of approximately one hundred and twenty employees across the region, with the majority of hiring concentrated in client advisory and technology functions.

I want to close by acknowledging the market environment we are operating in. There is no shortage of uncertainty — geopolitical tensions, evolving monetary policy, and continued volatility in global capital markets. But I believe that uncertainty is precisely where disciplined institutions differentiate themselves. Our investment philosophy, our risk framework, and our operational infrastructure are all designed to perform in conditions like these. The Q1 results are evidence of that.

I am confident in the team's ability to execute on our priorities, and I am grateful for the continued trust of our shareholders, our clients, and this Board. I will now pause for questions.

─────────────────────────────────────────
END OF SCRIPT`;

const sampleScriptDuo=`SCRIPT: Spanish Customer Service Dialogue — Healthcare Billing
Language: Spanish | Topic: Healthcare | Duration: 20–35 min

Read your assigned speaker's lines exactly as written. Speak naturally — you may add small filler words like "um," "yes," or "I see" where they feel natural, but do not skip or significantly alter any line. The other speaker's lines are shown in gray for reference only.

─────────────────────────────────────────

SPEAKER B: Good morning, thank you for calling Clínica del Sol billing department, my name is Roberto, how can I help you today?

SPEAKER A: Hi, yes, good morning. I'm calling because I received a bill in the mail for three hundred and forty dollars, and honestly I'm a little confused because I thought my insurance covered my visit last month.

SPEAKER B: I completely understand, and I'm sorry for any confusion. Could I please get your full name and date of birth so I can pull up your account?

SPEAKER A: Sure, it's Elena Martínez, date of birth March fifteenth, nineteen eighty-eight.

SPEAKER B: Thank you, Elena. And can you confirm the last four digits of your Social Security number or your patient ID if you have it handy?

SPEAKER A: Um, let me look... okay yes, my patient ID is seven seven four two nine.

SPEAKER B: Perfect, thank you. Okay, I'm looking at your account now. I can see the visit from — it looks like March third — and I can see the charge of three hundred and forty dollars. Can I ask, did you receive an Explanation of Benefits from your insurance company as well?

SPEAKER A: I did get something in the mail but honestly I didn't really understand it. It looked like they paid part of it, but then this bill showed up and I wasn't sure if it was a mistake.

SPEAKER B: That makes sense, these documents can be confusing. So what I'm seeing here is that your insurance processed the claim for the office visit and they covered that portion in full. The three hundred and forty dollar charge is actually for laboratory services — specifically, a blood panel and a lipid screening — that were ordered during your visit. Lab services are billed separately from the visit itself.

SPEAKER A: Oh. I had no idea that was going to be billed separately. Nobody told me that when I was there. I mean, the doctor just said he wanted to run a few tests and I said okay. I didn't know I was going to get a separate bill for it.

SPEAKER B: I hear you, and I understand that's frustrating. You're right that it should have been communicated more clearly at the time of your visit. The lab panel is performed by an independent laboratory that contracts with our clinic, which is why it comes on a separate bill. That said, I do want to see what I can do to help you today.

SPEAKER A: I appreciate that. So is the three forty the amount after insurance, or is that before?

SPEAKER B: That is after insurance. Your plan has a separate lab deductible, and based on what I'm seeing, you haven't yet met that deductible for the year, so the full lab cost is being applied to it. The original lab charge was four hundred and eighty dollars, your insurance has adjusted it to three hundred and forty, and that remaining balance is your responsibility under your plan terms.

SPEAKER A: Okay. That's still a lot. I mean, I don't have three hundred and forty dollars just sitting there right now. Is there anything I can do?

SPEAKER B: Absolutely, we have a couple of options for you. The first option is if you're able to pay the balance in full today or within the next ten business days, we can apply a ten percent prompt-payment discount, which would bring your balance down to three hundred and six dollars.

SPEAKER A: Okay, that's a little better. What's the other option?

SPEAKER B: The second option is a payment plan. For a balance in this range, we can set you up on a three-month installment plan with no interest. That would be approximately one hundred and thirteen dollars per month, automatically charged to a card on file, on the same date each month.

SPEAKER A: Three months. Okay, that's more manageable. Can I choose which date of the month the charge goes through?

SPEAKER B: Yes, you can choose any date between the first and the twenty-eighth of the month.

SPEAKER A: Let's do the fifteenth then. Can I set that up now over the phone?

SPEAKER B: Absolutely. I'll need a credit or debit card to put on file. Whenever you're ready.

SPEAKER A: Okay, one second, let me grab my card. Okay, it's Visa, four seven seven one — do you need the whole number?

SPEAKER B: Yes please, go ahead.

SPEAKER A: Four seven seven one, eight eight zero two, three three one five, nine nine two four. Expiration is oh nine twenty-seven, and the security code is four two one.

SPEAKER B: Got it, thank you. And just to confirm, the billing address on file for this card — is that the same address we have for you, which shows as twelve forty Calle Serrano, Apartment four B?

SPEAKER A: Yes, that's correct.

SPEAKER B: Perfect. So I've got you set up on a three-month installment plan, first payment of one hundred and thirteen dollars on April fifteenth, and the same amount on May fifteenth and June fifteenth. You'll receive a confirmation email at the address we have on file. Is your email still e.martinez at gmail dot com?

SPEAKER A: Yes, that's right.

SPEAKER B: Great. You should receive that confirmation within a few minutes. Is there anything else I can help you with today?

SPEAKER A: Actually, yes. I want to make sure this doesn't happen again. Is there a way for someone to tell me upfront if I'm going to get a separate bill for something the doctor orders?

SPEAKER B: That's a really valid request. What I would suggest is that at your next visit, before any tests or procedures are ordered, you ask the front desk or the nurse specifically whether any services will be billed by a third party. You can also ask them to flag your account so that a financial counselor contacts you before any elective procedures to review potential out-of-pocket costs. Would you like me to make a note on your account requesting that for future visits?

SPEAKER A: Yes please, that would be great. Thank you, Roberto, I appreciate your help. I was a little frustrated when I called but this has been really clear.

SPEAKER B: I completely understand, and I'm glad I could help clarify everything. I hope you have a great rest of your day, Elena. Take care.

SPEAKER A: You too, bye.

SPEAKER B: Goodbye.

─────────────────────────────────────────
END OF SCRIPT`;


const initialActiveTasks=[
  {id:"2b3c6133-1f38-4cd1-affa-3cd4d0442b03",project:"Spanish Conversational Audio – Batch 3",projId:"p1",topic:"Daily Life",lang:"Spanish",partner:"Maria González",duration:"20–45 min",deadline:"May 8, 2025",status:"Ready to Record",window:{date:"2025-04-14",start:"10:00",end:"10:45"},specialNotes:"Speak naturally and at a conversational pace. Avoid long pauses. Feel free to go slightly over the target duration if the conversation is flowing well."},
  {id:"23b80486-327e-4a29-bfe5-44fbcc4a3fde",project:"Arabic Business Scripted",projId:"p3",topic:"Business",lang:"Arabic",duration:"30–60 min",deadline:"Jun 1, 2025",status:"Solo",solo:true,scripted:true,specialNotes:"Focus on formal business vocabulary. Speak as if presenting to a board.",script:sampleScriptSolo},
  {id:"19654a9f-5641-4555-9abc-6589d999b33e",project:"Spanish Healthcare Scripted",projId:"p1",topic:"Healthcare",lang:"Spanish",partner:"Diego Fernández",duration:"20–35 min",deadline:"May 15, 2025",status:"Ready to Record",scripted:true,script:sampleScriptDuo,window:{date:"2025-04-16",start:"14:00",end:"14:45"}},
  {id:"c18c6465-fa47-49de-9f49-270d0bab4fb7",project:"Spanish Family Conversations – 3-Person",projId:"p6",topic:"Family",lang:"Spanish",partners:["Kwame Boateng","Zara Mensah"],numParticipants:3,duration:"25–45 min",deadline:"Jun 20, 2025",status:"Ready to Record",window:{date:"2025-04-18",start:"11:00",end:"11:45"}},
  {id:"e230e51f-9c22-4cec-b736-1695607be4ff",project:"Spanish Family Conversations – 3-Person",projId:"p6",topic:"Culture",lang:"Spanish",partners:["Maria González","Diego Fernández"],numParticipants:3,duration:"25–45 min",deadline:"Jun 20, 2025",status:"Ready to Record"},
];

export default function App(){
  const[role,setRole]=useState("Content Creator");
  const[screen,setScreen]=useState("main");
  const[claimProj,setClaimProj]=useState(null);
  const[contactTask,setContactTask]=useState(null);
  const[taskList,setTaskList]=useState(initialActiveTasks);
  const[allProjects,setAllProjects]=useState(initialOpsProjects);
  const[recorderKey,setRecorderKey]=useState(0);
  const[showInviteNotif,setShowInviteNotif]=useState(false);
  const[notifPhase,setNotifPhase]=useState(null);
  const[invitations,setInvitations]=useState(initialInvitations);
  const[acceptedInv,setAcceptedInv]=useState({});
  const[creatorNotifs,setCreatorNotifs]=useState(initialCreatorNotifs);
  const[opsNotifs,setOpsNotifs]=useState(initialOpsNotifs);
  const[buddyRequests,setBuddyRequests]=useState(initialBuddyRequests);
  const[discoverable,setDiscoverable]=useState(false);
  const[timezone,setTimezone]=useState("America/Chicago");
  let taskCounter=useRef(4);

  const isAdmin=role==="Admin";
  const isOps=role==="Ops Manager"||isAdmin; // Admin sees everything Ops sees
  const isQaReviewer=role==="Reviewer (QA)";
  const isReviewer=role==="Reviewer"||role==="Reviewer & Creator"||isQaReviewer;
  const isCreator=role==="Content Creator"||role==="Reviewer & Creator";

  const profile=role==="Reviewer"?reviewerProfile:isQaReviewer?qaReviewerProfile:role==="Content Creator"?creatorProfile:combinedProfile;

  const creatorUnread=creatorNotifs.filter(n=>!n.read).length;
  const opsUnread=opsNotifs.filter(n=>!n.read).length;
  const buddyReqCount=buddyRequests.length;
  // Ops attention count — matches logic in OpsTaskManagementScreen
  const opsAttentionCount=allTasksData.filter(t=>
    (t.status==="Unassigned"&&t.assignmentMode==="direct")||
    (t.status==="Unassigned"&&t.assignmentMode==="pool"&&(t.daysUnclaimed||0)>=7)||
    (t.pastDeadline&&t.status!=="Completed")
  ).length;

  const opsNav=[
    {label:"Recording Batches",active:screen==="main",onClick:()=>setScreen("main")},
    {label:"Reviewer Batches",active:screen==="ops-review",onClick:()=>setScreen("ops-review")},
    {label:"Task Management",active:screen==="task-mgmt",onClick:()=>setScreen("task-mgmt"),badge:opsAttentionCount,badgeVariant:"attention"},
    {label:"Orchestration",active:screen==="orchestration",onClick:()=>setScreen("orchestration")},
    {label:"Projects",active:screen==="ops-projects",onClick:()=>setScreen("ops-projects")},
    {label:"Users",active:screen==="users",onClick:()=>setScreen("users")},
    ...(isAdmin?[{label:"Rate Management",active:screen==="rate-mgmt",onClick:()=>setScreen("rate-mgmt")}]:[]),
    {label:"Notifications",active:screen==="notifications",onClick:()=>setScreen("notifications"),badge:opsUnread},
  ];
  const reviewerNav=[
    {label:"My Tasks",active:screen==="main",onClick:()=>setScreen("main")},
    {label:"History",active:screen==="history",onClick:()=>setScreen("history")},
  ];
  const creatorNav=[
    {label:"Available Batches",active:screen==="main"&&!claimProj,onClick:()=>{setScreen("main");setClaimProj(null);}},
    {label:"My Recordings",active:screen==="recordings",onClick:()=>setScreen("recordings")},
    {label:"My Buddies",active:screen==="partners",onClick:()=>setScreen("partners"),badge:buddyReqCount},
    {label:"History",active:screen==="history",onClick:()=>setScreen("history")},
    {label:"Notifications",active:screen==="notifications",onClick:()=>setScreen("notifications"),badge:creatorUnread},
  ];
  const combinedNav=[
    {label:"Available Batches",active:screen==="main"&&!claimProj,onClick:()=>{setScreen("main");setClaimProj(null);}},
    {label:"My Tasks",active:screen==="tasks",onClick:()=>setScreen("tasks")},
    {label:"My Recordings",active:screen==="recordings",onClick:()=>setScreen("recordings")},
    {label:"My Buddies",active:screen==="partners",onClick:()=>setScreen("partners"),badge:buddyReqCount},
    {label:"History",active:screen==="history",onClick:()=>setScreen("history")},
    {label:"Notifications",active:screen==="notifications",onClick:()=>setScreen("notifications"),badge:creatorUnread},
  ];

  const nav=isOps?opsNav:role==="Reviewer & Creator"?combinedNav:isReviewer?reviewerNav:creatorNav;
  const user={name:profile.name,i:profile.initials,role:profile.roleLabel};

  const handleClaim=(proj)=>{
    if(proj.solo){
      // Solo batch: if it has multiple topics, show picker first
      if(proj.topics&&proj.topics.length>1){
        setClaimProj(proj);
        setScreen("solo-topic");
        return;
      }
      // Single topic — go straight to contact
      makeSoloTask(proj,proj.topics[0]);
    } else {
      setClaimProj(proj);
      setScreen("claim");
    }
  };
  const makeSoloTask=(proj,topic)=>{
    const soloTask={
      id:`TSK-00${taskCounter.current++}`,
      project:proj.title,projId:proj.id,
      topic,lang:proj.langs?.[0]||null,duration:proj.durationRange||"30–45 min",
      deadline:proj.deadline,status:"Solo",solo:true,
      scripted:!!proj.scripted,
      script:proj.scripted?sampleScriptSolo:null,
      specialNotes:proj.specialNotes||null
    };
    setContactTask(soloTask);
    setClaimProj(null);
    setScreen("contact");
  };
  const handleClaimDone=(claimData)=>{
    if(claimData){
      const{proj,topic,partner,window:win}=claimData;
      const status=partner?"Ready to Record":"Awaiting Buddy";
      const newTask={
        id:`TSK-00${taskCounter.current++}`,
        project:proj.title,projId:proj.id,topic,partner:partner||null,
        lang:proj.langs?.[0]||null,
        window:win||null,duration:proj.durationRange||"30–45 min",
        deadline:proj.deadline,status,
        scripted:!!proj.scripted&&(proj.numParticipants||2)<=2,
        script:proj.scripted&&(proj.numParticipants||2)<=2?sampleScriptDuo:null,
        specialNotes:proj.specialNotes||null
      };
      setTaskList(prev=>[...prev,newTask]);
    }
    setClaimProj(null);
    setScreen("recordings");
  };
  const handleContact=(task)=>{setContactTask(task);setScreen("contact");};
  const handleRecorder=()=>setScreen("recorder");
  const handleRecorderDone=()=>{
    setScreen("record-again");
  };
  const handleRecorderEndSession=()=>{
    if(contactTask?.partner||contactTask?.partners?.length>0){setScreen("post-recording");}
    else{setContactTask(null);setScreen("recordings");}
  };

  let mainContent=null;
  if(screen==="profile") mainContent=<ProfileScreen profile={profile} discoverable={discoverable} setDiscoverable={setDiscoverable} timezone={timezone} setTimezone={setTimezone} isCreator={isCreator}/>;
  else if(screen==="users"&&isOps) mainContent=<UserManagementScreen/>;
  else if(screen==="claim"&&claimProj) mainContent=<CreateFindMeetingScreen proj={claimProj} profile={profile} onBack={()=>{setClaimProj(null);setScreen("main");}} onClaim={handleClaimDone}/>;
  else if(screen==="solo-topic"&&claimProj) mainContent=<SoloTopicPickerScreen proj={claimProj} profile={profile} onBack={()=>{setClaimProj(null);setScreen("main");}} onPick={topic=>makeSoloTask(claimProj,topic)}/>;
  else if(screen==="contact"&&contactTask) mainContent=<ContactScreen task={contactTask} onRecorder={handleRecorder} onBack={()=>contactTask?.solo?setScreen("main"):setScreen("recordings")}/>;
  else if(screen==="recorder"&&contactTask) mainContent=<RecorderScreen key={recorderKey} task={contactTask} myName={profile.name} myInitials={profile.initials} onBack={()=>setScreen("contact")} onFinish={handleRecorderDone} onFinishAndEnd={handleRecorderEndSession} onFinishAndRecordAgain={(newTopic)=>{setContactTask(prev=>({...prev,topic:newTopic}));setRecorderKey(k=>k+1);}} batchTopics={allProjects.find(p=>p.id===contactTask.projId)?.topics||[contactTask.topic]} isUnscripted={!contactTask?.solo&&!contactTask?.scripted}/>;
  else if(screen==="record-again"&&contactTask) mainContent=<RecordAgainScreen task={contactTask} batchTopics={allProjects.find(p=>p.id===contactTask.projId)?.topics||[contactTask.topic]} onRecordAgain={(newTopic)=>{setContactTask(prev=>({...prev,topic:newTopic}));setScreen("recorder");}} onDone={()=>{if(contactTask?.partner){setScreen("post-recording");}else{setContactTask(null);setScreen("recordings");}}}/>;
  else if(screen==="post-recording"&&contactTask) mainContent=<PostRecordingScreen task={contactTask} onDone={()=>{setContactTask(null);setScreen("recordings");}}/>;
  else if(screen==="recordings"&&isCreator) mainContent=<MyRecordingsScreen onContact={handleContact} taskList={taskList} invitations={invitations} setInvitations={setInvitations} acceptedInv={acceptedInv} setAcceptedInv={setAcceptedInv}/>;
  else if(screen==="partners"&&isCreator) mainContent=<MyPartnersScreen buddyRequests={buddyRequests} onBuddyRequestsChange={setBuddyRequests} discoverable={discoverable} onGoToProfile={()=>setScreen("profile")}/>;
  else if(screen==="tasks"&&isReviewer) mainContent=<ReviewerScreen profile={profile}/>;
  else if(screen==="history") mainContent=<HistoryScreen kinds={isCreator&&isReviewer?["create","review"]:isReviewer?["review"]:["create"]}/>;
  else if(screen==="ops-review"&&isOps) mainContent=<OpsReviewerBatchesScreen/>;
  else if(screen==="task-mgmt"&&isOps) mainContent=<OpsTaskManagementScreen/>;
  else if(screen==="orchestration"&&isOps) mainContent=<OrchestrationScreen/>;
  else if(screen==="ops-projects"&&isOps) mainContent=<OpsProjectsScreen recordingBatches={allProjects}/>;
  else if(screen==="rate-mgmt"&&isAdmin) mainContent=<RateManagementScreen/>;
  else if(screen==="notifications"&&isOps) mainContent=<NotificationsScreen notifs={opsNotifs} setNotifs={setOpsNotifs} onNavigate={(action)=>setScreen(action)}/>;
  else if(screen==="notifications") mainContent=<NotificationsScreen notifs={creatorNotifs} setNotifs={setCreatorNotifs} onNavigate={(action)=>setScreen(action)}/>;
  else if(isOps) mainContent=<OpsRecordingProjectsScreen projects={allProjects} onAddProject={p=>{setAllProjects(prev=>[...prev,p]);}} />;
  else if(isReviewer&&!isCreator) mainContent=<ReviewerScreen profile={profile} tasks={isQaReviewer?qaReviewTasks:undefined}/>;
  else mainContent=<CreatorScreen profile={profile} onClaim={handleClaim} projects={allProjects}/>;

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100vh",fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",fontSize:14,color:TEXT_PRIMARY,background:SURFACE_ALT}}>
      {/* Live invite notification — shown when user navigates to profile as a demo trigger */}
      {showInviteNotif&&<div style={{position:"fixed",inset:0,background:OVERLAY_MAX,display:"flex",alignItems:"center",justifyContent:"center",zIndex:500}}>
        <div style={{background:SURFACE,borderRadius:16,padding:28,width:420,boxShadow:"0 16px 48px rgba(0,0,0,0.25)"}}>
          {notifPhase==="later"
            ?<>
              <div style={{textAlign:"center",marginBottom:20}}>
                <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>No problem!</div>
                <p style={{fontSize:13,color:TEXT_MUTED,lineHeight:1.6,margin:0}}>You can find this invitation in <strong>My Recordings</strong> whenever you're ready to respond.</p>
              </div>
              <button onClick={()=>{setShowInviteNotif(false);setNotifPhase(null);}} style={{width:"100%",padding:"11px",borderRadius:9,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Got it</button>
            </>
            :<>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                <div style={{width:48,height:48,borderRadius:"50%",background:PL,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:16,color:P,flexShrink:0}}>PN</div>
                <div>
                  <div style={{fontWeight:700,fontSize:16,marginBottom:2}}>Priya N. invited you to record</div>
                  <div style={{fontSize:12,color:TEXT_SUBTLE}}>Just now</div>
                </div>
              </div>
              <div style={{background:SURFACE_SUNKEN,border:`1px solid ${BORDER}`,borderRadius:10,padding:"12px 14px",marginBottom:20}}>
                <div style={{fontSize:13,fontWeight:500,color:TEXT_SECONDARY,marginBottom:3}}>French Medical Dialogues · Healthcare</div>
                <div style={{fontSize:12,color:TEXT_MUTED}}>⏱ 30–45 min · Deadline: Apr 30, 2025</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setAcceptedInv(prev=>({...prev,"INV-005":true}));setShowInviteNotif(false);setNotifPhase(null);setScreen("recordings");}} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:P,color:SURFACE,fontSize:13,fontWeight:600,cursor:"pointer"}}>Accept</button>
                <button onClick={()=>setShowInviteNotif(false)} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${DANGER_BORDER}`,background:SURFACE,color:DANGER,fontSize:13,fontWeight:500,cursor:"pointer"}}>Decline</button>
                <button onClick={()=>setNotifPhase("later")} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${BORDER}`,background:SURFACE,color:TEXT_MUTED,fontSize:13,cursor:"pointer"}}>Decide Later</button>
              </div>
            </>
          }
        </div>
      </div>}
      <Switcher role={role} setRole={r=>{setRole(r);setScreen("main");setClaimProj(null);setContactTask(null);}}/>
      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        <Sidebar nav={nav} user={user} onProfile={()=>{setScreen("profile");if(isCreator)setShowInviteNotif(true);}}/>
        {mainContent}
      </div>
    </div>
  );
}
